/* ===== app-utils.js ===== */
const { useState, useEffect, useCallback, useRef, useMemo } = React;
window.useState = useState; window.useEffect = useEffect; window.useCallback = useCallback; window.useRef = useRef; window.useMemo = useMemo;

// System-wide error reporting. Posts to /api/errors/log fire-and-forget;
// the server dedupes (10-min, per-hash+user) and rate-caps (20/hr/subscriber)
// before sending the system_error alert email. Safe to call from anywhere.
window.__reportError = function(info) {
  try {
    // Rate-limit (429) and auth (401) failures are operational noise, not bugs — and each
    // report BURNS the same per-IP request budget that's already exhausted during a 429
    // storm (2026-07-13 lockout: logged-out restored tabs + their /errors/log posts starved
    // the login endpoint). Skip them, and cap reporting at one post per 5s per tab — the
    // __reportingError flag below only stops CONCURRENT posts, not rapid sequential ones.
    var sc = info && info.status_code;
    if (sc === 429 || sc === 401) return;
    var now = Date.now();
    if (window.__lastErrorReportAt && (now - window.__lastErrorReportAt) < 5000) return;
    var body = {
      area: info && info.area || window.__currentPage || (location.hash || location.pathname || 'unknown'),
      source: info && info.source || 'client',
      message: info && info.message != null ? String(info.message) : '',
      stack: info && info.stack ? String(info.stack) : null,
      url: location.href,
      method: info && info.method || null,
      status_code: info && info.status_code || null,
    };
    if (window.__reportingError) return;
    window.__reportingError = true;
    window.__lastErrorReportAt = now;
    fetch('/api/errors/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'same-origin',
      body: JSON.stringify(body),
    }).catch(function() { /* reporting is best-effort — a failed report must never become its own unhandled rejection */ })
      .finally(function() { window.__reportingError = false; });
  } catch (e) { /* swallow — never let error reporting break the UI */ }
};

window.addEventListener('error', function(ev) {
  if (ev && ev.target && ev.target !== window) return; // skip resource-load failures
  window.__reportError({
    source: 'unhandled',
    message: ev && ev.message ? ev.message : 'window.onerror',
    stack: ev && ev.error && ev.error.stack ? ev.error.stack : null,
  });
});

window.addEventListener('unhandledrejection', function(ev) {
  var reason = ev && ev.reason;
  var msg = reason && reason.message ? reason.message : String(reason || 'unhandledrejection');
  var stack = reason && reason.stack ? reason.stack : null;
  var info = { source: 'unhandled', message: msg, stack: stack };
  // If thrown by api() / apiUpload(), surface which call failed so error_log
  // captures the endpoint, method, and status — minified stacks alone don't.
  if (reason && reason.apiPath) {
    info.method = reason.apiMethod;
    info.status_code = reason.apiStatus;
    info.message = reason.apiMethod + ' ' + reason.apiPath + ' → ' + msg;
  }
  window.__reportError(info);
});

// Chart.js hover fix under the app-wide body zoom (index.html). Chrome reports
// MouseEvent offsetX/offsetY in visual (zoomed) px, but Chart.js hit-tests them against
// its layout-px canvas size, so tooltips/highlights land ~25% right/below the cursor and
// the far edge of every chart goes dead. Normalize offsets on canvas-targeted mouse
// events in the capture phase, before Chart.js's own canvas listeners read them:
// local = (client − rect) × (layoutSize / visualSize). Self-measuring (no APP_ZOOM
// dependency) and a no-op at zoom 1 or on browsers that already report layout px.
(function() {
  function fixCanvasOffsets(e) {
    var t = e.target;
    if (!t || t.tagName !== 'CANVAS') return;
    var rect = t.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    var sx = t.offsetWidth / rect.width, sy = t.offsetHeight / rect.height;
    if (Math.abs(sx - 1) < 0.001 && Math.abs(sy - 1) < 0.001) return;
    var ox = (e.clientX - rect.left) * sx, oy = (e.clientY - rect.top) * sy;
    Object.defineProperty(e, 'offsetX', { get: function() { return ox; }, configurable: true });
    Object.defineProperty(e, 'offsetY', { get: function() { return oy; }, configurable: true });
  }
  ['mousemove', 'mouseout', 'click', 'mousedown', 'mouseup'].forEach(function(type) {
    document.addEventListener(type, fixCanvasOffsets, true);
  });
})();

// APP_ZOOM self-heal: the global is inlined in index.html, but a session running a
// STALE cached index.html (served after the body-zoom CSS landed but before the global
// did) gets the CURRENT bundle with APP_ZOOM undefined — every ÷APP_ZOOM popover
// compensation then no-ops and fixed menus land ~25% down/right of their button.
// Derive it from the computed body zoom so the bundle never depends on the HTML copy.
if (!window.APP_ZOOM) {
  var _bodyZoom = parseFloat(getComputedStyle(document.body).zoom);
  window.APP_ZOOM = (_bodyZoom && isFinite(_bodyZoom)) ? _bodyZoom : 1;
}

// Render chart canvases at visual resolution: with the body zoomed 1.25×, Chart.js's
// default backing store (layout px × devicePixelRatio) gets upscaled by the zoom and
// text goes soft. Hit-testing is unaffected (Chart.js divides canvas.width back out).
if (window.Chart) {
  Chart.defaults.devicePixelRatio = (window.devicePixelRatio || 1) * (window.APP_ZOOM || 1);
}

// React error boundary — catches render/lifecycle crashes in the wrapped subtree,
// reports the REAL error + React componentStack (immune to the cross-origin "Script error."
// mask), and shows a non-fatal fallback instead of white-screening the app. Wrapped
// per-page and keyed on the route, so navigating away clears a tripped boundary.
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, message: null };
    this.handleRetry = this.handleRetry.bind(this);
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, message: (error && error.message) ? error.message : 'Unexpected error' };
  }
  componentDidCatch(error, info) {
    try {
      var cs = (info && info.componentStack) ? info.componentStack : '';
      var stk = (error && error.stack ? error.stack : String(error)) + (cs ? '\n--- React componentStack ---' + cs : '');
      if (window.__reportError) window.__reportError({
        source: 'unhandled',
        area: this.props.area || window.__currentPage || 'unknown',
        message: (error && error.message) ? error.message : String(error),
        stack: stk,
      });
    } catch (e) { /* the boundary's own reporting must never throw */ }
  }
  handleRetry() { this.setState({ hasError: false, message: null }); }
  render() {
    if (!this.state.hasError) return this.props.children;
    var btn = { padding: '8px 16px', borderRadius: 8, border: '1px solid #2A2E3A', cursor: 'pointer', fontFamily: 'inherit', fontSize: 14, fontWeight: 600 };
    return React.createElement('div', { style: { padding: 24, maxWidth: 560, margin: '40px auto', background: '#171A23', border: '1px solid #2A2E3A', borderRadius: 12, color: '#E8EAF0' } },
      React.createElement('div', { style: { fontSize: 18, fontWeight: 700, marginBottom: 8 } }, 'Something went wrong on this page'),
      React.createElement('div', { style: { fontSize: 14, color: '#9AA0B5', marginBottom: 18, wordBreak: 'break-word' } }, this.state.message || 'An unexpected error occurred. The team has been notified.'),
      React.createElement('div', { style: { display: 'flex', gap: 10 } },
        React.createElement('button', { onClick: this.handleRetry, style: Object.assign({}, btn, { background: '#4F6EF7', borderColor: '#4F6EF7', color: '#fff' }) }, 'Try again'),
        React.createElement('button', { onClick: function() { location.reload(); }, style: Object.assign({}, btn, { background: 'transparent', color: '#E8EAF0' }) }, 'Reload page')
      )
    );
  }
}
window.ErrorBoundary = ErrorBoundary;

function LIcon({ name, size, color, spin, style: extraStyle }) {
  var pascal = name.split('-').map(function(s) { return s.charAt(0).toUpperCase() + s.slice(1); }).join('');
  var Icon = window.LucideReact && window.LucideReact[pascal];
  if (!Icon) return null;
  var s = spin ? { animation: 'spin 1s linear infinite', ...(extraStyle || {}) } : extraStyle;
  return React.createElement(Icon, { size: size || 16, color: color, style: s });
}
window.LIcon = LIcon;

// Custom hover tooltip that renders as real DOM (not the native `title`
// overlay). Visible in screen captures and streams; doesn't disappear on
// modifier-key press. Use this in place of `title=` whenever the user
// might need to screenshot or share the tooltip content.
function Tooltip({ text, children, style, wrapperStyle }) {
  var [visible, setVisible] = useState(false);
  var [pos, setPos] = useState({ x: 0, y: 0 });
  if (!text) return React.createElement('span', { style: style }, children);
  function show(e) {
    var rect = e.currentTarget.getBoundingClientRect();
    // gBCR returns visual px; fixed-position styles on the zoomed body are in zoomed px.
    var z = window.APP_ZOOM || 1;
    setPos({ x: (rect.left + rect.width / 2) / z, y: rect.top / z });
    setVisible(true);
  }
  function hide() { setVisible(false); }
  var spanStyle = Object.assign({ display: 'inline-block', position: 'relative' }, style || {});
  return React.createElement('span', { onMouseEnter: show, onMouseLeave: hide, onFocus: show, onBlur: hide, style: spanStyle },
    children,
    visible && React.createElement('div', {
      role: 'tooltip',
      style: Object.assign({
        position: 'fixed',
        top: pos.y - 8, left: pos.x,
        transform: 'translate(-50%, -100%)',
        background: '#1F2937', color: '#F9FAFB',
        padding: '8px 12px', borderRadius: 6,
        border: '1px solid #374151',
        fontSize: 12, lineHeight: 1.5,
        whiteSpace: 'pre-line', textAlign: 'left',
        maxWidth: 360, minWidth: 60,
        zIndex: 10000, pointerEvents: 'none',
        boxShadow: '0 4px 12px rgba(0,0,0,0.4)',
        fontWeight: 400,
      }, wrapperStyle || {})
    }, text)
  );
}
window.Tooltip = Tooltip;

const API_BASE = '/api';

var themes = {
  dark: {
    bg: '#0F1117', surface: '#1A1D27', surfaceHover: '#22252F',
    border: '#2A2D3A', borderFocus: '#4F6EF7', cardShadow: 'none',
    text: '#E8EAF0', textMuted: '#8B8FA3', textDim: '#5C6078',
    accent: '#4F6EF7', accentHover: '#3D5CE5', accentSoft: 'rgba(79,110,247,0.12)',
    success: '#34D399', successSoft: 'rgba(52,211,153,0.12)',
    warning: '#FBBF24', warningSoft: 'rgba(251,191,36,0.12)',
    danger: '#F87171', dangerSoft: 'rgba(248,113,113,0.12)',
    purple: '#A78BFA', purpleSoft: 'rgba(167,139,250,0.12)',
  },
  light: {
    bg: '#F6F7F9', surface: '#FFFFFF', surfaceHover: '#F3F4F6',
    border: '#E5E7EB', borderFocus: '#4F6EF7', cardShadow: '0 1px 3px rgba(16,24,40,0.06), 0 1px 2px rgba(16,24,40,0.04)',
    text: '#111827', textMuted: '#4B5563', textDim: '#6B7280',
    accent: '#4F6EF7', accentHover: '#3D5CE5', accentSoft: 'rgba(79,110,247,0.10)',
    success: '#059669', successSoft: 'rgba(5,150,105,0.10)',
    warning: '#D97706', warningSoft: 'rgba(217,119,6,0.10)',
    danger: '#DC2626', dangerSoft: 'rgba(220,38,38,0.10)',
    purple: '#7C3AED', purpleSoft: 'rgba(124,58,237,0.10)',
  }
};

// ─── SIDENAV: shared sidebar style kit ───
// Single source of truth for the left nav across ALL shells (main SPA, ads.html,
// ship.html). The bundles concatenate app-utils.js first with shared top-level
// scope, so every shell must reference these instead of hand-copied style
// objects — copies drifted apart until normalized 2026-08-11. The sidebar is
// deliberately light-themed in every shell (hardcoded colors, not C.*).
// Sidebar widths — defined ONCE here; anything that positions against the
// sidebar (full-screen product/vendor editor overlays) must use these, never a literal.
var SIDENAV_NAV_W = 216;   // pinned sidebar + hover drawer (216 so 15px 'FBA Reconciliation' fits on one line)
var SIDENAV_RAIL_W = 56;   // unpinned icon rail
var SIDENAV = {
  ICON: 16,        // icon size in nav rows
  CHEVRON: 14,     // chevron-down icon size
  NAV_W: SIDENAV_NAV_W,
  RAIL_W: SIDENAV_RAIL_W,
  nav: { width: SIDENAV_NAV_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 10px', display: 'flex', flexDirection: 'column', flexShrink: 0, overflow: 'hidden', zIndex: 10 },
  rail: { width: SIDENAV_RAIL_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 6px', display: 'flex', flexDirection: 'column', alignItems: 'center', flexShrink: 0, overflow: 'hidden', zIndex: 10 },
  drawer: function(hover) { return { position: 'fixed', top: 0, left: 0, bottom: 0, width: SIDENAV_NAV_W, background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '16px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1100, boxShadow: hover ? '4px 0 16px rgba(0,0,0,0.1)' : 'none', opacity: hover ? 1 : 0, transition: 'opacity 0.15s ease, box-shadow 0.15s ease', pointerEvents: hover ? 'auto' : 'none' }; },
  logoBox: { padding: '0 12px 12px', borderBottom: '1px solid #E5E7EB', marginBottom: 10, textAlign: 'center', minHeight: 44, display: 'flex', alignItems: 'center', justifyContent: 'center' },
  logoImg: { height: 44, maxWidth: '100%', objectFit: 'contain', display: 'block' },
  railLogoBox: { padding: '0 0 12px', borderBottom: '1px solid #E5E7EB', marginBottom: 10, width: '100%', textAlign: 'center', display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 44 },
  railLogoImg: { width: 44, height: 44, objectFit: 'contain', borderRadius: 6 },
  sectionLabel: { padding: '0 10px 8px', fontSize: 10, fontWeight: 500, color: '#9CA3AF', textTransform: 'uppercase', letterSpacing: 0.6 },
  scroller: { flex: 1, overflowY: 'auto', overflowX: 'hidden', minHeight: 0 },
  railList: { flex: 1, display: 'flex', flexDirection: 'column', width: '100%' },
  item: function(active) { return { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 9px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 15, lineHeight: '21px', fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 3, background: active ? 'rgba(79,110,247,0.10)' : 'transparent', color: active ? '#4F6EF7' : '#374151', transition: 'background 0.2s', textDecoration: 'none' }; },
  // minHeight 31 = item's 21px line + 10px padding, so a collapsed rail row sits on the same 34px pitch as an expanded row (9-05)
  railItem: function(active) { return { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0', minHeight: 31, boxSizing: 'border-box', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%', marginBottom: 3, background: active ? 'rgba(79,110,247,0.10)' : 'transparent', color: active ? '#4F6EF7' : '#374151', textDecoration: 'none' }; },
  chevron: function(expanded) { return { fontSize: 10, color: '#9CA3AF', transition: 'transform 0.3s cubic-bezier(0.4,0,0.2,1)', transform: expanded ? 'rotate(180deg)' : 'rotate(0deg)', padding: '2px 4px', cursor: 'pointer', display: 'flex', alignItems: 'center' }; },
  childWrap: function(expanded) { return { overflow: 'hidden', transition: 'max-height 0.3s cubic-bezier(0.4,0,0.2,1), margin 0.3s cubic-bezier(0.4,0,0.2,1)', maxHeight: expanded ? 500 : 0, paddingLeft: 18, marginBottom: expanded ? 4 : 0 }; },
  child: function(active) { return { display: 'flex', alignItems: 'center', gap: 7, padding: '4px 9px', border: 'none', borderRadius: 6, cursor: 'pointer', fontSize: 14, fontWeight: active ? 600 : 400, fontFamily: 'inherit', width: '100%', textAlign: 'left', marginBottom: 2, background: active ? 'rgba(79,110,247,0.08)' : 'transparent', color: active ? '#4F6EF7' : '#4B5563', borderLeft: active ? '2px solid #4F6EF7' : '2px solid transparent', textDecoration: 'none' }; },
  bottomWrap: { borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 8 },
  railBottomWrap: { borderTop: '1px solid #E5E7EB', paddingTop: 8, marginTop: 8, width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' },
  bottomBtn: function(opts) { opts = opts || {}; return { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 500, fontFamily: 'inherit', width: '100%', textAlign: 'left', background: 'transparent', color: opts.active ? '#4F6EF7' : '#374151', textDecoration: 'none', marginTop: opts.topGap ? 4 : 0 }; },
  railBottomBtn: function(opts) { opts = opts || {}; return { display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '5px 0', border: 'none', borderRadius: 8, cursor: 'pointer', width: '100%', background: 'transparent', color: opts.active ? '#4F6EF7' : '#374151', textDecoration: 'none', marginTop: opts.topGap ? 4 : 0 }; },
  divider: { borderTop: '1px solid #E5E7EB', margin: '10px 0' },
  // the "opens a new tab" arrow (arrow-up-right, 13px) at the right edge of a sidebar row that is a
  // real target=_blank link: Customer Service / Advertising / Shipping in the main app, AtharvaToolz
  // in the three sub-app shells (Anthony 2026-10-13)
  newTab: { color: '#9CA3AF', flexShrink: 0, marginLeft: 'auto' },
};
// Hover/transition rules for the kit's rows — one source for the main SPA, ads and shipping
// shells (ads had no hover at all before 9-05). Rows opt in with className side-nav-btn /
// side-nav-child / side-bottom-btn.
(function() {
  if (typeof document === 'undefined' || document.getElementById('sidenav-kit-styles')) return;
  var st = document.createElement('style');
  st.id = 'sidenav-kit-styles';
  st.textContent =
    '.side-nav-btn{transition:background 0.15s ease!important}' +
    '.side-nav-btn:hover{background:rgba(79,110,247,0.06)!important}' +
    '.side-nav-child{transition:background 0.15s ease!important}' +
    '.side-nav-child:hover{background:rgba(79,110,247,0.06)!important}' +
    '.side-bottom-btn{transition:background 0.15s ease!important}' +
    '.side-bottom-btn:hover{background:rgba(0,0,0,0.04)!important}';
  document.head.appendChild(st);
})();

// ─── Responsive: phones and tablets (UX review item 1, 2026-09-27) ───
// Below VIEWPORT_NARROW_MAX every shell drops the pinned sidebar / 56px icon rail for a 48px
// top bar + hamburger drawer (NarrowNav below), and pages stack their card grids. Widths are
// the CSS px the LAYOUT sees: window.innerWidth is visual px and the body is zoomed by
// APP_ZOOM (index.html), so layout width = innerWidth / zoom. Wide (>= 900) renders exactly
// as before — every narrow branch is behind isNarrow, and the rg-* classes below only act
// under html.vp-narrow.
var VIEWPORT_NARROW_MAX = 900;
var VIEWPORT_PHONE_MAX = 600;
function viewportFlags(innerWidth, zoom) {
  var z = (typeof zoom === 'number' && isFinite(zoom) && zoom > 0) ? zoom : 1;
  // no usable width (hidden iframe, test harness): assume a desktop so nothing changes
  var w = (typeof innerWidth === 'number' && isFinite(innerWidth) && innerWidth > 0) ? innerWidth / z : 1280;
  return { width: w, isNarrow: w < VIEWPORT_NARROW_MAX, isPhone: w < VIEWPORT_PHONE_MAX };
}
function _readViewport() {
  return viewportFlags(typeof window !== 'undefined' ? window.innerWidth : 0, typeof window !== 'undefined' ? (window.APP_ZOOM || 1) : 1);
}
// One module-level listener (resize + orientationchange, throttled to 150 ms) feeds every
// useViewport() and keeps html.vp-narrow / html.vp-phone in sync for the CSS helpers.
var _vpState = _readViewport();
var _vpSubs = [];
function _vpApply() {
  var n = _readViewport();
  var changed = n.isNarrow !== _vpState.isNarrow || n.isPhone !== _vpState.isPhone;
  _vpState = n;
  try {
    var cl = document.documentElement.classList;
    cl.toggle('vp-narrow', n.isNarrow);
    cl.toggle('vp-phone', n.isPhone);
  } catch (e) {}
  if (changed) _vpSubs.slice().forEach(function(fn) { try { fn(n); } catch (e) {} });
}
(function() {
  if (typeof window === 'undefined' || !window.addEventListener) return;
  var timer = null;
  var onChange = function() {
    if (timer) return;
    timer = setTimeout(function() { timer = null; _vpApply(); }, 150);
  };
  window.addEventListener('resize', onChange);
  window.addEventListener('orientationchange', onChange);
  _vpApply();
})();
// Re-renders only when a breakpoint flag flips (not on every pixel of a resize drag), so
// `width` is the layout width at the last flip — use the flags for layout decisions.
function useViewport() {
  var _s = useState(_vpState); var vp = _s[0], setVp = _s[1];
  useEffect(function() {
    var fn = function(n) { setVp(n); };
    _vpSubs.push(fn);
    if (_vpState.isNarrow !== vp.isNarrow || _vpState.isPhone !== vp.isPhone) setVp(_vpState);
    return function() { var i = _vpSubs.indexOf(fn); if (i !== -1) _vpSubs.splice(i, 1); };
  }, []);
  return vp;
}
window.viewportFlags = viewportFlags;
window.useViewport = useViewport;
// Non-hook read for code that positions against the sidebar (full-screen editors): on narrow
// widths there is no sidebar, so they start at left 0.
function isNarrowViewport() { return !!_vpState.isNarrow; }
window.isNarrowViewport = isNarrowViewport;

// Top-bar title from document.title ("AtharvaToolz | PO Detail" → "PO Detail",
// "Batch 12 · AtharvaToolz Shipping" → "Batch 12").
function shortAppTitle(t) {
  var s = String(t || '').replace(/^AtharvaToolz\s*\|\s*/, '').replace(/\s*·\s*AtharvaToolz\b.*$/, '');
  if (/^AtharvaToolz\b/.test(s)) s = s.replace(/^AtharvaToolz\s*/, '');
  return s || 'AtharvaToolz';
}
function useDocTitle() {
  var _t = useState(function() { return typeof document !== 'undefined' ? document.title : ''; });
  var title = _t[0], setTitle = _t[1];
  useEffect(function() {
    var sync = function() { setTitle(document.title); };
    sync();
    var el = document.querySelector('title');
    if (!el || typeof MutationObserver === 'undefined') return;
    var mo = new MutationObserver(sync);
    mo.observe(el, { childList: true, characterData: true, subtree: true });
    return function() { mo.disconnect(); };
  }, []);
  return title;
}

// Narrow-width chrome shared by every shell: a 48px top bar (hamburger "Open Menu", the page
// title, the shell's own top-area controls in `right`) and the full sidebar content as a
// slide-in drawer over a dimmed backdrop. Backdrop tap, Escape, a tapped link, or any change of
// `navKey` (= navigation) closes it. The pin preference is never touched here.
var NARROW_TOPBAR_H = 48;
function narrowDrawerStyle() {
  return { position: 'fixed', top: 0, left: 0, bottom: 0, width: 288, maxWidth: 'calc(86vw * var(--invzoom, 1))', background: '#FFFFFF', borderRight: '1px solid #E5E7EB', padding: '14px 10px', display: 'flex', flexDirection: 'column', overflow: 'hidden', zIndex: 1160, boxShadow: '6px 0 24px rgba(0,0,0,0.25)', boxSizing: 'border-box', animation: 'narrowDrawerIn 0.18s ease' };
}
function NarrowNav(props) {
  var _o = useState(false); var open = _o[0], setOpen = _o[1];
  var docTitle = useDocTitle();
  useEffect(function() { setOpen(false); }, [props.navKey]);
  useEffect(function() {
    if (!open) return;
    var onKey = function(e) { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return function() { document.removeEventListener('keydown', onKey); };
  }, [open]);
  var close = function() { setOpen(false); };
  var title = props.title || shortAppTitle(docTitle);
  return React.createElement(React.Fragment, null,
    React.createElement('header', { className: 'narrow-topbar', style: { height: NARROW_TOPBAR_H, minHeight: NARROW_TOPBAR_H, display: 'flex', alignItems: 'center', gap: 6, padding: '0 8px 0 4px', background: C.surface, borderBottom: '1px solid ' + C.border, flexShrink: 0, boxSizing: 'border-box', position: 'relative', zIndex: 20 } },
      React.createElement('button', { type: 'button', 'aria-label': 'Open Menu', 'aria-expanded': open, onClick: function() { setOpen(true); },
        style: { width: 44, height: 44, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: C.text, padding: 0, flexShrink: 0 } },
        React.createElement(LIcon, { name: 'menu', size: 22 })),
      React.createElement('div', { style: { flex: 1, minWidth: 0, fontSize: 16, fontWeight: 600, color: C.text, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' } }, title),
      props.right || null),
    open && React.createElement('div', { className: 'narrow-backdrop', onClick: close, 'aria-hidden': true, style: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1150 } }),
    open && React.createElement('nav', { className: 'narrow-drawer', role: 'dialog', 'aria-modal': true, 'aria-label': 'Menu',
      // a tapped link navigates (or opens a new tab): close; group headers are buttons and stay open
      onClickCapture: function(e) { var a = e.target && e.target.closest ? e.target.closest('a[href]') : null; if (a) setOpen(false); },
      style: narrowDrawerStyle() },
      React.createElement('button', { type: 'button', 'aria-label': 'Close Menu', onClick: close,
        style: { position: 'absolute', top: 8, right: 8, width: 36, height: 36, display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', borderRadius: 8, cursor: 'pointer', color: '#6B7280', zIndex: 2 } },
        React.createElement(LIcon, { name: 'x', size: 18 })),
      props.drawer));
}
window.NarrowNav = NarrowNav;
window.shortAppTitle = shortAppTitle;
// Style of a shell's <main> on narrow widths: full width, 12px sides, no horizontal page
// scroll (tables keep their own scroll containers).
function narrowMainStyle(extra) {
  return Object.assign({ flex: 1, minWidth: 0, minHeight: 0, width: '100%', padding: 12, boxSizing: 'border-box', overflowX: 'hidden', overflowY: 'auto' }, extra || {});
}
window.narrowMainStyle = narrowMainStyle;
(function() {
  if (typeof document === 'undefined' || document.getElementById('narrow-layout-styles')) return;
  var st = document.createElement('style');
  st.id = 'narrow-layout-styles';
  st.textContent =
    '@keyframes narrowDrawerIn{from{transform:translateX(-100%)}to{transform:translateX(0)}}' +
    // touch targets: every drawer row at least 40px tall
    '.narrow-drawer .side-nav-btn,.narrow-drawer .side-nav-child,.narrow-drawer .side-bottom-btn,.narrow-drawer .snav{min-height:40px!important;box-sizing:border-box}' +
    // open groups: the 500px max-height cap (SIDENAV.childWrap) would clip taller 40px rows
    '.narrow-drawer div[style*="max-height: 500px"]{max-height:none!important}' +
    // page helpers — only under html.vp-narrow, so wide layouts are untouched
    '.vp-narrow .rg-stack{grid-template-columns:minmax(0,1fr)!important}' +
    '.vp-narrow .rg-2x2{grid-template-columns:repeat(2,minmax(0,1fr))!important}' +
    '.vp-narrow .rg-wrap{flex-wrap:wrap!important}' +
    '.vp-narrow .rg-scrollx{overflow-x:auto!important;overflow-y:hidden!important}' +
    // tabs keep their width and their underline (the -2px overlap would be clipped by the scroller)
    '.vp-narrow .rg-scrollx>*{flex-shrink:0!important;white-space:nowrap!important;margin-bottom:0!important}';
  document.head.appendChild(st);
})();

// C is a mutable reference that always points to current theme
var C = Object.assign({}, themes.dark);
var _currentTheme = 'dark';

function setThemeColors(name) {
  _currentTheme = name;
  // Expose the active theme on <html> so plain CSS can adapt (e.g. scrollbar colors: white in
  // dark mode, dark in light mode).
  try { document.documentElement.setAttribute('data-theme', name); } catch (e) {}
  var t = themes[name] || themes.dark;
  Object.keys(t).forEach(function(k) { C[k] = t[k]; });
  // Update statusColors in-place
  var sc = {
    draft: { bg: C.warningSoft, text: C.warning },
    confirmed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
    shipped: { bg: C.accentSoft, text: C.accent },
    'checked-in': { bg: C.purpleSoft, text: C.purple },
    received: { bg: C.successSoft, text: C.success },
    closed: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted },
    cancelled: { bg: C.dangerSoft, text: C.danger },
  };
  Object.keys(sc).forEach(function(k) { statusColors[k] = sc[k]; });
  _updateDtStyles();
}

// Inject interactive styles for DataTable hover/focus effects
var _dtStyleEl = document.createElement('style');
_dtStyleEl.id = 'dt-interactive-styles';
document.head.appendChild(_dtStyleEl);
function _updateDtStyles() {
  _dtStyleEl.textContent = [
    '.dt-input:focus { border-color: ' + C.accent + ' !important; box-shadow: 0 0 0 2px ' + C.accentSoft + ' !important; outline: none; }',
    '.dt-select:hover, .dt-btn:hover { border-color: ' + C.accent + ' !important; background: ' + C.surfaceHover + ' !important; }',
    '.dt-select:focus { border-color: ' + C.accent + ' !important; box-shadow: 0 0 0 2px ' + C.accentSoft + ' !important; outline: none; }',
    '.dt-row-btn:hover { background: ' + C.surfaceHover + ' !important; }',
    '.dt-th:hover { color: ' + C.accent + ' !important; }',
    '.dt-filter-btn:hover { background: ' + C.surfaceHover + ' !important; opacity: 1 !important; }',
    '.filter-pill:hover { background: ' + C.accentSoft + ' !important; border-color: ' + C.accent + ' !important; }',
  ].join('\n');
}
_updateDtStyles();

function getThemeName() { return _currentTheme; }

function getStatusColors() {
  return {
    draft: { bg: C.warningSoft, text: C.warning },
    submitted: { bg: 'rgba(59,130,246,0.12)', text: '#3B82F6' },
    approved: { bg: 'rgba(34,197,94,0.12)', text: '#22C55E' },
    confirmed: { bg: 'rgba(20,184,166,0.12)', text: '#14B8A6' },
    next: { bg: 'rgba(6,182,212,0.12)', text: '#06B6D4' },
    shipped: { bg: C.accentSoft, text: C.accent },
    'checked-in': { bg: C.purpleSoft, text: C.purple },
    receiving: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' },
    received: { bg: C.successSoft, text: C.success },
    closed: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted },
    cancelled: { bg: C.dangerSoft, text: C.danger },
  };
}

var statusColors = getStatusColors();

// Auth token management (cookie-based — kept for backward compat during transition)
var _authToken = null;
// Auth is cookie-based (httpOnly po_session, sent via credentials:'include'). The token is
// kept in-memory only — never in localStorage, where XSS could exfiltrate it. We also clear
// any token persisted by older builds.
function setAuthToken(token) { _authToken = token; try { localStorage.removeItem('po_token'); } catch (e) {} }
function getAuthToken() { return _authToken; }

async function api(path, options = {}) {
  var method = (options && options.method) || 'GET';
  var headers = { 'Content-Type': 'application/json', ...options.headers };
  const res = await fetch(`${API_BASE}${path}`, { ...options, headers, credentials: 'include' });
  if (res.status === 401) {
    setAuthToken(null); window.location.reload();
    // reload() is async — never resolve, so pending .then(setState) chains can't run
    // with undefined during the teardown window (crashed the PO list — error_log 295).
    return new Promise(function() {});
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({ error: res.statusText }));
    const err = new Error(errBody.error || 'Request failed');
    err.apiPath = path; err.apiMethod = method; err.apiStatus = res.status;
    err.apiBody = errBody;
    throw err;
  }
  return res.json();
}

async function apiUpload(path, file) {
  const form = new FormData();
  form.append('file', file);
  const res = await fetch(`${API_BASE}${path}`, { method: 'POST', body: form, credentials: 'include' });
  if (res.status === 401) {
    setAuthToken(null); window.location.reload();
    // reload() is async — never resolve, so pending .then(setState) chains can't run
    // with undefined during the teardown window (crashed the PO list — error_log 295).
    return new Promise(function() {});
  }
  if (!res.ok) {
    const errBody = await res.json().catch(() => ({}));
    const err = new Error(errBody.error || 'Upload failed');
    err.apiPath = path; err.apiMethod = 'POST'; err.apiStatus = res.status;
    throw err;
  }
  return res.json();
}

// Permission helpers
function hasPermission(user, area, level) {
  if (!user) return false;
  if (user.role === 'admin') return true;
  var perms = user.permissions;
  if (!perms || !perms[area]) return false;
  return !!perms[area][level || 'read'];
}
// Legacy role helpers (now permission-aware)
function canEdit(roleOrUser) {
  if (typeof roleOrUser === 'string') return ['admin', 'manager'].includes(roleOrUser);
  return hasPermission(roleOrUser, 'purchase_orders', 'write');
}
function canManageUsers(roleOrUser) {
  if (typeof roleOrUser === 'string') return roleOrUser === 'admin';
  return hasPermission(roleOrUser, 'settings', 'write');
}
function canUpdatePOStatus(roleOrUser, newStatus) {
  if (typeof roleOrUser === 'string') {
    if (roleOrUser === 'admin' || roleOrUser === 'manager') return true;
    if (roleOrUser === 'warehouse' && ['checked-in', 'received'].includes(newStatus)) return true;
    return false;
  }
  return hasPermission(roleOrUser, 'purchase_orders', 'write');
}

function Badge({ status }) {
  var sc = getStatusColors();
  const c = sc[status] || sc.draft || { bg: C.warningSoft, text: C.warning };
  return React.createElement('span', {
    style: { background: c.bg, color: c.text, padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }
  }, status);
}

function Btn({ children, variant = 'default', size = 'md', onClick, disabled, style, className }) {
  const base = { border: 'none', cursor: disabled ? 'not-allowed' : 'pointer', fontWeight: 500, display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s', fontFamily: 'inherit', opacity: disabled ? 0.5 : 1 };
  const sizes = { sm: { padding: '6px 12px', fontSize: 12, borderRadius: 6 }, md: { padding: '8px 16px', fontSize: 13, borderRadius: 8 } };
  const variants = {
    default: { background: C.surface, color: C.text, border: '1px solid ' + C.border },
    primary: { background: C.accent, color: '#fff' },
    danger: { background: C.dangerSoft, color: C.danger },
    ghost: { background: 'transparent', color: C.textMuted },
    success: { background: C.successSoft, color: C.success },
  };
  var v = variant || 'default';
  var hoverHandlers = disabled ? {} : {
    onMouseOver: function(e) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; },
    onMouseOut: function(e) { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; }
  };
  return React.createElement('button', { onClick, disabled, className, ...hoverHandlers, style: { ...base, ...sizes[size], ...variants[v], ...style } }, children);
}

// Per-SKU "max units per PO" (products.max_po_qty) → the largest whole-case
// multiple at or below it; a max under one case still allows one case. Mirrors
// server/services/po-cap.js — keep the two in sync. Returns null when unset.
function effectivePoCap(maxPoQty, caseQty) {
  var max = parseInt(maxPoQty) || 0;
  if (max <= 0) return null;
  var cq = parseInt(caseQty) || 1;
  if (cq <= 1) return max;
  var whole = Math.floor(max / cq) * cq;
  return whole > 0 ? whole : cq;
}

function Inp({ label, style: extraStyle, ...props }) {
  var baseStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' };
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    label && React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    React.createElement('input', {
      // Keep Chrome's password manager out of credential/settings fields — 'new-password'
      // is the only value it reliably honors on password inputs. Login uses its own inputs.
      autoComplete: props.type === 'password' ? 'new-password' : 'off',
      style: Object.assign({}, baseStyle, extraStyle || {}),
      onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
      onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
      ...props
    })
  );
}

function Sel({ label, options, value, onChange }) {
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    label && React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    React.createElement('select', {
      style: { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', outline: 'none', minWidth: 0, transition: 'border-color 0.15s, box-shadow 0.15s' },
      onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
      onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
      value: value || '', onChange
    },
      React.createElement('option', { value: '' }, '— Select —'),
      options.map(o => React.createElement('option', { key: o.value, value: o.value }, o.label))
    )
  );
}

// ─── SEARCHABLE SELECT ───
// Custom dropdown with a type-to-filter box. Native <select> popups misbehave under the
// app-wide body zoom (Chrome keeps snapping the popup's scroll back to the selected option
// on long lists), so browsable pickers use this instead — the list is our own scroll
// container, unaffected by zoom. Hover moves the highlight but never scrolls the list;
// only keyboard navigation scrolls, so wheel-scrolling is never fought over.
// Props: options [{value, label, sub?}], value, onChange(value), placeholder, disabled, style
function SearchSelect({ options, value, onChange, placeholder, disabled, style: extraStyle }) {
  var _o = React.useState(false); var open = _o[0], setOpen = _o[1];
  var _s = React.useState(''); var search = _s[0], setSearch = _s[1];
  var _h = React.useState(-1); var highlight = _h[0], setHighlight = _h[1];
  var rootRef = React.useRef(null);
  var listRef = React.useRef(null);
  var inputRef = React.useRef(null);

  var selected = null;
  for (var i = 0; i < options.length; i++) {
    if (String(options[i].value) === String(value)) { selected = options[i]; break; }
  }

  // Rank matches: labels starting with the query first, then any word starting with it,
  // then substring matches anywhere (label or sub) — so typing "v" jumps straight to the
  // V's instead of surfacing every name that merely contains a v.
  var q = search.trim().toLowerCase();
  var filtered;
  if (!q) {
    filtered = options;
  } else {
    var starts = [], wordStarts = [], contains = [];
    options.forEach(function(o) {
      var label = String(o.label).toLowerCase();
      if (label.indexOf(q) === 0) { starts.push(o); return; }
      var words = label.split(/[^a-z0-9]+/);
      for (var w = 0; w < words.length; w++) {
        if (words[w] && words[w].indexOf(q) === 0) { wordStarts.push(o); return; }
      }
      if (label.indexOf(q) !== -1 || (o.sub && String(o.sub).toLowerCase().indexOf(q) !== -1)) contains.push(o);
    });
    filtered = starts.concat(wordStarts, contains);
  }

  React.useEffect(function() {
    if (!open) return;
    var onDown = function(e) { if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', onDown);
    return function() { document.removeEventListener('mousedown', onDown); };
  }, [open]);

  // On open: focus the filter box and bring the current selection into view
  React.useEffect(function() {
    if (!open) return;
    if (inputRef.current) inputRef.current.focus();
    var idx = -1;
    for (var j = 0; j < options.length; j++) {
      if (String(options[j].value) === String(value)) { idx = j; break; }
    }
    setHighlight(idx);
    if (idx >= 0 && listRef.current) {
      var el = listRef.current.querySelector('[data-idx="' + idx + '"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  }, [open]);

  var pick = function(opt) {
    setOpen(false); setSearch('');
    if (onChange) onChange(opt.value);
  };

  var moveHighlight = function(delta) {
    if (!filtered.length) return;
    var next = Math.max(0, Math.min(filtered.length - 1, highlight + delta));
    setHighlight(next);
    if (listRef.current) {
      var el = listRef.current.querySelector('[data-idx="' + next + '"]');
      if (el) el.scrollIntoView({ block: 'nearest' });
    }
  };
  var onKeyDown = function(e) {
    if (e.key === 'ArrowDown') { e.preventDefault(); moveHighlight(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); moveHighlight(-1); }
    else if (e.key === 'Enter') {
      e.preventDefault();
      if (highlight >= 0 && filtered[highlight]) pick(filtered[highlight]);
      else if (filtered.length === 1) pick(filtered[0]);
    }
    else if (e.key === 'Escape') { e.preventDefault(); setOpen(false); setSearch(''); }
  };

  var triggerStyle = Object.assign({
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, width: '100%',
    background: C.bg, border: '1px solid ' + (open ? C.accent : C.border), color: C.text,
    padding: '8px 12px', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', textAlign: 'left',
    cursor: disabled ? 'default' : 'pointer', opacity: disabled ? 0.55 : 1,
    boxShadow: open ? '0 0 0 2px ' + C.accent + '33' : 'none', transition: 'border-color 0.15s, box-shadow 0.15s'
  }, extraStyle || {});

  return React.createElement('div', { ref: rootRef, style: { position: 'relative', minWidth: 0 } },
    React.createElement('button', {
      type: 'button', disabled: disabled,
      onClick: function() { if (!disabled) { setOpen(!open); setSearch(''); } },
      style: triggerStyle
    },
      React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: selected ? C.text : C.textDim } },
        selected ? selected.label : (placeholder || 'Select…')),
      React.createElement(LIcon, { name: 'chevron-down', size: 14, color: C.textMuted })
    ),
    open && React.createElement('div', {
      style: { position: 'absolute', top: '100%', left: 0, right: 0, marginTop: 4, zIndex: 1200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.35)', overflow: 'hidden' }
    },
      React.createElement('input', {
        ref: inputRef, value: search,
        onChange: function(e) { setSearch(e.target.value); setHighlight(0); },
        onKeyDown: onKeyDown,
        placeholder: 'Type to search…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', outline: 'none', boxSizing: 'border-box' }
      }),
      React.createElement('div', { ref: listRef, style: { maxHeight: 280, overflowY: 'auto' } },
        filtered.length === 0
          ? React.createElement('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, 'No matches')
          : filtered.map(function(o, idx) {
              var isSel = selected && String(o.value) === String(selected.value);
              return React.createElement('div', {
                key: o.value, 'data-idx': idx,
                onClick: function() { pick(o); },
                onMouseEnter: function() { setHighlight(idx); },
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: isSel ? C.accent : C.text, background: idx === highlight ? C.surfaceHover : 'transparent' }
              },
                React.createElement('div', { style: { minWidth: 0, flex: 1 } },
                  React.createElement('div', { style: { fontWeight: isSel ? 600 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.label),
                  o.sub ? React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, o.sub) : null
                ),
                isSel ? React.createElement(LIcon, { name: 'check', size: 14, color: C.accent }) : null
              );
            })
      )
    )
  );
}

function Card({ children, style, onClick }) {
  return React.createElement('div', {
    onClick,
    style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: 20, boxShadow: C.cardShadow, ...style }
  }, children);
}

// ─── In-app dialogs (Anthony 2026-09-29: "for any confirmation toasts can we make them system
// specific in the center of the screen instead of the chrome default?"): promise-based replacements
// for window.confirm / prompt / alert, drawn like the app's own Modal and hosted once per shell by
// AppDialogHost. Enter = OK, Escape = cancel, the prompt's input autofocuses; several requests queue
// one after another. With no host mounted (a bare print window, a test) they fall back to the
// browser dialogs so nothing can hang.
//   appConfirm(message, { title, okLabel, cancelLabel, danger })  -> Promise<boolean>
//   appPrompt(message, defaultValue, { title, okLabel, placeholder }) -> Promise<string|null>
//   appAlert(message, { title })                                  -> Promise<void>
//   appChoice(message, ['Label', ...], { title, cancelLabel })     -> Promise<index | -1>
var _appDialogQueue = [];
var _appDialogSetter = null;
function _appDialogNext() { if (_appDialogSetter) _appDialogSetter(_appDialogQueue[0] || null); }
function _appDialog(kind, message, opts) {
  opts = opts || {};
  var text = String(message == null ? '' : message);
  if (!_appDialogSetter) {
    if (kind === 'confirm') return Promise.resolve(window.confirm(text));
    if (kind === 'prompt') return Promise.resolve(window.prompt(text, opts.defaultValue == null ? '' : String(opts.defaultValue)));
    window.alert(text);
    return Promise.resolve(kind === 'choice' ? -1 : undefined);
  }
  return new Promise(function(resolve) {
    var done = false;
    var item = { kind: kind, message: text, opts: opts, resolve: function(v) {
      if (done) return; done = true;
      var at = _appDialogQueue.indexOf(item); if (at !== -1) _appDialogQueue.splice(at, 1);
      resolve(v); _appDialogNext();
    } };
    _appDialogQueue.push(item);
    if (_appDialogQueue.length === 1) _appDialogNext();
  });
}
function appConfirm(message, opts) { return _appDialog('confirm', message, opts); }
function appPrompt(message, defaultValue, opts) { return _appDialog('prompt', message, Object.assign({ defaultValue: defaultValue }, opts || {})); }
function appAlert(message, opts) { return _appDialog('alert', message, opts); }
function appChoice(message, choices, opts) { return _appDialog('choice', message, Object.assign({ choices: choices || [] }, opts || {})); }
function AppDialogHost() {
  var ds = useState(null); var d = ds[0], setD = ds[1];
  var vs = useState(''); var value = vs[0], setValue = vs[1];
  var cardRef = useRef(null);
  var prevFocusRef = useRef(null); // what had the focus before the dialog: it gets it back afterwards
  useEffect(function() { _appDialogSetter = setD; _appDialogNext(); return function() { if (_appDialogSetter === setD) _appDialogSetter = null; }; }, []);
  useEffect(function() {
    if (!d) {
      var back = prevFocusRef.current; prevFocusRef.current = null;
      if (back && back.isConnected && typeof back.focus === 'function') { try { back.focus(); } catch (e) {} }
      return;
    }
    var act = document.activeElement;
    if (!prevFocusRef.current && act && act !== document.body && !(act.closest && act.closest('[data-app-dialog]'))) prevFocusRef.current = act;
    if (d.kind === 'prompt') setValue(d.opts.defaultValue == null ? '' : String(d.opts.defaultValue));
    var el = cardRef.current; if (!el) return;
    var btns = el.querySelectorAll('button');
    var f = el.querySelector('input') || (d.kind === 'choice' ? btns[1] : btns[btns.length - 1]) || el;
    setTimeout(function() { try { f.focus(); if (f.select) f.select(); } catch (e) {} }, 0);
  }, [d]);
  if (!d) return null;
  var o = d.opts;
  var cancel = function() { d.resolve(d.kind === 'confirm' ? false : d.kind === 'prompt' ? null : d.kind === 'choice' ? -1 : undefined); };
  var ok = function() { d.resolve(d.kind === 'confirm' ? true : d.kind === 'prompt' ? value : undefined); };
  var title = o.title || (d.kind === 'alert' ? 'Notice' : d.kind === 'prompt' ? 'Enter a value' : 'Please confirm');
  // an OK button that reads "Cancel order" beside a plain "Cancel" is a trap: the way out says "Go back"
  var cancelText = o.cancelLabel || (/^cancel/i.test(o.okLabel || '') ? 'Go back' : 'Cancel');
  var onKey = function(e) {
    if (e.key === 'Escape') { e.preventDefault(); cancel(); }
    else if (e.key === 'Enter' && d.kind !== 'choice' && !(e.target && e.target.tagName === 'BUTTON' && e.target !== cardRef.current.querySelectorAll('button')[cardRef.current.querySelectorAll('button').length - 1])) { e.preventDefault(); ok(); }
  };
  // A choice keeps every button on ONE line (the card grows to fit them) and can ask for a red
  // Cancel (`cancelDanger`) — Anthony 2026-09-29, the case-pack question.
  var oneLine = { whiteSpace: 'nowrap', flexShrink: 0 };
  var buttons = d.kind === 'choice'
    ? [React.createElement(Btn, { key: 'c', variant: o.cancelDanger ? 'danger' : 'default', onClick: cancel, style: oneLine }, cancelText)].concat((o.choices || []).map(function(label, i) {
        return React.createElement(Btn, { key: 'b' + i, variant: i === 0 ? 'primary' : 'default', onClick: function() { d.resolve(i); }, style: oneLine }, label);
      }))
    : [d.kind !== 'alert' && React.createElement(Btn, { key: 'c', variant: o.cancelDanger ? 'danger' : 'default', onClick: cancel }, cancelText),
       React.createElement(Btn, { key: 'ok', variant: o.danger ? 'danger' : 'primary', onClick: ok }, o.okLabel || 'OK')];
  return React.createElement('div', {
    // data-app-dialog: the pages' click-away / blur auto-saves treat anything inside as "not away"
    'data-app-dialog': '',
    style: { position: 'fixed', inset: 0, zIndex: 2000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    onMouseDown: function(e) { if (e.target === e.currentTarget && d.kind !== 'alert') cancel(); }
  },
    // No focus ring on the dialog's buttons at all (Anthony 2026-09-29: "little circles around Round
    // Up", then "we don't need a ring around that button"). Enter and Escape still work, and the
    // default button is the filled one.
    React.createElement('style', null, '[data-app-dialog] button,[data-app-dialog] button:focus,[data-app-dialog] button:focus-visible{outline:none;box-shadow:none}'),
    React.createElement('div', { ref: cardRef, role: 'dialog', 'aria-modal': true, tabIndex: -1, onKeyDown: onKey, onMouseDown: function(e) { e.stopPropagation(); },
      style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: d.kind === 'choice' ? 'max-content' : 440, minWidth: d.kind === 'choice' ? 440 : undefined, maxWidth: 'calc(100vw - 40px)', padding: 22, boxShadow: '0 20px 60px rgba(0,0,0,0.45)', outline: 'none' } },
      React.createElement('div', { style: { fontSize: 15, fontWeight: 600, color: C.text, marginBottom: 8 } }, title),
      React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.5, whiteSpace: 'pre-wrap', wordBreak: 'break-word', maxHeight: '50vh', overflowY: 'auto' } }, d.message),
      d.kind === 'prompt' && React.createElement('div', { style: { marginTop: 12 } },
        React.createElement(Inp, { value: value, placeholder: o.placeholder || '', onChange: function(e) { setValue(e.target.value); }, style: { width: '100%' } })),
      // no overflow on this row: a scrolling row clips the focus ring top and bottom and leaves only
      // its rounded ends showing (the "little circles"); a choice wraps only on a phone-width screen
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 18, flexWrap: d.kind === 'choice' && window.innerWidth >= 600 ? 'nowrap' : 'wrap' } }, buttons)
    )
  );
}

// Escape closes the top-most open modal (polish 2026-10-11; the Orders detail modal only had the X
// and the backdrop). The stack keeps a dialog opened on top of a modal (appConfirm inside Order
// Details) from closing both: only the last-opened modal's listener acts, a key a dialog or a
// picker already handled (defaultPrevented) is left alone, and a key pressed on a <select> is
// ignored because Escape there closes its dropdown.
var _modalStack = [];
function Modal({ open, onClose, title, width, maxHeight, children }) {
  var onCloseRef = useRef(onClose); onCloseRef.current = onClose;
  useEffect(function() {
    if (!open) return;
    var me = {};
    _modalStack.push(me);
    var onKey = function(e) {
      if (e.key !== 'Escape' || e.defaultPrevented) return;
      if (e.target && e.target.tagName === 'SELECT') return;
      if (_modalStack[_modalStack.length - 1] !== me) return;
      var close = onCloseRef.current;
      if (!close) return;
      // The apps mount through the legacy ReactDOM.render root, where state set from a native key
      // listener renders after EACH setter: a close that clears the selected order and then its
      // detail rendered in between with the order gone and the detail still there (crash on the
      // first live Escape). One render for the whole close, as a React event handler would get.
      var batched = typeof ReactDOM !== 'undefined' && ReactDOM && ReactDOM.unstable_batchedUpdates;
      if (batched) batched(function() { close(); }); else close();
    };
    document.addEventListener('keydown', onKey);
    return function() {
      document.removeEventListener('keydown', onKey);
      var i = _modalStack.indexOf(me);
      if (i >= 0) _modalStack.splice(i, 1);
    };
  }, [open]);
  if (!open) return null;
  return React.createElement('div', {
    style: { position: 'fixed', inset: 0, zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' },
    onMouseDown: function(e) { if (e.target === e.currentTarget) onClose(); }
  },
    React.createElement('div', {
      style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, width: width || 560, maxWidth: 'calc(calc(100vw * var(--invzoom, 1)) - 40px)', maxHeight: maxHeight || 'calc(85vh * var(--invzoom, 1))', overflowX: 'hidden', overflowY: 'auto', padding: 24, boxSizing: 'border-box' },
      onMouseDown: function(e) { e.stopPropagation(); }
    },
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 } },
        React.createElement('h3', { style: { margin: 0, fontSize: 16, fontWeight: 600, color: C.text } }, title),
        React.createElement('button', { onClick: onClose,
          onMouseOver: function(e) { e.currentTarget.style.background = C.danger + '20'; e.currentTarget.style.color = C.danger; },
          onMouseOut: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; },
          style: { background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', display: 'inline-flex', alignItems: 'center', padding: 6, borderRadius: 6, transition: 'all 0.15s' } }, React.createElement(LIcon, { name: 'x', size: 18 }))
      ),
      children
    )
  );
}

// fill: the empty state takes the table's remaining height and centres in it (a fill-height table,
// Anthony 2026-09-23: "center the message")
function Empty({ title, sub, fill }) {
  return React.createElement('div', { style: Object.assign({ textAlign: 'center', padding: '60px 20px', color: C.textDim },
    fill ? { flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px' } : {}) },
    React.createElement('div', { style: { fontSize: 15, fontWeight: 500, color: C.textMuted, marginBottom: sub ? 4 : 0 } }, title),
    sub ? React.createElement('div', { style: { fontSize: 13 } }, sub) : null
  );
}

function Toast({ message, type, onClose }) {
  // Per-type timeout: errors and warnings carry data the user may need to read/copy,
  // so they stay longer than success toasts.
  var timeout = type === 'error' ? 12000 : type === 'warning' ? 7000 : 3500;
  useEffect(() => { const t = setTimeout(onClose, timeout); return () => clearTimeout(t); }, [onClose, timeout]);
  var toastRef = React.useRef(null);
  // Errors do NOT close on outside-click \u2014 Paula needs time to read/copy before clicking elsewhere.
  useEffect(() => {
    if (type === 'error') return;
    var handler = function(e) { if (toastRef.current && toastRef.current.contains(e.target)) return; onClose(); };
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [onClose, type]);
  // Detect MSKU-like tokens in error/warning messages (2-5 caps, dash, alphanumeric with
  // optional segments). Match real seller SKUs like ALRE-852, MIAM-105699-3PK, VDIM-142.
  var skus = (type === 'error' || type === 'warning') && typeof message === 'string'
    ? Array.from(new Set(message.match(/\b[A-Z]{2,5}-[A-Z0-9]+(?:-[A-Z0-9]+){0,3}\b/g) || []))
    : [];
  var [copied, setCopied] = useState(false);
  var doCopy = function(e) {
    e.stopPropagation();
    if (!skus.length) return;
    var text = skus.join(', ');
    var done = function() { setCopied(true); setTimeout(function() { setCopied(false); }, 2000); };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(function() {});
    } else {
      var ta = document.createElement('textarea'); ta.value = text;
      document.body.appendChild(ta); ta.select();
      try { document.execCommand('copy'); done(); } catch (e2) {}
      document.body.removeChild(ta);
    }
  };
  const bg = type === 'error' ? C.dangerSoft : type === 'warning' ? C.warningSoft : C.successSoft;
  const clr = type === 'error' ? C.danger : type === 'warning' ? C.warning : C.success;
  return React.createElement('div', { ref: toastRef,
    style: { position: 'fixed', top: 20, right: 20, zIndex: 2000, background: bg, color: clr, padding: '12px 20px', borderRadius: 8, fontSize: 13, fontWeight: 500, border: '1px solid ' + clr + '33', boxShadow: '0 8px 30px rgba(0,0,0,0.3)', animation: 'slideIn 0.2s ease', display: 'flex', alignItems: 'center', gap: 10, maxWidth: 600 }
  }, React.createElement('span', { style: { wordBreak: 'break-word' } }, message),
    skus.length > 0 && React.createElement('button', {
      onClick: doCopy, title: 'Copy SKUs: ' + skus.join(', '),
      style: { background: clr + '22', border: '1px solid ' + clr + '55', color: clr, cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: '4px 10px', borderRadius: 4, whiteSpace: 'nowrap', flexShrink: 0, fontFamily: 'inherit' }
    }, copied ? 'Copied!' : 'Copy ' + skus.length + ' SKU' + (skus.length > 1 ? 's' : '')),
    React.createElement('button', { onClick: onClose,
      style: { background: 'none', border: 'none', color: clr, cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 0, opacity: 0.7, flexShrink: 0 }
    }, '\u00D7')
  );
}

function TH({ children, style }) {
  return React.createElement('th', { style: { padding: '8px 12px', textAlign: 'left', fontWeight: 600, fontSize: 11, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap', ...style } }, children);
}

function TD({ children, style, onClick }) {
  return React.createElement('td', { style: { padding: '8px 12px', wordBreak: 'break-word', ...style }, onClick: onClick }, children);
}

// Column preferences — loaded from server, saved back on change. Saves send
// ONLY the keys that changed since load (diff vs baseline): the main app and
// the ship/ads apps each hold a copy, and full-object saves let a stale copy
// revert the other app's layouts.
var _colPrefs = {};
var _colPrefsBaseline = {};
var _colPrefsLoaded = false;
var _colPrefsSaveTimer = null;

function loadColumnPrefs() {
  if (_colPrefsLoaded) return Promise.resolve(_colPrefs);
  return fetch('/api/auth/preferences', { credentials: 'include' })
    .then(function(r) { return r.ok ? r.json() : {}; })
    .then(function(prefs) {
      _colPrefs = (prefs && prefs.columns) || {};
      try { _colPrefsBaseline = JSON.parse(JSON.stringify(_colPrefs)); } catch (e) { _colPrefsBaseline = {}; }
      _colPrefsLoaded = true;
      return _colPrefs;
    })
    .catch(function() { return {}; });
}

function saveColumnPrefs() {
  clearTimeout(_colPrefsSaveTimer);
  _colPrefsSaveTimer = setTimeout(function() {
    var dirty = {};
    Object.keys(_colPrefs).forEach(function(k) {
      if (JSON.stringify(_colPrefs[k]) !== JSON.stringify(_colPrefsBaseline[k])) dirty[k] = _colPrefs[k];
    });
    // Deleted keys (e.g. reset column order) go up as null — loaders treat
    // null as unset, and the server merge keeps explicit nulls
    Object.keys(_colPrefsBaseline).forEach(function(k) {
      if (!(k in _colPrefs)) dirty[k] = null;
    });
    if (!Object.keys(dirty).length) return;
    fetch('/api/auth/preferences', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ columns: dirty })
    }).then(function(r) {
      if (!r.ok) return;
      Object.keys(dirty).forEach(function(k) {
        if (dirty[k] === null) delete _colPrefsBaseline[k];
        else { try { _colPrefsBaseline[k] = JSON.parse(JSON.stringify(dirty[k])); } catch (e) {} }
      });
    }).catch(function(e) { console.error('[silent-catch]', e); });
  }, 500);
}

function resetColumnPrefsCache() {
  _colPrefs = {};
  _colPrefsLoaded = false;
}

// scrollMode (opt-in): fixed-height viewport with scroll-to-load-more instead of page
// buttons — pageSize becomes the batch loaded per scroll (client mode only).
// compact (opt-in): smaller cell + header type for dense side-panel tables.
// enableViews (opt-in, needs storageKey): "View: Default" dropdown in the toolbar — saves
// the current table state (column filters, sort, order, visibility, page size) as a named
// per-user view; the active view is remembered and re-applied on the next visit.
// ── Column filter kinds (2026-09-05) ─────────────────────────────────────────────
// Every column filters as one of: 'list' (checkbox list of the actual values), 'number'
// (comparisons only), 'date' (on / before / after) or 'text' (contains / equals / …).
// A column can pin its kind with `filterKind`; `filterOptions` (array or 'auto') implies
// 'list'. Otherwise the kind is inferred from the data. Server-paged tables only see one
// page, so they never infer 'list' — pass `filterOptions` explicitly for those.
var DT_IN_SEP = '\u001f'; // 'in' filter separator (values may contain commas); legacy = ','
function _dtSplitIn(v) { v = v || ''; return (v.indexOf(DT_IN_SEP) !== -1 ? v.split(DT_IN_SEP) : v.split(',')).map(function(x) { return x.trim(); }).filter(function(x) { return x !== ''; }); }
function _dtColValue(col, row) {
  try {
    if (col.filterValue) return col.filterValue(row);
    if (row[col.key] !== undefined) return row[col.key];
    if (col.sortValue) return col.sortValue(row);
    if (col.render) { var r = col.render(row); if (typeof r === 'string' || typeof r === 'number') return r; }
  } catch (e) {}
  return undefined;
}
function _dtIsNumLike(v) {
  if (typeof v === 'number') return !isNaN(v);
  if (typeof v !== 'string') return false;
  return /^[-+]?\$?\s?[\d,]*\.?\d+\s?%?$/.test(v.trim());
}
function _dtToNum(v) {
  if (typeof v === 'number') return v;
  if (v == null) return NaN;
  var s = String(v).replace(/[^0-9.\-]/g, '');
  return s === '' || s === '-' || s === '.' ? NaN : Number(s);
}
// Numeric column-filter comparison (gt/gte/lt/lte/eq). Both sides go through
// _dtToNum so "$1,234" / "12%" compare like the displayed numbers. A missing
// value (null/'') is NOT zero and never satisfies a numeric comparison.
function _dtNumCompare(mode, rawVal, search) {
  search = search == null ? '' : String(search).trim();
  if (search === '' || rawVal == null || rawVal === '') return false;
  var n = _dtToNum(rawVal), s = _dtToNum(search);
  if (isNaN(n) || isNaN(s)) return false;
  switch (mode) {
    case 'gt': return n > s;
    case 'gte': return n >= s;
    case 'lt': return n < s;
    case 'lte': return n <= s;
    case 'eq': return n === s;
    default: return false;
  }
}
window._dtNumCompare = _dtNumCompare;
function _dtIsDateLike(v) {
  if (v instanceof Date) return !isNaN(v);
  if (typeof v !== 'string') return false;
  return /^\d{4}-\d{2}-\d{2}([T ]|$)/.test(v.trim()) && !isNaN(new Date(v).getTime());
}
// Calendar day as YYYYMMDD, matching the day the cell shows. A date-only value (YYYY-MM-DD, the
// pg DATE -> JSON round-trip at UTC midnight, or a naive date-time with no zone) uses its leading
// date, the way fmtDateOnly prints it; a zoned timestamp (TIMESTAMPTZ, "...T23:30:00.000Z") uses
// the viewer's local day, the way the date cells print it. Review 2026-09-28, medium #30: zoned
// timestamps were filtered on their UTC day, so after 8 PM ET "on today" missed rows the table
// showed under today.
function _dtToDay(v) {
  if (v == null || v === '') return null;
  var s = typeof v === 'string' ? v.trim() : v;
  var m = typeof s === 'string' && s.match(/^(\d{4})-(\d{2})-(\d{2})(.*)$/);
  if (m) {
    var zoned = /(Z|[+-]\d{2}:?\d{2})$/i.test(m[4]);
    var utcMidnight = /^[T ]00:00:00(\.0+)?(Z|\+00:?00)$/i.test(m[4]);
    if (!zoned || utcMidnight) return Number(m[1]) * 10000 + Number(m[2]) * 100 + Number(m[3]);
  }
  var d = s instanceof Date ? s : new Date(s);
  if (isNaN(d.getTime())) return null;
  return d.getFullYear() * 10000 + (d.getMonth() + 1) * 100 + d.getDate();
}
function _dtModesForKind(kind) {
  if (kind === 'list') return ['in'];
  if (kind === 'number') return ['eq', 'gt', 'gte', 'lt', 'lte', 'blank', 'not_blank'];
  if (kind === 'date') return ['on', 'before', 'after', 'blank', 'not_blank'];
  return ['contains', 'equals', 'starts', 'ends', 'blank', 'not_blank'];
}
function _dtInferFilterKind(col, rows, serverMode) {
  if (col.filterKind) return col.filterKind;
  if (col.filterOptions) return 'list';
  var vals = [];
  for (var i = 0; i < rows.length && vals.length < 400; i++) {
    var v = _dtColValue(col, rows[i]);
    if (v != null && String(v).trim() !== '') vals.push(v);
  }
  if (!vals.length) return 'text';
  var nums = 0, dates = 0, seen = {}, distinct = 0;
  vals.forEach(function(v) {
    if (_dtIsNumLike(v)) nums++;
    else if (_dtIsDateLike(v)) dates++;
    var k = String(v); if (!seen[k]) { seen[k] = true; distinct++; }
  });
  if (nums / vals.length >= 0.9) return 'number';
  if (dates / vals.length >= 0.9) return 'date';
  if (!serverMode && distinct <= 200 && distinct <= Math.max(12, vals.length * 0.5)) return 'list';
  return 'text';
}
function _dtListOptions(col, rows) {
  if (Array.isArray(col.filterOptions)) return col.filterOptions.map(function(o) { return String(o); });
  var seen = {}, out = [];
  for (var i = 0; i < rows.length; i++) {
    var v = _dtColValue(col, rows[i]);
    if (v == null) continue;
    var s = String(v).trim(); if (s === '' || seen[s]) continue;
    seen[s] = true; out.push(s);
    if (out.length >= 500) break;
  }
  var allNum = out.length > 0 && out.every(_dtIsNumLike);
  out.sort(allNum ? function(a, b) { return _dtToNum(a) - _dtToNum(b); } : function(a, b) { return a.localeCompare(b, undefined, { sensitivity: 'base', numeric: true }); });
  return out;
}

// ── Invisible reCAPTCHA v2 → one-shot token for Firebase text-message codes (2026-09-05) ──
// Google will not text a verification code without proof the request came from a browser.
// The site key comes from the server (Firebase project); the widget renders once into a
// fixed host div, is reset before every execute, and its badge is hidden (attribution text
// is shown next to the phone step instead, per Google's terms).
var _rcLoad = null, _rcWidget = null, _rcSiteKey = null, _rcPending = null;
function loadRecaptchaApi() {
  if (window.grecaptcha && window.grecaptcha.render) return Promise.resolve();
  if (_rcLoad) return _rcLoad;
  _rcLoad = new Promise(function(resolve, reject) {
    var cb = '__rcOnload' + Date.now();
    var timer = setTimeout(function() { _rcLoad = null; reject(new Error('The verification widget did not load. Check your connection or use email.')); }, 12000);
    window[cb] = function() { clearTimeout(timer); resolve(); };
    if (!document.getElementById('rc-badge-style')) {
      var st = document.createElement('style'); st.id = 'rc-badge-style';
      st.textContent = '.grecaptcha-badge{visibility:hidden}';
      document.head.appendChild(st);
    }
    var sc = document.createElement('script');
    sc.src = 'https://www.google.com/recaptcha/api.js?onload=' + cb + '&render=explicit';
    sc.async = true; sc.defer = true;
    sc.onerror = function() { clearTimeout(timer); _rcLoad = null; reject(new Error('The verification widget could not be loaded.')); };
    document.head.appendChild(sc);
  });
  return _rcLoad;
}
function getRecaptchaToken(siteKey) {
  if (!siteKey) return Promise.reject(new Error('Text-message codes are not available right now.'));
  return loadRecaptchaApi().then(function() {
    return new Promise(function(resolve, reject) {
      var host = document.getElementById('app-recaptcha-host');
      if (!host) { host = document.createElement('div'); host.id = 'app-recaptcha-host'; document.body.appendChild(host); }
      if (_rcWidget !== null && _rcSiteKey !== siteKey) { host.innerHTML = ''; _rcWidget = null; }
      if (_rcPending) { try { _rcPending.reject(new Error('Superseded')); } catch (e) {} _rcPending = null; }
      var timer = setTimeout(function() { done('err', new Error('The verification challenge timed out. Try again or use email.')); }, 90000);
      function done(kind, arg) { clearTimeout(timer); var p = _rcPending; _rcPending = null; if (!p) return; if (kind === 'ok') p.resolve(arg); else p.reject(arg); }
      _rcPending = { resolve: resolve, reject: reject };
      if (_rcWidget === null) {
        var el = document.createElement('div'); host.appendChild(el);
        _rcSiteKey = siteKey;
        _rcWidget = window.grecaptcha.render(el, {
          sitekey: siteKey, size: 'invisible', badge: 'bottomleft',
          callback: function(token) { done('ok', token); },
          'expired-callback': function() { done('err', new Error('The verification challenge expired. Try again.')); },
          'error-callback': function() { done('err', new Error('The verification challenge could not run. Try again or use email.')); },
        });
      } else {
        try { window.grecaptcha.reset(_rcWidget); } catch (e) {}
      }
      try { window.grecaptcha.execute(_rcWidget); } catch (e) { done('err', e); }
    });
  });
}
window.getRecaptchaToken = getRecaptchaToken;
// Load the Google script ahead of the first execute (login page mount); errors surface later, on use.
window.warmRecaptcha = function() { try { loadRecaptchaApi().catch(function() {}); } catch (e) {} };
var RECAPTCHA_NOTICE = 'Protected by reCAPTCHA. Google\u2019s Privacy Policy and Terms of Service apply.';
window.RECAPTCHA_NOTICE = RECAPTCHA_NOTICE;

// ── System checkbox (2026-09-05) — the rounded-square look the table selection columns use,
// for menus/popovers that used to fall back to the browser's native checkbox. Purely visual:
// the parent handles the click.
function SysCheckbox(props) {
  var size = props.size || 16;
  var on = !!props.checked, mixed = !!props.indeterminate && !on;
  return React.createElement('span', { 'aria-checked': mixed ? 'mixed' : on, role: 'checkbox', style: Object.assign({
    width: size, height: size, borderRadius: 4, flex: '0 0 auto', boxSizing: 'border-box',
    border: '2px solid ' + (on || mixed ? C.accent : C.textDim), background: on ? C.accent : 'transparent',
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.12s',
  }, props.style || {}) },
    on ? React.createElement(LIcon, { name: 'check', size: Math.max(10, size - 5), color: '#fff' }) : null,
    mixed ? React.createElement('span', { style: { width: size - 8, height: 2, background: C.accent, borderRadius: 1 } }) : null);
}
window.SysCheckbox = SysCheckbox;

// Selection columns (the checkbox column every list adds as _select / select / _sel) never sort:
// clicking beside the box used to sort the rows by ticked state (Anthony 2026-09-07).
function colNoSort(col) { return !!(col && (col.noSort || /^_?sel(ect)?$/.test(String(col.key || '')))); }

function DataTable({ columns, data, onRowClick, rowHref, rowStyle, onAction, emptyTitle, emptySub, storageKey, defaultSort, defaultSortDir, onSortedData, onPageData, bulkActions, extraToolbar, serverTotal, onServerFetch, serverSearch, initialPage, onPageChange, initialScroll, onScrollChange, scrollMode, scrollHeight, compact, enableViews, viewsPortalEl, noColumnFilters, fontSize, cellPadding, defaultPageSize }) {
  // columns: [{ key, label, render?, defaultVisible?, style?, sortKey?, locked?, stopClick?, noFilter? }]
  // defaultPageSize: rows per page until the user picks one (their pick is saved per storageKey and wins)

  var initVisible = {};
  columns.forEach(function(col) {
    initVisible[col.key] = col.defaultVisible !== false;
  });

  if (storageKey && _colPrefs[storageKey]) {
    var saved = _colPrefs[storageKey];
    columns.forEach(function(col) {
      if (saved[col.key] !== undefined) {
        initVisible[col.key] = saved[col.key];
      }
    });
  }

  // Locked columns are always visible
  columns.forEach(function(col) {
    if (col.locked) initVisible[col.key] = true;
  });

  var rowOptions = [25, 50, 100, 200, 400, 800];
  var initPageSize = (defaultPageSize && defaultPageSize > 0) ? defaultPageSize : 50;
  if (storageKey && _colPrefs[storageKey + '_pageSize']) {
    initPageSize = _colPrefs[storageKey + '_pageSize'];
  }

  const [visible, setVisible] = useState(initVisible);
  // Per-user column order (drag a header onto another to reorder; persists via /auth/preferences)
  const [colOrder, setColOrder] = useState((storageKey && _colPrefs[storageKey + '_order']) || null);
  const [dragCol, setDragCol] = useState(null);
  const [dragOverCol, setDragOverCol] = useState(null);
  // Named per-user views (enableViews): [{ name, state }] under storageKey + '_views'
  const [userViews, setUserViews] = useState((storageKey && _colPrefs[storageKey + '_views']) || []);
  const [activeView, setActiveView] = useState((storageKey && _colPrefs[storageKey + '_activeView']) || null);
  // Starred view auto-applied every time this table mounts (per user)
  const [defaultView, setDefaultView] = useState((storageKey && _colPrefs[storageKey + '_defaultView']) || null);
  const [viewMenuOpen, setViewMenuOpen] = useState(false);
  const [viewName, setViewName] = useState('');
  const viewMenuRef = useRef(null);
  const [prefsReady, setPrefsReady] = useState(_colPrefsLoaded);
  const [showColMenu, setShowColMenu] = useState(false);
  // Last manually-clicked sort persists per user under storageKey + '_sort' (same prefs
  // store as visibility/order/pageSize); defaultSort only applies until the first click.
  var _savedSort = (storageKey && _colPrefs[storageKey + '_sort']) || null;
  const [sortCol, setSortCol] = useState((_savedSort && _savedSort.col) || defaultSort || null);
  const [sortDir, setSortDir] = useState((_savedSort && _savedSort.col && _savedSort.dir) || defaultSortDir || 'asc');
  const [currentPage, _setCurrentPage] = useState(initialPage || 0);
  var setCurrentPage = function(p) { _setCurrentPage(p); if (onPageChange) onPageChange(p); };
  const [pageSize, setPageSize] = useState(initPageSize);
  const menuRef = useRef(null);
  const scrollRef = useRef(null);
  const scrollRestoredRef = useRef(false);
  useEffect(function() {
    // Restore scroll position once data is available (not on initial empty mount)
    if (!scrollRestoredRef.current && scrollRef.current && initialScroll && data && data.length > 0) {
      scrollRef.current.scrollTop = initialScroll;
      scrollRestoredRef.current = true;
    }
  }, [data]);
  var _scrollTotalRef = useRef(0);
  var handleScroll = function() {
    if (onScrollChange && scrollRef.current) onScrollChange(scrollRef.current.scrollTop);
    if (scrollMode && !onServerFetch && scrollRef.current) {
      var el = scrollRef.current;
      if (el.scrollTop + el.clientHeight >= el.scrollHeight - 240
          && (currentPage + 1) * pageSize < _scrollTotalRef.current) {
        setCurrentPage(currentPage + 1); // reveal the next batch
      }
    }
  };

  // Column filters: { colKey: { mode: 'contains'|'equals'|'starts'|'blank'|'not_blank', value: '' } }
  const [colFilters, setColFilters] = useState({});
  const [filterMenuCol, setFilterMenuCol] = useState(null);
  const filterMenuRef = useRef(null);
  const filterInputRef = useRef(null);

  var activeFilterCount = Object.keys(colFilters).filter(function(k) {
    var f = colFilters[k];
    if (!f || !f.mode) return false;
    if (f.mode === 'blank' || f.mode === 'not_blank') return true;
    return f.value && f.value.trim() !== '';
  }).length;

  // Pending filter edits (not yet applied)
  const [pendingFilter, setPendingFilter] = useState(null); // { mode, value, matchCase } for current filterMenuCol

  var openFilterMenu = function(colKey) {
    var existing = colFilters[colKey] || {};
    var col = columns.find(function(c) { return c.key === colKey; });
    var kind = col ? _dtInferFilterKind(col, data, !!onServerFetch) : 'text';
    var modes = _dtModesForKind(kind);
    var mode = existing.mode && modes.indexOf(existing.mode) !== -1 ? existing.mode : modes[0];
    setPendingFilter({ kind: kind, mode: mode, value: existing.value || '', matchCase: existing.matchCase || false, q: '' });
    setFilterMenuCol(colKey);
    setShowColMenu(false);
    setShowBulkMenu(false);
  };
  var applyFilter = function(colKey) {
    if (!pendingFilter) return;
    var next = Object.assign({}, colFilters);
    if (!pendingFilter.mode || pendingFilter.mode === '') { delete next[colKey]; }
    else { next[colKey] = { mode: pendingFilter.mode, value: pendingFilter.value || '', matchCase: pendingFilter.matchCase || false }; }
    setColFilters(next);
    setCurrentPage(0);
    setFilterMenuCol(null);
    setPendingFilter(null);
  };
  var clearFilter = function(colKey) {
    var next = Object.assign({}, colFilters);
    delete next[colKey];
    setColFilters(next);
    setCurrentPage(0);
    setFilterMenuCol(null);
    setPendingFilter(null);
  };
  var clearAllFilters = function() { setColFilters({}); setCurrentPage(0); setPendingFilter(null); setFilterMenuCol(null); };

  // Rebuild visibility + page size from saved prefs for the CURRENT storageKey/columns.
  // Reads through a latest-ref because storageKey can change while this instance stays
  // mounted (the PO list swaps its key per status tab) — mount-time init alone would keep
  // serving the previous tab's state and cross-pollute saves under the new key.
  var _latestRef = useRef();
  _latestRef.current = { storageKey: storageKey, columns: columns, defaultSort: defaultSort, defaultSortDir: defaultSortDir };
  var syncFromPrefs = function() {
    var key = _latestRef.current.storageKey;
    var saved = (key && _colPrefs[key]) || {};
    var next = {};
    _latestRef.current.columns.forEach(function(col) {
      next[col.key] = saved[col.key] !== undefined ? saved[col.key] : (col.defaultVisible !== false);
      if (col.locked) next[col.key] = true;
    });
    setVisible(next);
    if (key && _colPrefs[key + '_pageSize']) setPageSize(_colPrefs[key + '_pageSize']);
    setColOrder((key && _colPrefs[key + '_order']) || null);
    // Restore the user's last clicked sort; a saved view applied below still wins
    var savedSort = key && _colPrefs[key + '_sort'];
    if (savedSort && savedSort.col) {
      setSortCol(savedSort.col);
      setSortDir(savedSort.dir || 'asc');
    } else {
      setSortCol(_latestRef.current.defaultSort || null);
      setSortDir(_latestRef.current.defaultSortDir || 'asc');
    }
    // Saved views: refresh the list, then apply the starred default view (wins), else
    // whichever view was active last visit
    var vlist = (key && _colPrefs[key + '_views']) || [];
    setUserViews(vlist);
    var dvName = key && _colPrefs[key + '_defaultView'];
    setDefaultView(dvName || null);
    var avName = key && _colPrefs[key + '_activeView'];
    var pick = (dvName && vlist.find(function(v) { return v.name === dvName; }))
      || (avName && vlist.find(function(v) { return v.name === avName; })) || null;
    setActiveView(pick ? pick.name : null);
    if (pick) applyViewState(pick.state);
  };

  // Load prefs from server on first render
  useEffect(function() {
    if (!_colPrefsLoaded) {
      loadColumnPrefs().then(function() {
        syncFromPrefs();
        setPrefsReady(true);
      });
    }
  }, []);

  // Re-sync when the storage key changes on a live instance (e.g. PO list tab switch)
  var _prevKeyRef = useRef(storageKey);
  useEffect(function() {
    if (_prevKeyRef.current === storageKey) return;
    _prevKeyRef.current = storageKey;
    syncFromPrefs();
  }, [storageKey]);

  // Reset to page 0 when data length changes substantially (client mode only)
  var _prevDataLen = useRef(data.length);
  var _hadDataRef = useRef(data.length > 0);
  useEffect(function() {
    if (!onServerFetch && data.length !== _prevDataLen.current) {
      // Skip reset on initial load (empty → populated) — respect caller's initialPage
      var isInitialLoad = !_hadDataRef.current && data.length > 0;
      if (data.length > 0) _hadDataRef.current = true;
      if (!isInitialLoad) {
        // Only reset page if the data set changed significantly (>10% or full reload)
        var diff = Math.abs(data.length - _prevDataLen.current);
        if (diff > Math.max(5, _prevDataLen.current * 0.1) || _prevDataLen.current === 0) {
          setCurrentPage(0);
        }
      }
      _prevDataLen.current = data.length;
    }
  }, [data.length]);

  // Server-side pagination: call onServerFetch when state changes
  var _serverFiltersKey = onServerFetch ? JSON.stringify(colFilters) : '';
  var _fetchIdRef = useRef(0);
  useEffect(function() {
    if (!onServerFetch) return;
    _fetchIdRef.current++;
    var myId = _fetchIdRef.current;
    var timer = setTimeout(function() {
      if (myId === _fetchIdRef.current) {
        onServerFetch({ page: currentPage, pageSize: pageSize, sortCol: sortCol, sortDir: sortDir, filters: colFilters, search: serverSearch || '' });
      }
    }, 10);
    return function() { clearTimeout(timer); };
  }, [currentPage, pageSize, sortCol, sortDir, _serverFiltersKey, serverSearch]);

  // Server-side: reset page when search changes
  var _prevServerSearch = useRef(serverSearch);
  useEffect(function() {
    if (onServerFetch && serverSearch !== _prevServerSearch.current) {
      _prevServerSearch.current = serverSearch;
      setCurrentPage(0);
    }
  }, [serverSearch]);
  // Server-side: a filter change that shrinks the result set below the current page used to strand
  // the table on an empty page with the pager hidden (review M6 #76; Orders channel/status/date
  // filters, Catalog filter). Snap back to page 0 — the client-mode clamp above is server-exempt.
  useEffect(function() {
    if (onServerFetch && currentPage > 0 && data.length === 0 && (serverTotal || 0) > 0) setCurrentPage(0);
  }, [data.length, serverTotal]);

  var [showBulkMenu, setShowBulkMenu] = useState(false);
  var bulkMenuRef = useRef(null);
  var bulkBtnRef = useRef(null);
  var [bulkMenuPos, setBulkMenuPos] = useState({ top: 0, right: 0 });

  var toggleBulkMenu = function() {
    if (!showBulkMenu && bulkBtnRef.current) {
      var rect = bulkBtnRef.current.getBoundingClientRect();
      var z = window.APP_ZOOM || 1; // gBCR/innerWidth are visual px; the fixed menu positions in zoomed px
      setBulkMenuPos({ top: (rect.bottom + 4) / z, right: (window.innerWidth - rect.right) / z });
    }
    setShowBulkMenu(!showBulkMenu);
    setShowColMenu(false);
  };

  useEffect(function() {
    function handleClick(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) setShowColMenu(false);
      if (bulkMenuRef.current && !bulkMenuRef.current.contains(e.target)) setShowBulkMenu(false);
      if (filterMenuRef.current && !filterMenuRef.current.contains(e.target)) setFilterMenuCol(null);
    }
    if (showColMenu || showBulkMenu || filterMenuCol) document.addEventListener('mousedown', handleClick);
    return function() { document.removeEventListener('mousedown', handleClick); };
  }, [showColMenu, showBulkMenu, filterMenuCol]);

  // The actions menu is position:fixed with coordinates captured at open — re-anchor it
  // to the button on scroll/resize (capture phase catches inner scrollers too), otherwise
  // it detaches and floats as the page scrolls.
  useEffect(function() {
    if (!showBulkMenu) return;
    function reposition() {
      if (!bulkBtnRef.current) return;
      var rect = bulkBtnRef.current.getBoundingClientRect();
      var z = window.APP_ZOOM || 1;
      setBulkMenuPos({ top: (rect.bottom + 4) / z, right: (window.innerWidth - rect.right) / z });
    }
    window.addEventListener('scroll', reposition, true);
    window.addEventListener('resize', reposition);
    return function() { window.removeEventListener('scroll', reposition, true); window.removeEventListener('resize', reposition); };
  }, [showBulkMenu]);

  // Auto-focus filter input when menu opens
  useEffect(function() {
    if (filterMenuCol && filterInputRef.current) filterInputRef.current.focus();
  }, [filterMenuCol]);

  var toggleCol = function(key) {
    var next = Object.assign({}, visible);
    next[key] = !next[key];
    var anyVisible = Object.keys(next).some(function(k) { return next[k]; });
    if (!anyVisible) return;
    setVisible(next);
    if (storageKey) { _colPrefs[storageKey] = next; saveColumnPrefs(); }
  };

  var changePageSize = function(newSize) {
    setPageSize(newSize);
    setCurrentPage(0);
    if (storageKey) { _colPrefs[storageKey + '_pageSize'] = newSize; saveColumnPrefs(); }
  };

  // Display order for a column list under the saved order. Columns the saved order
  // doesn't know (added to the app after the user last dragged) anchor right after the
  // nearest preceding known column from the definition order — NOT at the end of the
  // table, which would strand every newly shipped column on the far right.
  var orderCols = function(cols) {
    if (!colOrder || !colOrder.length) return cols;
    var idx = {};
    colOrder.forEach(function(k, i) { idx[k] = i; });
    var lastKnown = -1;
    var keyed = cols.map(function(c, i) {
      if (idx[c.key] !== undefined) { lastKnown = idx[c.key]; return [c, lastKnown, i]; }
      return [c, lastKnown + (i + 1) / (cols.length + 1), i];
    });
    var out = keyed.sort(function(a, b) { return a[1] - b[1] || a[2] - b[2]; })
      .map(function(x) { return x[0]; });
    // Sticky (left-pinned) columns only work at the front — keep them there regardless
    var stk = out.filter(function(c) { return c.sticky; });
    return stk.length ? stk.concat(out.filter(function(c) { return !c.sticky; })) : out;
  };

  var dropColumn = function(targetKey) {
    var dk = dragCol;
    setDragCol(null);
    setDragOverCol(null);
    if (!dk || dk === targetKey) return;
    // Build the full current display order (all non-subRow columns, hidden ones included,
    // so a hidden column keeps a sane slot when toggled back on)
    var order = orderCols(columns.filter(function(c) { return !c.subRow; }))
      .map(function(c) { return c.key; });
    var from = order.indexOf(dk), to = order.indexOf(targetKey);
    if (from < 0 || to < 0 || from === to) return;
    order.splice(from, 1);
    order.splice(to, 0, dk);
    setColOrder(order);
    if (storageKey) { _colPrefs[storageKey + '_order'] = order; saveColumnPrefs(); }
  };

  var resetColOrder = function() {
    setColOrder(null);
    if (storageKey) { delete _colPrefs[storageKey + '_order']; saveColumnPrefs(); }
  };

  // ── Saved views (enableViews) ──
  var currentViewState = function() {
    return { filters: colFilters, sortCol: sortCol, sortDir: sortDir, visible: visible, order: colOrder, pageSize: pageSize };
  };
  var applyViewState = function(st) {
    st = st || {};
    setColFilters(st.filters || {});
    setSortCol(st.sortCol !== undefined ? st.sortCol : (defaultSort || null));
    setSortDir(st.sortDir || defaultSortDir || 'asc');
    var nextVis = {};
    columns.forEach(function(col) {
      nextVis[col.key] = st.visible && st.visible[col.key] !== undefined ? st.visible[col.key] : (col.defaultVisible !== false);
      if (col.locked) nextVis[col.key] = true;
    });
    setVisible(nextVis);
    setColOrder(st.order || null);
    if (st.pageSize) setPageSize(st.pageSize);
    setCurrentPage(0);
  };
  var selectView = function(v) {
    setViewMenuOpen(false);
    if (!v) {
      // "Default" = factory state; also clear the base prefs so a reload matches what's shown
      setActiveView(null);
      applyViewState(null);
      if (storageKey) {
        delete _colPrefs[storageKey + '_activeView'];
        delete _colPrefs[storageKey];
        delete _colPrefs[storageKey + '_order'];
        delete _colPrefs[storageKey + '_sort'];
        saveColumnPrefs();
      }
      return;
    }
    setActiveView(v.name);
    applyViewState(v.state);
    if (storageKey) { _colPrefs[storageKey + '_activeView'] = v.name; saveColumnPrefs(); }
  };
  var saveCurrentView = function() {
    var name = (viewName || '').trim();
    if (!name || !storageKey) return;
    var next = userViews.filter(function(v) { return v.name !== name; });
    next.push({ name: name, state: currentViewState() });
    setUserViews(next);
    setActiveView(name);
    setViewName('');
    setViewMenuOpen(false);
    _colPrefs[storageKey + '_views'] = next;
    _colPrefs[storageKey + '_activeView'] = name;
    saveColumnPrefs();
  };
  var deleteUserView = function(v) {
    if (!window.confirm('Delete the view "' + v.name + '"?')) return;
    var next = userViews.filter(function(x) { return x.name !== v.name; });
    setUserViews(next);
    if (activeView === v.name) setActiveView(null);
    if (defaultView === v.name) setDefaultView(null);
    if (storageKey) {
      _colPrefs[storageKey + '_views'] = next;
      if (activeView === v.name) delete _colPrefs[storageKey + '_activeView'];
      if (defaultView === v.name) delete _colPrefs[storageKey + '_defaultView'];
      saveColumnPrefs();
    }
  };
  var toggleDefaultView = function(v) {
    var next = defaultView === v.name ? null : v.name;
    setDefaultView(next);
    if (storageKey) {
      if (next) _colPrefs[storageKey + '_defaultView'] = next;
      else delete _colPrefs[storageKey + '_defaultView'];
      saveColumnPrefs();
    }
  };
  useEffect(function() {
    if (!viewMenuOpen) return;
    var onDoc = function(e) { if (viewMenuRef.current && !viewMenuRef.current.contains(e.target)) setViewMenuOpen(false); };
    document.addEventListener('mousedown', onDoc);
    return function() { document.removeEventListener('mousedown', onDoc); };
  }, [viewMenuOpen]);

  // Apply the starred default view (or the remembered active one) on mount. syncFromPrefs
  // only runs on the first-ever prefs load or a storageKey change — a remount with prefs
  // already cached would otherwise show the view's name without its filters/sort.
  useEffect(function() {
    if (!enableViews || !storageKey || !_colPrefsLoaded) return;
    var vlist = _colPrefs[storageKey + '_views'] || [];
    var dvName = _colPrefs[storageKey + '_defaultView'];
    var avName = _colPrefs[storageKey + '_activeView'];
    var pick = (dvName && vlist.find(function(v) { return v.name === dvName; }))
      || (avName && vlist.find(function(v) { return v.name === avName; })) || null;
    if (pick) { setActiveView(pick.name); applyViewState(pick.state); }
  }, []);

  var handleSort = function(col) {
    var nextDir = 'asc';
    if (sortCol === col.key) {
      nextDir = sortDir === 'asc' ? 'desc' : 'asc';
      setSortDir(nextDir);
    } else {
      setSortCol(col.key);
      setSortDir('asc');
    }
    // Remember the last clicked sort per user — restored on the next visit
    if (storageKey) { _colPrefs[storageKey + '_sort'] = { col: col.key, dir: nextDir }; saveColumnPrefs(); }
  };
  // A column that stops being sortable cannot stay the sort key (the Orders list sorts by anything
  // only inside a search; outside one the server sorts by date whatever is asked): the arrow would
  // sit on a column the server ignores. Back to the default sort (polish 2026-10-11). The saved
  // per-user sort is left alone so it applies again once the column is sortable.
  var _noSortKeys = columns.filter(colNoSort).map(function(c) { return c.key; }).join('|');
  useEffect(function() {
    if (!sortCol) return;
    var cur = columns.find(function(c) { return c.key === sortCol; });
    if (!cur || !colNoSort(cur)) return;
    setSortCol(defaultSort || null);
    setSortDir(defaultSortDir || 'asc');
  }, [_noSortKeys, sortCol]);

  var visibleCols = columns.filter(function(col) { return visible[col.key]; });

  // Separate normal columns from subRow columns
  var normalCols = orderCols(visibleCols.filter(function(col) { return !col.subRow; }));
  var subRowCols = visibleCols.filter(function(col) { return col.subRow; });

  // Dynamic font size based on normal column count (subRow cols don't affect width)
  var colCount = normalCols.length;
  var tableFontSize = fontSize || (compact ? 12 : (colCount <= 6 ? 14 : colCount <= 10 ? 14 : 13));
  var cellPad = cellPadding || '6px 8px';

  // Apply column filters
  var filteredData = data;
  var filterKeys = Object.keys(colFilters);
  if (filterKeys.length > 0) {
    var _filterColByKey = {};
    columns.forEach(function(c) { _filterColByKey[c.key] = c; });
    filteredData = data.filter(function(row) {
      return filterKeys.every(function(colKey) {
        var f = colFilters[colKey];
        if (!f || !f.mode) return true;
        // filterValue → raw field → sortValue → render() when it returns plain text, so
        // computed columns (margin, vendor name, ROI…) filter on what the user sees.
        var cd = _filterColByKey[colKey];
        var rawVal = cd ? _dtColValue(cd, row) : row[colKey];
        var caseSensitive = f.matchCase;
        var val = rawVal == null ? '' : String(rawVal);
        var search = (f.value || '').trim();
        if (!caseSensitive) { val = val.toLowerCase(); search = search.toLowerCase(); }
        switch (f.mode) {
          case 'contains': return search === '' || val.includes(search);
          case 'equals': return search === '' || val === search;
          case 'starts': return search === '' || val.startsWith(search);
          case 'ends': return search === '' || val.endsWith(search);
          case 'blank': return rawVal == null || String(rawVal).trim() === '';
          case 'not_blank': return rawVal != null && String(rawVal).trim() !== '';
          // Numeric modes: a missing value (null/'') is NOT zero — it can never satisfy
          // a numeric comparison. Number(null) === 0 would silently include such rows.
          case 'gt': case 'lt': case 'gte': case 'lte': case 'eq':
            return _dtNumCompare(f.mode, rawVal, search);
          case 'on': case 'before': case 'after': {
            var _d = _dtToDay(rawVal), _q = _dtToDay(search);
            if (_d == null || _q == null) return false;
            return f.mode === 'on' ? _d === _q : f.mode === 'before' ? _d < _q : _d > _q;
          }
          case 'in': {
            if (!search) return true;
            var inVals = _dtSplitIn(search).map(function(v) { return v.toLowerCase(); });
            return inVals.includes(String(rawVal == null ? '' : rawVal).trim().toLowerCase());
          }
          default: return true;
        }
      });
    });
  }

  // Sort the data
  var sortedData = filteredData;
  if (sortCol) {
    var col = columns.find(function(c) { return c.key === sortCol; });
    sortedData = [].concat(filteredData).sort(function(a, b) {
      var aVal, bVal;
      if (col && typeof col.sortValue === 'function') {
        aVal = col.sortValue(a); bVal = col.sortValue(b);
      } else {
        var sk = (col && col.sortKey) || sortCol;
        aVal = a[sk]; bVal = b[sk];
      }
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';
      var aNum = Number(aVal), bNum = Number(bVal);
      if (!isNaN(aNum) && !isNaN(bNum) && String(aVal) !== '' && String(bVal) !== '') {
        return sortDir === 'asc' ? aNum - bNum : bNum - aNum;
      }
      var aStr = String(aVal).toLowerCase(), bStr = String(bVal).toLowerCase();
      if (aStr < bStr) return sortDir === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }

  // Paginate
  var totalRows = sortedData.length;
  var totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
  // Only reset page if we have data and it truly overflowed (avoid resetting during loading)
  if (!onServerFetch && totalRows > 0 && currentPage >= totalPages) setCurrentPage(Math.max(0, totalPages - 1));
  var pageData = sortedData.slice(currentPage * pageSize, (currentPage + 1) * pageSize);
  var showingFrom = totalRows === 0 ? 0 : currentPage * pageSize + 1;
  var showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  if (scrollMode && !onServerFetch) {
    // Accumulate batches from the top: currentPage = batches loaded − 1
    pageData = sortedData.slice(0, (currentPage + 1) * pageSize);
    showingFrom = totalRows === 0 ? 0 : 1;
    showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  }
  _scrollTotalRef.current = totalRows;

  // Server-side override: data is already filtered/sorted/paginated by server
  if (onServerFetch) {
    filteredData = data; sortedData = data; pageData = data;
    totalRows = serverTotal || 0;
    totalPages = Math.max(1, Math.ceil(totalRows / pageSize));
    showingFrom = totalRows === 0 ? 0 : currentPage * pageSize + 1;
    showingTo = Math.min((currentPage + 1) * pageSize, totalRows);
  }

  // Expose sorted data to parent for shift-select.
  // No deps array: fires every render so the parent's ref stays in sync with the current sortedData
  // (data prop reference can change without length/sortCol/sortDir changing).
  useEffect(function() {
    if (onSortedData) onSortedData(sortedData);
    if (onPageData) onPageData(pageData);
  });

  var _isEmpty = data.length === 0;

  if (filteredData.length === 0 && activeFilterCount > 0) {
    return React.createElement(Card, { style: { padding: 0, overflow: 'auto' } },
      React.createElement('div', { style: { textAlign: 'center', padding: '40px 20px' } },
        React.createElement('div', { style: { fontSize: 14, color: C.textMuted, marginBottom: 8 } }, 'No rows match the active filters (' + activeFilterCount + ' filter' + (activeFilterCount > 1 ? 's' : '') + ')'),
        React.createElement('button', { onClick: clearAllFilters, style: { background: C.accent, border: 'none', color: '#fff', padding: '6px 14px', borderRadius: 6, fontSize: 12, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 500 } }, React.createElement(LIcon, { name: 'x', size: 12 }), ' Clear All Filters')
      )
    );
  }

  return React.createElement(Card, { style: Object.assign({ padding: 0, overflow: 'visible', position: 'relative' },
    (scrollHeight === 'fill') ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {}) },
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderBottom: '1px solid ' + C.border + '44', position: 'relative', flexWrap: 'wrap', gap: 8 } },
      // Rows per page
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, scrollMode ? 'Load' : 'Show'),
        rowOptions.map(function(n) {
          return React.createElement('button', {
            key: n, onClick: function() { changePageSize(n); },
            className: pageSize !== n ? 'dt-row-btn' : '',
            style: {
              padding: '2px 8px', fontSize: 11, borderRadius: 4, border: 'none', cursor: 'pointer',
              fontFamily: 'inherit', fontWeight: pageSize === n ? 600 : 400,
              background: pageSize === n ? C.accent : 'transparent',
              color: pageSize === n ? '#fff' : C.textMuted,
              transition: 'background 0.15s'
            }
          }, String(n));
        }),
        React.createElement('span', { style: { fontSize: 11, color: C.textMuted } }, scrollMode ? 'rows per scroll' : 'rows')
      ),
      // Active filters indicator
      activeFilterCount > 0 && React.createElement('button', {
        onClick: clearAllFilters,
        style: { background: C.warningSoft, border: '1px solid ' + C.warning + '44', color: C.warning, padding: '3px 10px', borderRadius: 4, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, display: 'flex', alignItems: 'center', gap: 4 }
      }, React.createElement(LIcon, { name: 'zap', size: 12 }), ' ' + activeFilterCount + ' filter' + (activeFilterCount > 1 ? 's' : '') + ' active — Clear'),
      // Bulk Actions + Columns toggle
      React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
        // Saved views dropdown (opt-in) — portals to viewsPortalEl when the page wants it
        // rendered outside the table toolbar (state stays in this component either way)
        (function() {
          if (!enableViews || !storageKey) return null;
          var ui = React.createElement('div', { key: '__views', ref: viewMenuRef, style: { position: 'relative', display: 'inline-block' } },
          React.createElement('button', {
            onClick: function() { setViewMenuOpen(!viewMenuOpen); },
            className: 'dt-btn',
            title: 'Saved views — save the current filters, sort, column order and visibility under a name',
            style: { background: 'none', border: '1px solid ' + (activeView ? C.accent + '77' : C.border), color: activeView ? C.accent : C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, fontWeight: activeView ? 600 : 400, maxWidth: 220 }
          },
            React.createElement(LIcon, { name: 'bookmark', size: 12 }),
            React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, 'View: ' + (activeView || 'Default')),
            React.createElement(LIcon, { name: 'chevron-down', size: 11 })),
          viewMenuOpen && React.createElement('div', {
            style: Object.assign({ position: 'absolute', top: '100%', zIndex: 120, marginTop: 4, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 6, minWidth: 240, boxShadow: '0 8px 30px rgba(0,0,0,0.4)' }, viewsPortalEl ? { left: 0 } : { right: 0 })
          },
            [{ v: null, label: 'Default' }].concat(userViews.map(function(v) { return { v: v, label: v.name }; })).map(function(it) {
              var isActive = it.v === null ? !activeView : activeView === it.v.name;
              var isStarred = it.v !== null && defaultView === it.v.name;
              return React.createElement('div', {
                key: it.v === null ? '__default' : it.v.name,
                onClick: function() { selectView(it.v); },
                style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '6px 8px', borderRadius: 5, cursor: 'pointer', fontSize: 12, color: isActive ? C.accent : C.text, fontWeight: isActive ? 600 : 400 },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.bg; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              },
                React.createElement('span', { style: { display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
                  isActive && React.createElement(LIcon, { name: 'check', size: 12, color: C.accent }), it.label),
                it.v !== null && React.createElement('span', { style: { display: 'inline-flex', gap: 2, flexShrink: 0 } },
                  React.createElement('span', {
                    title: isStarred ? 'Default view for every list — click to unset' : 'Make this my default view for every list',
                    onClick: function(e) { e.stopPropagation(); toggleDefaultView(it.v); },
                    style: { display: 'inline-flex', padding: 2, opacity: isStarred ? 1 : 0.45 }
                  }, React.createElement(LIcon, { name: 'star', size: 12, color: isStarred ? '#F59E0B' : C.textMuted })),
                  React.createElement('span', {
                    title: 'Delete view',
                    onClick: function(e) { e.stopPropagation(); deleteUserView(it.v); },
                    style: { display: 'inline-flex', padding: 2, opacity: 0.55 }
                  }, React.createElement(LIcon, { name: 'x', size: 12, color: C.textMuted }))));
            }),
            React.createElement('div', { style: { borderTop: '1px solid ' + C.border + '66', marginTop: 6, paddingTop: 8, display: 'flex', gap: 6, alignItems: 'center' } },
              React.createElement('input', {
                value: viewName,
                onChange: function(e) { setViewName(e.target.value); },
                onKeyDown: function(e) { if (e.key === 'Enter') saveCurrentView(); },
                placeholder: 'Save current as…',
                style: { flex: 1, minWidth: 0, background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '5px 8px', borderRadius: 5, fontSize: 12, fontFamily: 'inherit' }
              }),
              React.createElement('button', {
                onClick: saveCurrentView,
                disabled: !viewName.trim(),
                style: { background: viewName.trim() ? C.accent : C.border, border: 'none', color: '#fff', padding: '5px 10px', borderRadius: 5, fontSize: 11, fontWeight: 600, cursor: viewName.trim() ? 'pointer' : 'default', fontFamily: 'inherit' }
              }, 'Save'))));
          return viewsPortalEl && window.ReactDOM && ReactDOM.createPortal
            ? ReactDOM.createPortal(ui, viewsPortalEl) : ui;
        })(),
        extraToolbar,
        // Bulk Actions dropdown
        bulkActions && (bulkActions.count > 0 || bulkActions.alwaysShow) && React.createElement('div', { style: { position: 'relative' } },
          React.createElement('button', {
            ref: bulkBtnRef,
            onClick: toggleBulkMenu,
            // Same hover as a primary Btn — this was the only toolbar button with no hover feedback
            onMouseOver: function(e) { e.currentTarget.style.filter = 'brightness(1.15)'; e.currentTarget.style.transform = 'translateY(-1px)'; },
            onMouseOut: function(e) { e.currentTarget.style.filter = ''; e.currentTarget.style.transform = ''; },
            style: { background: C.accent, border: 'none', color: '#fff', cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, fontWeight: 600, transition: 'all 0.15s' }
          }, React.createElement(LIcon, { name: 'zap', size: 12 }),
            // Fixed-width count slot so the button (and everything left of it)
            // doesn't jitter when the selection count appears/changes
            React.createElement('span', { style: { display: 'inline-block', minWidth: 52, textAlign: 'left' } }, ' Actions' + (bulkActions.count > 0 ? ' (' + bulkActions.count + ')' : ''))),
          showBulkMenu && React.createElement('div', {
            ref: bulkMenuRef,
            style: { position: 'fixed', top: bulkMenuPos.top, right: bulkMenuPos.right, zIndex: 10000, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '6px 0', minWidth: 220, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - ' + bulkMenuPos.top + 'px - 10px)', overflowY: 'auto' }
          },
            (bulkActions.items || []).map(function(item, idx) {
              if (item.divider) return React.createElement('div', { key: 'div-' + idx, style: { height: 1, background: C.border + '44', margin: '4px 0' } });
              if (item.section) return React.createElement('div', { key: 'sec-' + idx, style: { padding: '6px 14px 4px', fontSize: 9, fontWeight: 700, color: C.textDim, textTransform: 'uppercase', letterSpacing: '0.05em' } }, item.section);
              if (item.header) return React.createElement('div', { key: 'hdr-' + idx, style: { padding: '6px 14px', fontSize: 11, color: C.textMuted, fontWeight: 600, borderBottom: '1px solid ' + C.border + '44', marginBottom: 4 } }, item.header);
              return React.createElement('button', {
                key: item.label + '-' + idx,
                disabled: !!item.disabled,
                title: item.title || undefined,
                onClick: function() { if (item.disabled) return; setShowBulkMenu(false); item.onClick(); },
                style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 14px', cursor: item.disabled ? 'not-allowed' : 'pointer', fontSize: 12, color: item.disabled ? C.textDim : (item.danger ? C.danger : C.text), opacity: item.disabled ? 0.5 : 1, fontFamily: 'inherit', borderRadius: 0 },
                onMouseEnter: function(e) { if (!item.disabled) e.currentTarget.style.background = C.surfaceHover; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; }
              }, item.icon ? React.createElement(LIcon, { name: item.icon, size: 14, color: item.disabled ? C.textDim : (item.danger ? C.danger : C.textMuted) }) : null, item.label);
            }),
            bulkActions.count > 0 && bulkActions.onClear && React.createElement('div', { style: { height: 1, background: C.border + '44', margin: '4px 0' } }),
            bulkActions.count > 0 && bulkActions.onClear && React.createElement('button', {
              onClick: function() { setShowBulkMenu(false); bulkActions.onClear(); },
              style: { display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left', background: 'none', border: 'none', padding: '7px 14px', cursor: 'pointer', fontSize: 12, color: C.textMuted, fontFamily: 'inherit', borderRadius: 0 },
              onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; },
              onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; }
            }, React.createElement(LIcon, { name: 'x', size: 14, color: C.textMuted }), 'Clear selection')
          )
        ),
        // Columns toggle
        React.createElement('div', { style: { position: 'relative' } },
        React.createElement('button', {
          onClick: function() { setShowColMenu(!showColMenu); },
          className: 'dt-btn',
          style: { background: 'none', border: '1px solid ' + C.border, color: C.textMuted, cursor: 'pointer', padding: '4px 10px', borderRadius: 4, fontSize: 11, fontFamily: 'inherit', display: 'flex', alignItems: 'center', gap: 4, transition: 'border-color 0.15s, background 0.15s' }
        }, React.createElement(LIcon, { name: 'settings', size: 12 }), ' Columns'),
        showColMenu && React.createElement('div', {
          ref: menuRef,
          style: { position: 'absolute', top: '100%', right: 0, zIndex: 100, background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 8, minWidth: 180, boxShadow: '0 8px 30px rgba(0,0,0,0.4)', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 240px)', overflowY: 'auto' }
        },
          columns.filter(function(col) { return !col.locked; })
            .slice().sort(function(a, b) { return String(a.label || a.key).localeCompare(String(b.label || b.key)); })
            .map(function(col) {
            return React.createElement('div', {
              key: col.key,
              onClick: function() { toggleCol(col.key); },
              style: { display: 'flex', alignItems: 'center', gap: 8, padding: '5px 8px', cursor: 'pointer', borderRadius: 4, fontSize: 12, color: visible[col.key] ? C.text : C.textDim, userSelect: 'none' }
            },
              React.createElement(SysCheckbox, { checked: !!visible[col.key], size: 16 }),
              col.label
            );
          }),
          colOrder && colOrder.length > 0 && React.createElement('button', {
            onClick: function() { resetColOrder(); setShowColMenu(false); },
            style: { display: 'flex', alignItems: 'center', gap: 6, width: '100%', marginTop: 6, background: 'none', border: 'none', borderRadius: 0, padding: '8px 8px 4px', borderTop: '1px solid ' + C.border + '66', cursor: 'pointer', fontSize: 12, color: C.textMuted, fontFamily: 'inherit' }
          }, React.createElement(LIcon, { name: 'undo-2', size: 13 }), 'Reset column order')
        )
      )
      ) // close bulk actions + columns wrapper
    ),
    // Table — scrollHeight 'fill' makes the scroll area flex-fill its parent
    // (the DataTable root becomes a flex column; page owns the height).
    // Works with pagination (default) or scrollMode batches.
    React.createElement('div', { ref: scrollRef, onScroll: handleScroll, style: scrollHeight === 'fill'
      ? { overflow: 'auto', flex: _isEmpty ? '0 0 auto' : 1, minHeight: filterMenuCol ? 340 : 0 } // empty: the height goes to the centred empty state
      : scrollMode
      ? { overflow: 'auto', height: scrollHeight || 600, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 210px)', minHeight: filterMenuCol ? 340 : 0, transition: 'min-height 0.15s' }
      : { overflow: 'auto', maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 210px)', minHeight: filterMenuCol ? 340 : 0, transition: 'min-height 0.15s' } },
    React.createElement('table', { style: { width: '100%', fontSize: tableFontSize, tableLayout: 'auto' } },
      React.createElement('thead', null,
        React.createElement('tr', { style: { borderBottom: '1px solid ' + C.border } },
          normalCols.map(function(col) {
            var isSorted = sortCol === col.key;
            var arrow = isSorted ? (sortDir === 'asc' ? ' ▲' : ' ▼') : '';
            var hasFilter = colFilters[col.key] && colFilters[col.key].mode;
            var isFilterOpen = filterMenuCol === col.key;
            var isFilterable = !noColumnFilters && !col.locked && !col.noFilter && col.key.charAt(0) !== '_';
            var colWidth = col.width || col.colWidth;
            return React.createElement('th', {
              key: col.key,
              className: 'dt-th',
              onClick: colNoSort(col) ? undefined : function() { handleSort(col); },
              style: {
                padding: cellPad, textAlign: 'left', fontWeight: 600, fontSize: compact ? 10 : (colCount <= 8 ? 11 : 10),
                color: isSorted ? C.accent : C.textMuted,
                textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap',
                cursor: colNoSort(col) ? 'default' : 'pointer', userSelect: 'none', transition: 'color 0.15s', position: 'sticky', top: 0, zIndex: col.sticky ? 21 : 20, background: C.bg,
                left: col.sticky ? 0 : undefined,
                width: colWidth,
                boxShadow: dragOverCol === col.key && dragCol !== col.key ? 'inset 3px 0 0 ' + C.accent : undefined,
                opacity: dragCol === col.key ? 0.35 : 1
              },
              // Drag a header onto another to reorder columns (saved per user via storageKey)
              draggable: !col.sticky,
              onDragStart: function(e) {
                setDragCol(col.key);
                try { e.dataTransfer.setData('text/plain', col.key); } catch (err) {}
                e.dataTransfer.effectAllowed = 'move';
              },
              onDragOver: function(e) {
                if (!dragCol || dragCol === col.key) return;
                e.preventDefault();
                e.dataTransfer.dropEffect = 'move';
                if (dragOverCol !== col.key) setDragOverCol(col.key);
              },
              onDragLeave: function() { if (dragOverCol === col.key) setDragOverCol(null); },
              onDrop: function(e) { e.preventDefault(); dropColumn(col.key); },
              onDragEnd: function() { setDragCol(null); setDragOverCol(null); }
            },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 4 } },
                col.headerRender
                  ? React.createElement('span', { style: { display: 'inline-flex', alignItems: 'center', gap: 4 } }, col.headerRender(), colNoSort(col) ? null : arrow)
                  : React.createElement('span', null, col.label + (colNoSort(col) ? '' : arrow)),
                // Filter button — its own 22px hit target so a near miss never sorts the column
                isFilterable && React.createElement('span', {
                  className: 'dt-filter-btn',
                  onClick: function(e) { e.stopPropagation(); if (isFilterOpen) { setFilterMenuCol(null); setPendingFilter(null); } else { openFilterMenu(col.key); } },
                  onMouseDown: function(e) { e.stopPropagation(); },
                  style: { cursor: 'pointer', opacity: (hasFilter || isFilterOpen) ? 1 : 0.55, marginLeft: 6, width: 22, height: 22, borderRadius: 6, flex: '0 0 auto',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center', background: (hasFilter || isFilterOpen) ? C.accentSoft : 'transparent' }
                }, React.createElement(LIcon, { name: 'filter', size: 13, color: hasFilter ? C.accent : C.textMuted }))
              ),
              // Filter dropdown — kind-aware (list / number / date / text)
              isFilterOpen && pendingFilter && (function() {
                var kind = pendingFilter.kind || 'text';
                var setP = function(patch) { setPendingFilter(Object.assign({}, pendingFilter, patch)); };
                var valueOff = pendingFilter.mode === 'blank' || pendingFilter.mode === 'not_blank';
                var pillStyle = function(active) {
                  return { padding: '3px 9px', fontSize: 11, borderRadius: 99, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontWeight: active ? 600 : 400,
                    background: active ? C.accent : C.bg, color: active ? '#fff' : C.textMuted, transition: 'all 0.12s' };
                };
                var pillRow = function(modes, mb) {
                  return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 4, marginBottom: mb || 6 } },
                    modes.map(function(m) {
                      return React.createElement('button', { key: m.val, onClick: function() { setP({ mode: m.val }); }, style: pillStyle(pendingFilter.mode === m.val) }, m.lbl);
                    }));
                };
                var actionBtns = function() {
                  return [
                    React.createElement('button', { key: 'clr', onClick: function() { clearFilter(col.key); },
                      style: { background: 'none', border: 'none', color: C.textMuted, padding: '5px 10px', fontSize: 11, cursor: 'pointer', fontFamily: 'inherit' } }, 'Clear'),
                    React.createElement('button', { key: 'ok', onClick: function() { applyFilter(col.key); },
                      style: { background: C.accent, border: 'none', color: '#fff', padding: '5px 14px', borderRadius: 6, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600 } }, 'Apply')
                  ];
                };
                var body;
                if (kind === 'list') {
                  var options = _dtListOptions(col, data);
                  var q = (pendingFilter.q || '').trim().toLowerCase();
                  var shown = q ? options.filter(function(o) { return o.toLowerCase().indexOf(q) !== -1; }) : options;
                  var selectedVals = _dtSplitIn(pendingFilter.value);
                  var isSel = function(o) { return selectedVals.indexOf(o) !== -1; };
                  body = React.createElement(React.Fragment, null,
                    options.length > 8 && React.createElement('input', {
                      ref: filterInputRef, type: 'text', placeholder: 'Search ' + options.length + ' values…', value: pendingFilter.q || '',
                      onChange: function(e) { setP({ q: e.target.value }); },
                      onKeyDown: function(e) { if (e.key === 'Enter') applyFilter(col.key); if (e.key === 'Escape') { setFilterMenuCol(null); setPendingFilter(null); } },
                      className: 'dt-input',
                      style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: '6px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', outline: 'none' }
                    }),
                    React.createElement('div', { style: { display: 'flex', gap: 10, marginBottom: 8, alignItems: 'center' } },
                      React.createElement('button', {
                        onClick: function() { var merged = selectedVals.slice(); shown.forEach(function(o) { if (merged.indexOf(o) === -1) merged.push(o); }); setP({ mode: 'in', value: merged.join(DT_IN_SEP) }); },
                        style: { background: 'none', border: 'none', color: C.accent, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', fontWeight: 600, padding: 0 }
                      }, q ? 'Select shown' : 'All'),
                      React.createElement('button', {
                        onClick: function() { setP({ mode: 'in', value: '' }); },
                        style: { background: 'none', border: 'none', color: C.textMuted, fontSize: 11, cursor: 'pointer', fontFamily: 'inherit', padding: 0 }
                      }, 'Clear'),
                      selectedVals.length > 0 && React.createElement('span', { style: { marginLeft: 'auto', fontSize: 11, color: C.textMuted } }, selectedVals.length + ' selected')
                    ),
                    React.createElement('div', { style: { maxHeight: 220, overflowY: 'auto', marginBottom: 10 } },
                      shown.length === 0 && React.createElement('div', { style: { fontSize: 12, color: C.textDim, padding: '6px 0' } }, options.length === 0 ? 'No values in this column' : 'No matches'),
                      shown.map(function(opt) {
                        var checked = isSel(opt);
                        return React.createElement('div', {
                          key: opt, role: 'option', 'aria-selected': checked,
                          onClick: function() {
                            var next = checked ? selectedVals.filter(function(v) { return v !== opt; }) : selectedVals.concat([opt]);
                            setP({ mode: 'in', value: next.join(DT_IN_SEP) });
                          },
                          style: { display: 'flex', alignItems: 'center', gap: 8, padding: '4px 2px', cursor: 'pointer', fontSize: 12, color: C.text, userSelect: 'none', borderRadius: 4 }
                        },
                          React.createElement(SysCheckbox, { checked: checked, size: 16 }),
                          React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, opt)
                        );
                      })
                    ),
                    React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } }, actionBtns())
                  );
                } else {
                  var modes = kind === 'number'
                    ? [{ val: 'eq', lbl: '=' }, { val: 'gt', lbl: '>' }, { val: 'gte', lbl: '≥' }, { val: 'lt', lbl: '<' }, { val: 'lte', lbl: '≤' }]
                    : kind === 'date'
                      ? [{ val: 'on', lbl: 'On' }, { val: 'before', lbl: 'Before' }, { val: 'after', lbl: 'After' }]
                      : [{ val: 'contains', lbl: 'Contains' }, { val: 'equals', lbl: 'Equals' }, { val: 'starts', lbl: 'Starts with' }, { val: 'ends', lbl: 'Ends with' }];
                  body = React.createElement(React.Fragment, null,
                    React.createElement('input', {
                      ref: filterInputRef,
                      type: kind === 'date' ? 'date' : 'text',
                      inputMode: kind === 'number' ? 'decimal' : undefined,
                      placeholder: kind === 'number' ? 'Enter a number' : kind === 'date' ? '' : 'Type to filter...',
                      value: pendingFilter.value || '',
                      onChange: function(e) { setP({ value: e.target.value }); },
                      onKeyDown: function(e) { if (e.key === 'Enter') applyFilter(col.key); if (e.key === 'Escape') { setFilterMenuCol(null); setPendingFilter(null); } },
                      disabled: valueOff,
                      className: 'dt-input',
                      style: { width: '100%', boxSizing: 'border-box', background: C.bg, border: '1px solid ' + C.border, color: valueOff ? C.textDim : C.text, padding: '7px 10px', borderRadius: 6, fontSize: 12, marginBottom: 8, fontFamily: 'inherit', outline: 'none', colorScheme: kind === 'date' ? (getThemeName() === 'dark' ? 'dark' : 'light') : undefined }
                    }),
                    pillRow(modes),
                    pillRow([{ val: 'blank', lbl: 'Is blank' }, { val: 'not_blank', lbl: 'Not blank' }], 10),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 8 } },
                      kind === 'text' && !valueOff
                        ? React.createElement('div', { onClick: function() { setP({ matchCase: !pendingFilter.matchCase }); }, style: { display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, color: C.textMuted, cursor: 'pointer', userSelect: 'none', flex: 1 } },
                            React.createElement(SysCheckbox, { checked: !!pendingFilter.matchCase, size: 14 }),
                            'Aa')
                        : React.createElement('div', { style: { flex: 1 } }),
                      actionBtns()
                    )
                  );
                }
                return React.createElement('div', {
                  ref: filterMenuRef,
                  onClick: function(e) { e.stopPropagation(); },
                  style: { position: 'absolute', top: '100%', left: 0, zIndex: 200, background: C.surface, border: '1px solid ' + C.border, borderRadius: 10, padding: '12px 14px', minWidth: 250, maxWidth: 340, boxShadow: '0 8px 30px rgba(0,0,0,0.35)', textTransform: 'none', letterSpacing: 'normal', fontWeight: 400, maxHeight: 'calc(calc(100vh * var(--invzoom, 1)) - 240px)', overflowY: 'auto', cursor: 'default' }
                },
                  React.createElement('div', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 10 } },
                    'Filter ' + col.label, React.createElement('span', { style: { fontWeight: 400, color: C.textDim, marginLeft: 6 } }, kind === 'list' ? 'values' : kind)),
                  body
                );
              })()
            );
          }),
          onAction && React.createElement('th', { style: { width: 40, padding: cellPad } })
        )
      ),
      React.createElement('tbody', null,
        pageData.map(function(row, ri) {
          var totalNormalCols = normalCols.length + (onAction ? 1 : 0);
          var _rowHref = rowHref ? rowHref(row) : null;
          var _rs = rowStyle ? (rowStyle(row) || null) : null;
          var _rbg = (_rs && _rs.background) || 'transparent';
          var mainRow = React.createElement('tr', {
            key: row.id || ri,
            // Suppress Chrome's auto-scroll on middle-click so onAuxClick can open a new tab.
            onMouseDown: (_rowHref || onRowClick) ? function(e) { if (_rowHref && e.button === 1) e.preventDefault(); if (onRowClick && e.shiftKey) e.preventDefault(); } : undefined,
            onClick: onRowClick ? function(e) { if (_rowHref && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(_rowHref, '_blank'); return; } onRowClick(row, e); } : undefined,
            onAuxClick: onRowClick && _rowHref ? function(e) { if (e.button === 1) { e.preventDefault(); window.open(_rowHref, '_blank'); } } : undefined,
            style: Object.assign({ borderBottom: subRowCols.length > 0 ? 'none' : '1px solid ' + C.border, cursor: onRowClick ? 'pointer' : 'default', userSelect: 'none' }, _rs || {}),
            onMouseEnter: function(e) { var next = e.currentTarget.nextElementSibling; e.currentTarget.style.background = C.surfaceHover; if (next && next.dataset.subrow) next.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { var next = e.currentTarget.nextElementSibling; e.currentTarget.style.background = _rbg; if (next && next.dataset.subrow) next.style.background = _rbg; }
          },
            normalCols.map(function(col) {
              var content = col.render ? col.render(row) : (row[col.key] != null ? String(row[col.key]) : '');
              if (col.copyable) {
                var copyVal = typeof col.copyValue === 'function' ? col.copyValue(row) : row[col.key];
                if (copyVal != null && String(copyVal).trim() !== '') {
                  content = React.createElement(CopyCell, { value: copyVal }, content);
                }
              }
              var baseStyle = Object.assign({ padding: cellPad, whiteSpace: col.wrap ? 'normal' : 'nowrap' }, col.style || { color: C.textMuted }, col.sticky ? { position: 'sticky', left: 0, zIndex: 10, background: C.bg } : {});
              if (col.width || col.colWidth) { baseStyle.width = col.width || col.colWidth; baseStyle.minWidth = col.width || col.colWidth; baseStyle.maxWidth = col.width || col.colWidth; }
              var tdProps = { key: col.key, style: baseStyle };
              if (col.stopClick) tdProps.onClick = function(e) { e.stopPropagation(); };
              return React.createElement(TD, tdProps, content);
            }),
            onAction && React.createElement(TD, { style: { padding: cellPad } },
              React.createElement('button', {
                onClick: function(e) { e.stopPropagation(); onAction('delete', row); },
                style: { background: 'none', border: 'none', color: C.textDim, cursor: 'pointer' }
              }, React.createElement(LIcon, { name: 'trash-2', size: 14 }))
            )
          );
          if (subRowCols.length === 0) return mainRow;
          var subRow = React.createElement('tr', {
            key: (row.id || ri) + '_sub', 'data-subrow': 'true',
            onMouseDown: (_rowHref || onRowClick) ? function(e) { if (_rowHref && e.button === 1) e.preventDefault(); if (onRowClick && e.shiftKey) e.preventDefault(); } : undefined,
            onClick: onRowClick ? function(e) { if (_rowHref && (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1)) { window.open(_rowHref, '_blank'); return; } onRowClick(row, e); } : undefined,
            onAuxClick: onRowClick && _rowHref ? function(e) { if (e.button === 1) { e.preventDefault(); window.open(_rowHref, '_blank'); } } : undefined,
            style: { borderBottom: '1px solid ' + C.border, cursor: onRowClick ? 'pointer' : 'default', userSelect: 'none' },
            onMouseEnter: function(e) { var prev = e.currentTarget.previousElementSibling; e.currentTarget.style.background = C.surfaceHover; if (prev) prev.style.background = C.surfaceHover; },
            onMouseLeave: function(e) { var prev = e.currentTarget.previousElementSibling; e.currentTarget.style.background = 'transparent'; if (prev) prev.style.background = 'transparent'; }
          },
            React.createElement('td', { colSpan: totalNormalCols, style: { padding: '0 ' + (cellPad.split(' ')[1] || '12px') + ' 6px', fontSize: tableFontSize - 1 } },
              React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: '2px 16px' } },
                subRowCols.map(function(col) {
                  var content = col.render ? col.render(row) : (row[col.key] != null ? String(row[col.key]) : '');
                  if (!content && content !== 0) return null;
                  return React.createElement('span', { key: col.key, style: Object.assign({ color: C.textMuted }, col.style) },
                    React.createElement('span', { style: { fontSize: tableFontSize - 2, color: C.textDim, marginRight: 4 } }, col.label + ':'),
                    content
                  );
                })
              )
            )
          );
          return React.createElement(React.Fragment, { key: row.id || ri }, mainRow, subRow);
        })
      )
    ),
    ), // close table wrapper div
    // Empty state
    _isEmpty && React.createElement(Empty, { title: emptyTitle || 'No data', sub: emptySub || '', fill: scrollHeight === 'fill' }),
    // Pagination footer (scroll mode: progress line only — batches load on scroll)
    !_isEmpty && scrollMode && React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 12, color: C.textMuted }
    },
      React.createElement('span', null, 'Showing ' + showingTo.toLocaleString() + ' of ' + totalRows.toLocaleString() + (activeFilterCount > 0 ? ' (filtered from ' + data.length.toLocaleString() + ')' : '')),
      showingTo < totalRows && React.createElement('span', { style: { color: C.textDim } }, 'scroll to load ' + Math.min(pageSize, totalRows - showingTo) + ' more')
    ),
    !_isEmpty && !scrollMode && totalRows > pageSize && React.createElement('div', {
      style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 14px', borderTop: '1px solid ' + C.border + '44', fontSize: 13, color: C.textMuted }
    },
      React.createElement('span', null, 'Showing ' + showingFrom + '–' + showingTo + ' of ' + totalRows.toLocaleString() + (activeFilterCount > 0 ? ' (filtered from ' + data.length.toLocaleString() + ')' : '')),
      React.createElement('div', { style: { display: 'flex', gap: 4 } },
        React.createElement('button', {
          onClick: function() { setCurrentPage(0); }, disabled: currentPage === 0,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage === 0 ? C.textDim : C.textMuted, cursor: currentPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, '« First'),
        React.createElement('button', {
          onClick: function() { setCurrentPage(Math.max(0, currentPage - 1)); }, disabled: currentPage === 0,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage === 0 ? C.textDim : C.textMuted, cursor: currentPage === 0 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, '‹ Prev'),
        React.createElement('span', { style: { padding: '5px 10px', fontSize: 12, color: C.text, fontWeight: 500 } }, 'Page ' + (currentPage + 1) + ' of ' + totalPages),
        React.createElement('button', {
          onClick: function() { setCurrentPage(Math.min(totalPages - 1, currentPage + 1)); }, disabled: currentPage >= totalPages - 1,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage >= totalPages - 1 ? C.textDim : C.textMuted, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, 'Next ›'),
        React.createElement('button', {
          onClick: function() { setCurrentPage(totalPages - 1); }, disabled: currentPage >= totalPages - 1,
          style: { padding: '5px 10px', fontSize: 12, borderRadius: 4, border: '1px solid ' + C.border, background: 'transparent', color: currentPage >= totalPages - 1 ? C.textDim : C.textMuted, cursor: currentPage >= totalPages - 1 ? 'default' : 'pointer', fontFamily: 'inherit' }
        }, 'Last »')
      )
    )
  );
}

// CSV download helpers
function downloadCSV(filename, csvContent) {
  var blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
  var url = URL.createObjectURL(blob);
  var a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

// One CSV cell rule for every export in the app (review 2026-09-28, medium #31): quoted when it
// holds a quote, comma or line break, and defused when it would start a spreadsheet formula
// (= + - @ tab CR) — a marketplace title or note like "=HYPERLINK(...)" ran as a formula when the
// file was opened in Excel. Plain numbers ("-3.5") stay numbers. Same rule as server/services/csv.js
// and the shipping app's shipCsvCell.
function escapeCSV(val) {
  if (val == null) return '';
  var s = String(val);
  if (/^[=+\-@\t\r]/.test(s) && !/^[-+]?\d+(\.\d+)?$/.test(s)) s = "'" + s;
  return /[",\n\r]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
}

function arrayToCSV(headers, rows) {
  var lines = [headers.map(escapeCSV).join(',')];
  rows.forEach(function(row) {
    lines.push(headers.map(function(h) { return escapeCSV(row[h]); }).join(','));
  });
  return lines.join('\n');
}

function downloadTemplate(filename, headers) {
  downloadCSV(filename, headers.join(',') + '\n');
}

// ─── MARKETPLACE CHANNEL ICONS (SVG logos) ───
function channelIcon(channel, size) {
  var s = size || 20;
  var svgProps = { width: s, height: s, viewBox: '0 0 24 24', style: { display: 'block', flexShrink: 0 } };

  if (channel === 'walmart') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#0071DC' }),
      React.createElement('path', { d: 'M12 4.5 L12 9.5 M12 14.5 L12 19.5 M5.2 8 L9.5 10.5 M14.5 13.5 L18.8 16 M18.8 8 L14.5 10.5 M9.5 13.5 L5.2 16', stroke: '#FFC220', strokeWidth: 2.2, strokeLinecap: 'round', fill: 'none' })
    );
  }
  if (channel === 'amazon') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#232F3E' }),
      React.createElement('path', { d: 'M7 13.5 Q12 17 17 13.5', stroke: '#FF9900', strokeWidth: 2, strokeLinecap: 'round', fill: 'none' }),
      React.createElement('path', { d: 'M15.5 12 L17 13.5 L15.5 15', stroke: '#FF9900', strokeWidth: 1.8, strokeLinecap: 'round', strokeLinejoin: 'round', fill: 'none' }),
      React.createElement('text', { x: 12, y: 11.5, textAnchor: 'middle', fill: '#FFFFFF', fontSize: 7, fontWeight: 700, fontFamily: 'Arial' }, 'a')
    );
  }
  if (channel === 'ebay') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#FFFFFF' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 11.5, fill: 'none', stroke: '#E0E0E0', strokeWidth: 0.5 }),
      React.createElement('text', { x: 3.5, y: 15.5, fill: '#E53238', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'e'),
      React.createElement('text', { x: 8, y: 15.5, fill: '#0064D2', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'b'),
      React.createElement('text', { x: 12.5, y: 15.5, fill: '#F5AF02', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'a'),
      React.createElement('text', { x: 17.2, y: 15.5, fill: '#86B817', fontSize: 8.5, fontWeight: 700, fontFamily: 'Arial' }, 'y')
    );
  }
  if (channel === 'mercadolibre') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#FFE600' }),
      React.createElement('ellipse', { cx: 12, cy: 12, rx: 9, ry: 6.5, fill: 'none', stroke: '#2D3277', strokeWidth: 1.4 }),
      React.createElement('path', { d: 'M6.5 12.5 Q9 9.5 11 11.5 Q12 12.5 13 11.5 Q15 9.5 17.5 12.5', stroke: '#2D3277', strokeWidth: 1.3, strokeLinecap: 'round', fill: 'none' })
    );
  }
  if (channel === 'shopify') {
    return React.createElement('svg', Object.assign({}, svgProps, { viewBox: '0 0 24 24' }),
      React.createElement('circle', { cx: 12, cy: 12, r: 12, fill: '#96BF48' }),
      React.createElement('path', { d: 'M14.5 5.5 L14.2 6.5 C14.2 6.5 13.5 6.3 12.7 6.4 C11.5 6.5 11.4 7.2 11.4 7.5 C11.5 8.5 14.6 8.8 14.8 11.3 C14.9 13.2 13.7 14.5 11.8 14.6 C9.6 14.7 8.4 13.4 8.4 13.4 L8.9 11.5 C8.9 11.5 10.1 12.5 11.1 12.4 C11.7 12.4 11.9 11.9 11.9 11.6 C11.8 10.3 9.3 10.4 9.1 8.1 C8.9 6.2 10.3 5.2 12.1 5 C12.9 4.9 13.3 5.1 13.3 5.1 L14.5 5.5 Z M10.5 18 L15 17 L15 7', stroke: '#FFFFFF', strokeWidth: 0.3, fill: '#FFFFFF', fillOpacity: 0.9 })
    );
  }
  // Fallback icons for non-marketplace channels
  var fallbacks = { email: 'mail', phone: 'phone', chat: 'message-square', other: 'pin' };
  return React.createElement(LIcon, { name: fallbacks[channel] || 'pin', size: s * 0.8 });
}

// ─── FILL BAR (color-coded progress) ───
function FillBar({ pct }) {
  var p = Math.min(Math.max(Number(pct) || 0, 0), 100);
  var color = p >= 90 ? C.danger : p >= 70 ? C.warning : p >= 40 ? C.accent : C.success;
  return React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
    React.createElement('div', { style: { flex: 1, height: 6, background: C.bg, borderRadius: 3, minWidth: 40, overflow: 'hidden' } },
      React.createElement('div', { style: { width: p + '%', height: '100%', background: color, borderRadius: 3, transition: 'width 0.3s' } })
    ),
    React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: color, minWidth: 32, textAlign: 'right' } }, p.toFixed(0) + '%')
  );
}

// ─── BIN BADGE (type/status colored badge) ───
function BinBadge({ type, value }) {
  var colors = {};
  if (type === 'bin_type') {
    colors = { shelf: { bg: C.accentSoft, text: C.accent }, rack: { bg: C.purpleSoft, text: C.purple }, floor: { bg: C.warningSoft, text: C.warning }, pick: { bg: C.successSoft, text: C.success }, overstock: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted }, staging: { bg: 'rgba(6,182,212,0.12)', text: '#06B6D4' }, dock: { bg: 'rgba(245,158,11,0.12)', text: '#F59E0B' } };
  } else {
    colors = { active: { bg: C.successSoft, text: C.success }, full: { bg: C.dangerSoft, text: C.danger }, unsellable: { bg: C.accentSoft, text: C.accent }, damaged: { bg: C.warningSoft, text: C.warning }, inactive: { bg: 'rgba(139,143,163,0.12)', text: C.textMuted } };
  }
  var c = colors[value] || { bg: C.warningSoft, text: C.warning };
  return React.createElement('span', {
    style: { background: c.bg, color: c.text, padding: '2px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }
  }, value);
}

// ─── CODE 128B BARCODE SVG GENERATOR ───
var _c128Patterns = [
  '11011001100','11001101100','11001100110','10010011000','10010001100',
  '10001001100','10011001000','10011000100','10001100100','11001001000',
  '11001000100','11000100100','10110011100','10011011100','10011001110',
  '10111001100','10011101100','10011100110','11001110010','11001011100',
  '11001001110','11011100100','11001110100','11100101100','11100100110',
  '11101100100','11100110100','11100110010','11011011000','11011000110',
  '11000110110','10100011000','10001011000','10001000110','10110001000',
  '10001101000','10001100010','11010001000','11000101000','11000100010',
  '10110111000','10110001110','10001101110','10111011000','10111000110',
  '10001110110','11101110110','11010001110','11000101110','11011101000',
  '11011100010','11011101110','11101011000','11101000110','11100010110',
  '11101101000','11101100010','11100011010','11101111010','11001000010',
  '11110001010','10100110000','10100001100','10010110000','10010000110',
  '10000101100','10000100110','10110010000','10110000100','10011010000',
  '10011000010','10000110100','10000110010','11000010010','11001010000',
  '11110111010','11000010100','10001111010','10100111100','10010111100',
  '10010011110','10111100100','10011110100','10011110010','11110100100',
  '11110010100','11110010010','11011011110','11011110110','11110110110',
  '10101111000','10100011110','10001011110','10111101000','10111100010',
  '10001111100','11110101000','11110100010','10111011110','10111101110',
  '11101011110','11110101110','11010000100','11010010000','11010011100','11000111010',
  '1100011101011'
];

function generateCode128SVG(text, barHeight, moduleWidth) {
  barHeight = barHeight || 60;
  moduleWidth = moduleWidth || 2;
  var startCode = 104; // Code B
  var codes = [startCode];
  var checksum = startCode;
  for (var i = 0; i < text.length; i++) {
    var val = text.charCodeAt(i) - 32;
    if (val < 0 || val > 94) val = 0;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106); // stop
  var bits = '';
  for (var j = 0; j < codes.length; j++) bits += _c128Patterns[codes[j]];
  var totalWidth = bits.length * moduleWidth;
  var bars = '';
  for (var k = 0; k < bits.length; k++) {
    if (bits[k] === '1') {
      bars += '<rect x="' + (k * moduleWidth) + '" y="0" width="' + moduleWidth + '" height="' + barHeight + '"/>';
    }
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ' + totalWidth + ' ' + barHeight + '" width="100%">' + bars + '</svg>';
}

// Print-optimized: renders in mm units so barcode prints at exact physical size with no CSS scaling
function generateCode128SVGmm(text, targetWidthMm, barHeightMm) {
  barHeightMm = barHeightMm || 10;
  var startCode = 104;
  var codes = [startCode];
  var checksum = startCode;
  for (var i = 0; i < text.length; i++) {
    var val = text.charCodeAt(i) - 32;
    if (val < 0 || val > 94) val = 0;
    codes.push(val);
    checksum += val * (i + 1);
  }
  codes.push(checksum % 103);
  codes.push(106);
  var bits = '';
  for (var j = 0; j < codes.length; j++) bits += _c128Patterns[codes[j]];
  var modW = targetWidthMm / bits.length;
  var bars = '';
  for (var k = 0; k < bits.length; k++) {
    if (bits[k] === '1') {
      bars += '<rect x="' + (k * modW).toFixed(4) + '" y="0" width="' + modW.toFixed(4) + '" height="' + barHeightMm + '"/>';
    }
  }
  return '<svg xmlns="http://www.w3.org/2000/svg" width="' + targetWidthMm + 'mm" height="' + barHeightMm + 'mm" viewBox="0 0 ' + targetWidthMm + ' ' + barHeightMm + '">' + bars + '</svg>';
}

// Generate barcode as data URL image using bwip-js (reliable Code 128)
function generateBarcodeImg(text, widthMm, heightMm) {
  if (!text || !window.bwipjs) return '';
  try {
    var canvas = document.createElement('canvas');
    bwipjs.toCanvas(canvas, {
      bcid: 'code128',
      text: text,
      scale: 3,
      height: Math.max(6, Math.round(heightMm * 0.8)),
      includetext: false,
      textxalign: 'center',
    });
    var dataUrl = canvas.toDataURL('image/png');
    return '<img src="' + dataUrl + '" style="width:' + widthMm + 'mm;height:' + heightMm + 'mm;image-rendering:pixelated;" />';
  } catch (e) {
    console.error('Barcode generation failed:', e);
    return '';
  }
}

// HTML-escape for values interpolated into print popups (document.write) and any other markup
// string (review M6 #71). Vendor titles, SKUs, barcodes, PO numbers and bin codes all come from
// vendor confirmations, CSV imports and channel syncs — a bare '&' broke label layout and
// markup would run with the user's session in the same-origin popup.
function escHtml(s) {
  return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

// Calendar-date formatter for DATE columns (review M6 #75). pg returns a DATE as a JS Date at
// server-local midnight, serialised "YYYY-MM-DDT00:00:00.000Z" (prod Node runs in UTC), so
// `new Date(v).toLocaleDateString()` in an Eastern browser printed the PREVIOUS day. Read the
// civil date off the string and build a local Date from its parts; never parse it as an instant.
// Returns '' for empty input so exports stay blank; callers add their own '—'.
function fmtDateOnly(v, opts, locale) {
  if (v == null || v === '') return '';
  var m = String(v).match(/^(\d{4})-(\d{2})-(\d{2})/);
  var d = m ? new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10)) : new Date(v);
  if (isNaN(d.getTime())) return String(v);
  return d.toLocaleDateString(locale || undefined, opts || undefined);
}

// ─── Product label rendering shared by the label printers (Mercado Libre labels, 2026-09-29) ───
// A label = { code, title, sku, condition, expiry }; `tpl` is a label_templates row, null falls
// back to the 2.25 x 1.25 in roll the older printers assumed. The markup mirrors the FBA / FBM
// printers (barcode, code in bold, title, bottom row) so a MELI label on the same stock looks
// like an FNSKU label. `opts.skuPrefix` prints "SKU: " before the SKU, as Mercado Libre's own
// label file does.
function labelTemplateGeometry(tpl) {
  return {
    widthMm: tpl ? parseFloat(tpl.width_mm) : 57.15,
    heightMm: tpl ? parseFloat(tpl.height_mm) : 31.75,
    fontSize: tpl ? (tpl.font_size || 12) : 12,
    fields: tpl ? (tpl.fields || ['fnsku', 'sku', 'title']) : ['fnsku', 'sku', 'title'],
    showBarcode: tpl ? tpl.show_barcode !== false : true,
    isSheet: tpl ? tpl.label_type === 'sheet' : false,
    cols: tpl ? (tpl.columns || 1) : 1,
    rowsPerSheet: tpl ? (tpl.rows_per_sheet || 1) : 1,
    pageW: tpl && tpl.page_width_mm ? parseFloat(tpl.page_width_mm) : 215.9,
    pageH: tpl && tpl.page_height_mm ? parseFloat(tpl.page_height_mm) : 279.4,
  };
}

function productLabelBottomLine(label, fields, opts) {
  var parts = [];
  if (fields.indexOf('condition') >= 0 && label.condition) parts.push(label.condition);
  if (fields.indexOf('sku') >= 0 && label.sku) parts.push((opts && opts.skuPrefix ? 'SKU: ' : '') + label.sku);
  if (fields.indexOf('expiry') >= 0 && label.expiry) parts.push('Exp: ' + label.expiry);
  return parts.join(' | ');
}

// The title line of every product label: up to two lines, the way Mercado Libre's own label wraps
// a long title (Anthony 2026-09-29: "some of the titles are cut off", then "fix those too" for
// the FBA, FBM and WFS printers). A 2 x 1 label has the room: barcode, code, two title lines and
// the SKU line add up to about 18 of its 21 mm.
function productLabelTitleCss(pt, lines) {
  if (lines === 1) return 'font-size:' + pt + 'pt;color:#333;text-align:center;line-height:1.1;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;';
  return 'font-size:' + pt + 'pt;color:#333;text-align:center;line-height:1.15;overflow:hidden;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;max-width:100%;word-break:break-word;';
}

// How many title lines the label has room for between the code line and the bottom row, and at
// what size: two when two fit (the type comes down half a point at a time, to 6 pt, to make them
// fit), else one at the template's size. A 2 x 1 label at 12 pt has room for two lines at 6 pt;
// the FBA 2 x 1 at 16 pt does not, its title stays on one line unless the template's type is
// made smaller. Sizes in mm; a text line is 1.15 of its type size.
function productLabelTitleFit(heightMm, fontSize, barcodeHeightMm) {
  var pad = 2, gap = 0.3;
  var mm = function(pt) { return pt * 0.3528; };
  var codePt = Math.max(5, fontSize - 4), bottomPt = Math.max(4, fontSize - 6);
  var titlePt = Math.max(4, fontSize - 6);
  var used = pad * 2 + (barcodeHeightMm || 0) + gap + mm(codePt) * 1.2 + gap + mm(bottomPt) * 1.2 + gap;
  var room = heightMm - used;
  for (var pt = titlePt; pt >= 6; pt -= 0.5) {
    if (mm(pt) * 1.15 * 2 <= room) return { lines: 2, pt: pt };
  }
  return { lines: 1, pt: titlePt };
}

function productLabelHtml(label, tpl, opts) {
  var g = labelTemplateGeometry(tpl);
  var code = label.code || '';
  var small = Math.max(4, g.fontSize - 6);
  var fit = productLabelTitleFit(g.heightMm, g.fontSize, g.showBarcode ? Math.max(4, g.heightMm * 0.28) : 0);
  var lbl = '<div style="width:' + g.widthMm + 'mm;height:' + g.heightMm + 'mm;box-sizing:border-box;padding:2mm;display:flex;flex-direction:column;justify-content:center;align-items:center;overflow:hidden;page-break-inside:avoid;">';
  if (g.showBarcode && code) {
    lbl += '<div style="text-align:center;margin-bottom:0.3mm;">' + generateBarcodeImg(code, g.widthMm - 4, Math.max(4, g.heightMm * 0.28)) + '</div>';
  }
  if (code) lbl += '<div style="font-family:monospace;font-size:' + Math.max(5, g.fontSize - 4) + 'pt;font-weight:bold;letter-spacing:0.5px;margin-bottom:0.3mm;text-align:center;">' + escHtml(code) + '</div>';
  if (label.title && g.fields.indexOf('title') >= 0) {
    lbl += '<div style="' + productLabelTitleCss(fit.pt, fit.lines) + '">' + escHtml(label.title) + '</div>';
  }
  var bottom = productLabelBottomLine(label, g.fields, opts);
  if (bottom) lbl += '<div style="font-size:' + small + 'pt;color:#333;text-align:center;margin-top:0.3mm;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;">' + escHtml(bottom) + '</div>';
  return lbl + '</div>';
}

// Lays the labels out: pages of cols x rows for a sheet template, a plain flow for a roll.
function productLabelPagesHtml(labelHtmls, tpl) {
  var g = labelTemplateGeometry(tpl);
  if (!g.isSheet) return '<div style="display:flex;flex-wrap:wrap;gap:0;justify-content:center;">' + labelHtmls.join('') + '</div>';
  var mH = (g.pageW - (g.cols * g.widthMm)) / (g.cols + 1);
  var mV = (g.pageH - (g.rowsPerSheet * g.heightMm)) / (g.rowsPerSheet + 1);
  var perPage = g.cols * g.rowsPerSheet;
  var pages = [];
  for (var p = 0; p < labelHtmls.length; p += perPage) {
    pages.push('<div style="width:' + g.pageW + 'mm;height:' + g.pageH + 'mm;box-sizing:border-box;padding:' + mV + 'mm ' + mH + 'mm;display:flex;flex-wrap:wrap;align-content:flex-start;gap:0;page-break-after:always;"><div style="display:grid;grid-template-columns:repeat(' + g.cols + ',' + g.widthMm + 'mm);grid-template-rows:repeat(' + g.rowsPerSheet + ',' + g.heightMm + 'mm);gap:0;width:100%;">' + labelHtmls.slice(p, p + perPage).join('') + '</div></div>');
  }
  return pages.join('');
}

// Opens the print window and drives printing from the opener: the page CSP blocks an inline
// script in the popup and window.onload is unreliable for document.write popups. Returns false
// when the browser blocked the popup.
function openLabelPrintWindow(bodyHtml, title, tpl) {
  var g = labelTemplateGeometry(tpl);
  var pw = window.open('', '_blank', 'width=600,height=800');
  if (!pw) return false;
  pw.document.write('<!DOCTYPE html><html><head><title>' + escHtml(title || 'Labels') + '</title><style>@media print{body{margin:0;}@page{size:' + (g.isSheet ? g.pageW + 'mm ' + g.pageH + 'mm' : g.widthMm + 'mm ' + g.heightMm + 'mm') + ';margin:0;}div{page-break-inside:avoid;}}</style></head><body style="font-family:Arial,sans-serif;margin:0;padding:0;">' + bodyHtml + '</body></html>');
  pw.document.close();
  setTimeout(function() { try { pw.focus(); pw.print(); } catch (e) { /* popup closed */ } }, 500);
  return true;
}

// Splits a title into at most two lines that fit `maxMm` at `pt`, shrinking the type half a
// point at a time (never under 4 pt) before cutting. `doc` is a jsPDF document with the font
// already set; its splitTextToSize measures the real glyph widths.
function productLabelTitleLines(doc, title, maxMm, pt, maxLines) {
  var cap = maxLines === 1 ? 1 : 2;
  var lines = [];
  for (;;) {
    doc.setFontSize(pt);
    lines = doc.splitTextToSize(String(title), maxMm);
    if (lines.length <= cap || pt <= 4) break;
    pt = Math.max(4, pt - 0.5);
  }
  if (lines.length > cap) {
    lines = lines.slice(0, cap);
    lines[cap - 1] = lines[cap - 1].slice(0, Math.max(0, lines[cap - 1].length - 2)) + '…';
  }
  return { lines: lines, pt: pt };
}

// The same labels as one PDF (jsPDF + bwip-js), for a vendor who applies the labels: one page
// per label on a roll, cols x rows per page on a sheet. Returns the jsPDF document, or null when
// a library is missing.
function productLabelsPdf(labels, tpl, opts) {
  var jspdfNs = window.jspdf || window.jsPDF;
  var JsPDFCtor = jspdfNs && (jspdfNs.jsPDF || jspdfNs);
  if (!JsPDFCtor || !window.bwipjs) return null;
  var g = labelTemplateGeometry(tpl);
  var pad = 2;
  var barcodeHeightMm = Math.max(4, g.heightMm * 0.28);
  var barcodeWidthMm = g.widthMm - pad * 2;
  var ptToMm = function(pt) { return pt * 0.3528; };
  var clip = function(str, maxMm, sizePt) {
    if (!str) return '';
    var maxChars = Math.floor(maxMm / (ptToMm(sizePt) * 0.5));
    return str.length > maxChars ? str.slice(0, Math.max(0, maxChars - 1)) + '…' : str;
  };
  var renderAt = function(doc, label, x, y) {
    var cursorY = y + pad;
    var code = label.code || '';
    if (g.showBarcode && code) {
      var canvas = document.createElement('canvas');
      window.bwipjs.toCanvas(canvas, { bcid: 'code128', text: code, scale: 3, height: barcodeHeightMm, includetext: false, paddingleft: 4, paddingright: 4 });
      doc.addImage(canvas.toDataURL('image/png'), 'PNG', x + pad, cursorY, barcodeWidthMm, barcodeHeightMm);
      cursorY += barcodeHeightMm + 0.5;
    }
    if (code) {
      var codePt = Math.max(5, g.fontSize - 4);
      doc.setFont('courier', 'bold'); doc.setFontSize(codePt);
      cursorY += ptToMm(codePt) * 0.75;
      doc.text(code, x + g.widthMm / 2, cursorY, { align: 'center' });
      cursorY += 0.4;
    }
    if (label.title && g.fields.indexOf('title') >= 0) {
      // Up to two lines, measured with the real font (Anthony 2026-09-29: "some of the titles are
      // cut off"): the type shrinks half a point at a time before anything is cut, and only a
      // title that still needs a third line at 4 pt loses its tail.
      var fit = productLabelTitleFit(g.heightMm, g.fontSize, g.showBarcode ? barcodeHeightMm : 0);
      var titlePt = fit.pt;
      doc.setFont('helvetica', 'normal');
      var titleLines = productLabelTitleLines(doc, label.title, g.widthMm - pad * 2, titlePt, fit.lines);
      titlePt = titleLines.pt;
      doc.setFontSize(titlePt);
      titleLines.lines.forEach(function(line, idx) {
        cursorY += ptToMm(titlePt) * (idx === 0 ? 0.75 : 1.15);
        doc.text(line, x + g.widthMm / 2, cursorY, { align: 'center' });
      });
    }
    var bottom = productLabelBottomLine(label, g.fields, opts);
    if (bottom) {
      var botPt = Math.max(4, g.fontSize - 6);
      doc.setFont('helvetica', 'normal'); doc.setFontSize(botPt);
      doc.text(clip(bottom, g.widthMm - pad * 2, botPt), x + g.widthMm / 2, y + g.heightMm - pad, { align: 'center' });
    }
  };
  var doc;
  if (g.isSheet) {
    var perPage = g.cols * g.rowsPerSheet;
    var mH = (g.pageW - g.cols * g.widthMm) / (g.cols + 1);
    var mV = (g.pageH - g.rowsPerSheet * g.heightMm) / (g.rowsPerSheet + 1);
    var pageOrient = g.pageW >= g.pageH ? 'landscape' : 'portrait';
    doc = new JsPDFCtor({ orientation: pageOrient, unit: 'mm', format: [g.pageW, g.pageH] });
    labels.forEach(function(label, i) {
      var slot = i % perPage;
      if (i > 0 && slot === 0) doc.addPage([g.pageW, g.pageH], pageOrient);
      renderAt(doc, label, mH + (slot % g.cols) * (g.widthMm + mH), mV + Math.floor(slot / g.cols) * (g.heightMm + mV));
    });
  } else {
    var rollOrient = g.widthMm >= g.heightMm ? 'landscape' : 'portrait';
    doc = new JsPDFCtor({ orientation: rollOrient, unit: 'mm', format: [g.widthMm, g.heightMm] });
    labels.forEach(function(label, i) {
      if (i > 0) doc.addPage([g.widthMm, g.heightMm], rollOrient);
      renderAt(doc, label, 0, 0);
    });
  }
  return doc;
}
window.labelTemplateGeometry = labelTemplateGeometry; window.productLabelHtml = productLabelHtml; window.productLabelPagesHtml = productLabelPagesHtml;
window.productLabelTitleCss = productLabelTitleCss; window.productLabelTitleLines = productLabelTitleLines; window.productLabelTitleFit = productLabelTitleFit;
window.openLabelPrintWindow = openLabelPrintWindow; window.productLabelsPdf = productLabelsPdf;

function printBinLabel(binCode) {
  var barcodeSVG = generateCode128SVG(binCode, 80, 3);
  var html = '<!DOCTYPE html><html><head><title>Bin Label</title><style>' +
    '@page { size: 4in 4in; margin: 0; }' +
    'body { margin: 0; padding: 0; width: 4in; height: 4in; display: flex; flex-direction: column; align-items: center; justify-content: center; font-family: Arial, Helvetica, sans-serif; }' +
    '.bin-code { font-size: 48pt; font-weight: 800; letter-spacing: 2px; margin-bottom: 16px; text-align: center; }' +
    '.barcode { margin-bottom: 12px; }' +
    '.barcode svg { max-width: 3.4in; height: auto; }' +
    '.sub-text { font-size: 14pt; color: #333; letter-spacing: 1px; }' +
    '</style></head><body>' +
    '<div class="bin-code">' + escHtml(binCode) + '</div>' +
    '<div class="barcode">' + barcodeSVG + '</div>' +
    '<div class="sub-text">' + escHtml(binCode) + '</div>' +
    '</body></html>';
  var win = window.open('', '_blank', 'width=420,height=420');
  if (!win) return;
  win.document.write(html);
  win.document.close();
  // Drive printing from the opener via a timer — win.onload set after document.write does not
  // reliably fire, leaving the label stuck in preview. The barcode is inline SVG so 300ms is ample.
  setTimeout(function() { try { win.focus(); win.print(); } catch (e) { /* popup closed */ } }, 300);
}

// ─── PDF417 barcode generation (via bwip-js) ───
function renderPDF417Canvas(data, opts) {
  // Throws if the data exceeds the symbol's capacity (bwip-js auto-grows rows
  // for a given column count but hits PDF417's 90-row ceiling).
  var canvas = document.createElement('canvas');
  var bwipOpts = {
    bcid: 'pdf417',
    text: data,
    scale: opts.scale || 2,
    columns: opts.columns || 4,
    rowmult: opts.rowmult || 2,
    eclevel: opts.eclevel || 6,
    padding: 0,
  };
  if (opts.rows) bwipOpts.rows = opts.rows;
  bwipjs.toCanvas(canvas, bwipOpts);
  // If rotate option, rotate 90° CW by drawing to a new canvas with swapped dimensions
  if (opts.rotate) {
    var rotCanvas = document.createElement('canvas');
    rotCanvas.width = canvas.height;
    rotCanvas.height = canvas.width;
    var ctx = rotCanvas.getContext('2d');
    ctx.translate(rotCanvas.width, 0);
    ctx.rotate(Math.PI / 2);
    ctx.drawImage(canvas, 0, 0);
    canvas = rotCanvas;
  }
  return canvas;
}

function pdf417CanvasImg(canvas, imgStyle) {
  return '<img src="' + canvas.toDataURL('image/png') + '" style="' + (imgStyle || 'width:100%;height:auto;image-rendering:pixelated;') + '" />';
}

function generatePDF417SVG(data, opts) {
  opts = opts || {};
  try {
    return pdf417CanvasImg(renderPDF417Canvas(data, opts), opts.imgStyle);
  } catch (e) {
    console.error('PDF417 generation failed:', e);
    return '<div style="color:red;font-size:10px;">Barcode error</div>';
  }
}

// FBA case-label content barcode. The payload grows with each distinct FNSKU in
// the box, so a single fixed column count overflows on large mixed boxes (6
// columns tops out near 25 SKUs). Step up through wider symbols until the data
// fits AND the printed height stays within the label's vertical budget (aspect
// ≤ 0.63 of the 3.5in print width ≈ 2.2in tall). PDF417 itself caps out near
// 1,800 chars (~75 SKUs/box) — past that no symbol size can encode the box.
var CASE_PDF417_PRESETS = [
  { columns: 6, rows: 24, rowmult: 3, eclevel: 4 }, // typical boxes — same symbol as always
  { columns: 8, rowmult: 2, eclevel: 5 },
  { columns: 10, rowmult: 2, eclevel: 5 },
  { columns: 12, rowmult: 2, eclevel: 5 },
  { columns: 14, rowmult: 2, eclevel: 5 },
  { columns: 16, rowmult: 2, eclevel: 5 },
];
function generateCasePDF417SVG(data, imgStyle) {
  var best = null;
  for (var i = 0; i < CASE_PDF417_PRESETS.length; i++) {
    var p = CASE_PDF417_PRESETS[i];
    var canvas;
    try {
      canvas = renderPDF417Canvas(data, { scale: 4, columns: p.columns, rows: p.rows, rowmult: p.rowmult, eclevel: p.eclevel });
    } catch (e) { continue; }
    var aspect = canvas.height / canvas.width;
    if (aspect <= 0.63) return pdf417CanvasImg(canvas, imgStyle);
    if (!best || aspect < best.aspect) best = { canvas: canvas, aspect: aspect };
  }
  // Encodable but over the height budget at every size — a tall barcode beats none.
  if (best) return pdf417CanvasImg(best.canvas, imgStyle);
  console.error('PDF417 case barcode: data exceeds symbology capacity (' + data.length + ' chars)');
  return '<div style="color:red;font-size:10px;">Barcode error</div>';
}

// Build Amazon FBA 2D barcode data string
// Format: AMZN,PO:{shipmentId},FNSKU:{fnsku},QTY:{qty}[,EXP:{YYMMDD}]
// Multiple items repeat the FNSKU/QTY/EXP block
function buildAmazonCaseBarcodeData(shipmentId, items) {
  var parts = ['AMZN', 'PO:' + (shipmentId || '').toUpperCase()];
  (items || []).forEach(function(item) {
    parts.push('FNSKU:' + (item.fnsku || '').toUpperCase());
    parts.push('QTY:' + (item.qty || 1));
    if (item.expiry) parts.push('EXP:' + item.expiry); // YYMMDD
  });
  return parts.join(',');
}

// ─── SEARCH INPUT (with inline clear button) ───
function SearchInput(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var mergedStyle = Object.assign({ width: '100%', boxSizing: 'border-box' }, props.style || {}, { paddingRight: value ? 32 : (props.style && props.style.paddingRight) || 14 });
  var wrapStyle = { position: 'relative', width: mergedStyle.width, flex: props.flex };
  var inputProps = Object.assign({}, props, { style: mergedStyle, flex: undefined });
  delete inputProps.flex;
  return React.createElement('div', { style: wrapStyle },
    React.createElement('input', inputProps),
    value ? React.createElement('button', {
      type: 'button', 'aria-label': 'Clear search',
      onClick: function() { if (onChange) onChange({ target: { value: '' } }); },
      style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 4, display: 'flex', alignItems: 'center', borderRadius: 4 },
      onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover; e.currentTarget.style.color = C.text; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.color = C.textMuted; }
    }, React.createElement(LIcon, { name: 'x', size: 14 })) : null
  );
}

// ─── BIN FORMAT HELPER ───
// Normalize bin location: "A100" → "A-100", "A1" → "A-01", "A100/B5" → "A-100/B-05"
function formatSingleBin(val) {
  // LOC* bins (e.g. LOC800) are staging/location codes that carry no dash — leave them as typed.
  // Only the single-letter zone bins get the dash + zero-pad treatment (B80 -> B-80).
  var m = val.match(/^([A-Z]+)-?(\d+)$/);
  if (m && m[1] !== 'LOC') {
    var num = m[2].length < 2 ? ('0' + m[2]) : m[2];
    return m[1] + '-' + num;
  }
  return val;
}
function formatBin(raw) {
  if (!raw) return '';
  var val = raw.trim().toUpperCase();
  if (!val) return '';
  return val.split('/').map(function(part) { return formatSingleBin(part.trim()); }).join('/');
}
window.formatBin = formatBin;
// ─── Country list (ISO 3166-1 alpha-2) ───
// One source for every country dropdown in the app. app-utils.js is the first file
// in all four bundles, so app-pages / app-main / app-cs can all reach these.
// app-shipping.js keeps its own SHIP_ISO_COUNTRIES (shipping bundle, carrier-scoped).
var ISO_COUNTRIES = ('AD AE AF AG AI AL AM AO AQ AR AS AT AU AW AX AZ BA BB BD BE BF BG BH BI BJ BL BM BN BO BQ BR BS BT BV BW BY BZ CA CC CD CF CG CH CI CK CL CM CN CO CR CU CV CW CX CY CZ DE DJ DK DM DO DZ EC EE EG EH ER ES ET FI FJ FK FM FO FR GA GB GD GE GF GG GH GI GL GM GN GP GQ GR GS GT GU GW GY HK HM HN HR HT HU ID IE IL IM IN IO IQ IR IS IT JE JM JO JP KE KG KH KI KM KN KP KR KW KY KZ LA LB LC LI LK LR LS LT LU LV LY MA MC MD ME MF MG MH MK ML MM MN MO MP MQ MR MS MT MU MV MW MX MY MZ NA NC NE NF NG NI NL NO NP NR NU NZ OM PA PE PF PG PH PK PL PM PN PR PS PT PW PY QA RE RO RS RU RW SA SB SC SD SE SG SH SI SJ SK SL SM SN SO SR SS ST SV SX SY SZ TC TD TF TG TH TJ TK TL TM TN TO TR TT TV TW TZ UA UG UM US UY UZ VA VC VE VG VI VN VU WF WS YE YT ZA ZM ZW').split(' ');
var _isoRegionNames = null;
// Names come from Intl.DisplayNames rather than a 250-row label table; a code the
// browser cannot name falls back to the code itself.
function countryName(code) {
  if (!code) return '';
  try {
    if (!_isoRegionNames) _isoRegionNames = new Intl.DisplayNames(['en'], { type: 'region' });
    return _isoRegionNames.of(String(code).toUpperCase()) || code;
  } catch (e) { return code; }
}
// Sorted once by display name; every dropdown reuses the same array.
function countryOptions() {
  if (!window._isoCountriesSorted) {
    window._isoCountriesSorted = ISO_COUNTRIES.slice().sort(function(a, b) { return countryName(a).localeCompare(countryName(b)); });
  }
  return window._isoCountriesSorted;
}
// Options for a country SearchSelect (type to filter, or scroll). The ISO code rides in
// the label so typing "US" still surfaces United States — SearchSelect ranks a word start
// ("(US)") above a bare substring, which a name-only label would not give us. A legacy or
// free-text value that is not a known code keeps its own entry, so opening an old record
// does not silently rewrite it.
function countryOptionList(current) {
  var opts = countryOptions().map(function(c) { return { value: c, label: countryName(c) + ' (' + c + ')' }; });
  if (current && ISO_COUNTRIES.indexOf(String(current).toUpperCase()) === -1) {
    opts.unshift({ value: current, label: current + ' (saved)' });
  }
  return opts;
}
window.ISO_COUNTRIES = ISO_COUNTRIES; window.countryName = countryName;
window.countryOptions = countryOptions; window.countryOptionList = countryOptionList;
// ─── Font size for the rich-text toolbars ───
// Shared by the PO "Email Vendor" modal (app-main.js) and the PO e-mail template editor
// (app-pages.js), so a size set in either place behaves identically.
//
// execCommand('fontSize') only speaks the legacy 1-7 scale and emits <font size=N>, which
// e-mail clients render inconsistently; turning on styleWithCSS yields CSS keywords
// ('xx-large'), not px. So size 7 is applied purely as a sentinel and those exact <font>
// nodes are then rewritten into spans carrying a real px size. server/services/sanitize.js
// allowlists font-size, so the span survives the outbound sanitiser and reaches the vendor.
var FONT_SIZES = [
  { px: 12, label: 'Small' },
  { px: 14, label: 'Normal' },
  { px: 16, label: 'Medium' },
  { px: 18, label: 'Large' },
  { px: 24, label: 'Huge' },
];

function applyFontSize(rootEl, px) {
  if (!rootEl || !px) return;
  document.execCommand('fontSize', false, '7');
  // Only the nodes this call just created carry size="7" — anything the user genuinely
  // wanted at the legacy maximum would have come in through a pasted template, and those
  // are rewritten here too rather than left as <font>, which is the better outcome anyway.
  var marked = rootEl.querySelectorAll('font[size="7"]');
  for (var i = 0; i < marked.length; i++) {
    var f = marked[i];
    var span = document.createElement('span');
    span.style.fontSize = px + 'px';
    while (f.firstChild) span.appendChild(f.firstChild);
    if (f.parentNode) f.parentNode.replaceChild(span, f);
  }
}

// The <select> has to be clickable, so unlike the toolbar buttons it cannot preventDefault
// on mousedown — focusing it blurs the editor and drops the selection. Stash the range on
// the way in and put it back before applying.
function FontSizeSelect({ targetRef, size }) {
  var rangeRef = React.useRef(null);
  var small = size === 'sm';
  return React.createElement('select', {
    value: '',
    title: 'Font size',
    onMouseDown: function() {
      var sel = window.getSelection();
      rangeRef.current = (sel && sel.rangeCount) ? sel.getRangeAt(0).cloneRange() : null;
    },
    onChange: function(e) {
      var px = parseInt(e.target.value, 10);
      e.target.value = '';
      if (!px) return;
      var el = targetRef && targetRef.current;
      if (!el) return;
      el.focus();
      var r = rangeRef.current;
      if (r) {
        var sel = window.getSelection();
        sel.removeAllRanges();
        sel.addRange(r);
      }
      applyFontSize(el, px);
    },
    style: {
      padding: small ? '2px 4px' : '3px 6px', fontSize: small ? 11 : 12, borderRadius: 4,
      border: 'none', background: C.bg, color: C.text, cursor: 'pointer',
      fontFamily: 'inherit', outline: 'none',
      // Without an explicit colour-scheme the browser paints both the closed control and
      // its native option popup light, so the picker read as a white block on a dark
      // toolbar. A transparent background made the closed control worse still.
      colorScheme: getThemeName() === 'light' ? 'light' : 'dark'
    }
  },
    React.createElement('option', { value: '' }, 'Size'),
    FONT_SIZES.map(function(f) { return React.createElement('option', { key: f.px, value: f.px }, f.label + ' (' + f.px + ')'); })
  );
}
window.FONT_SIZES = FONT_SIZES; window.applyFontSize = applyFontSize; window.FontSizeSelect = FontSizeSelect;

// ─── COPY CELL COMPONENT ───
function CopyCell({ value, children }) {
  var [copied, setCopied] = React.useState(false);
  return React.createElement('div', {
    style: { display: 'inline-flex', alignItems: 'center', gap: 4, position: 'relative' },
    className: 'copy-cell'
  },
    children || value,
    React.createElement('span', {
      className: 'copy-icon',
      onClick: function(e) { e.stopPropagation(); navigator.clipboard.writeText(String(value || '')); setCopied(true); setTimeout(function() { setCopied(false); }, 1500); },
      style: { cursor: 'pointer', opacity: 0, transition: 'opacity 0.15s', display: 'inline-flex', alignItems: 'center' }
    }, React.createElement(LIcon, { name: copied ? 'check' : 'clipboard', size: 12, color: copied ? C.success : C.textMuted }))
  );
}

// ─── IMAGE LIGHTBOX ───
// Reusable full-screen image viewer. Pass `images` (string[]), `index` (current),
// `onIndexChange` (setter for index), `onClose`. Returns null when not active.
function ImageLightbox(props) {
  var images = props.images || [];
  var index = props.index;
  var onIndexChange = props.onIndexChange || function() {};
  var onClose = props.onClose || function() {};
  if (!images.length || index == null || index < 0 || index >= images.length) return null;
  var prev = function(e) { e && e.stopPropagation && e.stopPropagation(); onIndexChange((index - 1 + images.length) % images.length); };
  var next = function(e) { e && e.stopPropagation && e.stopPropagation(); onIndexChange((index + 1) % images.length); };
  return React.createElement('div', {
    onClick: onClose,
    style: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }
  },
    React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); onClose(); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '×'),
    images.length > 1 && React.createElement('div', {
      style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10002 }
    }, (index + 1) + ' / ' + images.length),
    images.length > 1 && React.createElement('button', {
      onClick: prev,
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '‹'),
    images.length > 1 && React.createElement('button', {
      onClick: next,
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '›'),
    React.createElement('img', {
      src: images[index],
      onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' }
    }),
    images.length > 1 && React.createElement('div', {
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 }
    },
      images.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { onIndexChange(i); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === index ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === index ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );
}
window.ImageLightbox = ImageLightbox;

// ─── PHOTO UPLOAD PANEL (QR-code phone-camera flow) ───
// Generates a token, shows QR + URL for the warehouse user to scan with their phone,
// polls for uploaded photos every 2s, displays thumbnails. Pass token back to parent
// via onTokenChange so the parent can include it when submitting the receive.
// Props:
//   onTokenChange(token|null) — fires when token becomes available or is reset
//   onPhotosChange(photos)    — optional, fires whenever photos array updates
//   resetSignal               — change this prop value to force a reset (e.g. after submit)
function PhotoUploadPanel(props) {
  var _o = React.useState(false); var open = _o[0], setOpen = _o[1];
  var _t = React.useState(null); var token = _t[0], setToken = _t[1];
  var _p = React.useState([]); var photos = _p[0], setPhotos = _p[1];
  var _l = React.useState(false); var loading = _l[0], setLoading = _l[1];
  var lastSignal = React.useRef(props.resetSignal);

  // Reset on signal change
  React.useEffect(function() {
    if (props.resetSignal !== lastSignal.current) {
      lastSignal.current = props.resetSignal;
      setOpen(false); setToken(null); setPhotos([]);
      if (props.onTokenChange) props.onTokenChange(null);
      if (props.onPhotosChange) props.onPhotosChange([]);
    }
  }, [props.resetSignal]);

  // Generate token when opening
  function handleOpen() {
    if (open) return;
    setOpen(true);
    setLoading(true);
    api('/receiving/photo-token', { method: 'POST', body: JSON.stringify({}) }).then(function(r) {
      setToken(r.token);
      if (props.onTokenChange) props.onTokenChange(r.token);
    }).catch(function() {})
      .finally(function() { setLoading(false); });
  }

  // Poll every 2s
  React.useEffect(function() {
    if (!token || !open) return;
    var alive = true;
    var fetchOnce = function() {
      api('/receiving/photo-status/' + token).then(function(r) {
        if (!alive) return;
        var next = r.photos || [];
        setPhotos(next);
        if (props.onPhotosChange) props.onPhotosChange(next);
      }).catch(function() {});
    };
    fetchOnce();
    var int = setInterval(fetchOnce, 2000);
    return function() { alive = false; clearInterval(int); };
  }, [token, open]);

  function deletePhoto(id) {
    api('/receiving/photo/' + id, { method: 'DELETE' }).then(function() {
      var next = photos.filter(function(p) { return p.id !== id; });
      setPhotos(next);
      if (props.onPhotosChange) props.onPhotosChange(next);
    }).catch(function() {});
  }

  // Render QR as a fixed-size <img> (data URL). A raw createSvgTag <svg> kept its intrinsic
  // pixel size — which grows with the URL length — and overflowed its 140px box, drawing on top
  // of the instructions and uploaded thumbnails to its right. An <img> is clamped to the box.
  var qrSrc = '';
  var phoneUrl = '';
  if (token && open) {
    phoneUrl = window.location.origin + '/u/' + token;
    try {
      var qr = window.qrcode(0, 'M');
      qr.addData(phoneUrl);
      qr.make();
      qrSrc = qr.createDataURL(6, 2);
    } catch (e) { qrSrc = ''; }
  }

  if (!open) {
    // Caller marks photos required (e.g. items with no catalog match must have a photo). Reflect
    // that in the label + amber styling instead of always saying "(optional)".
    var req = !!props.required;
    var edge = req ? '#F59E0B' : C.border, hoverEdge = req ? '#F59E0B' : C.accent;
    return React.createElement('button', {
      onClick: handleOpen,
      onMouseEnter: function(e) { e.currentTarget.style.background = hoverEdge + '22'; e.currentTarget.style.borderColor = hoverEdge; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.borderColor = edge; },
      style: { background: 'transparent', border: '1px dashed ' + edge, color: req ? '#B45309' : C.textMuted, padding: '8px 14px', borderRadius: 6, fontSize: 12, fontWeight: req ? 600 : 500, cursor: 'pointer', fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6, transition: 'all 0.15s ease' }
    }, React.createElement(LIcon, { name: 'camera', size: 14 }), req ? 'Add photos (required)' : 'Add photos (optional)');
  }

  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 12, marginBottom: 8 } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'flex-start', gap: 12 } },
      // QR + caption (left)
      React.createElement('div', { style: { flexShrink: 0, textAlign: 'center' } },
        loading
          ? React.createElement('div', { style: { width: 140, height: 140, background: C.bg, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', color: C.textDim, fontSize: 11 } }, 'Generating…')
          : (qrSrc
              ? React.createElement('img', { src: qrSrc, alt: 'Scan to upload photos', style: { width: 140, height: 140, boxSizing: 'border-box', background: '#fff', padding: 4, borderRadius: 6, display: 'block' } })
              : React.createElement('div', { style: { width: 140, height: 140, background: '#fff', borderRadius: 6 } })),
        React.createElement('div', { style: { fontSize: 10, color: C.textMuted, marginTop: 4 } }, 'Scan with phone camera')
      ),
      // Right: instructions, link, photos
      React.createElement('div', { style: { flex: 1, minWidth: 0 } },
        React.createElement('div', { style: { fontSize: 12, color: C.text, marginBottom: 6, display: 'flex', alignItems: 'center', gap: 8 } },
          React.createElement(LIcon, { name: 'camera', size: 14, color: C.accent }),
          React.createElement('span', { style: { fontWeight: 600 } }, 'Take photos with your phone'),
          React.createElement('button', { onClick: function() { setOpen(false); setToken(null); setPhotos([]); if (props.onTokenChange) props.onTokenChange(null); if (props.onPhotosChange) props.onPhotosChange([]); },
            style: { marginLeft: 'auto', background: 'none', border: 'none', color: C.textMuted, cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center' } },
            React.createElement(LIcon, { name: 'x', size: 14 }))
        ),
        React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 6 } },
          'Scan the QR with your phone camera, then take one or more photos. They appear here as they upload.'),
        phoneUrl && React.createElement('div', { style: { fontSize: 10, color: C.textDim, fontFamily: 'inherit', marginBottom: 8, wordBreak: 'break-all' } }, phoneUrl),
        photos.length > 0 && React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(54px, 1fr))', gap: 4 } },
          photos.map(function(p) {
            return React.createElement('div', { key: p.id, style: { position: 'relative', aspectRatio: '1', background: C.bg, borderRadius: 4, overflow: 'hidden', border: '1px solid ' + C.border } },
              React.createElement('img', { src: p.url, style: { width: '100%', height: '100%', objectFit: 'cover', display: 'block' } }),
              React.createElement('button', { onClick: function() { deletePhoto(p.id); },
                title: 'Remove',
                style: { position: 'absolute', top: 1, right: 1, background: 'rgba(220,38,38,0.85)', border: 'none', color: '#fff', cursor: 'pointer', borderRadius: 2, padding: 0, width: 16, height: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, lineHeight: 1 } }, '×')
            );
          })
        ),
        photos.length === 0 && React.createElement('div', { style: { fontSize: 11, color: C.textDim, fontStyle: 'italic' } }, 'Waiting for photos…')
      )
    )
  );
}
window.PhotoUploadPanel = PhotoUploadPanel;

// ─── UNEXPECTED ITEM MODAL ───
// Used by both FBA and WFS "Receive Unexpected Item" flows.
// Props:
//   open, onClose, onSuccess(result), lookupUrl, submitUrl
function UnexpectedItemModal(props) {
  var open = props.open, onClose = props.onClose, onSuccess = props.onSuccess;
  var lookupUrl = props.lookupUrl, submitUrl = props.submitUrl;
  // Optional: navigate to a sibling plan (FBA only). When provided, the split-shipment banner
  // renders the plan name as a link. Other callers (WFS/receiving) omit it → plain text.
  var onOpenPlan = props.onOpenPlan;

  var _st = React.useState('scan'); var step = _st[0], setStep = _st[1]; // 'scan' | 'receive' | 'manual'
  var _b = React.useState(''); var barcode = _b[0], setBarcode = _b[1];
  var _l = React.useState(null); var lookup = _l[0], setLookup = _l[1];
  var _sr = React.useState(false); var searching = _sr[0], setSearching = _sr[1];
  var _q = React.useState(''); var qty = _q[0], setQty = _q[1];
  var _bn = React.useState(''); var bin = _bn[0], setBin = _bn[1];
  var _n = React.useState(''); var notes = _n[0], setNotes = _n[1];
  var _t = React.useState(''); var manualTitle = _t[0], setManualTitle = _t[1];
  var _vs = React.useState(''); var manualVsku = _vs[0], setManualVsku = _vs[1];
  var _p = React.useState(''); var selectedPo = _p[0], setSelectedPo = _p[1];
  var _s = React.useState(false); var submitting = _s[0], setSubmitting = _s[1];
  var _e = React.useState(null); var error = _e[0], setError = _e[1];
  var _pp = React.useState(false); var needsPoPick = _pp[0], setNeedsPoPick = _pp[1];
  var _fu = React.useState(false); var forceUnexpected = _fu[0], setForceUnexpected = _fu[1];
  var _li = React.useState(-1); var lightboxIdx = _li[0], setLightboxIdx = _li[1];
  var _pt = React.useState(null); var photoToken = _pt[0], setPhotoToken = _pt[1];
  var _prs = React.useState(0); var photoResetSignal = _prs[0], setPhotoResetSignal = _prs[1];
  var _pc = React.useState(0); var photoCount = _pc[0], setPhotoCount = _pc[1];
  var lookupTimer = React.useRef(null);
  var lookupSeq = React.useRef(0);
  var binEdited = React.useRef(false);
  var titleEdited = React.useRef(false);
  var vskuEdited = React.useRef(false);
  var scanInputRef = React.useRef(null);

  React.useEffect(function() {
    if (!open) {
      setStep('scan'); setBarcode(''); setLookup(null); setSearching(false);
      setQty(''); setBin(''); setNotes('');
      setManualTitle(''); setManualVsku(''); setSelectedPo(''); setError(null);
      setNeedsPoPick(false); setForceUnexpected(false); setLightboxIdx(-1);
      setPhotoToken(null); setPhotoCount(0); setPhotoResetSignal(function(s) { return s + 1; });
      binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
    } else {
      // Auto-focus the scan input when modal opens
      setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 100);
    }
  }, [open]);

  // Pick FBM bin first (MIA), then RETURNS, then any. Mirrors the receiving modal's logic.
  var pickBinForProduct = function(prod) {
    if (!prod) return '';
    if (Array.isArray(prod.bin_quantities) && prod.bin_quantities.length > 0) {
      var fbmBin = prod.bin_quantities.find(function(b) { return b.warehouse === 'FBM' && b.bin && b.bin !== 'DEFAULT'; });
      if (fbmBin) return fbmBin.bin;
      var anyBin = prod.bin_quantities.find(function(b) { return b.bin && b.bin !== 'DEFAULT'; });
      if (anyBin) return anyBin.bin;
    }
    if (prod.current_bins) {
      var first = String(prod.current_bins).split(',')[0].trim();
      if (first && first !== 'DEFAULT') return first;
    }
    return '';
  };

  // Apply a catalog-result product as the active match: pre-fill bin/title/vsku and advance to receive step.
  var pickCatalogMatch = function(prod) {
    var fakeLookup = Object.assign({}, lookup || {}, { product: prod });
    setLookup(fakeLookup);
    if (!binEdited.current) {
      var b = pickBinForProduct(prod);
      if (b) setBin(b);
    }
    if (!titleEdited.current) setManualTitle(prod.vendor_title || prod.title || prod.sku || '');
    if (!vskuEdited.current && prod.vendor_sku) setManualVsku(prod.vendor_sku);
    if (prod.vendor_id && lookup && Array.isArray(lookup.plan_pos)) {
      var matched = lookup.plan_pos.find(function(p) { return p.vendor_id === prod.vendor_id; });
      if (matched) setSelectedPo(String(matched.id));
    }
    setStep('receive');
  };

  React.useEffect(function() {
    if (!open || step !== 'scan') return;
    if (!barcode || !barcode.trim()) { setLookup(null); setSearching(false); return; }
    if (lookupTimer.current) clearTimeout(lookupTimer.current);
    setSearching(true);
    var seq = ++lookupSeq.current; // review M6 #79: only the newest lookup may fill the form or auto-advance
    lookupTimer.current = setTimeout(function() {
      api(lookupUrl + '?q=' + encodeURIComponent(barcode.trim())).then(function(r) {
        if (seq !== lookupSeq.current) return;
        setLookup(r);
        // Existing exact-match auto-fill behavior
        if (r && r.product) {
          if (r.fbm_bin && r.fbm_bin.bin_code && !binEdited.current) setBin(r.fbm_bin.bin_code);
          else if (!binEdited.current) {
            var b = pickBinForProduct(r.product);
            if (b) setBin(b);
          }
          if (r.matched_po) setSelectedPo(String(r.matched_po.id));
          if (!titleEdited.current) setManualTitle(r.product.vendor_title || r.product.title || r.product.sku || '');
          if (!vskuEdited.current && r.product.vendor_sku) setManualVsku(r.product.vendor_sku);
        } else if (r && Array.isArray(r.catalog_results) && r.catalog_results.length === 1) {
          // Single fuzzy match — auto-pick it
          pickCatalogMatch(r.catalog_results[0]);
        }
      }).catch(function() { if (seq === lookupSeq.current) setLookup(null); })
      .finally(function() { if (seq === lookupSeq.current) setSearching(false); });
    }, 250);
    // Cleanup: a timer still pending when the barcode changes, the step advances or the modal
    // closes must never fire (it used to auto-pick a stale single match and jump to Receive).
    return function() { clearTimeout(lookupTimer.current); lookupSeq.current++; setSearching(false); };
  }, [barcode, open, step]);

  if (!open) return null;

  var product = lookup && lookup.product;
  var fbmBin = lookup && lookup.fbm_bin;
  var matchedPo = lookup && lookup.matched_po;
  // POs on this shipment, sorted alphabetically by vendor name (then PO#) for the picker dropdowns.
  var planPos = ((lookup && lookup.plan_pos) || []).slice().sort(function(a, b) {
    var an = (a.vendor_name || '').toLowerCase(), bn = (b.vendor_name || '').toLowerCase();
    if (an !== bn) return an < bn ? -1 : 1;
    return String(a.po_number || '').localeCompare(String(b.po_number || ''), undefined, { numeric: true });
  });
  var showPoPicker = needsPoPick || (!!product && !matchedPo && planPos.length > 1);
  var showManualFields = !product && barcode.trim().length > 0;
  // Sibling-split guard (FBA only — other lookups never return this field). If the scanned item
  // is still NEEDED on a sibling plan from the same split group, it isn't unexpected: it belongs
  // to that plan. Block the receive and point the packer there.
  var siblingMemberships = (lookup && lookup.sibling_memberships) || [];
  var siblingBlocking = siblingMemberships.filter(function(s) { return s && s.remaining > 0; });
  var siblingBlocked = siblingBlocking.length > 0;

  var ps = function(bg, fg) { return { fontSize: 10, fontWeight: 600, padding: '2px 7px', borderRadius: 4, background: bg, color: fg, letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-block' }; };

  var submit = async function() {
    if (siblingBlocked) {
      var sb = siblingBlocking[0];
      setError('This item belongs to split plan "' + (sb.plan_name || ('Plan ' + sb.plan_id)) + '" (' + sb.remaining + ' still to pack) — pack it there, it is not unexpected.');
      return;
    }
    var qtyN = parseInt(qty) || 0;
    var bc = barcode.trim();
    // Empty barcode is allowed — server uses a placeholder SKU. Require either a barcode or a title.
    if (!bc && !manualTitle.trim()) { setError('Enter a barcode or a vendor title'); return; }
    if (qtyN <= 0) { setError('Quantity must be greater than 0'); return; }
    if (!bin.trim()) { setError('Bin location is required'); return; }
    // If no barcode + no catalog match, confirm the item really has no printed barcode
    if (!bc && !product) {
      if (!(await appConfirm('No barcode entered. Confirm this item has no printed barcode (UPC/EAN) on the package?'))) return;
    }
    // Photos are mandatory for manually-entered unexpected items (not catalog-matched)
    if (!product && photoCount === 0) {
      setError('At least one photo is required for unexpected items not in our catalog. Use "Add photos" to scan the QR.');
      return;
    }

    setSubmitting(true); setError(null);
    var body = {
      barcode: barcode.trim(), qty: qtyN, bin_location: bin.trim(),
      // Send the resolved catalog product so the server matches the exact item the user
      // confirmed on screen (incl. fuzzy/keyword picks) instead of re-deriving from the
      // barcode — prevents catalog-matched extras filing as orphan UNEXPECTED-<keyword> lines.
      product_id: product ? product.id : null,
      notes: notes.trim() || null,
      vendor_title: manualTitle.trim() || null,
      vendor_sku: manualVsku.trim() || null,
      po_id: selectedPo ? parseInt(selectedPo) : null,
      force_unexpected: forceUnexpected,
      photo_token: photoToken || null,
    };
    api(submitUrl, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) })
      .then(function(resp) { onSuccess(resp); onClose(); })
      .catch(function(e) {
        var msg = e && e.message ? e.message : 'Failed to receive';
        if (msg.indexOf('Select which PO') !== -1) setNeedsPoPick(true);
        setError(msg);
      })
      .finally(function() { setSubmitting(false); });
  };

  var label = function(txt) { return React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', display: 'block', marginBottom: 4 } }, txt); };
  var inp = function(value, onChange, placeholder, extraStyle) {
    return React.createElement('input', { value: value || '', onChange: function(e) { onChange(e.target.value); }, placeholder: placeholder,
      style: Object.assign({ width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box', fontFamily: 'inherit' }, extraStyle || {}) });
  };

  var productImages = (product && product.images && product.images.length) ? product.images : (product && product.image_1_url ? [product.image_1_url] : []);
  var productCard = product && React.createElement('div', { style: { display: 'flex', gap: 10, padding: 10, background: C.surface, borderRadius: 6, marginBottom: 12, border: '1px solid ' + C.border } },
    productImages.length > 0
      ? React.createElement('img', {
          src: productImages[0],
          onClick: function() { setLightboxIdx(0); },
          title: productImages.length > 1 ? 'Click to view ' + productImages.length + ' images' : 'Click to enlarge',
          style: { width: 56, height: 56, borderRadius: 6, objectFit: 'cover', flexShrink: 0, cursor: 'pointer', transition: 'transform 0.1s' }
        })
      : React.createElement('div', { style: { width: 56, height: 56, borderRadius: 6, background: C.bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 } }, React.createElement(LIcon, { name: 'package', size: 24, color: C.textDim })),
    React.createElement('div', { style: { flex: 1, minWidth: 0 } },
      React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 4 } }, product.vendor_title || product.title || product.sku),
      React.createElement('div', { style: { display: 'flex', gap: 4, flexWrap: 'wrap', marginBottom: 4 } },
        product.sku && React.createElement('span', { style: ps(C.accent + '14', C.accent) }, 'SKU: ' + product.sku),
        product.vendor_sku && React.createElement('span', { style: ps(C.accent + '14', C.accent) }, 'VSKU: ' + product.vendor_sku),
        product.barcode && React.createElement('span', { style: ps('#06B6D4' + '18', '#06B6D4') }, 'UPC: ' + product.barcode),
        product.fnsku && React.createElement('span', { style: ps('#8B5CF6' + '18', '#8B5CF6') }, 'FNSKU: ' + product.fnsku),
        product.warehouse_code && React.createElement('span', { style: ps(product.warehouse_code === 'FBM' ? '#10B981' + '18' : C.textDim + '18', product.warehouse_code === 'FBM' ? '#10B981' : C.textMuted) }, product.warehouse_code)
      ),
      fbmBin && React.createElement('div', { style: { fontSize: 11, color: C.success, fontWeight: 600, marginTop: 2 } },
        React.createElement(LIcon, { name: 'map-pin', size: 11, color: C.success }), ' Bin: ', fbmBin.bin_code, ' (' + fbmBin.quantity + ' on hand)'
      ),
      matchedPo && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginTop: 2 } },
        'PO: ', React.createElement('span', { style: { fontWeight: 600, color: C.accent, fontFamily: 'inherit' } }, matchedPo.po_number), ' — ', matchedPo.vendor_name
      )
    )
  );

  var qtyBinNotes = [
    // Qty + Bin
    React.createElement('div', { key: 'qbin', style: { display: 'flex', gap: 8, marginBottom: 12 } },
      React.createElement('div', { style: { flex: 1 } },
        label('Qty *'),
        inp(qty, setQty, '0')
      ),
      React.createElement('div', { style: { flex: 2 } },
        label('Bin Location *'),
        React.createElement('input', {
          value: bin || '',
          onChange: function(e) { binEdited.current = true; setBin((e.target.value || '').toUpperCase()); },
          onBlur: function() { setBin(function(v) { return formatBin(v) || v; }); },
          placeholder: 'A-100',
          style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
        })
      )
    ),
    // Notes
    React.createElement('div', { key: 'notes', style: { marginBottom: 12 } },
      label('Notes (optional)'),
      inp(notes, setNotes, 'Appears in the PO receiving history')
    ),
    // Photo upload via QR
    React.createElement('div', { key: 'photo', style: { marginBottom: 14 } },
      React.createElement(window.PhotoUploadPanel, { required: !product, onTokenChange: setPhotoToken, onPhotosChange: function(p) { setPhotoCount(p.length); }, resetSignal: photoResetSignal })
    ),
  ];

  var poPicker = showPoPicker && React.createElement('div', { style: { marginBottom: 12 } },
    label('Purchase Order *'),
    React.createElement('select', { value: selectedPo || '', onChange: function(e) { setSelectedPo(e.target.value); },
      style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } },
      React.createElement('option', { value: '' }, 'Select a PO…'),
      planPos.map(function(p) {
        return React.createElement('option', { key: p.id, value: String(p.id) }, p.po_number + ' — ' + (p.vendor_name || 'Unknown vendor'));
      })
    )
  );

  var errorBox = error && React.createElement('div', { style: { padding: 10, background: 'rgba(248,113,113,0.1)', border: '1px solid rgba(248,113,113,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#F87171' } }, error);

  var siblingBanner = siblingBlocked && React.createElement('div', { style: { padding: 10, background: 'rgba(245,158,11,0.12)', border: '1px solid rgba(245,158,11,0.5)', borderRadius: 6, marginBottom: 12, fontSize: 12.5 } },
    React.createElement('div', { style: { fontWeight: 700, color: '#F59E0B', marginBottom: 4, display: 'flex', alignItems: 'center', gap: 6 } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 14, color: '#F59E0B' }),
      'Not an unexpected item — it belongs to a split shipment'),
    siblingBlocking.map(function(s, i) {
      var planLabel = s.plan_name || ('Plan ' + s.plan_id);
      var planEl = onOpenPlan
        ? React.createElement('a', {
            href: '#',
            onClick: function(e) { e.preventDefault(); onOpenPlan(s.plan_id); },
            title: 'Open ' + planLabel,
            style: { fontWeight: 700, color: C.accent, textDecoration: 'underline', cursor: 'pointer' }
          }, planLabel)
        : React.createElement('span', { style: { fontWeight: 700 } }, planLabel);
      return React.createElement('div', { key: i, style: { color: C.text, marginTop: 2 } },
        'Pack it in ', planEl,
        ' — shipment #', String(s.shipment_id), ': ',
        React.createElement('span', { style: { fontWeight: 700, color: '#F59E0B' } }, s.remaining + ' of ' + s.quantity + ' still to pack'), '.');
    }),
    React.createElement('div', { style: { color: C.textMuted, marginTop: 6 } },
      'Open that plan and scan this item into a box. Only use "Not a match? Receive manually" if you genuinely have an extra unit beyond that plan.')
  );

  // ─── STEP: Scan ───
  var scanStep = step === 'scan' && React.createElement('div', null,
    React.createElement('div', { style: { marginBottom: 6 } },
      label('Scan Barcode or FNSKU'),
      React.createElement('div', { style: { position: 'relative' } },
        React.createElement('input', {
          ref: scanInputRef,
          value: barcode,
          onChange: function(e) { setBarcode(e.target.value); },
          placeholder: 'Scan Barcode/FNSKU or type to search catalog..',
          style: { width: '100%', padding: '10px 36px 10px 12px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', boxSizing: 'border-box' }
        }),
        barcode && React.createElement('button', {
          onClick: function() {
            setBarcode(''); setLookup(null); setSearching(false);
            setManualTitle(''); setManualVsku(''); setBin(''); setSelectedPo('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            if (scanInputRef.current) scanInputRef.current.focus();
          },
          title: 'Clear',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.border; e.currentTarget.style.color = C.text; },
          onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; e.currentTarget.style.color = C.textMuted; },
          style: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 4, width: 24, height: 24, borderRadius: 4, color: C.textMuted, display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'background 0.15s, color 0.15s' }
        }, React.createElement(LIcon, { name: 'x', size: 14 }))
      )
    ),
    React.createElement('div', { style: { fontSize: 11, color: C.textDim, marginBottom: 14 } }, 'We\'ll search the catalog, preferring FBM items so the current bin shows up.'),

    // Searching…
    searching && React.createElement('div', { style: { padding: '20px 0', textAlign: 'center', color: C.textMuted, fontSize: 12 } }, 'Searching catalog…'),

    // Reusable "Not a match? Receive manually." button — danger pill styling, clears all fields
    (function() { return null; })(),

    // Match found (single product — exact or auto-picked single fuzzy)
    !searching && product && React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          title: 'Not a match — fill the unexpected item form manually',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'Not a match? Receive manually.')
      ),
      siblingBanner,
      productCard,
      siblingBlocked
        ? null
        : React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 } },
            React.createElement(Btn, { variant: 'primary', onClick: function() { setStep('receive'); }, style: { minWidth: 220, justifyContent: 'center' } },
              React.createElement(LIcon, { name: 'arrow-right', size: 14 }), ' Continue with this item'
            )
          )
    ),

    // Multi-match pick list (no exact, but multiple fuzzy results)
    !searching && !product && lookup && Array.isArray(lookup.catalog_results) && lookup.catalog_results.length > 1 && React.createElement('div', null,
      React.createElement('div', { style: { marginBottom: 10, display: 'flex', justifyContent: 'flex-end' } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          title: 'None of these match — fill the unexpected item form manually',
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'No matches? Receive manually.')
      ),
      React.createElement('div', { style: { fontSize: 12, color: C.textMuted, marginBottom: 6 } },
        lookup.catalog_capped
          ? 'Showing the first ' + lookup.catalog_results.length + ' of ' + lookup.catalog_total + ' catalog matches for "' + barcode.trim() + '". Narrow your search to see the rest.'
          : lookup.catalog_results.length + ' catalog matches for "' + barcode.trim() + '" — pick one to receive'),
      React.createElement('div', { style: { maxHeight: 360, overflow: 'auto', borderRadius: 8, border: '1px solid ' + C.border } },
        lookup.catalog_results.map(function(item, idx) {
          var imgUrl = (item.images && item.images.length) ? item.images[0] : item.image_1_url;
          var isFbm = (item.wh_code && /^FBM/i.test(item.wh_code)) || (item.sku && /-FBM(?:-|$)/i.test(item.sku));
          return React.createElement('div', { key: item.id,
            onClick: function() { pickCatalogMatch(item); },
            style: { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px', cursor: 'pointer',
              borderBottom: idx < lookup.catalog_results.length - 1 ? '1px solid ' + C.border + '44' : 'none',
              background: 'transparent', transition: 'background 0.1s' },
            onMouseEnter: function(e) { e.currentTarget.style.background = C.surface; },
            onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
          },
            React.createElement(ProductThumb, { src: imgUrl, size: 40, iconSize: 16 }),
            React.createElement('div', { style: { flex: 1, minWidth: 0 } },
              React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 6 } },
                React.createElement('span', { style: { fontSize: 13, fontWeight: 600, fontFamily: 'inherit', color: C.accent } }, item.sku),
                isFbm && React.createElement('span', { style: { background: C.success + '22', color: C.success, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, 'FBM'),
                item.wh_code && !isFbm && React.createElement('span', { style: { background: C.textDim + '22', color: C.textMuted, fontSize: 9, fontWeight: 700, padding: '1px 6px', borderRadius: 4, letterSpacing: '0.05em', textTransform: 'uppercase' } }, item.wh_code)
              ),
              React.createElement('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: 2 } }, item.vendor_title || item.title || ''),
              item.barcode && React.createElement('div', { style: { fontSize: 11, color: C.textMuted, fontFamily: 'inherit', marginTop: 2 } }, 'UPC: ', React.createElement('span', { style: { color: C.textDim } }, item.barcode))
            ),
            React.createElement(LIcon, { name: 'chevron-right', size: 16, color: C.textDim })
          );
        })
      )
    ),

    // No match at all
    !searching && !product && barcode.trim().length > 0 && (!lookup || !Array.isArray(lookup.catalog_results) || lookup.catalog_results.length === 0) && React.createElement('div', null,
      React.createElement('div', { style: { padding: 12, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 13, color: '#B45309' } },
        React.createElement(LIcon, { name: 'alert-triangle', size: 13, color: '#B45309' }), ' No catalog match for ',
        React.createElement('span', { style: { fontFamily: 'inherit', fontWeight: 600 } }, barcode.trim()),
        '. Enter the item details manually.'
      ),
      React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', gap: 8 } },
        React.createElement('button', {
          onClick: function() {
            // Keep the scanned barcode — it carries into the manual form's Barcode field so the
            // user doesn't have to re-type it (it's the unexpected item's real barcode).
            // Preserve plan_pos so the manual step can still render its required "Purchase Order"
            // dropdown. Wiping the whole lookup left planPos empty, so the dropdown never showed,
            // selectedPo could never be set, and the Receive button stayed permanently greyed out.
            setLookup(lookup && lookup.plan_pos ? { plan_pos: lookup.plan_pos } : null); setBin(''); setManualTitle(''); setManualVsku(''); setSelectedPo(''); setNotes(''); setQty('');
            binEdited.current = false; titleEdited.current = false; vskuEdited.current = false;
            setForceUnexpected(true); setStep('manual');
          },
          onMouseEnter: function(e) { e.currentTarget.style.background = C.danger + '33'; e.currentTarget.style.borderColor = C.danger; },
          onMouseLeave: function(e) { e.currentTarget.style.background = C.danger + '18'; e.currentTarget.style.borderColor = C.danger + '44'; },
          style: { background: C.danger + '18', border: '1px solid ' + C.danger + '44', color: C.danger, padding: '6px 12px', borderRadius: 4, fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit', whiteSpace: 'nowrap', transition: 'all 0.15s ease' }
        }, 'No matches? Receive manually.')
      )
    )
  );

  var backToScanBtn = React.createElement(Btn, { onClick: function() { setStep('scan'); setTimeout(function() { if (scanInputRef.current) scanInputRef.current.focus(); }, 50); } },
    React.createElement(LIcon, { name: 'arrow-left', size: 14 }), ' Back'
  );

  // ─── STEP: Receive (catalog match) ───
  var receiveStep = step === 'receive' && React.createElement('div', null,
    productCard,
    siblingBanner,
    poPicker,
    qtyBinNotes,
    errorBox,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
      backToScanBtn,
      React.createElement(Btn, { variant: 'primary', onClick: submit, disabled: submitting || !(parseInt(qty) > 0) || !bin.trim() || siblingBlocked },
        submitting ? 'Receiving…' : React.createElement('span', null, React.createElement(LIcon, { name: 'check', size: 14 }), ' Receive')
      )
    )
  );

  // ─── STEP: Manual (no catalog match) ───
  var manualStep = step === 'manual' && React.createElement('div', null,
    React.createElement('div', { style: { padding: 10, background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)', borderRadius: 6, marginBottom: 12, fontSize: 12, color: '#B45309' } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 12, color: '#B45309' }), ' No catalog match — details below will be stored on the PO line.'
    ),
    React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, marginBottom: 12 } },
      React.createElement('div', null,
        label('Barcode'),
        inp(barcode, setBarcode, 'Scan or enter barcode. If the item has no barcode leave BLANK.')
      ),
      React.createElement('div', null,
        label('Vendor SKU'),
        inp(manualVsku, function(v) { vskuEdited.current = true; setManualVsku(v); }, 'Vendor item #…')
      )
    ),
    React.createElement('div', { style: { marginBottom: 12 } },
      label('Item Name'),
      inp(manualTitle, function(v) { titleEdited.current = true; setManualTitle(v); }, 'Item name / description')
    ),
    poPicker || (planPos.length > 0 && React.createElement('div', { style: { marginBottom: 12 } },
      label('Purchase Order *'),
      React.createElement('select', { value: selectedPo || '', onChange: function(e) { setSelectedPo(e.target.value); },
        style: { width: '100%', padding: '8px 10px', fontSize: 13, border: '1px solid ' + C.border, borderRadius: 6, background: C.bg, color: C.text, outline: 'none', fontFamily: 'inherit' } },
        React.createElement('option', { value: '' }, 'Select a PO…'),
        planPos.map(function(p) {
          return React.createElement('option', { key: p.id, value: String(p.id) }, p.po_number + ' — ' + (p.vendor_name || 'Unknown vendor'));
        })
      )
    )),
    qtyBinNotes,
    errorBox,
    React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', gap: 8 } },
      backToScanBtn,
      React.createElement(Btn, { variant: 'primary', onClick: submit, disabled: submitting || !barcode.trim() || !(parseInt(qty) > 0) || !bin.trim() || !selectedPo },
        submitting ? 'Receiving…' : React.createElement('span', null, React.createElement(LIcon, { name: 'check', size: 14 }), ' Receive')
      )
    )
  );

  var lightboxOverlay = lightboxIdx >= 0 && productImages.length > 0 && React.createElement('div', {
    onClick: function() { setLightboxIdx(-1); },
    style: { position: 'fixed', inset: 0, zIndex: 10000, background: 'rgba(0,0,0,0.88)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }
  },
    React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx(-1); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'none'; e.currentTarget.style.transform = 'scale(1)'; },
      style: { position: 'absolute', top: 16, right: 16, background: 'none', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 28, lineHeight: 1, width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '×'),
    productImages.length > 1 && React.createElement('div', { style: { position: 'absolute', top: 20, left: '50%', transform: 'translateX(-50%)', color: '#fff', fontSize: 13, fontWeight: 600, zIndex: 10002 } },
      (lightboxIdx + 1) + ' / ' + productImages.length),
    productImages.length > 1 && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx((lightboxIdx - 1 + productImages.length) % productImages.length); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', left: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '‹'),
    productImages.length > 1 && React.createElement('button', {
      onClick: function(e) { e.stopPropagation(); setLightboxIdx((lightboxIdx + 1) % productImages.length); },
      onMouseEnter: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.35)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1.1)'; },
      onMouseLeave: function(e) { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; e.currentTarget.style.transform = 'translateY(-50%) scale(1)'; },
      style: { position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', background: 'rgba(255,255,255,0.15)', border: 'none', color: '#fff', cursor: 'pointer', fontSize: 24, fontWeight: 700, width: 44, height: 44, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10002, transition: 'background 0.15s, transform 0.15s' }
    }, '›'),
    React.createElement('img', {
      src: productImages[lightboxIdx],
      onClick: function(e) { e.stopPropagation(); },
      style: { maxWidth: 'calc(85vw * var(--invzoom, 1))', maxHeight: 'calc(85vh * var(--invzoom, 1))', objectFit: 'contain', borderRadius: 8, cursor: 'default' }
    }),
    productImages.length > 1 && React.createElement('div', {
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'absolute', bottom: 20, left: '50%', transform: 'translateX(-50%)', display: 'flex', gap: 8, padding: '8px 12px', background: 'rgba(0,0,0,0.6)', borderRadius: 8 }
    },
      productImages.map(function(url, i) {
        return React.createElement('img', { key: i, src: url, onClick: function() { setLightboxIdx(i); },
          style: { width: 52, height: 52, objectFit: 'cover', borderRadius: 6, border: '2px solid ' + (i === lightboxIdx ? '#fff' : 'transparent'), cursor: 'pointer', opacity: i === lightboxIdx ? 1 : 0.6, transition: 'all 0.15s' } });
      })
    )
  );

  var title = step === 'scan' ? 'Scan barcode or FNSKU' : (step === 'manual' ? 'Enter item manually' : 'Receive unexpected item');
  return React.createElement(React.Fragment, null,
    React.createElement(Modal, { open: open, onClose: onClose, title: title, width: 640 },
      React.createElement('div', null, scanStep, receiveStep, manualStep)
    ),
    lightboxOverlay
  );
}

window.UnexpectedItemModal = UnexpectedItemModal;

// ─── @mention autocomplete for note inputs ───
// Cache the mentionable roster once per session; lookups happen as the user types.
var _mentionableUsers = null;
var _mentionableLoading = null;
function loadMentionableUsers() {
  if (_mentionableUsers) return Promise.resolve(_mentionableUsers);
  if (_mentionableLoading) return _mentionableLoading;
  _mentionableLoading = api('/auth/users/mentionable')
    .then(function(rows) { _mentionableUsers = rows || []; return _mentionableUsers; })
    .catch(function(e) { _mentionableLoading = null; console.error('[mentions] load failed', e); return []; });
  return _mentionableLoading;
}
window.invalidateMentionableUsers = function() { _mentionableUsers = null; _mentionableLoading = null; };

// MentionInput — drop-in textarea with @-autocomplete.
// Props:
//   value, onChange(value), placeholder, rows, style, disabled,
//   onKeyDown (forwarded after the popup has had its chance to consume the key)
function MentionInput(props) {
  var value = props.value || '';
  var onChange = props.onChange;
  var inputRef = useRef(null);
  var [users, setUsers] = useState([]);
  var [popup, setPopup] = useState(null); // { query, start, end, anchor: {top, left}, items, index }
  useEffect(function() { loadMentionableUsers().then(setUsers); }, []);

  // Detect the @token the cursor is currently in (if any) and rebuild the popup.
  function rebuildPopup(text, caret) {
    if (caret == null) { setPopup(null); return; }
    // Walk left from caret to find an @, breaking on whitespace.
    var i = caret;
    while (i > 0) {
      var ch = text.charAt(i - 1);
      if (ch === '@') {
        // Must be at string start or preceded by whitespace.
        if (i - 2 < 0 || /\s/.test(text.charAt(i - 2))) {
          var query = text.slice(i, caret);
          // Don't fire on whitespace-only or trailing space.
          if (/\s/.test(query)) { setPopup(null); return; }
          var q = query.toLowerCase();
          var filtered = users.filter(function(u) {
            var dn = (u.display_name || '').toLowerCase();
            var un = (u.username || '').toLowerCase();
            return dn.indexOf(q) !== -1 || un.indexOf(q) !== -1;
          }).slice(0, 8);
          if (!filtered.length) { setPopup(null); return; }
          setPopup({ query: query, start: i - 1, end: caret, items: filtered, index: 0 });
          return;
        }
        setPopup(null);
        return;
      }
      if (/\s/.test(ch)) { setPopup(null); return; }
      i--;
    }
    setPopup(null);
  }

  function handleChange(e) {
    var v = e.target.value;
    if (onChange) onChange(v);
    setTimeout(function() {
      var el = inputRef.current; if (!el) return;
      rebuildPopup(v, el.selectionStart);
    }, 0);
  }

  function pickUser(u) {
    if (!popup || !inputRef.current) return;
    var before = value.slice(0, popup.start);
    var after = value.slice(popup.end);
    // Insert the display name (spaces collapsed to dots) so the chip reads as a
    // human name rather than an email-style username.
    var token = (u.display_name || u.username || '').replace(/\s+/g, '.');
    var inserted = '@' + token;
    var newVal = before + inserted + ' ' + after;
    if (onChange) onChange(newVal);
    setPopup(null);
    setTimeout(function() {
      var el = inputRef.current; if (!el) return;
      var pos = before.length + inserted.length + 1;
      el.focus();
      try { el.setSelectionRange(pos, pos); } catch (e) {}
    }, 0);
  }

  function handleKeyDown(e) {
    if (popup) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setPopup(Object.assign({}, popup, { index: (popup.index + 1) % popup.items.length })); return; }
      if (e.key === 'ArrowUp')   { e.preventDefault(); setPopup(Object.assign({}, popup, { index: (popup.index - 1 + popup.items.length) % popup.items.length })); return; }
      if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); pickUser(popup.items[popup.index]); return; }
      if (e.key === 'Escape') { e.preventDefault(); setPopup(null); return; }
    }
    if (props.onKeyDown) props.onKeyDown(e);
  }

  return React.createElement('div', { style: { position: 'relative' } },
    React.createElement('textarea', Object.assign({}, {
      ref: inputRef,
      value: value,
      onChange: handleChange,
      onKeyDown: handleKeyDown,
      onSelect: function() { var el = inputRef.current; if (el) rebuildPopup(value, el.selectionStart); },
      onPaste: props.onPaste,
      placeholder: props.placeholder,
      rows: props.rows || 2,
      disabled: props.disabled,
      style: Object.assign({ width: '100%', boxSizing: 'border-box', fontFamily: 'inherit', resize: 'vertical' }, props.style || {}),
    })),
    popup && React.createElement('div', {
      style: { position: 'absolute', left: 0, bottom: '100%', marginBottom: 4, zIndex: 50, background: C.surface, border: '1px solid ' + C.border, borderRadius: 6, boxShadow: '0 6px 24px rgba(0,0,0,0.4)', minWidth: 200, maxWidth: 320, padding: 4 }
    },
      popup.items.map(function(u, idx) {
        var active = idx === popup.index;
        return React.createElement('div', {
          key: u.id,
          onMouseDown: function(e) { e.preventDefault(); pickUser(u); },
          onMouseEnter: function() { setPopup(Object.assign({}, popup, { index: idx })); },
          style: { padding: '6px 10px', borderRadius: 4, cursor: 'pointer', background: active ? C.accent + '22' : 'transparent', fontSize: 13, color: C.text, fontWeight: 600 }
        }, u.display_name || u.username);
      })
    )
  );
}
window.MentionInput = MentionInput;

// Renders note body text with @mentions visually highlighted as inline chips
// and URLs as clickable new-tab links. Render-time only — notes stay plain text.
function renderNoteBody(text) {
  var parts = [];
  var re = /(@[a-zA-Z0-9_.-]+)|(https?:\/\/[^\s<>"']+)|(\bwww\.[^\s<>"']+)/g;
  var last = 0; var match;
  var i = 0;
  while ((match = re.exec(text)) !== null) {
    if (match.index > last) parts.push(text.slice(last, match.index));
    if (match[1]) {
      parts.push(React.createElement('span', { key: 'm' + (i++), style: { background: 'rgba(251,191,36,0.22)', color: '#FBBF24', padding: '1px 6px', borderRadius: 4, fontWeight: 700, border: '1px solid rgba(251,191,36,0.45)' } }, match[0]));
    } else {
      // Trailing sentence punctuation belongs to the prose, not the URL;
      // a trailing ")" stays only when the URL itself contains a "(".
      var url = match[0];
      var trail = '';
      while (/[.,;:!?"']$/.test(url) || (url.slice(-1) === ')' && url.indexOf('(') === -1)) {
        trail = url.slice(-1) + trail;
        url = url.slice(0, -1);
      }
      // color:inherit — own-note bubbles are white-on-accent, a default blue link would vanish
      parts.push(React.createElement('a', {
        key: 'u' + (i++),
        href: match[2] ? url : 'https://' + url,
        target: '_blank', rel: 'noopener noreferrer',
        style: { color: 'inherit', textDecoration: 'underline', textUnderlineOffset: 2, fontWeight: 600, wordBreak: 'break-all' }
      }, url));
      if (trail) parts.push(trail);
    }
    last = match.index + match[0].length;
  }
  if (last < text.length) parts.push(text.slice(last));
  return parts;
}
window.renderNoteBody = renderNoteBody;

// Renders a note's attachments inside its bubble: image thumbnails (click opens
// full size in a new tab) and download chips for documents. Notes fetched from
// GET /purchase-orders/:poId/notes carry an attachments array + po_id.
function renderNoteAttachments(note) {
  var atts = note.attachments || [];
  if (!atts.length) return null;
  function fmtSize(b) {
    if (!b) return '';
    if (b < 1024) return b + ' B';
    if (b < 1024 * 1024) return Math.round(b / 1024) + ' KB';
    return (b / (1024 * 1024)).toFixed(1) + ' MB';
  }
  return React.createElement('div', { style: { display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: note.message ? 6 : 0 } },
    atts.map(function(a) {
      var url = API_BASE + '/purchase-orders/' + note.po_id + '/notes/attachment/' + a.id;
      if (/^image\//i.test(a.mime_type || '')) {
        return React.createElement('a', { key: a.id, href: url, target: '_blank', rel: 'noopener noreferrer', style: { display: 'block' } },
          React.createElement('img', { src: url, alt: a.name, title: a.name,
            style: { maxWidth: 180, maxHeight: 140, borderRadius: 8, display: 'block', border: '1px solid rgba(0,0,0,0.15)' } }));
      }
      return React.createElement('a', { key: a.id, href: url, target: '_blank', rel: 'noopener noreferrer', title: a.name,
        style: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 10px', borderRadius: 8,
          background: 'rgba(0,0,0,0.12)', color: 'inherit', textDecoration: 'none', fontSize: 12, fontWeight: 600, maxWidth: 240 } },
        React.createElement(LIcon, { name: 'paperclip', size: 12 }),
        React.createElement('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, a.name),
        a.file_size ? React.createElement('span', { style: { opacity: 0.7, fontWeight: 400, flexShrink: 0 } }, fmtSize(a.file_size)) : null);
    })
  );
}
window.renderNoteAttachments = renderNoteAttachments;

// Deep link to a bill in the Bill.com web app. app.bill.com redirects to the
// org's regional host (app02.us.…) and lands on Bill Details — verified live 2026-08-13.
function bdcBillUrl(bdcId) { return 'https://app.bill.com/neo/bills/' + encodeURIComponent(bdcId); }
window.bdcBillUrl = bdcBillUrl;

// Hover wrapper for Bill.com-linked elements (native title= can't hold links).
// Wraps `children` and shows the shared card on hover: muted tip line, then either
// one row per bill in `bills` ([{bdc_id, invoice_number, amount, payment_status}] —
// each its own Bill.com link) or the single detail/bdcBillId pair. Used by the
// PO Billed badge and the accounting cash-cycle BILL.COM pill — one look everywhere.
function BdcBillHover({ tip, detail, bdcBillId, bills, children }) {
  var _o = useState(false), open = _o[0], setOpen = _o[1];
  var _p = useState(null), pos = _p[0], setPos = _p[1];
  var closeTimer = useRef(null);
  var show = function(e) {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    var r = e.currentTarget.getBoundingClientRect();
    setPos({ x: r.left + r.width / 2, y: r.bottom + 6 });
    setOpen(true);
  };
  // Grace delay so the pointer can travel from the pill into the card.
  var hide = function() { closeTimer.current = setTimeout(function() { setOpen(false); }, 250); };
  var cancelHide = function() { if (closeTimer.current) clearTimeout(closeTimer.current); };
  // Portaled to <body>: cash-cycle "complete" rows fade themselves with opacity 0.6,
  // which would fade any in-row descendant — including this fixed-position card.
  return React.createElement('span', { onMouseEnter: show, onMouseLeave: hide, style: { display: 'inline-flex' } },
    children,
    open && pos && ReactDOM.createPortal(React.createElement('div', {
      onMouseEnter: cancelHide, onMouseLeave: hide,
      onClick: function(e) { e.stopPropagation(); },
      style: { position: 'fixed', left: pos.x, top: pos.y, transform: 'translateX(-50%)', zIndex: 10500,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: '9px 12px',
        boxShadow: '0 8px 24px rgba(0,0,0,0.25)', minWidth: 190, maxWidth: 300, cursor: 'default',
        fontSize: 12, fontWeight: 400, color: C.text, textTransform: 'none', letterSpacing: 'normal', textAlign: 'left', lineHeight: 1.45, textDecoration: 'none' }
    },
      React.createElement('div', { style: { color: C.textMuted, fontSize: 11 } }, tip),
      (bills && bills.length > 0)
        ? bills.map(function(bill) {
            var label = 'Invoice ' + (bill.invoice_number || '—')
              + (bill.amount != null ? ' · $' + Number(bill.amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
              + (bill.payment_status === 'paid' ? ' · paid' : '');
            return React.createElement('a', {
              key: bill.bdc_id,
              href: bdcBillUrl(bill.bdc_id), target: '_blank', rel: 'noopener noreferrer',
              onClick: function(e) { e.stopPropagation(); },
              onMouseDown: function(e) { e.stopPropagation(); },
              style: { display: 'flex', alignItems: 'center', gap: 5, marginTop: 5, color: C.accent, fontWeight: 600, fontSize: 12, textDecoration: 'none', whiteSpace: 'nowrap' }
            }, label, React.createElement(LIcon, { name: 'external-link', size: 11 }));
          })
        : [
            detail && React.createElement('div', { key: 'd', style: { marginTop: 5, fontWeight: 600 } }, detail),
            bdcBillId && React.createElement('a', {
              key: 'l',
              href: bdcBillUrl(bdcBillId), target: '_blank', rel: 'noopener noreferrer',
              onClick: function(e) { e.stopPropagation(); },
              onMouseDown: function(e) { e.stopPropagation(); },
              style: { display: 'inline-flex', alignItems: 'center', gap: 5, marginTop: 6, color: C.accent, fontWeight: 600, fontSize: 12, textDecoration: 'none' }
            }, 'Open in Bill.com', React.createElement(LIcon, { name: 'external-link', size: 12 }))
          ]
    ), document.body)
  );
}
window.BdcBillHover = BdcBillHover;

// Billed/Unbilled pill for POs. big=true renders the PO-detail-header size.
// bills = full linked_bills array (several bills can invoice one PO); falls back
// to the single bdcBillId/invoiceNumber/amount fields when the array is absent.
function BilledBadge({ billed, isTransfer, source, bdcBillId, invoiceNumber, amount, bills, big }) {
  var b = billed || isTransfer;
  var n = bills ? bills.length : 0;
  var tip = isTransfer ? 'Internal transfer — no Bill.com bill needed'
    : !b ? 'No Bill.com bill linked yet'
    : n > 1 ? n + ' Bill.com bills are linked to this PO' + (source ? ' (' + source + ')' : '')
    : 'A Bill.com bill is linked to this PO' + (source ? ' (' + source + ')' : '');
  var detail = bdcBillId
    ? 'Invoice ' + (invoiceNumber || '—') + (amount != null ? ' · $' + Number(amount).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '')
    : null;
  var pillStyle = big
    ? { padding: '3px 10px', borderRadius: 4, fontSize: 11, fontWeight: 600 }
    : { padding: '2px 8px', borderRadius: 99, fontSize: 10, fontWeight: 600 };
  return React.createElement(BdcBillHover, { tip: tip, detail: detail, bdcBillId: bdcBillId, bills: bills },
    React.createElement('span', { style: Object.assign({ background: (b ? C.success : C.textMuted) + '22', color: b ? C.success : C.textMuted, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' }, pillStyle) }, b ? 'Billed' : 'Unbilled')
  );
}
window.BilledBadge = BilledBadge;

// Global paste sanitizer — strips leading/trailing whitespace from pastes into any search input.
// Catches stray spaces from copy/paste (e.g. UPC "844828086538 " from another system).
(function() {
  function isSearchInput(el) {
    if (!el || el.tagName !== 'INPUT') return false;
    var t = (el.type || '').toLowerCase();
    if (t && t !== 'text' && t !== 'search') return false;
    var ph = (el.placeholder || '').toLowerCase();
    return ph.indexOf('search') !== -1;
  }
  document.addEventListener('paste', function(e) {
    var el = e.target;
    if (!isSearchInput(el)) return;
    var cd = e.clipboardData || window.clipboardData;
    if (!cd) return;
    var pasted = cd.getData('text');
    if (pasted == null) return;
    var trimmed = pasted.trim();
    if (trimmed === pasted) return; // nothing to fix
    e.preventDefault();
    var start = el.selectionStart || 0;
    var end = el.selectionEnd || 0;
    var newVal = el.value.substring(0, start) + trimmed + el.value.substring(end);
    // Use the native value setter so React's controlled-input change detection fires
    var nativeSetter = Object.getOwnPropertyDescriptor(window.HTMLInputElement.prototype, 'value').set;
    nativeSetter.call(el, newVal);
    el.dispatchEvent(new Event('input', { bubbles: true }));
    var caret = start + trimmed.length;
    try { el.setSelectionRange(caret, caret); } catch(_) {}
  }, true);
})();

// Session heartbeat — ping server when user is active to prevent timeout
(function() {
  var HEARTBEAT_INTERVAL = 5 * 60 * 1000; // 5 minutes
  var _lastUserActivity = Date.now();
  var _heartbeatTimer = null;

  function onUserActivity() { _lastUserActivity = Date.now(); }

  ['mousemove', 'mousedown', 'keydown', 'scroll', 'touchstart'].forEach(function(evt) {
    document.addEventListener(evt, onUserActivity, { passive: true });
  });

  function sendHeartbeat() {
    // Only ping if user was active in the last interval
    if (Date.now() - _lastUserActivity < HEARTBEAT_INTERVAL) {
      fetch('/api/auth/heartbeat', { credentials: 'include' }).catch(function(e) { console.error('[silent-catch]', e); });
    }
  }

  _heartbeatTimer = setInterval(sendHeartbeat, HEARTBEAT_INTERVAL);
})();

// ─── Number inputs: no mousewheel spin ───
// Chrome increments/decrements a FOCUSED <input type=number> when the wheel fires over it —
// scrolling the page past a focused qty/cost field silently changes its value. While a number
// input is focused, swallow wheel events on that element (attached per-focused-input so normal
// page scrolling stays on the passive fast path). Typing and arrow keys are unaffected.
(function() {
  function preventSpin(e) { e.preventDefault(); }
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (el.tagName === 'INPUT' && el.type === 'number') el.addEventListener('wheel', preventSpin, { passive: false });
  });
  document.addEventListener('focusout', function(e) {
    var el = e.target;
    if (el.tagName === 'INPUT' && el.type === 'number') el.removeEventListener('wheel', preventSpin);
  });
})();

// ─── Global Undo (Ctrl+Z) for React controlled inputs ───
(function() {
  var undoStacks = new WeakMap();
  var nativeSetter = Object.getOwnPropertyDescriptor(HTMLInputElement.prototype, 'value').set;
  var textareaSetter = Object.getOwnPropertyDescriptor(HTMLTextAreaElement.prototype, 'value').set;

  // Track previous values on focus and before each change
  document.addEventListener('focusin', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    if (!undoStacks.has(el)) undoStacks.set(el, []);
  }, true);

  document.addEventListener('input', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    if (!undoStacks.has(el)) undoStacks.set(el, []);
    var stack = undoStacks.get(el);
    // Store the value before this input event (use a short delay to capture after React re-render)
    if (!el._undoPrev && el._undoPrev !== '') el._undoPrev = '';
    if (stack.length === 0 || stack[stack.length - 1] !== el._undoPrev) {
      stack.push(el._undoPrev);
      if (stack.length > 50) stack.shift();
    }
    el._undoPrev = el.value;
  }, true);

  // Capture the value before input changes
  document.addEventListener('keydown', function(e) {
    var el = e.target;
    if (el.tagName !== 'INPUT' && el.tagName !== 'TEXTAREA') return;
    el._undoPrev = el.value;

    // Handle Ctrl+Z
    if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
      var stack = undoStacks.get(el);
      if (!stack || stack.length === 0) return;
      e.preventDefault();
      var prev = stack.pop();
      var setter = el.tagName === 'TEXTAREA' ? textareaSetter : nativeSetter;
      setter.call(el, prev);
      el._undoPrev = prev;
      el.dispatchEvent(new Event('input', { bubbles: true }));
    }
  }, true);
})();

// Expose all shared symbols to window for cross-file access
window.C = C; window.themes = themes; window.statusColors = statusColors;
window.setThemeColors = setThemeColors; window.getThemeName = getThemeName; window.getStatusColors = getStatusColors;
window.api = api; window.apiUpload = apiUpload;

// thumbUrl(originUrl, width?) — proxy external product images through our local
// resize+cache route. Returns originUrl unchanged for empty/relative/local URLs
// or hosts we don't proxy (the route's allowlist is the source of truth, but the
// client matches it here so we don't waste a round-trip for non-allowlisted hosts).
// width should be one of [72, 144, 256, 512]; omit for the full-size original.
var THUMB_PROXY_HOSTS = { 'images.zentail.com': 1, 'm.media-amazon.com': 1, 'cloudinary.images-iherb.com': 1 };
function thumbUrl(originUrl, width) {
  if (!originUrl || typeof originUrl !== 'string') return originUrl;
  if (originUrl.indexOf('/api/img') === 0) return originUrl;
  var host;
  try { host = new URL(originUrl).hostname; } catch (e) { return originUrl; }
  if (!THUMB_PROXY_HOSTS[host]) return originUrl;
  var qs = 'u=' + encodeURIComponent(originUrl);
  if (width) qs += '&w=' + width;
  return '/api/img?' + qs;
}
window.thumbUrl = thumbUrl;

// ProductThumb — a product image that falls back to the package icon when the bytes never
// arrive (0-byte object at the origin, dead URL, 404 from /api/img — error_log 761,
// 2026-10-06). Draws the same square the inline img/placeholder pairs drew: `size` is the
// side, `radius` the corner, `iconSize` the placeholder icon; `onClick` makes it a pointer.
function ProductThumb(props) {
  var st = React.useState(false), failed = st[0], setFailed = st[1];
  var src = props.src || null;
  React.useEffect(function() { setFailed(false); }, [src]);
  var size = props.size || 40, radius = props.radius || 6;
  var box = Object.assign({ width: size, height: size, borderRadius: radius, flexShrink: 0 }, props.style || {});
  if (!src || failed) {
    return React.createElement('div', { style: Object.assign({}, box, { background: C.surface, display: 'flex', alignItems: 'center', justifyContent: 'center' }) },
      React.createElement(LIcon, { name: 'package', size: props.iconSize || Math.round(size * 0.4), color: C.textDim }));
  }
  return React.createElement('img', { src: src, alt: props.alt || '', loading: 'lazy', decoding: 'async', title: props.title, onClick: props.onClick,
    onError: function() { setFailed(true); },
    style: Object.assign({}, box, { objectFit: 'cover', border: '1px solid ' + C.border, cursor: props.onClick ? 'pointer' : undefined }) });
}
window.ProductThumb = ProductThumb;
window.setAuthToken = setAuthToken; window.getAuthToken = getAuthToken;
window.hasPermission = hasPermission; window.canEdit = canEdit; window.canManageUsers = canManageUsers; window.canUpdatePOStatus = canUpdatePOStatus;
// ─── State / province dropdown shared by every address form (Anthony 2026-09-19) ───
// US (50 states, DC, the territories and the military codes), Canada and Australia get a
// select of "code — name" and store the code; any other country keeps a text field. A value
// already saved that is not in the list (e.g. "Florida" spelled out) stays visible as
// "(saved)" until someone picks the code. onChange receives an event-shaped { target: { value } }
// so the forms' existing handlers work unchanged.
var STATE_LISTS = {
  US: [['AL', 'Alabama'], ['AK', 'Alaska'], ['AZ', 'Arizona'], ['AR', 'Arkansas'], ['CA', 'California'], ['CO', 'Colorado'], ['CT', 'Connecticut'], ['DE', 'Delaware'],
    ['DC', 'District of Columbia'], ['FL', 'Florida'], ['GA', 'Georgia'], ['HI', 'Hawaii'], ['ID', 'Idaho'], ['IL', 'Illinois'], ['IN', 'Indiana'], ['IA', 'Iowa'],
    ['KS', 'Kansas'], ['KY', 'Kentucky'], ['LA', 'Louisiana'], ['ME', 'Maine'], ['MD', 'Maryland'], ['MA', 'Massachusetts'], ['MI', 'Michigan'], ['MN', 'Minnesota'],
    ['MS', 'Mississippi'], ['MO', 'Missouri'], ['MT', 'Montana'], ['NE', 'Nebraska'], ['NV', 'Nevada'], ['NH', 'New Hampshire'], ['NJ', 'New Jersey'], ['NM', 'New Mexico'],
    ['NY', 'New York'], ['NC', 'North Carolina'], ['ND', 'North Dakota'], ['OH', 'Ohio'], ['OK', 'Oklahoma'], ['OR', 'Oregon'], ['PA', 'Pennsylvania'], ['RI', 'Rhode Island'],
    ['SC', 'South Carolina'], ['SD', 'South Dakota'], ['TN', 'Tennessee'], ['TX', 'Texas'], ['UT', 'Utah'], ['VT', 'Vermont'], ['VA', 'Virginia'], ['WA', 'Washington'],
    ['WV', 'West Virginia'], ['WI', 'Wisconsin'], ['WY', 'Wyoming'],
    ['PR', 'Puerto Rico'], ['VI', 'U.S. Virgin Islands'], ['GU', 'Guam'], ['AS', 'American Samoa'], ['MP', 'Northern Mariana Islands'],
    ['AA', 'Armed Forces Americas'], ['AE', 'Armed Forces Europe'], ['AP', 'Armed Forces Pacific']],
  CA: [['AB', 'Alberta'], ['BC', 'British Columbia'], ['MB', 'Manitoba'], ['NB', 'New Brunswick'], ['NL', 'Newfoundland and Labrador'], ['NS', 'Nova Scotia'],
    ['NT', 'Northwest Territories'], ['NU', 'Nunavut'], ['ON', 'Ontario'], ['PE', 'Prince Edward Island'], ['QC', 'Quebec'], ['SK', 'Saskatchewan'], ['YT', 'Yukon']],
  AU: [['ACT', 'Australian Capital Territory'], ['NSW', 'New South Wales'], ['NT', 'Northern Territory'], ['QLD', 'Queensland'], ['SA', 'South Australia'],
    ['TAS', 'Tasmania'], ['VIC', 'Victoria'], ['WA', 'Western Australia']],
};
var STATE_COUNTRY_ALIASES = { USA: 'US', 'UNITED STATES': 'US', 'UNITED STATES OF AMERICA': 'US', CAN: 'CA', CANADA: 'CA', AUS: 'AU', AUSTRALIA: 'AU' };
function stateListFor(country) {
  var c = String(country == null || country === '' ? 'US' : country).trim().toUpperCase();
  return STATE_LISTS[STATE_COUNTRY_ALIASES[c] || c] || null;
}
function stateCodesFor(country) { var l = stateListFor(country); return l ? l.map(function(x) { return x[0]; }) : null; }
function StateSelect({ label, value, onChange, country, style: extraStyle, placeholder, compact, disabled, title }) {
  var list = stateListFor(country);
  if (!list) return React.createElement(Inp, { label: label, value: value || '', onChange: onChange, style: extraStyle, placeholder: placeholder || 'State / province', disabled: disabled, title: title });
  var cur = String(value || '').trim().toUpperCase();
  var known = list.some(function(x) { return x[0] === cur; });
  var baseStyle = { background: C.bg, border: '1px solid ' + C.border, color: C.text, padding: compact ? '4px 8px' : '8px 12px', borderRadius: compact ? 4 : 6, fontSize: compact ? 11 : 13,
    fontFamily: 'inherit', outline: 'none', minWidth: 0, width: '100%', boxSizing: 'border-box', cursor: disabled ? 'default' : 'pointer', transition: 'border-color 0.15s, box-shadow 0.15s' };
  var select = React.createElement('select', {
    value: cur, disabled: disabled, title: title,
    onChange: function(e) { if (onChange) onChange({ target: { value: e.target.value } }); },
    onFocus: function(e) { e.target.style.borderColor = C.accent; e.target.style.boxShadow = '0 0 0 2px ' + C.accent + '33'; },
    onBlur: function(e) { e.target.style.borderColor = C.border; e.target.style.boxShadow = 'none'; },
    style: Object.assign({}, baseStyle, extraStyle || {})
  },
    React.createElement('option', { value: '' }, placeholder || '— State —'),
    (cur && !known) ? React.createElement('option', { value: cur }, cur + ' (saved)') : null,
    list.map(function(x) { return React.createElement('option', { key: x[0], value: x[0] }, x[0] + ' — ' + x[1]); }));
  if (!label) return select;
  return React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: 4, minWidth: 0 } },
    React.createElement('label', { style: { fontSize: 11, fontWeight: 600, color: C.textMuted, textTransform: 'uppercase', letterSpacing: '0.06em' } }, label),
    select);
}
window.STATE_LISTS = STATE_LISTS; window.stateListFor = stateListFor; window.stateCodesFor = stateCodesFor; window.StateSelect = StateSelect;
window.Badge = Badge; window.Btn = Btn; window.Inp = Inp; window.Sel = Sel; window.SearchSelect = SearchSelect;
window.Card = Card; window.Modal = Modal; window.Empty = Empty; window.Toast = Toast;
window.AppDialogHost = AppDialogHost; window.appConfirm = appConfirm; window.appPrompt = appPrompt; window.appAlert = appAlert; window.appChoice = appChoice;
window.TH = TH; window.TD = TD; window.DataTable = DataTable; window.CopyCell = CopyCell; window.SearchInput = SearchInput;
window.loadColumnPrefs = loadColumnPrefs; window.saveColumnPrefs = saveColumnPrefs; window.resetColumnPrefsCache = resetColumnPrefsCache;
window.downloadCSV = downloadCSV; window.escapeCSV = escapeCSV; window.arrayToCSV = arrayToCSV; window.downloadTemplate = downloadTemplate;
window.channelIcon = channelIcon;
window.FillBar = FillBar; window.BinBadge = BinBadge;

// Gold "TOP" chip for products tagged by the nightly top-revenue job — pass any row that
// carries the product's top_revenue_* columns (catalog rows, PO line items).
function TopRevChip(row) {
  if (!row || !row.is_top_revenue) return null;
  var amt = row.top_revenue_amount != null ? '$' + Math.round(Number(row.top_revenue_amount)).toLocaleString() : '';
  var tip = 'Top revenue' + (row.top_revenue_rank ? ' #' + row.top_revenue_rank : '') +
            (amt ? ' · ' + amt + ' / ' + (row.top_revenue_window_days || 90) + 'd' : '');
  return React.createElement('span', { title: tip, style: { fontSize: 9, fontWeight: 700, padding: '1px 5px',
    borderRadius: 6, background: '#EAB30818', color: '#EAB308', marginLeft: 6, verticalAlign: 'middle',
    letterSpacing: '0.03em', whiteSpace: 'nowrap' } }, 'TOP');
}
window.TopRevChip = TopRevChip;

// Amazon Transparency program logo — shown in every Transparency pill/chip app-wide.
var TRANSPARENCY_LOGO_SRC = '/img/transparency-logo.png';
// Purple "T-CODE" chip for products enrolled in Amazon Transparency — every unit needs a
// serialized Transparency label (from the brand's code pool) in addition to the FNSKU.
function TransparencyChip(row) {
  if (!row || !row.requires_transparency) return null;
  return React.createElement('span', {
    title: 'Amazon Transparency — every unit needs a serialized Transparency label in addition to the FNSKU label',
    style: { fontSize: 9, fontWeight: 700, padding: '1px 5px', borderRadius: 6, background: '#8B5CF618', color: '#8B5CF6',
      marginLeft: 6, verticalAlign: 'middle', letterSpacing: '0.03em', whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3 } },
    React.createElement('img', { src: TRANSPARENCY_LOGO_SRC, alt: '', style: { width: 10, height: 10, borderRadius: 2, display: 'block' } }),
    'T-CODE');
}
window.TransparencyChip = TransparencyChip;
// Standard row/list pill with the Transparency logo — use anywhere an item/PO needs the
// Transparency marker (FBA pack lists, create-shipment PO list, ...).
function TransparencyPill(props) {
  return React.createElement('span', {
    title: 'Amazon Transparency — every unit needs a serialized Transparency label in addition to the FNSKU label',
    style: Object.assign({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600, padding: '3px 9px', borderRadius: 4, background: '#A78BFA' + '1A', color: '#A78BFA', letterSpacing: '0.03em', whiteSpace: 'nowrap' }, (props && props.style) || {}) },
    React.createElement('img', { src: TRANSPARENCY_LOGO_SRC, alt: '', style: { width: 13, height: 13, borderRadius: 2, display: 'block' } }),
    'TRANSPARENCY');
}
window.TransparencyPill = TransparencyPill;

// Carrier display names + carrier tracking-page URLs — shared by the shipping module
// and the Orders detail modal (moved from app-shipping.js so every bundle can render
// clickable tracking numbers).
var SHIP_CARRIER_DISPLAY = { dhl_global_mail: 'DHL', dhl_ecommerce_us: 'DHL', stamps_com: 'USPS', usps: 'USPS', globalpost: 'GlobalPost', gp_plus: 'GlobalPost', fedex: 'FedEx', ups: 'UPS', amazon_shipping: 'Amazon Shipping', other: 'Other' };
function shipCarrierLabel(c) {
  if (!c) return c;
  return SHIP_CARRIER_DISPLAY[String(c).toLowerCase()] || c;
}
function shipTrackingUrl(carrier, tracking) {
  var c = String(carrier || '').toLowerCase();
  var t = encodeURIComponent(tracking || '');
  if (c.indexOf('dhl') !== -1) return 'https://webtrack.dhlecs.com/?trackingNumber=' + t;
  if (c.indexOf('usps') !== -1 || c.indexOf('stamps') !== -1) return 'https://tools.usps.com/go/TrackConfirmAction?tLabels=' + t;
  if (c.indexOf('globalpost') !== -1 || c === 'gp_plus') return 'https://t.17track.net/en#nums=' + t;
  if (c.indexOf('ups') === 0) return 'https://www.ups.com/track?tracknum=' + t;
  if (c.indexOf('fedex') !== -1) return 'https://www.fedex.com/fedextrack/?trknbr=' + t;
  return 'https://t.17track.net/en#nums=' + t;
}
// Service codes → display names: globalmail_parcel_direct_ddp →
// "Globalmail Parcel Direct DDP". Strings that already look like display
// text (no underscores, mixed case) pass through untouched.
var SHIP_SERVICE_ACRONYMS = { ddp: 'DDP', ddu: 'DDU', usps: 'USPS', dhl: 'DHL' };
function shipServiceLabel(code) {
  if (!code) return code;
  var s = String(code);
  if (s.indexOf('_') === -1 && s !== s.toLowerCase()) return s;
  return s.split('_').map(function(w) {
    var lw = w.toLowerCase();
    if (SHIP_SERVICE_ACRONYMS[lw]) return SHIP_SERVICE_ACRONYMS[lw];
    return w.charAt(0).toUpperCase() + w.slice(1);
  }).join(' ');
}
window.shipServiceLabel = shipServiceLabel;
window.shipCarrierLabel = shipCarrierLabel; window.shipTrackingUrl = shipTrackingUrl;
window.generateCode128SVG = generateCode128SVG; window.generateCode128SVGmm = generateCode128SVGmm; window.generateBarcodeImg = generateBarcodeImg; window.printBinLabel = printBinLabel;
window.generatePDF417SVG = generatePDF417SVG; window.generateCasePDF417SVG = generateCasePDF417SVG; window.buildAmazonCaseBarcodeData = buildAmazonCaseBarcodeData;

// ─── ReportParamSelect — shared multi-select dropdown (moved here from
// app-reports.js so the shipping/ads bundles can use it too). Two modes:
// preloaded list (default; client-side filtered) and search mode (`search`
// prop, high-cardinality fields) querying fetchReportFieldValues (defined in
// app-reports.js — only bundles that pass search:true need it). Selected
// values stay pinned at the top so they can always be deselected.
// The menu's reset row names the plural of the filter's label — "All batches", "All countries" —
// where a bare "s" used to be appended ("All batchs" on the Shipments Batch filter, Anthony 2026-10-13).
function pluralLower(word) {
  var w = String(word || '').trim().toLowerCase();
  if (/(ss|us|is|x|z|ch|sh)$/.test(w)) return w + 'es';
  // Already plural: the rule and auto-batch editors pass 'Tags', 'Countries', 'Carriers' ("All tagses", Anthony 2026-10-24)
  if (/s$/.test(w)) return w;
  if (/[^aeiou]y$/.test(w)) return w.slice(0, -1) + 'ies';
  return w + 's';
}
function ReportParamSelect({ label, values, loading, selected, onChange, search: searchable, source, fieldKey, onCreate, createLabel, valueColors, onManage, manageLabel }) {
  var h = React.createElement;
  var [open, setOpen] = useState(false);
  var [search, setSearch] = useState('');
  var [results, setResults] = useState(null);
  var [searching, setSearching] = useState(false);
  var [menuPos, setMenuPos] = useState(null);
  var searchSeq = useRef(0);
  var ref = useRef(null);
  var menuRef = useRef(null);

  // The menu portals to <body> (position: fixed) so it floats above modals and
  // never clips inside their scroll containers. Capture-phase listener: modal
  // content stopPropagation()s bubbling mousedowns, which used to keep the
  // menu open on click-away.
  var measure = function() {
    if (!ref.current) return;
    var r = ref.current.getBoundingClientRect();
    var z = window.APP_ZOOM || 1; // gBCR is visual px; fixed positions in zoomed px
    setMenuPos({ top: (r.bottom + 4) / z, left: r.left / z, minWidth: Math.max(260, r.width / z) });
  };
  useEffect(function() {
    if (!open) return;
    var onDown = function(e) {
      if (ref.current && ref.current.contains(e.target)) return;
      if (menuRef.current && menuRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', onDown, true);
    window.addEventListener('scroll', measure, true);
    window.addEventListener('resize', measure);
    return function() {
      document.removeEventListener('mousedown', onDown, true);
      window.removeEventListener('scroll', measure, true);
      window.removeEventListener('resize', measure);
    };
  }, [open]);

  // Search mode: debounced live lookup once 2+ characters are typed
  useEffect(function() {
    if (!searchable || !open) return;
    var term = search.trim();
    if (term.length < 2) { setResults(null); setSearching(false); return; }
    var seq = ++searchSeq.current;
    setSearching(true);
    var t = setTimeout(function() {
      fetchReportFieldValues(source, fieldKey, term).then(function(vals) {
        if (seq !== searchSeq.current) return;
        setResults(vals); setSearching(false);
      }).catch(function() {
        if (seq !== searchSeq.current) return;
        setResults([]); setSearching(false);
      });
    }, 300);
    return function() { clearTimeout(t); };
  }, [search, open, searchable, source, fieldKey]);

  var sel = selected || [];
  var summary = sel.length === 0 ? 'All'
    : sel.length === 1 ? sel[0]
    : sel.length + ' selected';
  var active = sel.length > 0;

  var list;
  if (searchable) {
    var found = results || [];
    list = sel.concat(found.filter(function(v) { return sel.indexOf(v) === -1; }));
  } else {
    list = (values || []).filter(function(v) {
      return !search || String(v).toLowerCase().indexOf(search.toLowerCase()) !== -1;
    });
  }

  var toggle = function(v) {
    if (sel.indexOf(v) !== -1) onChange(sel.filter(function(x) { return x !== v; }));
    else onChange(sel.concat([v]));
  };

  return h('div', { ref: ref, style: { position: 'relative', display: 'inline-block' } },
    h('button', {
      className: 'fbtn',
      onClick: function() { if (!open) measure(); setOpen(!open); setSearch(''); },
      style: {
        background: active ? C.accentSoft : C.bg,
        border: '1px solid ' + (active ? C.accent : C.border),
        color: active ? C.accent : C.text,
        padding: '7px 12px', borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'inherit',
      }
    },
      h('span', { style: { color: active ? C.accent : C.textMuted, fontWeight: 600 } }, label + ':'),
      summary,
      h(LIcon, { name: 'chevron-down', size: 12 })
    ),
    open && menuPos && ReactDOM.createPortal(h('div', {
      ref: menuRef,
      className: 'rps-menu', // on <body>, outside every enclosing menu: their click-away handlers look for this (shipping "Other" menu, 9-22)
      style: {
        position: 'fixed', top: menuPos.top, left: menuPos.left, zIndex: 5000,
        background: C.surface, border: '1px solid ' + C.border, borderRadius: 8,
        boxShadow: '0 8px 24px rgba(0,0,0,0.3)', minWidth: menuPos.minWidth, maxHeight: 'min(320px, calc(calc(100vh * var(--invzoom, 1)) - ' + Math.round(menuPos.top + 20) + 'px))', overflowY: 'auto',
      }
    },
      h('input', {
        autoFocus: true,
        value: search,
        onChange: function(e) { setSearch(e.target.value); },
        placeholder: searchable ? 'Type 2+ characters to search…' : 'Search…',
        style: { width: '100%', background: C.bg, border: 'none', borderBottom: '1px solid ' + C.border, color: C.text, padding: '8px 12px', fontSize: 13, outline: 'none', boxSizing: 'border-box' }
      }),
      h('div', {
        onClick: function() { onChange([]); setOpen(false); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: sel.length === 0 ? C.accent : C.text, fontWeight: sel.length === 0 ? 600 : 400, borderBottom: '1px solid ' + C.border }
      }, 'All ' + pluralLower(label)),
      onCreate && h('div', {
        onClick: function() { setOpen(false); onCreate(); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.accent, fontWeight: 600, borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 }
      }, h(LIcon, { name: 'plus', size: 13 }), createLabel || 'Create new'),
      onManage && h('div', {
        onClick: function() { setOpen(false); onManage(); },
        onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
        onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
        style: { padding: '8px 12px', cursor: 'pointer', fontSize: 13, color: C.accent, fontWeight: 600, borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 6 }
      }, h(LIcon, { name: 'settings', size: 13 }), manageLabel || 'Manage'),
      (searchable ? searching : loading)
        ? h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } }, searchable ? 'Searching…' : 'Loading values…')
        : list.length === 0
          ? h('div', { style: { padding: 12, color: C.textDim, fontSize: 12 } },
              searchable
                ? (search.trim().length < 2 ? 'Type at least 2 characters to search your data' : 'No matches')
                : (values && values.length ? 'No matches' : 'No values in your data yet'))
          : list.map(function(v) {
              var isSel = sel.indexOf(v) !== -1;
              return h('div', {
                key: v,
                onClick: function() { toggle(v); },
                onMouseOver: function(e) { e.currentTarget.style.background = C.surfaceHover; },
                onMouseOut: function(e) { e.currentTarget.style.background = 'transparent'; },
                style: { padding: '7px 12px', cursor: 'pointer', fontSize: 13, color: C.text, display: 'flex', alignItems: 'center', gap: 8 }
              },
                h('div', {
                  style: {
                    width: 15, height: 15, borderRadius: 4, flexShrink: 0,
                    border: '1px solid ' + (isSel ? C.accent : C.border),
                    background: isSel ? C.accent : 'transparent',
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  }
                }, isSel && h(LIcon, { name: 'check', size: 11, color: '#fff' })),
                valueColors && valueColors[v] && h('span', { style: { width: 10, height: 10, borderRadius: 3, background: valueColors[v], flexShrink: 0 } }),
                h('span', { style: { overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, v)
              );
            })
    ), document.body)
  );
}
window.ReportParamSelect = ReportParamSelect;

// ─── Sidebar pin state: server-side preference ───
// The pin (collapsed) state is stored in users.preferences.sidebar_collapsed so
// it follows the user across devices AND across the three app origins (main /
// ads / ship have separate localStorage). localStorage stays as the fast path
// for first paint; the server value wins once it arrives after login.
function syncSidebarPref(apply) {
  fetch('/api/auth/preferences', { credentials: 'include' })
    .then(function(r) { return r.ok ? r.json() : null; })
    .then(function(p) {
      if (p && typeof p.sidebar_collapsed === 'boolean') {
        try { localStorage.setItem('sidebar_collapsed', String(p.sidebar_collapsed)); } catch (e) {}
        apply(p.sidebar_collapsed);
      }
    })
    .catch(function() {});
}
function saveSidebarPref(collapsed) {
  try { localStorage.setItem('sidebar_collapsed', String(collapsed)); } catch (e) {}
  fetch('/api/auth/preferences', {
    method: 'PUT', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ sidebar_collapsed: collapsed })
  }).catch(function() {});
}
window.syncSidebarPref = syncSidebarPref;
window.saveSidebarPref = saveSidebarPref;

// ─── HOURLY CAT PARADE (easter egg) ───
// Once per hour: a pixel cat gallops in along the bottom of the page, stops at
// center, faces the user and waves ("Hola" bubble) — then a rat streaks past
// ("!" above the cat), the cat bolts after it, a dog chases the cat, and a
// grandmother with a raised broom chases all three. Shared across tabs via
// localStorage 'cat_last_run'; only visible tabs fire. Every element is
// pointer-events: none — it can never intercept clicks. window.catRun() = demo.
// The hourly clock now plays the INVOICE STORM scene (further down; before it
// the FORKLIFT FUMBLE), or the TRICK-OR-TREAT PARADE during Oct 24-31; the cat
// parade and the forklift stay available as manual demos and the rig
// (catSpawn/catGo/catTwoFrames/catbub) is shared.
// Sprites are ASCII pixel grids: '.' transparent, 'f' fur (cat color variant),
// other chars index the actor's palette. One <rect> per horizontal run.
var CAT_PAL = { F: '#00000033', w: '#F1F5F9', p: '#F472B6', e: '#1F2937' };
var RAT_PAL = { f: '#78716C', p: '#F9A8D4', e: '#1F2937', w: '#D6D3D1' };
var DOG_PAL = { f: '#C9974C', d: '#8B5E2F', w: '#F1F5F9', e: '#1F2937', r: '#DC2626', p: '#F472B6' };
var CAT_RUN_A = [
".....................ff....ff...",
".....................fpf...fpf..",
"....................fffffffffff.",
".FF.................fffffffffff.",
".ff.................ffffffeefff.",
".ff.................ffffffeewwww",
"..ff................ffffffffwwwp",
"...ff.ffffffffffffffffffffwwww..",
".....ffffFFffFFffFFfffffff......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffffffffffffffwwww.......",
".....fffffffffffffffwww.........",
"......ffffffffffffffff..........",
"......ff...ff...ff..ff..........",
".....ff....ff...ff...ff.........",
"....ff.....ff...ff....ff........",
"....ff.....ff...ff....ff........",
"....ww.....ww...ww....ww........",
"................................"
];
var CAT_RUN_B = [
".....................ff....ff...",
".....................fpf...fpf..",
"....................fffffffffff.",
"....................fffffffffff.",
"....................ffffffeefff.",
".FF.................ffffffeewwww",
".ff.................ffffffffwwwp",
"..ff..ffffffffffffffffffffwwww..",
".....ffffFFffFFffFFfffffff......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffFFffFFffFFfwwwww.......",
".....ffffffffffffffffwwww.......",
".....fffffffffffffffwww.........",
"......ffffffffffffffff..........",
"........ff..ff....ff..ff........",
"........ff..ff....ff..ff........",
"........ww..ww....ww..ww........",
"................................",
"................................",
"................................"
];
var CAT_WAVE_A = [
"..ff..........ff........",
".fppf........fppf.......",
".ffffffffffffffff.......",
".ffffffffffffffff.ww....",
".fffeefffffeeffff.ww....",
".fffeefffffeeffff.ff....",
".ffffffppffffffff.ff....",
".ffffwwwwwwwfffff.ff....",
".ffffwwweewwfffff.ff....",
"..ffffffffffffffffff....",
"..ffffffffffffffff......",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
".ffffwwwwwwwffff........",
"....ff....ff............",
"....ww....ww............",
"....ww....ww............",
"........................"
];
var CAT_WAVE_B = [
"..ff..........ff........",
".fppf........fppf.......",
".ffffffffffffffff.......",
".ffffffffffffffff.......",
".fffeefffffeeffff.......",
".fffeefffffeeffff.......",
".ffffffppffffffff.ww....",
".ffffwwwwwwwfffff.ww....",
".ffffwwweewwfffff.ff....",
"..ffffffffffffffffff....",
"..ffffffffffffffff......",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
"f.fffwwwwwwwffff........",
".ffffwwwwwwwffff........",
"....ff....ff............",
"....ww....ww............",
"....ww....ww............",
"........................"
];
var RAT_A = [
"..................ff......",
"ppp...............fpf.....",
"...pp....ffffffff.fff.....",
".....ppffffffffffffffffff.",
"......fffffffffffffffefff.",
"......fffffffffffffffffffp",
"......ffwwwwwwwffffffffff.",
"......ffwwwwwwwffffffff...",
".......fffffffffffff......",
".........ff......ff.......",
"........pp......pp........",
".........................."
];
var RAT_B = [
"..................ff......",
"..................fpf.....",
"ppppp....ffffffff.fff.....",
".....ppffffffffffffffffff.",
"......fffffffffffffffefff.",
"......fffffffffffffffffffp",
"......ffwwwwwwwffffffffff.",
"......ffwwwwwwwffffffff...",
".......fffffffffffff......",
"...........ff..ff.........",
"...........pp..pp.........",
".........................."
];
var DOG_A = [
"...........................ffffffff.....",
"..........................ddffffffff....",
"...dd.....................ddfffffefff...",
"...ff.....................ddfffffefff...",
"...ff.....................ddffffffffffee",
"....ff....................ddffffwwwwwww.",
"....ff....................dffffffwwww...",
"......ffffffffffffffffffffffffffff.pp...",
"......fffffffffffffffffffffrrrrrrr.pp...",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwww..........",
".......ffffffffffffffffffwwww...........",
".......fffffffffffffffffffff............",
"........ffffffffffffffffff..............",
"........ff...ff......ff...ff............",
".......ff....ff......ff....ff...........",
"......ff.....ff......ff.....ff..........",
"......ww.....ww......ww.....ww..........",
"........................................",
"........................................"
];
var DOG_B = [
"...........................ffffffff.....",
"..........................ddffffffff....",
"..........................ddfffffefff...",
"...dd.....................ddfffffefff...",
"...ff.....................ddffffffffffee",
"...ff.....................ddffffwwwwwww.",
"....ff....................dffffffwwww...",
"......ffffffffffffffffffffffffffff.pp...",
"......fffffffffffffffffffffrrrrrrr.pp...",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwwww.........",
"......ffffffffffffffffffwwwwww..........",
".......ffffffffffffffffffwwww...........",
".......fffffffffffffffffffff............",
"........ffffffffffffffffff..............",
".........ff...ff....ff...ff.............",
".........ff...ff....ff...ff.............",
".........ww...ww....ww...ww.............",
"........................................",
"........................................",
"........................................"
];
var LADY_PAL = { s: '#D9A574', g: '#E5E7EB', d: '#0D9488', D: '#0A5A54', w: '#F1F5F9', b: '#92400E', y: '#EAB308', e: '#1F2937', p: '#F9A8D4' };
var LADY_BASE = [
"..................................yyyyyyyy..",
"......................gggggggggg..yyyyyyyy..",
"..................ggggggggggggggg.yyyyyyyy..",
"..................ggggggggggggggg.yyyyyyyy..",
"..................ggggssssssssssg.yyyyyyyy..",
"......................ssssssssssg....bb.....",
"......................sssssseess.....bb.....",
"......................sssssseess.....bb.....",
"......................ssssssssss.....bb.....",
"......................ssppssssss.....bb.....",
"......................sssssseees.....bb.....",
"......................ssssssssss....sss.....",
"..........................sss.......sss.....",
"....................ddddddddddddddddd.......",
"..................ddddddddddddddddddd.......",
"..................dddddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
".................ssddddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
"....................dddwwwwwwwwddd..........",
"...................ddddwwwwwwwwdddd.........",
"...................ddddwwwwwwwwdddd.........",
"...................ddddwwDDDDwwdddd.........",
"...................ddddwwDDDDwwdddd.........",
"..................dddddwwwwwwwwddddd........",
"..................dddddwwwwwwwwddddd........",
"..................dddDdddddddddDdddd........",
"..................dddddddddddddddddd........",
"........................sss..sss............",
"........................sss..sss............"
];
var LADY_A = LADY_BASE.concat([
".....................eeee.....eeee..........",
".....................eeee.....eeee.........."
]);
// Frame B: broom bobs down 1px, free hand swings up, legs pass mid-stride.
var LADY_B = LADY_A.slice();
LADY_B[0] = "............................................";
LADY_B[5] = "......................ssssssssssg.yyyyyyyy..";
LADY_B[15] = "................ssdddddwwwwwwwwddd..........";
LADY_B[17] = "....................dddwwwwwwwwddd..........";
LADY_B[28] = ".........................sss.sss............";
LADY_B[29] = ".........................sss.sss............";
LADY_B[30] = ".......................eeee..eeee...........";
LADY_B[31] = ".......................eeee..eeee...........";
function catAsciiRects(rows, pal, fur, dx) {
  var out = '';
  rows.forEach(function(row, y) {
    var x = 0;
    while (x < row.length) {
      var c = row[x];
      if (c === '.') { x++; continue; }
      var x0 = x;
      while (x < row.length && row[x] === c) x++;
      var fill;
      if (c === 'f') fill = fur;
      else if (c === '1' && !pal['1']) fill = catShadeHex(fur, 0.68);
      else if (c === '3' && !pal['3']) fill = catShadeHex(fur, 1.30);
      else fill = pal[c] || fur;
      if (c === 'F') out += '<rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + fur + '"/><rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + pal.F + '"/>';
      else out += '<rect x="' + (x0 + (dx || 0)) + '" y="' + y + '" width="' + (x - x0) + '" height="1" fill="' + fill + '"/>';
    }
  });
  return out;
}
// ── Detail pipeline: 2x upscale + dark outline + auto-shading + accent
// patches (eye glints, whiskers, collar tag, broom texture). Runs once,
// lazily, on the base sprites above; only the processed frames render.
function catShadeHex(hex, k) {
  var n = parseInt(hex.slice(1), 16);
  var r = Math.min(255, Math.round(((n >> 16) & 255) * k));
  var g = Math.min(255, Math.round(((n >> 8) & 255) * k));
  var b = Math.min(255, Math.round((n & 255) * k));
  return '#' + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
}
function catDetail(rows, opts) {
  opts = opts || {};
  var W = rows[0].length;
  var g = [];
  var blank = function() { var a = []; for (var i = 0; i < W * 2 + 2; i++) a.push('.'); return a; };
  g.push(blank());
  rows.forEach(function(r) {
    var d = ['.'];
    for (var i = 0; i < r.length; i++) { d.push(r[i], r[i]); }
    d.push('.');
    g.push(d, d.slice());
  });
  g.push(blank());
  (opts.patches || []).forEach(function(p) {
    var bx = 2 * p[0] + 1, by = 2 * p[1] + 1, q = p[2], ch = p[3];
    var cells = q === 'full' ? [[bx, by], [bx + 1, by], [bx, by + 1], [bx + 1, by + 1]]
      : q === 'tl' ? [[bx, by]] : q === 'tr' ? [[bx + 1, by]] : q === 'bl' ? [[bx, by + 1]] : [[bx + 1, by + 1]];
    cells.forEach(function(c) { if (g[c[1]] && g[c[1]][c[0]] !== undefined) g[c[1]][c[0]] = ch; });
  });
  var y, x;
  for (y = 0; y < g.length; y++) for (x = 0; x < g[0].length; x++) {
    if (g[y][x] !== '.') continue;
    var nb = [g[y - 1] && g[y - 1][x], g[y + 1] && g[y + 1][x], g[y][x - 1], g[y][x + 1]];
    if (nb.some(function(c) { return c && c !== '.' && c !== 'O'; })) g[y][x] = 'O';
  }
  var out = g.map(function(r) { return r.slice(); });
  for (y = 1; y < g.length - 1; y++) for (x = 0; x < g[0].length; x++) {
    var c = g[y][x];
    if (opts.dark && opts.dark[c] && g[y + 1][x] === 'O') out[y][x] = opts.dark[c];
    else if (opts.light && opts.light[c] && g[y - 1][x] === 'O') out[y][x] = opts.light[c];
  }
  return out.map(function(r) { return r.join(''); });
}
CAT_PAL.O = '#141A23'; CAT_PAL['2'] = '#C7CDD6';
RAT_PAL.O = '#141A23'; RAT_PAL['1'] = '#57524E'; RAT_PAL['3'] = '#948E88';
DOG_PAL.O = '#141A23'; DOG_PAL['1'] = '#A0763B'; DOG_PAL['3'] = '#DDB06A'; DOG_PAL['2'] = '#C9CFD8'; DOG_PAL.y = '#EAB308';
LADY_PAL.O = '#141A23'; LADY_PAL['1'] = '#0A5A54'; LADY_PAL['3'] = '#16B3A5'; LADY_PAL['4'] = '#B9884F'; LADY_PAL['5'] = '#C3C9D1'; LADY_PAL['6'] = '#F5F7FA'; LADY_PAL['2'] = '#C9CFD8'; LADY_PAL['7'] = '#B45309';
var _catSprites = null;
function catSprites() {
  if (_catSprites) return _catSprites;
  var runOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[27,4,'tl','w'],[30,5,'br','e'],[30,6,'tr','e']] };
  var waveOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[5,4,'tl','w'],[12,4,'tl','w'],[5,7,'bl','e'],[11,7,'br','e'],[4,18,'br','O'],[10,18,'br','O']] };
  var ratOpts = { dark: { f: '1' }, light: { f: '3' }, patches: [[21,4,'tl','w']] };
  var dogOpts = { dark: { f: '1', w: '2' }, light: { f: '3' }, patches: [[33,2,'tl','w'],[38,4,'tl','w'],[30,9,'full','y']] };
  var ladyPatches = [[24,1,'full','5'],[27,2,'full','5'],[21,3,'full','5']];
  [35,37,39,41].forEach(function(cx) { for (var cy = 0; cy < 5; cy++) ladyPatches.push([cx,cy,'full','7']); });
  var ladyOpts = { dark: { d: '1', s: '4', g: '5', w: '2', y: '7' }, light: { d: '3', g: '6' }, patches: ladyPatches };
  _catSprites = {
    runA: catDetail(CAT_RUN_A, runOpts), runB: catDetail(CAT_RUN_B, runOpts),
    waveA: catDetail(CAT_WAVE_A, waveOpts), waveB: catDetail(CAT_WAVE_B, waveOpts),
    ratA: catDetail(RAT_A, ratOpts), ratB: catDetail(RAT_B, ratOpts),
    dogA: catDetail(DOG_A, dogOpts), dogB: catDetail(DOG_B, dogOpts),
    ladyA: catDetail(LADY_A, ladyOpts), ladyB: catDetail(LADY_B, ladyOpts)
  };
  return _catSprites;
}
function catSvgWrap(inner, vwb, vhb, w, h, cls) {
  return '<svg class="' + cls + '" viewBox="0 0 ' + vwb + ' ' + vhb + '" width="' + w + '" height="' + h + '" shape-rendering="crispEdges">' + inner + '</svg>';
}
function catTwoFrames(fn0, fn1, extra) {
  return fn0('catfr' + (extra || '')) + fn1('catfr f2' + (extra || ''));
}
function catHtml(kind, fur, fast) {
  var S = catSprites();
  var mk = function(rows, dx) { return function(cls) { return catSvgWrap(catAsciiRects(rows, CAT_PAL, fur, dx), 66, 42, 99, 63, cls); }; };
  if (kind === 'w') return catTwoFrames(mk(S.waveA, 8), mk(S.waveB, 8), ' wv');
  return catTwoFrames(mk(S.runA), mk(S.runB), fast ? ' fast' : '');
}
function catRatHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, RAT_PAL, RAT_PAL.f), 54, 26, 81, 39, cls); }; };
  return catTwoFrames(mk(S.ratA), mk(S.ratB), ' fast');
}
function catDogHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, DOG_PAL, DOG_PAL.f), 82, 46, 123, 69, cls); }; };
  return catTwoFrames(mk(S.dogA), mk(S.dogB), ' fast');
}
function catLadyHtml() {
  var S = catSprites();
  var mk = function(rows) { return function(cls) { return catSvgWrap(catAsciiRects(rows, LADY_PAL, LADY_PAL.d), 90, 66, 180, 132, cls); }; };
  return catTwoFrames(mk(S.ladyA), mk(S.ladyB), ' wv');
}
function catSpawn(html, w, h, x0, flip) {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:4px;left:0;width:' + w + 'px;height:' + h + 'px;z-index:99999;pointer-events:none;will-change:transform;';
  el.style.transform = 'translateX(' + x0 + 'px)' + flip;
  el.innerHTML = html;
  document.body.appendChild(el);
  el.getBoundingClientRect();
  return el;
}
function catGo(el, x, ms, flip) {
  el.style.transition = 'transform ' + ms + 'ms linear';
  el.style.transform = 'translateX(' + x + 'px)' + flip;
}
function catEnsureStyle() {
  if (document.getElementById('cat-style')) return;
  var st = document.createElement('style');
  st.id = 'cat-style';
  st.textContent =
    '@keyframes catblink { 0%, 49.9% { opacity: 1; } 50%, 100% { opacity: 0; } }' +
    '.catfr { position: absolute; inset: 0; animation: catblink 0.32s steps(1) infinite; }' +
    '.catfr.f2 { animation-delay: -0.16s; }' +
    '.catfr.wv { animation-duration: 0.5s; }' +
    '.catfr.f2.wv { animation-delay: -0.25s; }' +
    '.catfr.fast { animation-duration: 0.2s; }' +
    '.catfr.f2.fast { animation-delay: -0.1s; }' +
    '.catbub { position: fixed; z-index: 99999; pointer-events: none; background: #fff; color: #111; border: 2px solid #111; border-radius: 7px; font: 600 14px "DM Sans", system-ui, sans-serif; padding: 4px 10px; }' +
    '.catbub:after { content: ""; position: absolute; left: 14px; bottom: -7px; width: 9px; height: 9px; background: #fff; border-right: 2px solid #111; border-bottom: 2px solid #111; transform: rotate(45deg); }';
  document.head.appendChild(st);
}
function catRun() {
  try {
    catEnsureStyle();
    var fur = ['#64748B', '#D97706', '#9CA3AF', '#8B5A2B'][Math.floor(Math.random() * 4)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5;
    var flip = lr ? '' : ' scaleX(-1)';
    var enter = function(w) { return lr ? -w - 20 : vw + 20; };
    var exit = function(w) { return lr ? vw + 20 : -w - 20; };
    var mid = Math.round(vw / 2 - 50);
    var d1 = 2600, hold = 2600;
    var cat = catSpawn(catHtml('r', fur), 99, 63, enter(99), flip);
    catGo(cat, mid, d1, flip);
    var actors = [cat], bub = null, exc = null;
    setTimeout(function() {
      try {
        cat.innerHTML = catHtml('w', fur);
        bub = document.createElement('div');
        bub.className = 'catbub';
        bub.textContent = 'Hola';
        bub.style.left = (mid + (lr ? 55 : -8)) + 'px';
        bub.style.bottom = '75px';
        document.body.appendChild(bub);
      } catch (e) {}
    }, d1 + 60);
    setTimeout(function() {
      try {
        var rat = catSpawn(catRatHtml(), 81, 39, enter(81), flip);
        actors.push(rat);
        catGo(rat, exit(81), 2600, flip);
      } catch (e) {}
    }, d1 + hold - 600);
    setTimeout(function() {
      try {
        if (bub) { bub.remove(); bub = null; }
        exc = document.createElement('div');
        exc.textContent = '!';
        exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (mid + (lr ? 55 : 30)) + 'px;bottom:75px;font:800 30px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
        document.body.appendChild(exc);
      } catch (e) {}
    }, d1 + hold + 900);
    setTimeout(function() {
      try {
        if (exc) { exc.remove(); exc = null; }
        cat.innerHTML = catHtml('r', fur, true);
        catGo(cat, exit(99), 1900, flip);
      } catch (e) {}
    }, d1 + hold + 1800);
    setTimeout(function() {
      try {
        var dog = catSpawn(catDogHtml(), 123, 69, enter(123), flip);
        actors.push(dog);
        catGo(dog, exit(123), 2600, flip);
      } catch (e) {}
    }, d1 + hold + 2300);
    setTimeout(function() {
      try {
        var lady = catSpawn(catLadyHtml(), 180, 132, enter(180), flip);
        actors.push(lady);
        catGo(lady, exit(180), 4200, flip);
      } catch (e) {}
    }, d1 + hold + 2950);
    setTimeout(function() {
      try {
        actors.forEach(function(a) { try { a.remove(); } catch (e) {} });
        if (bub) bub.remove();
        if (exc) exc.remove();
      } catch (e) {}
    }, d1 + hold + 2950 + 4200 + 700);
  } catch (e) {}
}
function startCatClock() {
  var check = function() {
    try {
      if (document.hidden) return;
      var last = parseInt(localStorage.getItem('cat_last_run') || '0', 10);
      var now = Date.now();
      if (now - last < 3600 * 1000) return;
      // Claim the hour BEFORE the async auth probe (multi-tab dedupe), then
      // release it if this tab turns out to be logged out (login screen) —
      // the parade is for authenticated users only.
      localStorage.setItem('cat_last_run', String(now));
      fetch('/api/auth/me', { credentials: 'include' })
        .then(function(r) {
          if (r.ok) hourlyScene()();
          else localStorage.setItem('cat_last_run', String(last));
        })
        .catch(function() { try { localStorage.setItem('cat_last_run', String(last)); } catch (e) {} });
    } catch (e) {}
  };
  setInterval(check, 60000 + Math.floor(Math.random() * 5000));
  setTimeout(check, 15000 + Math.floor(Math.random() * 30000));
}
window.catRun = catRun;
try { if (typeof document !== 'undefined' && typeof localStorage !== 'undefined') startCatClock(); } catch (e) {}

// ─── FORKLIFT FUMBLE (the hourly cartoon until 2026-10-05; now a manual demo) ───
// A yellow forklift hauls a wobbling pallet of boxes in, stops at center, the
// top box slides off ("!"), the driver panics and reverses back out the way
// it came ("beep! beep!") — a warehouse worker sprints in chasing it and
// scoops the dropped box mid-run without breaking stride, then a portly
// supervisor waving paperwork storms after them both. Reuses the cat rig
// (catSpawn/catGo/catTwoFrames, catbub styles). Redrawn 2026-09-04 (Anthony:
// "same art style as the agents office") as smooth vector art — rounded flat
// shapes, The Office's palette, soft ground shadows — instead of the pixel
// sprite grids. Geometry stays in the old sprite's unit space (lift 126×70
// at 1.5 px/unit, worker 58×48, supervisor 62×50, box 24×14) so
// forkliftRun's drop/scoop math is unchanged: the front box stack sits at
// x 94-116 and box i occupies y [40-12i, 52-12i). Hard-hat color is random
// per run. window.forkliftRun() = demo.
var FORK_PAL = { h: '#F1F5F9' }; // driver's hard hat, randomised per run
var FORK_SCALE = 1.25; // Anthony 2026-09-04: "increase their size a little bit" — every px size and offset reads this
function fkPx(n) { return Math.round(n * FORK_SCALE); }
var FK = {
  body: '#EAB308', bodyDk: '#CA8A04', cage: '#2A333F', seat: '#39445A', mast: '#56617A', steel: '#8A94A2',
  skin: '#E8B98A', hair: '#4A3B2F', tire: '#0B0F16', hub: '#8A94A2', beacon: '#FBBF24',
  wood: '#B08968', woodDk: '#8F6B4E', tape: '#F1F5F9', vest: '#F97316', stripe: '#F1F5F9',
  pantsA: '#2A4879', pantsB: '#31548F', shirt: '#4C5A70', hat: '#FBBF24',
  suit: '#2E3950', shirtW: '#E5E7EB', tie: '#B91C1C', paper: '#F8FAFC', ink: '#94A3B8',
  shadow: 'rgba(0,0,0,0.28)', eye: '#1F2937',
};
function forkSvgWrap(inner, vwb, vhb, w, h, cls) {
  return '<svg class="' + cls + '" viewBox="0 0 ' + vwb + ' ' + vhb + '" width="' + w + '" height="' + h + '">' + inner + '</svg>';
}
function fkRect(x, y, w, h, r, fill, extra) {
  return '<rect x="' + x + '" y="' + y + '" width="' + w + '" height="' + h + '" rx="' + r + '" fill="' + fill + '"' + (extra || '') + '/>';
}
function fkCircle(cx, cy, r, fill, extra) { return '<circle cx="' + cx + '" cy="' + cy + '" r="' + r + '" fill="' + fill + '"' + (extra || '') + '/>'; }
function fkEllipse(cx, cy, rx, ry, fill) { return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="' + rx + '" ry="' + ry + '" fill="' + fill + '"/>'; }
function fkG(inner, transform) { return '<g' + (transform ? ' transform="' + transform + '"' : '') + '>' + inner + '</g>'; }
// Cardboard box: wood-toned carton, darker top edge, a tape stripe, a label.
function fkBox(x, y, w, h) {
  return fkRect(x, y, w, h, 2, FK.wood) + fkRect(x, y, w, 2.4, 1.2, FK.woodDk) +
    fkRect(x + w / 2 - 1.3, y, 2.6, h, 0.9, FK.tape) + fkRect(x + 2.5, y + h - 4.5, 6, 2.6, 0.8, FK.tape, ' opacity="0.7"');
}
// Head with short hair and a hard hat (color per actor), facing right.
function fkHead(cx, cy, hat) {
  return fkCircle(cx, cy, 6.5, FK.skin) +
    '<path d="M ' + (cx - 6.5) + ',' + (cy - 1) + ' Q ' + (cx - 6) + ',' + (cy - 8) + ' ' + cx + ',' + (cy - 7.5) +
    ' Q ' + (cx + 6) + ',' + (cy - 8) + ' ' + (cx + 6.5) + ',' + (cy - 1) + ' L ' + (cx + 4.5) + ',' + (cy - 1) +
    ' Q ' + (cx + 3) + ',' + (cy - 5) + ' ' + cx + ',' + (cy - 5) + ' Q ' + (cx - 3) + ',' + (cy - 5) + ' ' + (cx - 4.5) + ',' + (cy - 1) + ' Z" fill="' + FK.hair + '"/>' +
    fkCircle(cx + 3, cy - 0.5, 1, FK.eye) +
    fkRect(cx - 7.5, cy - 12, 15, 6.5, 3.2, hat) + fkRect(cx - 9, cy - 7, 18.5, 2.2, 1.1, hat);
}
// The lift itself. mode 'run' (top front box leans on frame B) | 'wob'
// (stacks teeter). hts = [rearHeight, frontHeight] in boxes (0-4 each).
var FORK_STACK_X = [70, 94];
function forkliftFrame(mode, hts, which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(62, 68.5, 50, 3, FK.shadow));
  // rear roll-cage post (behind the driver), roof, beacon
  k.push(fkRect(13, 3, 3, 40, 1.5, FK.cage));
  k.push(fkRect(11, 1.5, 44, 4, 2, FK.cage));
  k.push(fkRect(29.5, 0, 6, 2.6, 1.3, FK.beacon));
  // seat
  k.push(fkRect(19, 28, 14, 13, 3.5, FK.seat));
  // driver (bobs one unit on frame B)
  var dy = B ? -1 : 0;
  var driver =
    fkRect(33, 37, 13, 5, 2.5, FK.pantsA) + fkRect(42, 37, 5, 9, 2.2, FK.pantsB) +
    fkRect(22, 19, 14, 20, 6, FK.shirt) + fkRect(24, 20, 10, 15, 4.5, FK.vest) + fkRect(24, 27, 10, 2, 1, FK.stripe) +
    fkRect(33, 24, 13, 4.2, 2.1, FK.shirt, ' transform="rotate(12 33 26)"') + fkCircle(46, 27.5, 2.4, FK.skin) +
    fkHead(29, 13, FORK_PAL.h);
  k.push(fkG(driver, dy ? 'translate(0,' + dy + ')' : ''));
  // steering column + wheel
  k.push(fkRect(46, 27, 3, 12, 1.5, FK.cage));
  k.push('<circle cx="47.5" cy="27" r="4" fill="none" stroke="' + FK.cage + '" stroke-width="2"/>');
  // body: main deck, counterweight block, lower skirt, front bumper
  k.push(fkRect(8, 40, 58, 20, 6, FK.body));
  k.push(fkRect(8, 40, 12, 20, 6, FK.bodyDk));
  k.push(fkRect(8, 52, 58, 8, 4, FK.bodyDk, ' opacity="0.55"'));
  k.push(fkRect(60, 44, 8, 12, 3, FK.cage));
  // front roll-cage post (in front of the driver)
  k.push(fkRect(49, 3, 3, 38, 1.5, FK.cage));
  // wheels: tires, hubs, spokes (spokes turn 45° between frames)
  [[24, 61], [56, 61]].forEach(function(w) {
    k.push(fkCircle(w[0], w[1], 9, FK.tire));
    k.push(fkCircle(w[0], w[1], 3.6, FK.hub));
    var rot = B ? 45 : 0;
    k.push('<g transform="rotate(' + rot + ' ' + w[0] + ' ' + w[1] + ')">' +
      fkRect(w[0] - 6.5, w[1] - 0.9, 13, 1.8, 0.9, FK.hub) + fkRect(w[0] - 0.9, w[1] - 6.5, 1.8, 13, 0.9, FK.hub) + '</g>');
  });
  // mast rails + crossbars, carriage, fork tines
  k.push(fkRect(62, 4, 3, 58, 1.5, FK.mast) + fkRect(68, 4, 3, 58, 1.5, FK.mast));
  [12, 30, 46].forEach(function(y) { k.push(fkRect(62, y, 9, 2, 1, FK.steel)); });
  k.push(fkRect(60, 50, 12, 9, 2.5, FK.cage));
  k.push(fkRect(64, 57, 58, 3, 1.5, FK.steel));
  // pallet: deck on the forks, three blocks beneath
  k.push(fkRect(68, 52, 52, 4.5, 1.2, FK.wood));
  [70, 92, 114].forEach(function(x) { k.push(fkRect(x, 56.5, 6, 3.5, 1, FK.woodDk)); });
  // box stacks (bottom box on the deck; box i at y 40-12i)
  hts.forEach(function(hgt, s) {
    for (var i = 0; i < hgt; i++) {
      var off = 0;
      if (mode === 'wob') off = !B ? Math.min(i, 2) * 2 : (i === 0 ? 0 : -2);
      else if (B && i === hgt - 1 && s === 1) off = 2;
      k.push(fkBox(FORK_STACK_X[s] + off, 40 - 12 * i, 22, 12));
    }
  });
  return k.join('');
}
function forkliftHtml(mode, hts, fast) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkliftFrame(mode, hts, which), 126, 70, fkPx(189), fkPx(105), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), fast ? ' fast' : '');
}
// Warehouse worker (58×48), hi-vis vest and hard hat, sprinting right;
// carry frames hold the rescued box out front.
function forkWorkerFrame(carry, which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(30, 47, 15, 2.5, FK.shadow));
  // legs: a running stride that swaps each frame
  k.push(fkRect(24, 33, 6, 14, 3, FK.pantsA, ' transform="rotate(' + (B ? 22 : -22) + ' 27 34)"'));
  k.push(fkRect(30, 33, 6, 14, 3, FK.pantsB, ' transform="rotate(' + (B ? -22 : 22) + ' 33 34)"'));
  // torso + vest + stripe
  k.push(fkRect(21, 14, 17, 21, 7, FK.shirt));
  k.push(fkRect(23, 15, 13, 16, 5, FK.vest) + fkRect(23, 22, 13, 2.2, 1.1, FK.stripe));
  if (carry) {
    k.push(fkRect(33, 19, 11, 4.4, 2.2, FK.shirt) + fkRect(33, 25, 11, 4.4, 2.2, FK.shirt));
    k.push(fkBox(36, 15, 21, 12));
    k.push(fkCircle(44, 21.2, 2.3, FK.skin) + fkCircle(44, 27.2, 2.3, FK.skin));
  } else {
    k.push(fkRect(34, 17, 12, 4.4, 2.2, FK.shirt, ' transform="rotate(' + (B ? 30 : -28) + ' 35 19)"'));
    k.push(fkRect(13, 17, 12, 4.4, 2.2, FK.shirt, ' transform="rotate(' + (B ? -30 : 28) + ' 24 19)"'));
  }
  k.push(fkHead(29.5, 9.5, FK.hat));
  return k.join('');
}
function forkWorkerHtml(carry) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkWorkerFrame(carry, which), 58, 48, fkPx(87), fkPx(72), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// Portly supervisor (62×50): dark suit, white shirt, red tie, waving a sheet
// of paper overhead — a slower two-frame stomp.
function forkSupFrame(which) {
  var B = which === 'B';
  var k = [];
  k.push(fkEllipse(31, 49, 17, 2.6, FK.shadow));
  k.push(fkRect(24, 35, 7, 13, 3.5, FK.suit, ' transform="rotate(' + (B ? 14 : -14) + ' 27 36)"'));
  k.push(fkRect(32, 35, 7, 13, 3.5, FK.suit, ' transform="rotate(' + (B ? -14 : 14) + ' 35 36)"'));
  k.push(fkRect(17, 14, 28, 25, 12, FK.suit)); // the belly
  k.push(fkRect(27, 15, 9, 17, 3.5, FK.shirtW));
  k.push(fkRect(30, 16, 3, 12, 1.2, FK.tie));
  // waving arm + paper (higher on frame A)
  var ang = B ? 40 : 15;
  k.push(fkRect(41, 6, 4.4, 15, 2.2, FK.suit, ' transform="rotate(' + ang + ' 43 20)"'));
  var px = B ? 50 : 44, py = B ? 8 : 1.5;
  k.push(fkRect(px, py, 9, 11, 1.2, FK.paper) + fkRect(px + 1.5, py + 2.5, 6, 1.1, 0.5, FK.ink) +
    fkRect(px + 1.5, py + 5, 6, 1.1, 0.5, FK.ink) + fkRect(px + 1.5, py + 7.5, 4, 1.1, 0.5, FK.ink));
  // other arm pumping
  k.push(fkRect(12, 20, 11, 4.4, 2.2, FK.suit, ' transform="rotate(' + (B ? -20 : 20) + ' 20 22)"'));
  // head: bald on top, hair at the sides, glasses
  k.push(fkCircle(31, 9, 6.8, FK.skin));
  k.push(fkRect(24.5, 7, 3.2, 5, 1.6, FK.hair) + fkRect(34.3, 7, 3.2, 5, 1.6, FK.hair));
  k.push('<circle cx="34" cy="9" r="2.2" fill="none" stroke="' + FK.eye + '" stroke-width="0.9"/>' + fkCircle(34.6, 9, 0.9, FK.eye));
  return k.join('');
}
function forkSupHtml() {
  var mk = function(which) { return function(cls) { return forkSvgWrap(forkSupFrame(which), 62, 50, fkPx(93), fkPx(75), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
function forkBoxHtml() {
  return forkSvgWrap(fkBox(1, 1, 22, 12), 24, 14, fkPx(36), fkPx(21), '');
}
function forkliftRun() {
  try {
    catEnsureStyle();
    // Rear stack is always 4 boxes; front stack 3 or 4. The FRONT stack's top
    // box is the one that falls.
    var hf = Math.random() < 0.5 ? 4 : 3;
    FORK_PAL.h = ['#F1F5F9', '#FBBF24', '#3B82F6'][Math.floor(Math.random() * 3)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5;
    var flip = lr ? '' : ' scaleX(-1)';
    var chaseFlip = lr ? ' scaleX(-1)' : '';
    // Actor boxes in page px (unit sizes × FORK_SCALE)
    var LW = fkPx(189), LH = fkPx(105), WW = fkPx(87), WH = fkPx(72), SW = fkPx(93), SH = fkPx(75), BW = fkPx(36), BH = fkPx(21);
    // The forklift enters from one side and later REVERSES back out the same
    // side; the chasers enter from the opposite side and exit where it fled.
    var enter = function(w) { return lr ? -w - 20 : vw + 20; };
    var chaseEnter = function(w) { return lr ? vw + 20 : -w - 20; };
    var mid = Math.round(vw / 2 - fkPx(95));
    // Pacing (Anthony 2026-09-04, "slow them down"): travels ~40% longer than
    // the pixel version, and the beep sounds BEFORE the lift starts reversing.
    var d1 = 3600, dRev = 2700, dWorker = 3600, dSup = 5800;
    var tWob = d1 + 60, tDrop = d1 + 900, tBeep = d1 + 1700, tRev = d1 + 2300, tBeepOff = d1 + 3300, tWorker = d1 + 2600, tSup = d1 + 3400;
    var lift = catSpawn(forkliftHtml('run', [4, hf]), LW, LH, enter(LW), flip);
    catGo(lift, mid, d1, flip);
    var actors = [lift], bub = null, exc = null, boxEl = null;
    setTimeout(function() {
      try { lift.innerHTML = forkliftHtml('wob', [4, hf]); } catch (e) {}
    }, tWob);
    setTimeout(function() {
      try {
        exc = document.createElement('div');
        exc.textContent = '!';
        exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (mid + fkPx(lr ? 52 : 121)) + 'px;bottom:' + fkPx(112) + 'px;font:800 ' + fkPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
        document.body.appendChild(exc);
        var spawnBottom = fkPx(hf === 4 ? 81 : 63);
        var fall = spawnBottom - fkPx(6);
        boxEl = document.createElement('div');
        boxEl.style.cssText = 'position:fixed;bottom:' + spawnBottom + 'px;left:' + (mid + fkPx(lr ? 142 : 11)) + 'px;width:' + BW + 'px;height:' + BH + 'px;z-index:99999;pointer-events:none;will-change:transform;';
        boxEl.innerHTML = forkBoxHtml();
        document.body.appendChild(boxEl);
        boxEl.getBoundingClientRect();
        boxEl.style.transition = 'transform 340ms cubic-bezier(0.5,0,1,1)';
        boxEl.style.transform = 'translateY(' + fall + 'px)';
        setTimeout(function() { try { if (boxEl) { boxEl.style.transition = 'transform 110ms ease-out'; boxEl.style.transform = 'translateY(' + (fall - fkPx(8)) + 'px)'; } } catch (e) {} }, 360);
        setTimeout(function() { try { if (boxEl) { boxEl.style.transition = 'transform 110ms ease-in'; boxEl.style.transform = 'translateY(' + fall + 'px)'; } } catch (e) {} }, 480);
        lift.innerHTML = forkliftHtml('wob', [4, hf - 1]);
      } catch (e) {}
    }, tDrop);
    // "beep! beep!" — the driver notices, THEN backs out
    setTimeout(function() {
      try {
        if (exc) { exc.remove(); exc = null; }
        bub = document.createElement('div');
        bub.className = 'catbub';
        bub.textContent = 'beep! beep!';
        bub.style.left = (mid + fkPx(lr ? 44 : 113)) + 'px';
        bub.style.bottom = fkPx(116) + 'px';
        document.body.appendChild(bub);
      } catch (e) {}
    }, tBeep);
    setTimeout(function() {
      try {
        lift.innerHTML = forkliftHtml('run', [4, hf - 1], true);
        catGo(lift, enter(LW), dRev, flip);
      } catch (e) {}
    }, tRev);
    setTimeout(function() { try { if (bub) { bub.remove(); bub = null; } } catch (e) {} }, tBeepOff);
    setTimeout(function() {
      try {
        var worker = catSpawn(forkWorkerHtml(false), WW, WH, chaseEnter(WW), chaseFlip);
        actors.push(worker);
        catGo(worker, enter(WW), dWorker, chaseFlip);
        // Scoop the dropped box the moment the worker's leading edge reaches
        // it: swap to the carrying frames and remove the ground box.
        var bx = mid + fkPx(lr ? 142 : 11);
        var wx0 = chaseEnter(WW), wx1 = enter(WW);
        var grabX = lr ? bx + fkPx(30) : bx - fkPx(81);
        var frac = (grabX - wx0) / (wx1 - wx0);
        var scoopMs = Math.max(200, Math.min(dWorker - 200, Math.round(dWorker * frac)));
        setTimeout(function() {
          try {
            if (boxEl) { boxEl.remove(); boxEl = null; }
            worker.innerHTML = forkWorkerHtml(true);
          } catch (e) {}
        }, scoopMs);
      } catch (e) {}
    }, tWorker);
    setTimeout(function() {
      try {
        var sup = catSpawn(forkSupHtml(), SW, SH, chaseEnter(SW), chaseFlip);
        actors.push(sup);
        catGo(sup, enter(SW), dSup, chaseFlip);
      } catch (e) {}
    }, tSup);
    setTimeout(function() {
      try {
        actors.forEach(function(a) { try { a.remove(); } catch (e) {} });
        if (bub) bub.remove();
        if (exc) exc.remove();
        if (boxEl) boxEl.remove();
      } catch (e) {}
    }, tSup + dSup + 700);
  } catch (e) {}
}
window.forkliftRun = forkliftRun;

// ─── INVOICE STORM (the hourly cartoon) ───
// A gust of wind blows a flurry of invoices along the bottom of the page.
// Danny, the invoice-filing agent from The Office (his desk look: yellow
// shirt, cropped hair, beard), sprints in after them with an open folder
// ("my invoices!") and snaps them up one at a time. The last sheet slaps onto
// his face (no bubble: Anthony 2026-10-05 dropped the "mmf!") and he staggers
// off blind. One invoice fluttered to the
// floor behind him: the Filing floor's gray cat creeps up, wiggles, pounces
// and trots off with it. Took over the hourly clock 2026-10-05 (Anthony's
// pick); forkliftRun() and catRun() stay as manual demos. Same rig (catbub
// bubbles, catTwoFrames) and the forklift's vector helpers. Positions are
// planned along the wind: s runs from the upwind edge, and X(s, w) mirrors it
// when the wind blows right-to-left. window.stormRun() = demo.
var STORM_SCALE = 1.25;
function stPx(n) { return Math.round(n * STORM_SCALE); }
var ST = {
  shirt: '#EAB308', shirtDk: '#CA8A04', skin: '#9C6644', hair: '#1F1A17', // Danny as officeLook() draws 'invoice-filing'
  cat: '#8A94A2', // officePetFor('Filing')
  manila: '#E2B04A', manilaDk: '#C9952F', paper: '#FFFFFF', edge: '#64748B', line: '#CBD5E1', ink: '#475569', gust: '#94A3B8',
  accents: ['#14B8A6', '#3B82F6', '#F43F5E', '#A855F7', '#F59E0B', '#10B981', '#6366F1'],
};
function stCrescent(cx, cy, ro, ri, a0, a1) { // officeCrescent: filled band between two radii (degrees, 270 = top)
  var P = function(rad, a) { var t = a * Math.PI / 180; return (cx + Math.cos(t) * rad).toFixed(2) + ',' + (cy + Math.sin(t) * rad).toFixed(2); };
  var large = Math.abs(a1 - a0) > 180 ? 1 : 0;
  return 'M ' + P(ro, a0) + ' A ' + ro + ',' + ro + ' 0 ' + large + ' 1 ' + P(ro, a1) + ' L ' + P(ri, a1) + ' A ' + ri + ',' + ri + ' 0 ' + large + ' 0 ' + P(ri, a0) + ' Z';
}
// Danny's head at the Office walker scale (r 11.5): eye, beard, cropped cap
function stDannyHead(cx, cy) {
  var r = 11.5;
  return fkCircle(cx, cy, r, ST.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye) +
    '<path d="M ' + (cx - 0.3 * r) + ',' + (cy + 0.55 * r) + ' A ' + r + ',' + r + ' 0 0 0 ' + (cx + 0.9 * r) + ',' + (cy + 0.35 * r) +
    '" stroke="' + ST.hair + '" stroke-width="' + (0.3 * r) + '" fill="none" stroke-linecap="round"/>' +
    '<path d="' + stCrescent(cx, cy, 1.03 * r, 0.82 * r, 188, 352) + '" fill="' + ST.hair + '"/>';
}
// One invoice, 18×23: vendor color block, line items, bold total
function stInvoice(accent) {
  return fkRect(0, 0, 18, 23, 1.5, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.9"') +
    fkRect(2.2, 2.2, 7, 2.6, 0.7, accent) + fkRect(12, 2.6, 3.8, 1.4, 0.6, ST.line) +
    fkRect(2.2, 7.5, 13.6, 1.3, 0.6, ST.line) + fkRect(2.2, 10.4, 10.5, 1.3, 0.6, ST.line) +
    fkRect(2.2, 13.3, 13.6, 1.3, 0.6, ST.line) + fkRect(2.2, 16.2, 8, 1.3, 0.6, ST.line) +
    fkRect(10.2, 19.2, 5.6, 1.9, 0.7, ST.ink);
}
// Open folder held out front like a scoop, tipped forward: tall back panel
// with its tab, shorter front panel, k caught sheets standing up between.
function stFolderOpen(k) {
  var s = [fkRect(21, -68, 18, 25, 1.8, ST.manila) + fkRect(22, -71, 7, 4.5, 1.4, ST.manila)];
  for (var i = 0; i < k; i++) s.push(fkRect(23 + (i % 3) * 2.2, -67 + (i % 2) * 1.6 - Math.floor(i / 2) * 1.4, 13, 9, 0.9, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.7" transform="rotate(' + (-8 + i * 4) + ' 29 -62)"'));
  s.push('<path d="M 20.5,-58 L 39.5,-60 L 39.5,-43.8 Q 39.5,-42 37.7,-42 L 22.3,-42 Q 20.5,-42 20.5,-43.8 Z" fill="' + ST.manilaDk + '"/>');
  return fkG(s.join(''), 'rotate(16 26 -42)');
}
// Closed folder gripped at (hx, hy), k sheets peeking out of the top
function stFolderClosed(k, hx, hy, rot) {
  var s = [];
  for (var i = 0; i < k; i++) s.push(fkRect(2 + i * 1.6, -21 - (i % 2) * 1.2, 9, 6, 0.8, ST.paper, ' stroke="' + ST.edge + '" stroke-width="0.6"'));
  s.push(fkRect(0, -17, 15, 18, 1.5, ST.manila) + fkRect(9, -19, 5, 3, 1, ST.manila));
  return fkG(s.join(''), 'translate(' + (hx - 3) + ',' + (hy + 8) + ') rotate(' + rot + ' 3 -8)');
}
// Danny (84×100, feet at 30,97) sprinting, leaning in, open folder out front
function stDannyRun(k, which) {
  var B = which === 'B';
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow),
    fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? 30 : -30) + ' 0 -26)"'),
    fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? -30 : 30) + ' 0 -26)"')];
  var u = [fkRect(-2, -54, 28, 7.5, 3.75, ST.shirtDk, ' transform="rotate(6 -1 -50)"'), // far arm
    fkRect(-13, -58, 26, 36, 10, ST.shirt),
    fkRect(-1, -52, 28, 8, 4, ST.shirt, ' transform="rotate(12 0 -48)"'), // near arm
    stDannyHead(0, -68), stFolderOpen(k),
    fkCircle(25.5, -47, 3.6, ST.skin) + fkCircle(26.5, -42.5, 3.6, ST.skin)];
  p.push(fkG(u.join(''), 'translate(0,' + (B ? -1.5 : 0) + ') rotate(9 0 -28)'));
  return fkG(p.join(''), 'translate(30,97)');
}
// Danny blinded: the last invoice plastered over his face, staggering, one
// arm flailing overhead, the closed folder dangling from the other
function stDannySlap(k, which) {
  var B = which === 'B';
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow),
    fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? -10 : 14) + ' 0 -26)"'),
    fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? 16 : -8) + ' 0 -26)"')];
  var arm = function(ang, fill, sy) { return fkRect(-2, sy - 3.75, 27, 7.5, 3.75, fill, ' transform="rotate(' + ang + ' -1 ' + sy + ')"'); };
  var hand = function(ang, sy) { var t = ang * Math.PI / 180; return [-1 + Math.cos(t) * 26, sy + Math.sin(t) * 26]; };
  var farA = B ? -150 : -125, nearA = B ? 25 : 10;
  var fh = hand(farA, -51), nh = hand(nearA, -49);
  var u = [arm(farA, ST.shirtDk, -51), fkCircle(fh[0], fh[1], 3.6, ST.skin),
    fkRect(-13, -58, 26, 36, 10, ST.shirt), stDannyHead(0, -68),
    fkG(stInvoice(ST.accents[6]), 'translate(-1,-79) rotate(' + (B ? 6 : -6) + ' 9 11.5)'),
    arm(nearA, ST.shirt, -49), stFolderClosed(k, nh[0], nh[1], B ? 18 : -10), fkCircle(nh[0], nh[1], 3.6, ST.skin)];
  p.push(fkG(u.join(''), 'translate(0,' + (B ? -1 : 0) + ') rotate(-7 0 -28)'));
  return fkG(p.join(''), 'translate(30,97)');
}
// The Filing floor's gray cat (officeCatFig's shape, 58×44, feet at 26,41):
// walk, carry (the rescued invoice in its mouth), crouch (butt wiggle), leap
function stCatEars(dx, dy) {
  return '<path d="M ' + (6.5 + dx) + ',' + (-27 + dy) + ' L ' + (8 + dx) + ',' + (-33 + dy) + ' L ' + (11.5 + dx) + ',' + (-27.5 + dy) + ' Z" fill="' + ST.cat + '"/>' +
    '<path d="M ' + (12.5 + dx) + ',' + (-28 + dy) + ' L ' + (15.5 + dx) + ',' + (-33.5 + dy) + ' L ' + (17 + dx) + ',' + (-27 + dy) + ' Z" fill="' + ST.cat + '"/>';
}
function stCatFrame(pose, which) {
  var B = which === 'B', c = ST.cat, p = [];
  var tail = function(d) { return '<path d="' + d + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>'; };
  if (pose === 'leap') {
    p.push(tail('M -14,-17 Q -21,-19 -25,-14'));
    p.push(fkRect(-19, -14, 11, 4.2, 2.1, c, ' transform="rotate(18 -9 -12)"'));
    p.push(fkRect(8, -17, 12, 4.2, 2.1, c, ' transform="rotate(22 9 -15)"'));
    p.push('<ellipse cx="-1" cy="-18" rx="15" ry="6.5" fill="' + c + '" transform="rotate(-8 -1 -18)"/>');
    p.push(fkCircle(13, -25, 7, c) + stCatEars(1, -3) + fkCircle(15.5, -25.5, 1.3, FK.eye));
  } else if (pose === 'crouch') {
    p.push(fkEllipse(0, 1.2, 15, 2.2, FK.shadow));
    p.push(tail(B ? 'M -13,-6 Q -24,-9 -21,-19' : 'M -13,-6 Q -21,-13 -15,-21'));
    p.push(fkG(fkRect(-12, -7, 5, 7, 2.5, c) + fkEllipse(-1, -9, 13.5, 6.5, c), 'rotate(' + (B ? -7 : -3) + ' 9 -5)'));
    p.push(fkRect(6, -4.5, 7, 4.5, 2.2, c));
    p.push(fkCircle(13, -13, 7, c) + stCatEars(1, 9) + fkCircle(15.5, -13.5, 1.4, FK.eye));
  } else {
    p.push(fkEllipse(0, 1.2, 15, 2.2, FK.shadow));
    p.push(tail(B ? 'M -13,-9 Q -22,-10 -22,-22' : 'M -13,-9 Q -21,-12 -19,-25'));
    p.push(fkRect(-10, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? 22 : -22) + ' -7.75 -8)"'));
    p.push(fkRect(4, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? -22 : 22) + ' 6.25 -8)"'));
    p.push(fkEllipse(-1, -14, 13, 7.5, c));
    p.push(fkCircle(12, -22, 7, c) + stCatEars(0, 0) + fkCircle(14.5, -22.5, 1.1, FK.eye));
    if (pose === 'carry') p.push(fkG(stInvoice(ST.accents[1]), 'translate(16,-25) rotate(24) scale(0.5)'));
  }
  return fkG(p.join(''), 'translate(26,41)');
}
function stDannyHtml(pose, k) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(pose === 'slap' ? stDannySlap(k, which) : stDannyRun(k, which), 84, 100, stPx(84), stPx(100), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'slap' ? ' wv' : ' fast');
}
function stCatHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(stCatFrame(pose, which), 58, 44, stPx(58), stPx(44), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' ? ' wv' : ' fast');
}
function stPaperSvg(accent) { return forkSvgWrap(fkG(stInvoice(accent), 'translate(1,1)'), 20, 25, stPx(28), stPx(35), ''); }
function stGustSvg() {
  return forkSvgWrap('<path d="M 4,24 C 40,16 78,30 114,19 C 128,14 138,6 130,3.5 C 122,1.5 120,11 127,13" stroke="' + ST.gust + '" stroke-width="2.6" fill="none" stroke-linecap="round"/>' +
    '<path d="M 26,32 C 52,27 80,34 104,28" stroke="' + ST.gust + '" stroke-width="2" fill="none" stroke-linecap="round" opacity="0.7"/>', 150, 36, stPx(150), stPx(36), 'stgust');
}
function stEnsureStyle() {
  catEnsureStyle();
  if (document.getElementById('storm-style')) return;
  var st = document.createElement('style');
  st.id = 'storm-style';
  st.textContent =
    '@keyframes stFlut { 0%, 100% { transform: translateY(0) rotate(-14deg) scaleX(1); } 25% { transform: translateY(-10px) rotate(5deg) scaleX(0.5); } 50% { transform: translateY(-3px) rotate(18deg) scaleX(0.95); } 75% { transform: translateY(-12px) rotate(2deg) scaleX(0.45); } }' +
    '.stfl { position: absolute; inset: 0; animation: stFlut 1.4s ease-in-out infinite; }' +
    '.stflat { position: absolute; inset: 0; transform-origin: 50% 100%; transform: rotate(-3deg) scaleY(0.42); }' +
    '@keyframes stGust { 0% { opacity: 0; } 15%, 75% { opacity: 0.9; } 100% { opacity: 0; } }' +
    '.stgust { animation: stGust 1.4s linear forwards; }';
  document.head.appendChild(st);
}
// Actors here move on both axes (papers rise and fall, the cat pounces), so
// they use translate(x, y) instead of catSpawn/catGo's translateX.
function stSpawn(html, w, h, bottom, x, y, flip) {
  var el = document.createElement('div');
  el.style.cssText = 'position:fixed;bottom:' + bottom + 'px;left:0;width:' + w + 'px;height:' + h + 'px;z-index:99999;pointer-events:none;will-change:transform;';
  el.style.transform = 'translate(' + x + 'px,' + y + 'px)' + (flip || '');
  el.innerHTML = html;
  document.body.appendChild(el);
  el.getBoundingClientRect();
  return el;
}
function stMove(el, x, y, ms, ease, flip) {
  el.style.transition = 'transform ' + ms + 'ms ' + (ease || 'linear');
  el.style.transform = 'translate(' + x + 'px,' + y + 'px)' + (flip || '');
}
function stBubble(text, x, bottom) {
  var b = document.createElement('div');
  b.className = 'catbub';
  b.textContent = text;
  b.style.left = '0px';
  b.style.bottom = bottom + 'px';
  b.style.transform = 'translateX(' + x + 'px)';
  document.body.appendChild(b);
  b.getBoundingClientRect();
  return b;
}
function stormRun() {
  try {
    stEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the wind blows
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var DW = stPx(84), DH = stPx(100), CW = stPx(58), CH = stPx(44), PW = stPx(28), PH = stPx(35), GW = stPx(150), GH = stPx(36);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var accents = ST.accents.slice().sort(function() { return Math.random() - 0.5; });

    // The gust: three wind streaks race across ahead of the paper
    [[0, 70], [180, 128], [380, 34]].forEach(function(g) {
      at(g[0], function() {
        var el = keep(stSpawn(stGustSvg(), GW, GH, g[1], X(-GW, GW), 0, flip));
        stMove(el, X(vw + 20, GW), 0, 1400, 'linear', flip);
      });
    });

    // Seven invoices ride it in to a hover spot, then drift on slowly,
    // fluttering. [spot as a share of the width, bottom px, role]: catches
    // hover at folder height, the stray sinks to the floor, the face-slapper
    // hovers at head height and goes last.
    var tIn = 1400, drift = 0.025; // px per ms once settled
    var sheets = [[0.20, 40, 'catch'], [0.31, 150, 'stray'], [0.41, 44, 'catch'], [0.51, 38, 'catch'], [0.61, 46, 'catch'], [0.71, 40, 'catch'], [0.84, 64, 'face']]
      .map(function(d, i) { var t0 = 150 + 140 * i; return { i: i, spot: Math.round(vw * d[0]), bottom: d[1], role: d[2], t0: t0, tS: t0 + tIn, el: null }; });
    var stray = sheets[1], face = sheets[6];
    sheets.forEach(function(p) {
      at(p.t0, function() {
        var fl = '<div class="stfl" style="animation-duration:' + (1.15 + (p.i % 3) * 0.22).toFixed(2) + 's;animation-delay:-' + (p.i * 0.37).toFixed(2) + 's">' + stPaperSvg(accents[p.i]) + '</div>';
        p.el = keep(stSpawn(fl, PW, PH, p.bottom, X(-PW - 10, PW), -40 - (p.i % 3) * 18, ''));
        stMove(p.el, X(p.spot, PW), 0, tIn, 'cubic-bezier(0.2,0.7,0.3,1)');
      });
      if (p.role === 'stray') {
        at(p.tS, function() { stMove(p.el, X(p.spot + 70, PW), p.bottom - 4, 1700, 'cubic-bezier(0.45,0,0.75,1)'); });
        at(p.tS + 1700, function() { var f = p.el.firstChild; f.style.animation = 'none'; f.className = 'stflat'; });
      } else {
        at(p.tS, function() { stMove(p.el, X(p.spot + drift * 16000, PW), 0, 16000, 'linear'); });
      }
    });

    // Danny sprints in once they've all settled. A sheet is caught when the
    // folder's mouth (fOff px from his back edge) reaches its center; the
    // face-slapper when his face (faceOff) does. Both move linearly, so each
    // meeting time is solved up front.
    var tD = 2700, sD0 = -DW - 20, v = (vw + DW + 20) / 4400;
    var fOff = stPx(72), faceOff = stPx(46);
    var meetAt = function(p, off) { return Math.round((p.spot + PW / 2 - off - sD0 + v * tD - drift * p.tS) / (v - drift)); };
    var tSlap = meetAt(face, faceOff);
    var sSlap = sD0 + v * (tSlap - tD), sStop = sSlap + stPx(10);
    var headX = function(s) { return lr ? X(s, DW) + stPx(36) : X(s, DW) + DW - stPx(36); };
    var bubBottom = 4 + stPx(90);
    var danny = null, caught = 0;
    at(tD, function() {
      danny = keep(stSpawn(stDannyHtml('run', 0), DW, DH, 4, X(sD0, DW), 0, flip));
      stMove(danny, X(sSlap, DW), 0, tSlap - tD, 'linear', flip);
    });
    // "my invoices!" rides along over his head for the first stretch
    at(tD + 500, function() {
      var b = keep(stBubble('my invoices!', headX(sD0 + v * 500) - 16, bubBottom));
      b.style.transition = 'transform 1600ms linear';
      b.style.transform = 'translateX(' + (headX(sD0 + v * 2100) - 16) + 'px)';
      at(1600, function() { drop(b); });
    });
    sheets.forEach(function(p) {
      if (p.role !== 'catch') return;
      at(meetAt(p, fOff), function() {
        drop(p.el);
        caught++;
        if (danny) danny.innerHTML = stDannyHtml('run', caught);
      });
    });
    at(tSlap, function() {
      drop(face.el);
      danny.innerHTML = stDannyHtml('slap', caught);
      stMove(danny, X(sStop, DW), 0, 260, 'ease-out', flip);
    });
    // ...then staggers off blind at a slower pace
    at(tSlap + 1500, function() { stMove(danny, X(vw + 20, DW), 0, Math.round((vw + 20 - sStop) / (v * 0.6)), 'linear', flip); });

    // The cat creeps in behind him toward the stray on the floor, crouches,
    // wiggles, pounces (mouth lands on the sheet) and trots back out with it.
    var sLand = stray.spot + 70 + PW / 2 - stPx(40), sCrouch = sLand - stPx(60), sC0 = -CW - 20;
    var tCat = tSlap + 300, dCatIn = Math.max(1000, Math.min(4000, Math.round((sCrouch - sC0) / 0.26)));
    var tPounce = tCat + dCatIn + 1100, tLand = tPounce + 480, dCatOut = Math.round((sLand - sC0) / 0.3);
    var cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(stCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, flip));
      stMove(cat, X(sCrouch, CW), 0, dCatIn, 'linear', flip);
    });
    at(tCat + dCatIn, function() { cat.innerHTML = stCatHtml('crouch'); });
    at(tPounce, function() {
      cat.innerHTML = stCatHtml('leap');
      stMove(cat, X((sCrouch + sLand) / 2, CW), -stPx(28), 240, 'ease-out', flip);
    });
    at(tPounce + 240, function() { stMove(cat, X(sLand, CW), 0, 240, 'ease-in', flip); });
    at(tLand, function() { drop(stray.el); cat.innerHTML = stCatHtml('carry'); });
    at(tLand + 450, function() {
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sLand, CW) + 'px,0px)' + back;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', back);
    });
    at(Math.max(tLand + 450 + dCatOut, tSlap + 1500 + Math.round((vw + 20 - sStop) / (v * 0.6))) + 600, function() { els.forEach(drop); });
  } catch (e) {}
}
window.stormRun = stormRun;

// ─── TRICK-OR-TREAT PARADE (the hourly cartoon Oct 24-31, every year) ───
// Bats flutter past, then the Office agents cross the bottom of the page in
// costume, each with their desk look: Emma (Customer Support) as a witch
// with a broom, Danny as a mummy holding out a pumpkin pail, Sage as a
// vampire whose cape swishes, the Filing floor's gray cat in a pumpkin suit,
// and last a bedsheet ghost that stops at center, turns to the screen
// ("boo!") and fades away. Anthony 2026-10-05: plays on every hourly run in
// the week before Halloween (the viewer's local date); the Invoice Storm
// plays the rest of the year. Same rig and helpers as the Invoice Storm.
// window.paradeRun() = demo.
var HW = {
  emma: { hair: '#B8BCC4', skin: '#E8B98A', shirt: '#14B8A6', headset: true }, // officeLook('reamaze-cs'): ponytail, stud
  sage: { hair: '#8C3B22', skin: '#6B4530', shirt: '#84CC16', glasses: true }, // officeLook('shopify-faq'): bob, round glasses, stud
  witch: '#2E1A47', band: '#7C3AED', stocking: '#7C3AED', broom: '#8B5A2B', straw: '#D4A24C', strawDk: '#A97C2F',
  cape: '#1C1A26', lining: '#B91C1C', pants: '#1F2937', pantsB: '#2B3444', rim: '#8B93A7', gold: '#F2CF6B',
  wrap: '#EDE6D6', wrapDk: '#D9D0BC', wrapLn: '#B9AE96', pumpkin: '#F97316', pumpkinDk: '#C2410C', stem: '#4D7C0F', leaf: '#65A30D',
  ghost: '#F8FAFC', ghostLn: '#94A3B8', bat: '#2E1065', batLn: '#A78BFA', batEye: '#FBBF24', ink: '#1F2937',
};
// OFC_HAIR_SHORT_WALKER: the short cap over a walker-scale head at (0,-68), r 11.5
var HW_CAP_SHORT = 'M 9.5,-74.8 Q 5.4,-81.2 -0.7,-81.5 Q -9.5,-81.2 -13.2,-74.8 Q -14.9,-70.7 -14.9,-65.3 Q -15,-61.2 -12.9,-58.9 Q -10.5,-60.6 -10.5,-63.9 Q -10.8,-70.7 -6.1,-74.4 Q -0.7,-77.8 4.7,-76.8 Q 8.1,-76.1 9.5,-74.8 Z';
function hwRim(c) { return ' stroke="' + (c || HW.rim) + '" stroke-width="0.9"'; }
// officeHeadGroup at walker scale for the ponytail and bob styles (stud earrings, optional glasses/headset)
function hwHead(lk, style) {
  var r = 11.5, cx = 0, cy = -68, k = [];
  if (style === 'ponytail') k.push('<ellipse cx="' + (cx - 1.02 * r) + '" cy="' + (cy + 0.35 * r) + '" rx="' + (0.3 * r) + '" ry="' + (0.9 * r) + '" fill="' + lk.hair + '" transform="rotate(10 ' + (cx - 1.02 * r) + ' ' + (cy + 0.35 * r) + ')"/>');
  if (style === 'bob') k.push(fkRect(cx - 1.1 * r, cy - 0.62 * r, 1.45 * r, 1.58 * r, 0.42 * r, lk.hair));
  k.push(fkCircle(cx, cy, r, lk.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye));
  k.push('<path d="' + HW_CAP_SHORT + '" fill="' + lk.hair + '"/>');
  if (lk.glasses) {
    var gx = cx + 0.52 * r, gy = cy - 0.05 * r, gr = 0.3 * r;
    k.push('<circle cx="' + gx + '" cy="' + gy + '" r="' + gr + '" fill="rgba(255,255,255,0.10)" stroke="#2B3444" stroke-width="' + (0.09 * r) + '"/>' +
      '<path d="M ' + (gx - gr) + ',' + gy + ' L ' + (cx - 0.55 * r) + ',' + (cy - 0.1 * r) + '" stroke="#2B3444" stroke-width="' + (0.08 * r) + '" fill="none"/>');
  }
  k.push(fkCircle(cx - 0.48 * r, cy + 0.58 * r, 0.09 * r, HW.gold));
  if (lk.headset) {
    var hs = '#222B3A';
    k.push('<path d="M ' + (cx - r) + ',' + (cy - 0.35 * r) + ' A ' + (1.03 * r) + ',' + (1.03 * r) + ' 0 0 1 ' + (cx + r) + ',' + (cy - 0.35 * r) + '" stroke="' + hs + '" stroke-width="' + (0.2 * r) + '" fill="none" stroke-linecap="round"/>' +
      fkCircle(cx - 0.88 * r, cy + 0.06 * r, 0.29 * r, hs) +
      '<path d="M ' + (cx - 0.88 * r) + ',' + (cy + 0.35 * r) + ' Q ' + (cx - 0.7 * r) + ',' + (cy + 0.82 * r) + ' ' + (cx - 0.12 * r) + ',' + (cy + 0.88 * r) + '" stroke="' + hs + '" stroke-width="' + (0.15 * r) + '" fill="none" stroke-linecap="round"/>' +
      fkCircle(cx - 0.06 * r, cy + 0.88 * r, 0.13 * r, hs));
  }
  return k.join('');
}
// Walker legs (Office stroll proportions), swinging on two frames; extra = trim drawn on each leg
function hwLegs(B, fillA, fillB, swing, trimA, trimB) {
  return fkG(fkRect(-9.5, -28, 8, 28, 4, fillA) + (trimA || ''), 'rotate(' + (B ? swing : -swing) + ' -5.5 -26)') +
    fkG(fkRect(1.5, -28, 8, 28, 4, fillB) + (trimB || ''), 'rotate(' + (B ? -swing : swing) + ' 5.5 -26)');
}
// Jack-o'-lantern candy pail hanging from a hand at (x, y)
function hwPail(x, y) {
  return '<path d="M ' + (x - 6) + ',' + (y + 6) + ' Q ' + x + ',' + (y - 3) + ' ' + (x + 6) + ',' + (y + 6) + '" stroke="' + HW.ink + '" stroke-width="1.1" fill="none"/>' +
    '<ellipse cx="' + x + '" cy="' + (y + 11) + '" rx="8" ry="6.8" fill="' + HW.pumpkin + '"/>' +
    '<ellipse cx="' + x + '" cy="' + (y + 11) + '" rx="3.4" ry="6.8" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="0.9"/>' +
    '<path d="M ' + (x - 4.4) + ',' + (y + 10) + ' L ' + (x - 2.4) + ',' + (y + 7.4) + ' L ' + (x - 0.8) + ',' + (y + 10) + ' Z M ' + (x + 0.8) + ',' + (y + 10) + ' L ' + (x + 2.4) + ',' + (y + 7.4) + ' L ' + (x + 4.4) + ',' + (y + 10) + ' Z" fill="' + HW.ink + '"/>' +
    '<path d="M ' + (x - 4.5) + ',' + (y + 12.8) + ' Q ' + x + ',' + (y + 17.5) + ' ' + (x + 4.5) + ',' + (y + 12.8) + ' Z" fill="' + HW.ink + '"/>';
}
// Emma as a witch (90×112, feet at 40,106): pointed hat, cape, striped stockings, broom
function hwEmmaFrame(which) {
  var B = which === 'B', lk = HW.emma, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var stripes = function(x) { return [-22, -15, -8].map(function(y) { return fkRect(x, y, 8, 2.6, 0.6, HW.stocking); }).join(''); };
  k.push(hwLegs(B, HW.witch, HW.witch, 16, stripes(-9.5), stripes(1.5)));
  var u = [];
  u.push('<path d="M -8,-58 Q ' + (B ? -26 : -22) + ',-34 ' + (B ? -24 : -20) + ',-12 L -4,-14 Q -6,-36 2,-57 Z" fill="' + HW.witch + '"' + hwRim() + '/>'); // cape
  u.push('<path d="M -13,-36 L 13,-36 L 17,-20 L -17,-20 Z" fill="' + HW.witch + '"' + hwRim() + '/>'); // skirt
  u.push(fkRect(-13, -58, 26, 26, 10, lk.shirt));
  u.push(hwHead(lk, 'ponytail'));
  u.push('<ellipse cx="1" cy="-77.5" rx="17" ry="3.2" fill="' + HW.witch + '"' + hwRim() + '/>' +
    '<path d="M -9,-78 L 10,-78 L 3,-94 Q 0,-101 -9,-99 Q -3,-96 -2,-92 Z" fill="' + HW.witch + '"' + hwRim() + '/>' +
    fkRect(-8, -82.5, 17, 3.6, 1, HW.band) + fkRect(-0.8, -82.5, 3.6, 3.6, 0.6, HW.gold));
  // broom carried across the body: straw trailing low behind her
  u.push('<line x1="33" y1="-52" x2="-27" y2="-12" stroke="' + HW.broom + '" stroke-width="2.6" stroke-linecap="round"/>' +
    '<path d="M -23,-15 L -39,-9 Q -40,-3 -35,0 L -21,-11 Z" fill="' + HW.straw + '"/>' +
    '<path d="M -26,-12 L -37,-5 M -24,-11 L -33,-1" stroke="' + HW.strawDk + '" stroke-width="0.9"/>');
  u.push(fkRect(2, -53, 20, 7.5, 3.75, lk.shirt, ' transform="rotate(18 4 -49)"') + fkCircle(21, -43.5, 4.2, lk.skin));
  k.push(fkG(u.join(''), B ? 'translate(0,-1.2)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// Danny as a mummy: bandaged head to toe, a slit for his eyes, arms out, stiff shuffle, pumpkin pail
function hwMummyFrame(which) {
  var B = which === 'B', W = HW.wrap, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var ln = function(x1, y1, x2, y2) { return '<line x1="' + x1 + '" y1="' + y1 + '" x2="' + x2 + '" y2="' + y2 + '" stroke="' + HW.wrapLn + '" stroke-width="1.1" stroke-linecap="round"/>'; };
  var legWraps = function(x) { return [-22, -14, -6].map(function(y) { return ln(x + 1, y + 1.5, x + 7, y - 1.5); }).join(''); };
  k.push(hwLegs(B, W, W, 8, legWraps(-9.5), legWraps(1.5)));
  var u = [];
  u.push('<path d="M -12,-46 Q -22,' + (B ? -40 : -48) + ' -31,' + (B ? -35 : -43) + '" stroke="' + W + '" stroke-width="3.2" fill="none" stroke-linecap="round"/>'); // loose end
  u.push(fkRect(-1, -55, 28, 7.5, 3.75, HW.wrapDk, ' transform="rotate(' + (B ? -6 : -2) + ' 0 -51)"')); // far arm
  u.push(fkRect(-13, -58, 26, 36, 10, W) + ln(-9, -50, 9, -54) + ln(-11, -43, 11, -47) + ln(-11, -36, 11, -40) + ln(-9, -29, 9, -33));
  u.push(fkCircle(0, -68, 11.5, W) + ln(-8, -74, 8, -77) + ln(-10, -59.5, 9, -62.5) + ln(-11, -65, -2, -66.5));
  u.push(fkRect(-1, -71, 12, 4.4, 2.2, ST.skin) + fkCircle(6, -68.8, 1.6, FK.eye)); // eye slit
  u.push(fkG(fkRect(0, -52, 28, 8, 4, W) + ln(9, -51, 8, -45) + ln(17, -51, 16, -45), 'rotate(' + (B ? 0 : -4) + ' 1 -48)')); // near arm
  u.push(hwPail(27, B ? -48 : -50));
  k.push(fkG(u.join(''), B ? 'translate(0,-1)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// Sage as a vampire: high collar, black cape with a red lining that swishes, fangs, pumpkin pail
function hwSageFrame(which) {
  var B = which === 'B', lk = HW.sage, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  var u = [];
  u.push('<path d="M -6,-60 Q ' + (B ? -34 : -26) + ',-34 ' + (B ? -34 : -27) + ',-4 L -8,-8 Q -12,-34 0,-58 Z" fill="' + HW.cape + '"' + hwRim() + '/>' +
    '<path d="M -7,-57 Q ' + (B ? -28 : -21) + ',-34 ' + (B ? -28 : -22) + ',-7 L -10,-9 Q -13,-34 -3,-56 Z" fill="' + HW.lining + '"/>');
  u.push('<path d="M -12,-56 L -23,-84 L -15,-79 L -8,-86 L -3,-61 Z" fill="' + HW.cape + '"' + hwRim() + '/>' +
    '<path d="M -11,-57.5 L -20,-80 L -14.5,-76.5 L -8.5,-81.5 L -5,-62 Z" fill="' + HW.lining + '"/>'); // stand-up collar
  k.push(fkG(u.join(''), B ? 'translate(0,-1)' : ''));
  k.push(hwLegs(B, HW.pants, HW.pantsB, 16));
  var v = [];
  v.push(fkRect(-13, -58, 26, 36, 10, lk.shirt));
  v.push(hwHead(lk, 'bob'));
  v.push('<path d="M 8.6,-62.4 L 10.4,-62.6 L 9.6,-59.6 Z" fill="#FFFFFF"/>'); // fang
  v.push(fkRect(2, -53, 20, 7.5, 3.75, lk.shirt, ' transform="rotate(40 4 -49)"') + hwPail(18, -38) + fkCircle(18, -37.5, 4.2, lk.skin));
  k.push(fkG(v.join(''), B ? 'translate(0,-1)' : ''));
  return fkG(k.join(''), 'translate(40,106)');
}
// The Filing floor's gray cat in a pumpkin suit (58×46, feet at 26,43), stem cap between its ears
function hwCatFrame(which) {
  var B = which === 'B', c = ST.cat, k = [fkEllipse(0, 1.2, 15, 2.2, FK.shadow)];
  k.push('<path d="' + (B ? 'M -14,-12 Q -23,-14 -21,-26' : 'M -14,-12 Q -22,-16 -18,-28') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>');
  k.push(fkRect(-10, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? 22 : -22) + ' -7.75 -8)"'));
  k.push(fkRect(4, -9, 4.5, 9.5, 2.2, c, ' transform="rotate(' + (B ? -22 : 22) + ' 6.25 -8)"'));
  k.push('<ellipse cx="-1" cy="-16" rx="15" ry="11.5" fill="' + HW.pumpkin + '"/>' +
    '<ellipse cx="-1" cy="-16" rx="8.5" ry="11.5" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="1.2"/>' +
    '<ellipse cx="-1" cy="-16" rx="2.6" ry="11.5" fill="none" stroke="' + HW.pumpkinDk + '" stroke-width="1.2"/>');
  k.push(fkCircle(12, -26, 7, c) + stCatEars(0, -4) + fkCircle(14.5, -26.5, 1.1, FK.eye));
  k.push(fkRect(11, -38.5, 2.4, 6, 1, HW.stem) + '<path d="M 13.2,-36.5 Q 18,-40 19.5,-35.5 Q 15.5,-34.5 13.2,-35.2 Z" fill="' + HW.leaf + '"/>');
  return fkG(k.join(''), 'translate(26,43)');
}
// Bedsheet ghost (50×54, ground at 25,52), floating; 'side' drifts right, 'front' faces the screen
function hwGhostFrame(view, which) {
  var B = which === 'B', k = [fkEllipse(0, 1, 13, 2.2, 'rgba(0,0,0,0.18)')];
  var d = view === 'front'
    ? 'M 15,-10 C 18,-30 12,-47 0,-47 C -12,-47 -18,-30 -15,-10 Q -11,' + (B ? -4 : -6) + ' -7.5,-9 Q -4,' + (B ? -6 : -4) + ' 0,-9 Q 4,' + (B ? -4 : -6) + ' 7.5,-9 Q 11,' + (B ? -6 : -4) + ' 15,-10 Z'
    : 'M 14,-10 C 17,-28 13,-46 -1,-47 C -14,-47 -17,-32 -16,-18 Q -18,-10 -22,-6 Q -17,' + (B ? -2 : -4) + ' -12,-8 Q -8,' + (B ? -4 : -2) + ' -4,-8 Q 0,' + (B ? -2 : -4) + ' 4,-8 Q 8,' + (B ? -4 : -2) + ' 10,-8 Q 12,-6 14,-10 Z';
  k.push('<path d="' + d + '" fill="' + HW.ghost + '" stroke="' + HW.ghostLn + '" stroke-width="1.1"/>');
  if (view === 'front') k.push('<ellipse cx="-5" cy="-33" rx="2.4" ry="3.4" fill="' + HW.ink + '"/><ellipse cx="5" cy="-33" rx="2.4" ry="3.4" fill="' + HW.ink + '"/><ellipse cx="0" cy="-24" rx="2.6" ry="3.4" fill="' + HW.ink + '"/>');
  else k.push('<ellipse cx="6" cy="-34" rx="2" ry="3" fill="' + HW.ink + '"/><ellipse cx="11" cy="-34.5" rx="1.5" ry="2.8" fill="' + HW.ink + '"/>');
  return fkG(k.join(''), 'translate(25,52)');
}
// Bat (34×18, front view): wings up on frame A, down on frame B
function hwBatFrame(which) {
  var wings = which === 'B'
    ? 'M 14,8 Q 8,10 3,16 Q 6,12 9,15 Q 10,11 14,11 Z M 20,8 Q 26,10 31,16 Q 28,12 25,15 Q 24,11 20,11 Z'
    : 'M 14,8 L 4,1 Q 6,6 1,6 Q 5,9 3,12 Q 9,9 14,10 Z M 20,8 L 30,1 Q 28,6 33,6 Q 29,9 31,12 Q 25,9 20,10 Z';
  return '<path d="' + wings + '" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    '<path d="M 14.2,6.5 L 14.6,3.2 L 16.2,5.8 Z M 19.8,6.5 L 19.4,3.2 L 17.8,5.8 Z" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    '<ellipse cx="17" cy="9" rx="4" ry="3.4" fill="' + HW.bat + '"' + hwRim(HW.batLn) + '/>' +
    fkCircle(15.7, 8.3, 0.75, HW.batEye) + fkCircle(18.3, 8.3, 0.75, HW.batEye);
}
function hwHtml(frameFn, vw, vh, speed) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(frameFn(which), vw, vh, stPx(vw), stPx(vh), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), speed);
}
function hwEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('halloween-style')) return;
  var st = document.createElement('style');
  st.id = 'halloween-style';
  st.textContent =
    '@keyframes hwBob { 0%, 100% { transform: translateY(0); } 50% { transform: translateY(-7px); } }' +
    '.hwbob { position: absolute; inset: 0; animation: hwBob 1.6s ease-in-out infinite; }' +
    '@keyframes hwFlap { 0%, 100% { transform: translateY(0); } 30% { transform: translateY(-16px); } 65% { transform: translateY(6px); } }' +
    '.hwflap { position: absolute; inset: 0; animation: hwFlap 1.3s ease-in-out infinite; }';
  document.head.appendChild(st);
}
// Halloween week = Oct 24-31 by the viewer's own clock, every year
function hwIsHalloweenWeek(d) {
  d = d || new Date();
  return d.getMonth() === 9 && d.getDate() >= 24;
}
function paradeRun() {
  try {
    hwEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the parade walks
    var flip = lr ? '' : ' scaleX(-1)';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var end = 900 + 4200; // the last bat is gone by then

    // Bats first: [start ms, bottom px, crossing ms, flap period s]
    var BW = stPx(34), BH = stPx(18);
    [[0, 160, 3400, 1.2], [250, 205, 3900, 1.5], [600, 125, 3600, 1.1], [900, 235, 4200, 1.4]].forEach(function(b, i) {
      at(b[0], function() {
        var html = '<div class="hwflap" style="animation-duration:' + b[3] + 's;animation-delay:-' + (i * 0.4).toFixed(1) + 's">' + hwHtml(hwBatFrame, 34, 18, ' fast') + '</div>';
        var el = keep(stSpawn(html, BW, BH, b[1], X(-BW - 20, BW), 0, ''));
        stMove(el, X(vw + 20, BW), 0, b[2], 'linear');
      });
    });

    // The costumed agents walk across at one pace, about 1.3 s apart
    var PW = stPx(90), PH = stPx(112), CW = stPx(58), CH = stPx(46);
    var v = (vw + PW + 40) / 9000; // px per ms
    var walkers = [[700, hwEmmaFrame, PW, PH, 90, 112, ' wv'], [2000, hwMummyFrame, PW, PH, 90, 112, ' wv'],
      [3300, hwSageFrame, PW, PH, 90, 112, ' wv'], [4500, hwCatFrame, CW, CH, 58, 46, '']];
    walkers.forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(hwHtml(a[1], a[4], a[5], a[6]), a[2], a[3], 4, X(-a[2] - 20, a[2]), 0, flip));
        var ms = Math.round((vw + a[2] + 40) / v);
        stMove(el, X(vw + 20, a[2]), 0, ms, 'linear', flip);
      });
      end = Math.max(end, a[0] + Math.round((vw + a[2] + 40) / v));
    });

    // The ghost drifts in last, stops at center, turns to the screen, "boo!", fades away
    var GW = stPx(50), GH = stPx(54);
    // (same pace as the walkers so it keeps its distance, easing only over the last stretch)
    var sG = Math.round(vw / 2 - GW / 2), sEase = sG - stPx(40), tG = 5800, dG = Math.round((sEase + GW + 20) / v), tStop = tG + dG + 600;
    var ghost = null, ghostFrames = function(view) { return '<div class="hwbob">' + hwHtml(function(w) { return hwGhostFrame(view, w); }, 50, 54, ' wv') + '</div>'; };
    at(tG, function() {
      ghost = keep(stSpawn(ghostFrames('side'), GW, GH, 4, X(-GW - 20, GW), 0, flip));
      stMove(ghost, X(sEase, GW), 0, dG, 'linear', flip);
    });
    at(tG + dG, function() { stMove(ghost, X(sG, GW), 0, 600, 'ease-out', flip); });
    at(tStop + 250, function() { ghost.innerHTML = ghostFrames('front'); });
    at(tStop + 450, function() {
      var b = keep(stBubble('boo!', X(sG, GW) + Math.round(GW / 2) - 16, 4 + GH + 8));
      at(1300, function() { drop(b); });
    });
    at(tStop + 1750, function() { ghost.style.transition = 'opacity 700ms ease-in'; ghost.style.opacity = '0'; });
    end = Math.max(end, tStop + 2450);
    at(end + 600, function() { els.forEach(drop); });
  } catch (e) {}
}
window.paradeRun = paradeRun;

// ─── HOURLY SEASON CALENDAR (Anthony 2026-11-01) ───
// Which cartoon the hourly clock plays, by the viewer's own date, every
// year: the Trick-or-Treat Parade in Halloween week (Oct 24-31); the Leaf
// Pile from Nov 1 up to the week before Thanksgiving; the Thanksgiving
// Potluck over the seven days ending on Thanksgiving (the fourth Thursday of
// November); the Black Friday Stampede from Black Friday through Cyber
// Monday; the Holiday Sleigh Dec 18-31; Danny's Invoice Storm the rest of
// the year. A season whose scene isn't drawn yet plays the Storm.
function thanksgivingDay(year) { return 22 + ((11 - new Date(year, 10, 1).getDay()) % 7); } // the 4th Thursday of November
function hourlySceneName(d) {
  d = d || new Date();
  var m = d.getMonth(), day = d.getDate();
  if (hwIsHalloweenWeek(d)) return 'parade';
  if (m === 10) {
    var tg = thanksgivingDay(d.getFullYear());
    if (day < tg - 6) return 'leaf';
    if (day <= tg) return 'potluck';
    if (day <= tg + 4) return 'stampede';
    return 'storm';
  }
  if (m === 11 && day >= 18) return 'sleigh';
  return 'storm';
}
function hourlyScene(d) {
  var scenes = { parade: paradeRun, leaf: leafRun, potluck: potluckRun, stampede: stampedeRun, sleigh: sleighRun, storm: stormRun };
  return scenes[hourlySceneName(d)] || stormRun;
}
window.hourlySceneName = hourlySceneName;

// ─── LEAF PILE (the hourly cartoon from Nov 1 to the week before Thanksgiving) ───
// A leaf-blower gust tumbles maple leaves along the bottom of the page and
// they settle on the floor. Danny (the Office's invoice-filing agent, in his
// desk look) walks in behind a rake and sweeps them into a pile that grows
// ahead of him, parks it, steps back and leans on the rake ("phew!"). The
// Filing floor's gray cat creeps in from the other side, crouches, and leaps
// into the pile: leaves burst everywhere and the cat sits in the wreck with a
// leaf on its head. "!" — Danny drops the rake, throws up his hands and
// stomps off the way he came; the cat trots off with a leaf in its mouth.
// Anthony 2026-11-01 (picked over Fall Back Monday / Diwali Lights / Runaway
// Parcel). Same rig and helpers as the Invoice Storm. window.leafRun() = demo.
var LF = {
  leaves: ['#DC2626', '#EA580C', '#F59E0B', '#D97706', '#B45309', '#C2410C'],
  edge: '#7C2D12', vein: 'rgba(255,255,255,0.35)', handle: '#8B5A2B', tine: '#9CA3AF', mound: '#B45309',
};
var LF_LEAN = { rake: 10, lean: 0, drop: -6, walk: 4 }; // how far Danny's upper body tips forward in each pose
// One maple leaf, 20×22, stem down
function lfLeaf(fill) {
  return '<path d="M 10,0.5 L 12.3,5.6 L 18.5,4 L 16.6,9.2 L 19.5,13.6 L 14.6,13.4 L 13,17.6 L 10,15.8 L 7,17.6 L 5.4,13.4 L 0.5,13.6 L 3.4,9.2 L 1.5,4 L 7.7,5.6 Z" fill="' + fill + '" stroke="' + LF.edge + '" stroke-width="0.5" stroke-linejoin="round"/>' +
    '<path d="M 10,15.8 L 10,1.5 M 10,11.5 L 17.5,4.8 M 10,11.5 L 2.5,4.8 M 10,13 L 18.6,13.2 M 10,13 L 1.4,13.2" stroke="' + LF.vein + '" stroke-width="0.6" fill="none"/>' +
    '<path d="M 10,15.8 L 10.4,22" stroke="' + LF.edge + '" stroke-width="1" stroke-linecap="round"/>';
}
function lfLeafSvg(i) { return forkSvgWrap(lfLeaf(LF.leaves[i % LF.leaves.length]), 20, 22, stPx(24), stPx(26), ''); }
function lfRx(k) { return 7 + 3 * k; } // half-width of a pile of k leaves
// k leaves heaped at (cx, base): a mound with leaves tumbled over it
function lfMound(k, cx, base) {
  if (k <= 0) return '';
  var rx = lfRx(k), ry = 4 + 1.7 * k, s = [];
  s.push('<path d="M ' + (cx - rx) + ',' + base + ' Q ' + cx + ',' + (base - 2 * ry) + ' ' + (cx + rx) + ',' + base + ' Z" fill="' + LF.mound + '"/>');
  for (var i = 0; i < k; i++) {
    var fx = (((i * 37) % 11) - 5) / 5.5, top = ry * (1 - fx * fx); // the mound's height under this leaf
    var y = base - 1 - top * (0.25 + 0.65 * ((i * 53) % 7) / 7);
    s.push(fkG(lfLeaf(LF.leaves[i % LF.leaves.length]), 'translate(' + (cx + fx * rx * 0.85).toFixed(1) + ',' + y.toFixed(1) + ') rotate(' + ((i * 97) % 360) + ') scale(0.9) translate(-10,-11)'));
  }
  return s.join('');
}
function lfPileSvg(k) { return forkSvgWrap(lfMound(k, 55, 45), 110, 48, stPx(110), stPx(48), ''); }
// The pile after the cat lands in it: leaves lying flat all around
function lfScatterSvg() {
  var s = [];
  for (var i = 0; i < 7; i++) {
    var x = 55 + (((i * 43) % 13) - 6) * 6.2;
    s.push(fkG(lfLeaf(LF.leaves[(i + 1) % LF.leaves.length]), 'translate(' + x.toFixed(1) + ',44.5) scale(1,0.45) rotate(' + ((i * 71) % 360) + ') scale(0.9) translate(-10,-11)'));
  }
  return forkSvgWrap(s.join(''), 110, 48, stPx(110), stPx(48), '');
}
// A rake: handle from (hx, hy) down to its head on the floor at (ex, 0), tines fanned out
function lfRake(hx, hy, ex) {
  var t = [];
  for (var i = 0; i < 7; i++) t.push('M ' + (ex - 1.5) + ',-6 Q ' + (ex - 9.5 + 3.2 * i) + ',-3.5 ' + (ex - 10.5 + 3.5 * i) + ',0.4');
  return '<line x1="' + hx + '" y1="' + hy + '" x2="' + (ex - 1.5) + '" y2="-6" stroke="' + LF.handle + '" stroke-width="2.4" stroke-linecap="round"/>' +
    '<path d="' + t.join(' ') + '" stroke="' + LF.tine + '" stroke-width="1.1" fill="none" stroke-linecap="round"/>' +
    '<line x1="' + (ex - 10) + '" y1="-3.2" x2="' + (ex + 10) + '" y2="-3.2" stroke="' + LF.tine + '" stroke-width="1.4" stroke-linecap="round"/>';
}
function lfRakeSvg() { return forkSvgWrap(fkG(lfRake(14, -62, 14), 'translate(0,69)'), 70, 70, stPx(70), stPx(70), ''); }
// An arm from the shoulder (sx, sy) to the hand (hx, hy): a rounded bar and the hand
function lfArm(sx, sy, hx, hy, fill, skin) {
  var dx = hx - sx, dy = hy - sy, L = Math.sqrt(dx * dx + dy * dy), a = Math.atan2(dy, dx) * 180 / Math.PI;
  return fkRect(sx - 1, sy - 3.75, L + 1, 7.5, 3.75, fill, ' transform="rotate(' + a.toFixed(1) + ' ' + sx + ' ' + sy + ')"') + fkCircle(hx, hy, 3.6, skin || ST.skin);
}
// Where the shoulder (-1,-50) lands once the upper body tips `lean` degrees about the hips (0,-28)
function lfShoulder(lean) {
  var t = lean * Math.PI / 180;
  return [-Math.cos(t) + 22 * Math.sin(t), -28 - Math.sin(t) - 22 * Math.cos(t)];
}
// Danny (160×100, feet at 30,97) in the Storm's desk look: 'rake' walks behind the rake with k
// leaves piled ahead of it, 'lean' rests on the upright rake wiping his brow, 'drop' throws both
// hands up, 'walk' stomps off empty-handed
function lfDannyFrame(pose, k, which) {
  var B = which === 'B', lean = LF_LEAN[pose] || 0, S = lfShoulder(lean);
  var swing = pose === 'rake' ? 20 : pose === 'walk' ? 22 : 4;
  var p = [fkEllipse(0, 1, 17, 2.6, FK.shadow)];
  if (pose === 'drop') p.push(fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(-12 0 -26)"') + fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(12 0 -26)"'));
  else p.push(fkRect(-4, -28, 8, 28, 4, FK.pantsA, ' transform="rotate(' + (B ? swing : -swing) + ' 0 -26)"') + fkRect(-4, -28, 8, 28, 4, FK.pantsB, ' transform="rotate(' + (B ? -swing : swing) + ' 0 -26)"'));
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, ST.shirt) + stDannyHead(0, -68), 'rotate(' + lean + ' 0 -28)');
  var H1, H2, held = ''; // near hand, far hand, whatever is held between the arms
  if (pose === 'rake') { H1 = [20, -44]; H2 = [28, -33]; held = lfRake(20, -44, B ? 53 : 52); }
  else if (pose === 'lean') { H1 = [12, B ? -79 : -78]; H2 = [16, -58]; held = lfRake(16, -64, 16); }
  else if (pose === 'drop') { H1 = [-16, B ? -79 : -80]; H2 = [14, B ? -81 : -82]; }
  else { H1 = B ? [-7, -32] : [9, -30]; H2 = B ? [9, -30] : [-7, -32]; }
  var u = lfArm(S[0], S[1], H2[0], H2[1], ST.shirtDk) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], ST.shirt);
  p.push(fkG(u, 'translate(0,' + (B && pose !== 'drop' ? -1.5 : 0) + ')'));
  if (pose === 'rake') p.push(lfMound(k, 54 + lfRx(k), 0));
  return fkG(p.join(''), 'translate(30,97)');
}
function lfDannyHtml(pose, k) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(lfDannyFrame(pose, k, which), 160, 100, stPx(160), stPx(100), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'lean' || pose === 'drop' ? ' wv' : '');
}
// The gray cat: the Storm's walk / crouch / leap, plus 'sit' (facing the screen, a leaf on its
// head, 58×60) and 'carry' (a leaf in its mouth)
function lfCatFrame(pose, which) {
  var B = which === 'B', c = ST.cat;
  if (pose === 'sit') {
    var p = [fkEllipse(0, 1.2, 14, 2.2, FK.shadow),
      '<path d="' + (B ? 'M 6,-4 Q 18,-4 20,-14' : 'M 6,-4 Q 19,-6 18,-16') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>',
      '<ellipse cx="-2" cy="-13" rx="11" ry="12.5" fill="' + c + '"/>',
      fkRect(-9, -12, 4.5, 12, 2.2, c) + fkRect(1, -12, 4.5, 12, 2.2, c),
      fkCircle(-1, -29, 7.5, c) + stCatEars(-13, -7) + fkCircle(-3.6, -30, 1.2, FK.eye) + fkCircle(1.6, -30, 1.2, FK.eye),
      fkG(lfLeaf(LF.leaves[0]), 'translate(-4,-37) rotate(' + (B ? -24 : -18) + ') scale(0.7) translate(-10,-22)')];
    return fkG(p.join(''), 'translate(26,57)');
  }
  var base = stCatFrame(pose === 'carry' ? 'walk' : pose, which);
  if (pose === 'carry') base += fkG(lfLeaf(LF.leaves[2]), 'translate(26,41) translate(17,-21) rotate(62) scale(0.6) translate(-10,-11)');
  return base;
}
function lfCatHtml(pose) {
  var vh = pose === 'sit' ? 60 : 44;
  var mk = function(which) { return function(cls) { return forkSvgWrap(lfCatFrame(pose, which), 58, vh, stPx(58), stPx(vh), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' || pose === 'sit' ? ' wv' : ' fast');
}
// A leaf that has landed: stop tumbling and lie flat
function lfFlat(el, i) {
  var f = el && el.firstChild;
  if (!f) return;
  f.style.animation = 'none';
  f.className = 'lfflat';
  f.style.transform = 'rotate(' + ((i * 53) % 50 - 25) + 'deg) scaleY(0.55)';
}
function lfEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('leaf-style')) return;
  var st = document.createElement('style');
  st.id = 'leaf-style';
  st.textContent =
    '.lfflat { position: absolute; inset: 0; transform-origin: 50% 100%; }' +
    '.lfrake { position: absolute; inset: 0; transform-origin: 20% 100%; transition: transform 380ms cubic-bezier(0.55,0,1,0.6); }';
  document.head.appendChild(st);
}
function leafRun() {
  try {
    lfEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the leaves blow
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var DW = stPx(160), DH = stPx(100), CW = stPx(58), CH = stPx(44), LW = stPx(24), LH = stPx(26), PW = stPx(110), PH = stPx(48), GW = stPx(150), GH = stPx(36), RW = stPx(70), RH = stPx(70);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var floor = 6; // landed leaves lie on the figures' floor line

    // The gust: three wind streaks low along the floor
    [[0, 40], [180, 96], [380, 18]].forEach(function(g) {
      at(g[0], function() {
        var el = keep(stSpawn(stGustSvg(), GW, GH, g[1], X(-GW, GW), 0, flip));
        stMove(el, X(vw + 20, GW), 0, 1400, 'linear', flip);
      });
    });

    // Nine leaves tumble in and settle on the floor between 12% and 64% of the width
    var leaves = [0.12, 0.19, 0.27, 0.33, 0.40, 0.46, 0.52, 0.58, 0.64].map(function(f, i) {
      var t0 = 150 + 120 * i, tIn = 1500 + (i % 3) * 150;
      return { i: i, spot: Math.round(vw * f), t0: t0, tS: t0 + tIn, tIn: tIn, el: null };
    });
    leaves.forEach(function(p) {
      at(p.t0, function() {
        var fl = '<div class="stfl" style="animation-duration:' + (1.05 + (p.i % 3) * 0.2).toFixed(2) + 's;animation-delay:-' + (p.i * 0.31).toFixed(2) + 's">' + lfLeafSvg(p.i) + '</div>';
        p.el = keep(stSpawn(fl, LW, LH, floor, X(-LW - 10, LW), -50 - (p.i % 4) * 22, ''));
        stMove(p.el, X(p.spot, LW), 0, p.tIn, 'cubic-bezier(0.2,0.7,0.3,1)');
      });
      at(p.tS, function() { lfFlat(p.el, p.i); });
    });

    // Danny walks in behind the rake once they're down. A leaf is swept the moment the rake head
    // (rOff px from his back edge) reaches its center; the pile ahead of the rake grows by one.
    var tD = 3000, sD0 = -DW - 20, v = (vw + DW + 20) / 6500; // px per ms: a working walk, not the Storm's sprint
    var rOff = stPx(82), K = leaves.length;
    var sStop = Math.round(vw * 0.74) - rOff, tStop = tD + Math.round((sStop - sD0) / v);
    var sPileC = sStop + stPx(84 + lfRx(K)), sLean = sStop - stPx(26); // the pile's center once parked; where he steps back to
    var headX = function(s) { return lr ? X(s, DW) + stPx(30) : X(s, DW) + DW - stPx(30); };
    var danny = null, k = 0;
    at(tD, function() {
      danny = keep(stSpawn(lfDannyHtml('rake', 0), DW, DH, 4, X(sD0, DW), 0, flip));
      stMove(danny, X(sStop, DW), 0, tStop - tD, 'linear', flip);
    });
    leaves.forEach(function(p) {
      at(tD + Math.round((p.spot + LW / 2 - rOff - sD0) / v), function() {
        drop(p.el);
        k++;
        if (danny) danny.innerHTML = lfDannyHtml('rake', k);
      });
    });
    // He parks the pile, steps back and leans on the rake
    var pile = null;
    at(tStop, function() {
      pile = keep(stSpawn(lfPileSvg(k), PW, PH, 4, X(sPileC - PW / 2, PW), 0, ''));
      danny.innerHTML = lfDannyHtml('lean', 0);
      stMove(danny, X(sLean, DW), 0, 600, 'ease-out', flip);
    });
    at(tStop + 800, function() {
      var b = keep(stBubble('phew!', headX(sLean) - 12, 4 + stPx(96)));
      at(1500, function() { drop(b); });
    });

    // The cat creeps in from the far side, crouches beside the pile, and leaps into it
    var sC0 = vw + 20, sCrouch = sPileC + stPx(lfRx(K) + 12), sLand = sPileC - CW / 2;
    var tCat = tStop + 900, dCatIn = Math.max(1000, Math.min(4500, Math.round((sC0 - sCrouch) / 0.26)));
    var tCrouch = tCat + dCatIn, tPounce = tCrouch + 1100, tLand = tPounce + 520;
    var cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(lfCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, back));
      stMove(cat, X(sCrouch, CW), 0, dCatIn, 'linear', back);
    });
    at(tCrouch, function() { cat.innerHTML = lfCatHtml('crouch'); });
    at(tPounce, function() {
      cat.innerHTML = lfCatHtml('leap');
      stMove(cat, X((sCrouch + sLand) / 2, CW), -stPx(34), 270, 'ease-out', back);
    });
    at(tPounce + 270, function() { stMove(cat, X(sLand, CW), 0, 250, 'ease-in', back); });
    // Splash: the pile flattens, eight leaves fly out and flutter down, the cat sits up in the wreck
    at(tLand, function() {
      pile.innerHTML = lfScatterSvg();
      cat.style.height = stPx(60) + 'px';
      cat.innerHTML = lfCatHtml('sit');
      for (var i = 0; i < 8; i++) (function(i) {
        var dx = (i % 2 ? 1 : -1) * stPx(36 + ((i * 41) % 5) * 26), up = stPx(34 + ((i * 29) % 4) * 18);
        var fl = '<div class="stfl" style="animation-duration:' + (0.9 + (i % 3) * 0.15).toFixed(2) + 's;animation-delay:-' + (i * 0.23).toFixed(2) + 's">' + lfLeafSvg(i + 3) + '</div>';
        var el = keep(stSpawn(fl, LW, LH, floor, X(sPileC - LW / 2, LW), -stPx(8), ''));
        stMove(el, X(sPileC - LW / 2 + dx, LW), -up, 420, 'cubic-bezier(0.2,0.6,0.4,1)');
        at(420, function() { stMove(el, X(sPileC - LW / 2 + dx * 1.3, LW), 0, 800 + i * 70, 'cubic-bezier(0.5,0,0.8,0.6)'); });
        at(1220 + i * 70, function() { lfFlat(el, i + 5); });
      })(i);
    });
    // "!" — Danny drops the rake (it stands for a beat, then keels over) and throws up his hands
    var exc = null;
    at(tLand + 120, function() {
      exc = keep(document.createElement('div'));
      exc.textContent = '!';
      exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (headX(sLean) - 6) + 'px;bottom:' + (4 + stPx(98)) + 'px;font:800 ' + stPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
      document.body.appendChild(exc);
    });
    at(tLand + 1000, function() { drop(exc); });
    at(tLand + 450, function() {
      danny.innerHTML = lfDannyHtml('drop', 0);
      var rake = keep(stSpawn('<div class="lfrake">' + lfRakeSvg() + '</div>', RW, RH, 4, X(sLean + stPx(32), RW), 0, flip));
      at(80, function() { rake.firstChild.style.transform = 'rotate(80deg)'; });
    });
    // He turns and stomps off the way he came (the element shifts so his feet stay put when it
    // mirrors); the cat picks a leaf and trots off its own way
    var tWalk = tLand + 1600, sTurn = sLean - stPx(100), dWalk = Math.round((sTurn - sD0) / (v * 0.85));
    at(tWalk, function() {
      danny.innerHTML = lfDannyHtml('walk', 0);
      danny.style.transition = 'none';
      danny.style.transform = 'translate(' + X(sTurn, DW) + 'px,0px)' + back;
      danny.getBoundingClientRect();
      stMove(danny, X(sD0, DW), 0, dWalk, 'linear', back);
    });
    var tCatOut = tLand + 2000, dCatOut = Math.round((sC0 - sLand) / 0.3);
    at(tCatOut, function() {
      cat.style.height = CH + 'px';
      cat.innerHTML = lfCatHtml('carry');
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sLand, CW) + 'px,0px)' + flip;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', flip);
    });
    var tEnd = Math.max(tWalk + dWalk, tCatOut + dCatOut) + 300;
    at(tEnd - 700, function() { els.forEach(function(el) { if (el === danny || el === cat) return; el.style.transition = 'opacity 600ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.leafRun = leafRun;

// ─── THANKSGIVING POTLUCK (the hourly cartoon over the seven days ending on Thanksgiving) ───
// Emma, Sage and Danny stroll across the bottom of the page toward the break room, each
// carrying their dish: Emma a pumpkin pie, Sage a casserole, Danny the big silver platter
// with a dome over the turkey. Danny stops mid-screen when the dome starts to jiggle; it
// pops off and lands upside-down behind him, and the roast turkey — drumsticks for legs —
// hops off the platter ("gobble!") and sprints back the way they came. "!" — Danny drops
// the platter (the garnish drumstick bounces off it), turns and chases the bird; Emma and
// Sage walk on, none the wiser. The Filing floor's gray cat creeps in, sniffs the
// drumstick on the floor and trots off with it. Anthony 2026-11-01. Same rig and helpers
// as the Storm, the parade and the Leaf Pile. window.potluckRun() = demo.
var TG = {
  turkey: '#B45309', turkeyHi: '#D97706', bone: '#F5F5F4', silver: '#CBD5E1', silverDk: '#94A3B8', silverHi: '#E5E7EB', dome: '#D1D5DB',
  crust: '#D9A066', pumpkin: '#C2410C', cream: '#FFFBEB', dish: '#F8FAFC', dishLn: '#CBD5E1', lid: '#E5E7EB',
};
var TG_WHO = {
  emma: { skin: HW.emma.skin, shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A', head: function() { return hwHead(HW.emma, 'ponytail'); } },
  sage: { skin: HW.sage.skin, shirt: '#84CC16', shirtDk: '#65A30D', pantsA: HW.pants, pantsB: HW.pantsB, head: function() { return hwHead(HW.sage, 'bob'); } },
  danny: { skin: ST.skin, shirt: ST.shirt, shirtDk: ST.shirtDk, pantsA: FK.pantsA, pantsB: FK.pantsB, head: function() { return stDannyHead(0, -68); } },
};
// A cloche: half-disc of radius 14 standing on (cx, by), knob on top
function tgDome(cx, by) {
  return '<path d="M ' + (cx - 14) + ',' + by + ' A 14,14 0 0 1 ' + (cx + 14) + ',' + by + ' Z" fill="' + TG.dome + '" stroke="' + TG.silverDk + '" stroke-width="0.9"/>' +
    '<path d="M ' + (cx - 9) + ',' + (by - 6) + ' A 11,11 0 0 1 ' + (cx - 2) + ',' + (by - 12.5) + '" stroke="' + TG.silverHi + '" stroke-width="1.6" fill="none" stroke-linecap="round"/>' +
    fkRect(cx - 1, by - 17, 2, 3.5, 0.8, TG.silverDk) + fkCircle(cx, by - 17.5, 2.4, TG.silverDk);
}
function tgDomeSvg() { return forkSvgWrap(tgDome(16, 21), 32, 22, stPx(32), stPx(22), ''); }
function tgPlatter(cx, cy) {
  return '<ellipse cx="' + cx + '" cy="' + cy + '" rx="17" ry="3.8" fill="' + TG.silver + '" stroke="' + TG.silverDk + '" stroke-width="0.8"/><ellipse cx="' + cx + '" cy="' + (cy - 0.4) + '" rx="13" ry="2.2" fill="' + TG.silverHi + '"/>';
}
function tgPlatterSvg() { return forkSvgWrap(tgPlatter(20, 5), 40, 10, stPx(40), stPx(10), ''); }
// A drumstick (16×12): meaty end at the left, bone and knuckles to the right
function tgDrumstick() {
  return '<ellipse cx="6" cy="6.5" rx="5.2" ry="3.8" fill="' + TG.turkey + '" transform="rotate(-20 6 6.5)"/><line x1="9.5" y1="5" x2="13.5" y2="2.8" stroke="' + TG.bone + '" stroke-width="1.9" stroke-linecap="round"/>' +
    fkCircle(14, 2.3, 1.6, TG.bone) + fkCircle(13.2, 4.4, 1.3, TG.bone);
}
function tgDrumSvg() { return forkSvgWrap(tgDrumstick(), 16, 12, stPx(16), stPx(12), ''); }
// What each of them carries, held out front at chest height (figure coords, feet at 0,0)
function tgDish(who, withDome) {
  if (who === 'emma') return '<ellipse cx="26" cy="-44" rx="13" ry="4.2" fill="' + TG.crust + '"/><ellipse cx="26" cy="-45" rx="10.8" ry="2.8" fill="' + TG.pumpkin + '"/>' + fkCircle(26, -47.2, 2.2, TG.cream);
  if (who === 'sage') return '<ellipse cx="11.5" cy="-45" rx="2.4" ry="1.7" fill="' + TG.dish + '"/><ellipse cx="40.5" cy="-45" rx="2.4" ry="1.7" fill="' + TG.dish + '"/>' +
    fkRect(13, -49, 26, 8, 2.5, TG.dish, ' stroke="' + TG.dishLn + '" stroke-width="0.8"') + fkRect(14.5, -52, 23, 3.6, 1.6, TG.lid) + fkCircle(26, -53.5, 1.7, TG.silverDk);
  return tgPlatter(26, -44) + (withDome ? tgDome(26, -45) : '');
}
// A walker (90×112, feet at 40,106): 'carry' strolls with the dish, 'stand' holds it still,
// 'drop' throws both hands up, 'chase' sprints with both arms reaching ahead
function tgFigure(who, pose, which, withDome) {
  var B = which === 'B', lk = TG_WHO[who], k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  if (pose === 'drop') k.push(fkRect(-9.5, -28, 8, 28, 4, lk.pantsA, ' transform="rotate(-8 -5.5 -26)"') + fkRect(1.5, -28, 8, 28, 4, lk.pantsB, ' transform="rotate(8 5.5 -26)"'));
  else k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'carry' ? 16 : pose === 'chase' ? 30 : 3));
  var lean = pose === 'chase' ? 12 : pose === 'drop' ? -6 : 0, S = lfShoulder(lean);
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, lk.shirt) + lk.head(), 'rotate(' + lean + ' 0 -28)');
  var H1, H2, held = '';
  if (pose === 'carry' || pose === 'stand') { H1 = [21, -42]; H2 = [19, -46]; held = tgDish(who, withDome); }
  else if (pose === 'drop') { H1 = [-16, B ? -79 : -80]; H2 = [14, B ? -81 : -82]; }
  else { H1 = [25, B ? -52 : -50]; H2 = [27, B ? -56 : -58]; }
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  k.push(fkG(u, 'translate(0,' + (B && (pose === 'carry' || pose === 'chase') ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function tgHtml(who, pose, withDome) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(tgFigure(who, pose, which, withDome), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'chase' ? ' fast' : ' wv');
}
// The roast turkey (40×28, floor at 27): 'sit' on the platter with its drumsticks tucked up
// behind, 'run' with the drumsticks scissoring underneath
function tgTurkeyFrame(pose, which) {
  var B = which === 'B', k = [];
  var leg = function(x, y, ang) {
    return fkG('<ellipse cx="0" cy="0" rx="5" ry="3.6" fill="' + TG.turkey + '"/><line x1="3" y1="1" x2="9" y2="7" stroke="' + TG.bone + '" stroke-width="2" stroke-linecap="round"/>' + fkCircle(9.5, 7.5, 1.7, TG.bone) + fkCircle(8, 9.2, 1.4, TG.bone),
      'translate(' + x + ',' + y + ') rotate(' + ang + ')');
  };
  if (pose === 'sit') {
    k.push(leg(9, 13, -150) + leg(10, 17, -168));
    k.push('<ellipse cx="20" cy="18" rx="13" ry="8.5" fill="' + TG.turkey + '"/><ellipse cx="16" cy="14" rx="6.5" ry="3" fill="' + TG.turkeyHi + '"/>');
  } else {
    k.push(fkEllipse(20, 27, 12, 1.8, FK.shadow));
    k.push(leg(16, 19, B ? 40 : 95) + leg(23, 19, B ? 95 : 40));
    k.push(fkG('<ellipse cx="20" cy="14" rx="13" ry="8" fill="' + TG.turkey + '"/><ellipse cx="16" cy="10" rx="6.5" ry="3" fill="' + TG.turkeyHi + '"/>', 'rotate(' + (B ? 5 : 9) + ' 20 14)'));
  }
  return k.join('');
}
function tgTurkeyHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(tgTurkeyFrame(pose, which), 40, 28, stPx(34), stPx(24), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'sit' ? ' wv' : ' fast');
}
function tgCatHtml(pose) {
  var mk = function(which) { return function(cls) {
    var g = stCatFrame(pose === 'carry' ? 'walk' : pose, which);
    if (pose === 'carry') g += fkG(tgDrumstick(), 'translate(26,41) translate(13,-25) rotate(25) scale(0.9)');
    return forkSvgWrap(g, 58, 44, stPx(58), stPx(44), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'crouch' ? ' wv' : ' fast');
}
function tgEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('potluck-style')) return;
  var st = document.createElement('style');
  st.id = 'potluck-style';
  st.textContent =
    '@keyframes tgBob { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-5px) rotate(-9deg); } }' +
    '.tgbob { position: absolute; inset: 0; animation: tgBob 0.45s ease-in-out infinite; }' +
    '.tgspin { position: absolute; inset: 0; transform: rotate(170deg); transition: transform 800ms ease-out; }' +
    '.tgtilt { position: absolute; inset: 0; transition: transform 380ms ease-in; }';
  document.head.appendChild(st);
}
function potluckRun() {
  try {
    tgEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the break room is
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), CW = stPx(58), CH = stPx(44), TW = stPx(34), TH = stPx(24), DMW = stPx(32), DMH = stPx(22), PLW = stPx(40), PLH = stPx(10), DRW = stPx(16), DRH = stPx(12);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var floorY = function(bottom) { return bottom - (4 + stPx(6)); }; // translateY that puts an element's bottom edge on the figures' floor line
    var v = (vw + W + 40) / 9000; // the Office stroll, as in the parade
    var tCross = Math.round((vw + W + 40) / v);

    // Emma and Sage stroll straight across with their dishes
    [[0, 'emma'], [1300, 'sage']].forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(tgHtml(a[1], 'carry'), W, H, 4, X(-W - 20, W), 0, flip));
        stMove(el, X(vw + 20, W), 0, tCross, 'linear', flip);
      });
    });

    // Danny brings up the rear with the turkey under its dome and stops mid-screen
    var tD = 2600, sD0 = -W - 20, sStop = Math.round(vw / 2 - W / 2), tStop = tD + Math.round((sStop - sD0) / v);
    var headX = function(s) { return lr ? X(s, W) + stPx(40) : X(s, W) + W - stPx(40); };
    var danny = null;
    at(tD, function() {
      danny = keep(stSpawn(tgHtml('danny', 'carry', true), W, H, 4, X(sD0, W), 0, flip));
      stMove(danny, X(sStop, W), 0, tStop - tD, 'linear', flip);
    });
    at(tStop, function() { danny.innerHTML = tgHtml('danny', 'stand', true); });
    // The dome starts to jiggle: it leaves his frame for its own element, bobbing
    var sDome = sStop + stPx(50), domeBottom = 4 + stPx(50), dome = null;
    at(tStop + 500, function() {
      danny.innerHTML = tgHtml('danny', 'stand', false);
      dome = keep(stSpawn('<div class="tgbob">' + tgDomeSvg() + '</div>', DMW, DMH, domeBottom, X(sDome, DMW), 0, flip));
    });
    // ...then pops off, flips over in the air and lands upside-down behind him
    var tPop = tStop + 1500, sDomeLand = sStop - stPx(34);
    at(tPop, function() {
      dome.firstChild.className = 'tgspin';
      stMove(dome, X((sDome + sDomeLand) / 2, DMW), -stPx(46), 380, 'cubic-bezier(0.2,0.6,0.4,1)', flip);
    });
    at(tPop + 380, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom), 420, 'cubic-bezier(0.5,0,0.9,0.6)', flip); });
    at(tPop + 800, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom) - stPx(6), 120, 'ease-out', flip); });
    at(tPop + 920, function() { stMove(dome, X(sDomeLand, DMW), floorY(domeBottom), 120, 'ease-in', flip); });

    // The turkey sits up on the platter facing the way they came, hops down, "gobble!", and sprints off
    var sTurk = sStop + stPx(66) - TW / 2, turkBottom = 4 + stPx(50), sTurkDown = sStop - stPx(44), turkey = null;
    at(tPop, function() { turkey = keep(stSpawn(tgTurkeyHtml('sit'), TW, TH, turkBottom, X(sTurk, TW), 0, back)); });
    var tHop = tPop + 350;
    at(tHop, function() {
      turkey.innerHTML = tgTurkeyHtml('run');
      stMove(turkey, X((sTurk + sTurkDown) / 2, TW), -stPx(14), 200, 'ease-out', back);
    });
    at(tHop + 200, function() { stMove(turkey, X(sTurkDown, TW), floorY(turkBottom), 260, 'ease-in', back); });
    at(tHop + 460, function() {
      var b = keep(stBubble('gobble!', X(sTurkDown, TW) + Math.round(TW / 2) - 22, 4 + stPx(40)));
      at(1100, function() { drop(b); });
    });
    var tRun = tHop + 800, dTurk = Math.round((sTurkDown + TW + 20) / 0.55);
    at(tRun, function() { stMove(turkey, X(-TW - 20, TW), floorY(turkBottom), dTurk, 'linear', back); });

    // "!" — Danny drops the platter (the garnish drumstick bounces off it), then turns and gives chase
    var exc = null;
    at(tHop + 150, function() {
      exc = keep(document.createElement('div'));
      exc.textContent = '!';
      exc.style.cssText = 'position:fixed;z-index:99999;pointer-events:none;left:' + (headX(sStop) - 6) + 'px;bottom:' + (4 + stPx(114)) + 'px;font:800 ' + stPx(30) + 'px "DM Sans", system-ui, sans-serif;color:#FBBF24;text-shadow:2px 0 #111,-2px 0 #111,0 2px #111,0 -2px #111;';
      document.body.appendChild(exc);
    });
    at(tHop + 1050, function() { drop(exc); });
    var sPlat = sStop + stPx(46), platBottom = 4 + stPx(45), sDrum = sStop + stPx(66) + stPx(30) - DRW / 2, drumBottom = 4 + stPx(50), drum = null;
    at(tHop + 500, function() {
      danny.innerHTML = tgHtml('danny', 'drop');
      var plat = keep(stSpawn('<div class="tgtilt">' + tgPlatterSvg() + '</div>', PLW, PLH, platBottom, X(sPlat, PLW), 0, flip));
      drum = keep(stSpawn(tgDrumSvg(), DRW, DRH, drumBottom, X(sStop + stPx(66) - DRW / 2, DRW), 0, flip));
      at(30, function() {
        plat.firstChild.style.transform = 'rotate(-18deg)';
        stMove(plat, X(sPlat + stPx(6), PLW), floorY(platBottom), 380, 'cubic-bezier(0.5,0,1,1)', flip);
        stMove(drum, X(sDrum, DRW), -stPx(22), 220, 'ease-out', flip);
      });
      at(250, function() { stMove(drum, X(sDrum, DRW), floorY(drumBottom), 300, 'ease-in', flip); });
      at(410, function() { plat.firstChild.style.transition = 'transform 160ms ease-out'; plat.firstChild.style.transform = 'rotate(3deg)'; });
    });
    var tChase = tHop + 1200, sTurn = sStop - stPx(10), dChase = Math.round((sTurn + W + 20) / 0.42);
    at(tChase, function() {
      danny.innerHTML = tgHtml('danny', 'chase');
      danny.style.transition = 'none';
      danny.style.transform = 'translate(' + X(sTurn, W) + 'px,0px)' + back;
      danny.getBoundingClientRect();
      stMove(danny, X(-W - 20, W), 0, dChase, 'linear', back);
    });

    // The cat creeps in from the far side, sniffs the drumstick, and trots off with it
    var sC0 = vw + 20, sSniff = sDrum + DRW + stPx(4), tCat = tHop + 1900, dCatIn = Math.max(900, Math.min(4500, Math.round((sC0 - sSniff) / 0.26)));
    var tSniff = tCat + dCatIn, tTake = tSniff + 700, dCatOut = Math.round((sC0 - sSniff) / 0.3), cat = null;
    at(tCat, function() {
      cat = keep(stSpawn(tgCatHtml('walk'), CW, CH, 4, X(sC0, CW), 0, back));
      stMove(cat, X(sSniff, CW), 0, dCatIn, 'linear', back);
    });
    at(tSniff, function() { cat.innerHTML = tgCatHtml('crouch'); });
    at(tTake, function() {
      drop(drum);
      cat.innerHTML = tgCatHtml('carry');
      cat.style.transition = 'none';
      cat.style.transform = 'translate(' + X(sSniff, CW) + 'px,0px)' + flip;
      cat.getBoundingClientRect();
      stMove(cat, X(sC0, CW), 0, dCatOut, 'linear', flip);
    });
    var tEnd = Math.max(1300 + tCross, tChase + dChase, tRun + dTurk, tTake + dCatOut) + 300;
    at(tEnd - 700, function() { els.forEach(function(el) { if (el === danny || el === cat || el === turkey) return; el.style.transition = 'opacity 600ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.potluckRun = potluckRun;

// ─── BLACK FRIDAY STAMPEDE (the hourly cartoon from Black Friday through Cyber Monday) ───
// Emma walks in and plants herself by the edge, headset on: "ding!" "ding!" "ding!" — the
// orders are pouring in. A tidal wave of parcels and two runaway shopping carts races
// across the bottom of the page. Tracy (Logistics, her desk look) stands mid-screen facing
// the flood with a sheet of labels and slaps one onto every box as it rushes past. The
// forklift crawls through behind the wave with both stacks piled seven high and teetering
// ("beep! beep!"); Emma tags along behind it; last of all the Filing floor's gray cat
// rides a box out, and Tracy calls it a day. Anthony 2026-11-01. Same rig as the Storm;
// the boxes and the lift are the Forklift Fumble's. window.stampedeRun() = demo.
var BF = {
  tracy: { hair: '#B8BCC4', skin: '#E8B98A', shirt: '#0EA5E9', shirtDk: '#0284C7', pantsA: '#1F2937', pantsB: '#2B3444' }, // officeLook('tracking-claims'): curly, hoop earrings
  emma: { shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A' },
  label: '#FFFFFF', labelLn: '#94A3B8', bar: '#1F2937', cart: '#9CA3AF', cartDk: '#6B7280', tire: '#0B0F16', hoop: '#F2CF6B',
};
function bfBarcode(x, y, w, h) { var s = ''; for (var i = 0; i < 6; i++) s += fkRect(x + i * (w / 6), y, i % 3 === 0 ? 1.1 : 0.6, h, 0, BF.bar); return s; }
function bfLabel() { return fkRect(0, 0, 10, 6, 0.8, BF.label, ' stroke="' + BF.labelLn + '" stroke-width="0.5"') + bfBarcode(1.2, 1.3, 7.5, 3.2); }
// A parcel from the forklift's stacks, with Tracy's label slapped on once she's had it
function bfBoxHtml(labeled) {
  return forkSvgWrap(fkBox(1, 1, 22, 12) + (labeled ? fkG(bfLabel(), 'translate(5.5,3.4) scale(0.95)') : ''), 24, 14, fkPx(36), fkPx(21), '');
}
// Tracy's head at the walker scale: a halo of curls, the fringe cap, a hoop earring
function bfTracyHead() {
  var lk = BF.tracy, r = 11.5, cx = 0, cy = -68, k = [];
  [[-10, -8], [-6, -11.5], [0, -12.5], [6, -11.5], [10, -8], [-12.5, -2], [12.5, -2], [-12, 4.5], [12, 4.5]].forEach(function(p) { k.push(fkCircle(cx + p[0], cy + p[1], 4.4, lk.hair)); });
  k.push(fkCircle(cx, cy, r, lk.skin) + fkCircle(cx + 0.5 * r, cy - 0.05 * r, 0.14 * r, FK.eye));
  k.push('<path d="' + HW_CAP_SHORT + '" fill="' + lk.hair + '"/>');
  k.push('<circle cx="' + (cx - 0.6 * r) + '" cy="' + (cy + 0.95 * r) + '" r="1.7" fill="none" stroke="' + BF.hoop + '" stroke-width="0.9"/>');
  return k.join('');
}
// Tracy (90×112, feet at 40,106): 'walk' in, 'stand' facing the flood with her label sheet,
// 'slap' with the near hand down on a passing box
function bfTracyFrame(pose, which) {
  var B = which === 'B', lk = BF.tracy, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'walk' ? 16 : pose === 'slap' ? 10 : 2));
  var lean = pose === 'slap' ? 16 : 0, S = lfShoulder(lean);
  var torso = fkG(fkRect(-13, -58, 26, 36, 10, lk.shirt) + bfTracyHead(), 'rotate(' + lean + ' 0 -28)');
  var sheet = fkG(fkRect(0, 0, 13, 17, 1.2, BF.label, ' stroke="' + BF.labelLn + '" stroke-width="0.7"') + bfBarcode(2, 3, 9, 4) + bfBarcode(2, 10, 9, 4), 'translate(13,-55) rotate(-12)');
  var H1 = pose === 'slap' ? [30, B ? -25 : -27] : pose === 'walk' ? (B ? [-7, -32] : [9, -30]) : [18, B ? -34 : -35];
  var H2 = pose === 'walk' ? (B ? [9, -30] : [-7, -32]) : [15, -47];
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + (pose === 'walk' ? '' : sheet) + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  if (pose === 'slap') u += fkG(bfLabel(), 'translate(' + (H1[0] + 1) + ',' + (H1[1] - 2) + ') rotate(22)');
  k.push(fkG(u, 'translate(0,' + (B && pose === 'walk' ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function bfTracyHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfTracyFrame(pose, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'walk' ? '' : ' wv');
}
// Emma (desk look, headset): 'walk', or 'stand' with a hand to her earpiece as the orders ding in
function bfEmmaFrame(pose, which) {
  var B = which === 'B', lk = BF.emma, k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, pose === 'walk' ? 16 : 2));
  var S = lfShoulder(0);
  var torso = fkRect(-13, -58, 26, 36, 10, lk.shirt) + hwHead(HW.emma, 'ponytail');
  var H1 = pose === 'walk' ? (B ? [-7, -32] : [9, -30]) : [-8, B ? -65 : -64], H2 = pose === 'walk' ? (B ? [9, -30] : [-7, -32]) : [-6, -31];
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, HW.emma.skin) + torso + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, HW.emma.skin);
  k.push(fkG(u, 'translate(0,' + (B && pose === 'walk' ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function bfEmmaHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfEmmaFrame(pose, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), pose === 'walk' ? '' : ' wv');
}
// A runaway shopping cart (60×44, floor at 43) heaped with parcels, handle at the back
function bfCartFrame(which) {
  var B = which === 'B', k = [fkEllipse(31, 43, 26, 2.2, FK.shadow)];
  k.push(fkBox(17, 4, 16, 9) + fkBox(32, 6, 14, 8) + fkBox(24, -3, 13, 8));
  k.push('<path d="M 12,12 L 52,12 L 48,32 L 16,32 Z" fill="' + BF.cart + '" opacity="0.45" stroke="' + BF.cartDk + '" stroke-width="1.4" stroke-linejoin="round"/>');
  [18, 24, 29].forEach(function(y) { k.push('<line x1="' + (13.5 + (y - 12) * 0.2).toFixed(1) + '" y1="' + y + '" x2="' + (50.5 - (y - 12) * 0.2).toFixed(1) + '" y2="' + y + '" stroke="' + BF.cartDk + '" stroke-width="0.8"/>'); });
  [22, 32, 42].forEach(function(x) { k.push('<line x1="' + x + '" y1="12" x2="' + (x - (x - 32) * 0.1).toFixed(1) + '" y2="32" stroke="' + BF.cartDk + '" stroke-width="0.8"/>'); });
  k.push('<path d="M 12,12 L 4,2" stroke="' + BF.cartDk + '" stroke-width="2.2" stroke-linecap="round"/>' + fkRect(0, 0.5, 9, 3, 1.5, BF.cartDk));
  k.push('<path d="M 16,32 L 16,38 M 48,32 L 48,38" stroke="' + BF.cartDk + '" stroke-width="1.6"/>');
  [[16, 39.5], [48, 39.5]].forEach(function(w) { k.push(fkCircle(w[0], w[1], 3.6, BF.tire) + fkG(fkRect(w[0] - 3, w[1] - 0.5, 6, 1, 0.5, BF.cart), 'rotate(' + (B ? 45 : 0) + ' ' + w[0] + ' ' + w[1] + ')') + fkCircle(w[0], w[1], 1.1, BF.cart)); });
  return k.join('');
}
function bfCartHtml() {
  var mk = function(which) { return function(cls) { return forkSvgWrap(bfCartFrame(which), 60, 48, fkPx(90), fkPx(72), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// The Forklift Fumble's lift with both stacks seven boxes high, teetering ('wob' frames),
// in a viewBox tall enough to hold them
function bfForkliftHtml() {
  var extra = 38;
  var mk = function(which) { return function(cls) { return forkSvgWrap(fkG(forkliftFrame('wob', [7, 7], which), 'translate(0,' + extra + ')'), 126, 70 + extra, fkPx(189), fkPx(Math.round(105 * (70 + extra) / 70)), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), '');
}
// The gray cat sitting on a parcel, facing the screen (58×68, box on the floor)
function bfCatRideHtml() {
  var mk = function(which) { return function(cls) {
    var B = which === 'B', c = ST.cat, p = [
      '<path d="' + (B ? 'M 6,-4 Q 18,-4 20,-14' : 'M 6,-4 Q 19,-6 18,-16') + '" stroke="' + c + '" stroke-width="4.5" fill="none" stroke-linecap="round"/>',
      '<ellipse cx="-2" cy="-13" rx="11" ry="12.5" fill="' + c + '"/>',
      fkRect(-9, -12, 4.5, 12, 2.2, c) + fkRect(1, -12, 4.5, 12, 2.2, c),
      fkCircle(-1, -29, 7.5, c) + stCatEars(-13, -7) + fkCircle(-3.6, -30, 1.2, FK.eye) + fkCircle(1.6, -30, 1.2, FK.eye)];
    return forkSvgWrap(fkBox(5, 46, 36, 20) + fkG(p.join(''), 'translate(23,46)'), 58, 68, stPx(58), stPx(68), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
function bfEnsureStyle() {
  stEnsureStyle();
  if (document.getElementById('stampede-style')) return;
  var st = document.createElement('style');
  st.id = 'stampede-style';
  st.textContent =
    '@keyframes bfHop { 0%, 100% { transform: translateY(0) rotate(0); } 50% { transform: translateY(-9px) rotate(-4deg); } }' +
    '.bfhop { position: absolute; inset: 0; animation: bfHop 0.42s ease-in-out infinite; }';
  document.head.appendChild(st);
}
function stampedeRun() {
  try {
    bfEnsureStyle();
    FORK_PAL.h = ['#F1F5F9', '#FBBF24', '#3B82F6'][Math.floor(Math.random() * 3)];
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the flood runs
    var flip = lr ? '' : ' scaleX(-1)', back = lr ? ' scaleX(-1)' : '';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), BW = fkPx(36), BH = fkPx(21), KW = fkPx(90), KH = fkPx(72), LW = fkPx(189), LH = fkPx(162), RW = stPx(58), RH = stPx(68);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };
    var hop = function(html, period, delay) { return '<div class="bfhop" style="animation-duration:' + period.toFixed(2) + 's;animation-delay:-' + delay.toFixed(2) + 's">' + html + '</div>'; };

    // Emma walks in to the edge and takes the calls: "ding!" x3
    var v = (vw + W + 40) / 9000, sE = Math.round(vw * 0.06), tE = Math.round((sE + W + 20) / v), emma = null;
    var headXE = function(s) { return lr ? X(s, W) + stPx(40) : X(s, W) + W - stPx(40); };
    at(0, function() {
      emma = keep(stSpawn(bfEmmaHtml('walk'), W, H, 4, X(-W - 20, W), 0, flip));
      stMove(emma, X(sE, W), 0, tE, 'linear', flip);
    });
    at(tE, function() { emma.innerHTML = bfEmmaHtml('stand'); });
    [0, 1, 2].forEach(function(i) {
      at(tE + 400 + 480 * i, function() {
        var b = keep(stBubble('ding!', headXE(sE) - 24 + i * 30, 4 + stPx(114) + i * 12));
        at(1500, function() { drop(b); });
      });
    });

    // Tracy hurries in from the far side and stands mid-screen facing the flood, label sheet ready
    var sT = Math.round(vw / 2) - W + stPx(70), sHand = sT + W - stPx(70), tT = Math.round((vw + 20 - sT) / 0.4), tracy = null;
    at(0, function() {
      tracy = keep(stSpawn(bfTracyHtml('walk'), W, H, 4, X(vw + 20, W), 0, back));
      stMove(tracy, X(sT, W), 0, tT, 'linear', back);
    });
    at(tT, function() { tracy.innerHTML = bfTracyHtml('stand'); });
    var slapTimer = 0;
    var slap = function() {
      if (!tracy) return;
      tracy.innerHTML = bfTracyHtml('slap');
      slapTimer++;
      var mine = slapTimer;
      at(190, function() { if (slapTimer === mine) tracy.innerHTML = bfTracyHtml('stand'); });
    };

    // The wave: fourteen parcels and two carts race through; each box gets its label as it passes her hand
    var sBox0 = -BW - 20;
    for (var i = 0; i < 14; i++) (function(i) {
      var t0 = 1800 + 110 * i + ((i * 53) % 4) * 40, sp = 0.5 + ((i * 7) % 5) * 0.05, cross = Math.round((vw + BW + 40) / sp);
      var box = null;
      at(t0, function() {
        box = keep(stSpawn(hop(bfBoxHtml(false), 0.36 + (i % 3) * 0.05, i * 0.13), BW, BH, 4, X(sBox0, BW), 0, flip));
        stMove(box, X(vw + 20, BW), 0, cross, 'linear', flip);
      });
      at(t0 + Math.round((sHand - BW / 2 - sBox0) / sp), function() {
        if (box) box.firstChild.innerHTML = bfBoxHtml(true);
        slap();
      });
    })(i);
    [3000, 4300].forEach(function(t0, i) {
      at(t0, function() {
        var cart = keep(stSpawn(hop(bfCartHtml(), 0.5, i * 0.2), KW, KH, 4, X(-KW - 20, KW), 0, flip));
        stMove(cart, X(vw + 20, KW), 0, Math.round((vw + KW + 40) / 0.62), 'linear', flip);
      });
    });

    // The forklift crawls through behind the wave, stacks seven high and teetering, "beep! beep!"
    var tL = 7800, vL = 0.28, dL = Math.round((vw + LW + 40) / vL), lift = null;
    var sLift = function(t) { return -LW - 20 + vL * (t - tL); };
    at(tL, function() {
      lift = keep(stSpawn(bfForkliftHtml(), LW, LH, 4, X(-LW - 20, LW), 0, flip));
      stMove(lift, X(vw + 20, LW), 0, dL, 'linear', flip);
    });
    at(tL + 2000, function() {
      var cabX = function(s) { return lr ? X(s, LW) + fkPx(50) : X(s, LW) + LW - fkPx(50); };
      var b = keep(stBubble('beep! beep!', cabX(sLift(tL + 2000)) - 20, 4 + LH + 6));
      b.style.transition = 'transform 1400ms linear';
      b.style.transform = 'translateX(' + (cabX(sLift(tL + 3400)) - 20) + 'px)';
      at(1400, function() { drop(b); });
    });
    // Emma tags along behind the lift once it has gone by
    var tEmmaOff = tL + Math.round((sE + W + 20 + LW) / vL) + 400, dEmmaOff = Math.round((vw + 20 - sE) / 0.3);
    at(tEmmaOff, function() { emma.innerHTML = bfEmmaHtml('walk'); stMove(emma, X(vw + 20, W), 0, dEmmaOff, 'linear', flip); });

    // Last of all the cat rides a parcel out; Tracy turns and calls it a day
    var tCat = tL + dL - 1700, dCat = Math.round((vw + RW + 40) / 0.45);
    at(tCat, function() {
      var ride = keep(stSpawn(hop(bfCatRideHtml(), 0.5, 0), RW, RH, 4, X(-RW - 20, RW), 0, ''));
      stMove(ride, X(vw + 20, RW), 0, dCat, 'linear');
    });
    var tTracyOff = tCat + Math.round((sT + RW + 20 + W) / 0.45) + 500, sTurn = sT + W - stPx(90), dTracyOff = Math.round((vw + 20 - sTurn) / 0.3);
    at(tTracyOff, function() {
      tracy.innerHTML = bfTracyHtml('walk');
      tracy.style.transition = 'none';
      tracy.style.transform = 'translate(' + X(sTurn, W) + 'px,0px)' + flip;
      tracy.getBoundingClientRect();
      stMove(tracy, X(vw + 20, W), 0, dTracyOff, 'linear', flip);
    });
    var tEnd = Math.max(tEmmaOff + dEmmaOff, tCat + dCat, tTracyOff + dTracyOff, tL + dL) + 400;
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.stampedeRun = stampedeRun;

// ─── HOLIDAY SLEIGH (the hourly cartoon Dec 18-31) ───
// Snow drifts down over the bottom of the page. The Office agents cross in holiday trim,
// each with their desk look: Emma in an elf hat, Danny with a candy cane over his
// shoulder, Sage hugging a wrapped gift. Then the Filing floor's gray cat, in antlers
// and a red nose, trots through pulling a red sleigh — Santa at the reins, parcels
// heaped in the back — which eases to a stop at center. Santa waves: "ho ho ho!", and
// sleigh, cat and all fade away like the Halloween ghost. A Christmas tree waits in the
// corner they're all headed for (Anthony 2026-11-01, "in the corner of where they're all
// headed"). Same rig and helpers as the Storm and the parade. window.sleighRun() = demo.
var SL = {
  red: '#B91C1C', redDk: '#991B1B', gold: '#F2CF6B', white: '#F8FAFC', green: '#16A34A', greenDk: '#15803D', belt: '#1F2937',
  santaSkin: '#F3D2B3', antler: '#8B5A2B', nose: '#DC2626', wrap: '#DC2626', ribbon: '#F2CF6B', cane: '#F8FAFC', caneStripe: '#DC2626', snow: '#F8FAFC',
};
var SL_WHO = {
  emma: { skin: HW.emma.skin, shirt: '#14B8A6', shirtDk: '#0F9488', pantsA: '#374151', pantsB: '#3F4A5A', head: function() { return hwHead(HW.emma, 'ponytail'); } },
  sage: { skin: HW.sage.skin, shirt: '#84CC16', shirtDk: '#65A30D', pantsA: HW.pants, pantsB: HW.pantsB, head: function() { return hwHead(HW.sage, 'bob'); } },
  danny: { skin: ST.skin, shirt: ST.shirt, shirtDk: ST.shirtDk, pantsA: FK.pantsA, pantsB: FK.pantsB, head: function() { return stDannyHead(0, -68); } },
};
// Elf hat over a walker head at (0,-68): white brim, green cone, the tip flopping forward with a pom
function slElfHat() {
  return '<path d="M -11.5,-77 L 11.5,-77 L 4,-95 Q 1,-103 9,-104 Z" fill="' + SL.green + '"/>' +
    '<path d="M -4,-80 L 4,-95" stroke="' + SL.greenDk + '" stroke-width="1" fill="none"/>' +
    '<ellipse cx="0" cy="-77" rx="12.5" ry="3" fill="' + SL.white + '"/>' + fkCircle(10, -104.5, 3, SL.white);
}
// Candy cane held in a hand at (hx, hy), rising over the shoulder, hook curling forward
function slCandyCane(hx, hy) {
  var d = 'M ' + hx + ',' + (hy + 6) + ' L ' + (hx + 2) + ',' + (hy - 30) + ' Q ' + (hx + 3) + ',' + (hy - 40) + ' ' + (hx + 11) + ',' + (hy - 39) + ' Q ' + (hx + 16) + ',' + (hy - 38) + ' ' + (hx + 16) + ',' + (hy - 32);
  return '<path d="' + d + '" stroke="' + SL.cane + '" stroke-width="4.6" fill="none" stroke-linecap="round"/>' +
    '<path d="' + d + '" stroke="' + SL.caneStripe + '" stroke-width="4.6" fill="none" stroke-linecap="butt" stroke-dasharray="3.2 3.8"/>';
}
// A wrapped gift, 16×14, held out front with its bow on top
function slGift(x, y) {
  return fkRect(x, y, 16, 14, 1.5, SL.wrap) + fkRect(x + 6.5, y, 3, 14, 0.5, SL.ribbon) + fkRect(x, y + 5.5, 16, 3, 0.5, SL.ribbon) +
    '<path d="M ' + (x + 8) + ',' + (y - 1) + ' Q ' + (x + 2) + ',' + (y - 7) + ' ' + (x + 4) + ',' + (y - 1) + ' Z M ' + (x + 8) + ',' + (y - 1) + ' Q ' + (x + 14) + ',' + (y - 7) + ' ' + (x + 12) + ',' + (y - 1) + ' Z" fill="' + SL.ribbon + '"/>';
}
// The three walkers (90×112, feet at 40,106) in their holiday trim
function slFigure(who, which) {
  var B = which === 'B', lk = SL_WHO[who], k = [fkEllipse(0, 1.5, 18, 2.8, FK.shadow)];
  k.push(hwLegs(B, lk.pantsA, lk.pantsB, 16));
  var S = lfShoulder(0), H1, H2, held = '', hat = '';
  if (who === 'emma') { H1 = B ? [-7, -32] : [9, -30]; H2 = B ? [9, -30] : [-7, -32]; hat = slElfHat(); }
  else if (who === 'danny') { H1 = [12, -52]; H2 = B ? [-8, -31] : [6, -30]; }
  else { H1 = [21, -42]; H2 = [19, -46]; held = slGift(18, -53); }
  var torso = fkRect(-13, -58, 26, 36, 10, lk.shirt) + lk.head() + hat;
  var u = lfArm(S[0], S[1], H2[0], H2[1], lk.shirtDk, lk.skin) + torso + held + lfArm(S[0], S[1], H1[0], H1[1], lk.shirt, lk.skin);
  if (who === 'danny') u += slCandyCane(12, -52) + fkCircle(12, -52, 3.6, lk.skin);
  k.push(fkG(u, 'translate(0,' + (B ? -1.2 : 0) + ')'));
  return fkG(k.join(''), 'translate(40,106)');
}
function slHtml(who) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(slFigure(who, which), 90, 112, stPx(90), stPx(112), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
// The gray cat in antlers with a red nose, trotting (the Storm's walk frames)
function slCatHtml() {
  var mk = function(which) { return function(cls) {
    var g = stCatFrame('walk', which) +
      fkG('<path d="M 9,-28 L 7,-37 M 7.5,-33 L 3.5,-36 M 7.2,-34.5 L 10,-39 M 15,-28 L 17.5,-37 M 17,-33 L 21,-36 M 17.3,-34.5 L 14.5,-39" stroke="' + SL.antler + '" stroke-width="1.5" fill="none" stroke-linecap="round"/>' + fkCircle(18.6, -20.8, 1.7, SL.nose), 'translate(26,41)');
    return forkSvgWrap(g, 58, 44, stPx(58), stPx(44), cls);
  }; };
  return catTwoFrames(mk('A'), mk('B'), ' fast');
}
// The sleigh (170×98, floor at 97): red body on a gold runner, parcels in the back, Santa at the
// reins ('ride') or waving ('wave'); the harness runs ahead to the cat
function slSleighFrame(pose, which) {
  var B = which === 'B', k = [];
  var arm = function(ang) {
    var t = ang * Math.PI / 180;
    return fkRect(50, 8, 16, 5, 2.5, SL.red, ' transform="rotate(' + ang + ' 50 10.5)"') + fkCircle(50 + Math.cos(t) * 16, 10.5 + Math.sin(t) * 16, 2.6, SL.santaSkin);
  };
  k.push(fkEllipse(64, 82, 56, 2.6, FK.shadow));
  k.push('<path d="M 118,50 L 166,42" stroke="' + SL.antler + '" stroke-width="1.6" stroke-linecap="round"/>'); // harness to the cat
  k.push('<path d="M 10,62 Q 6,62 7,56 M 10,64 L 100,64 Q 118,64 121,50" stroke="' + SL.gold + '" stroke-width="2.6" fill="none" stroke-linecap="round"/>');
  k.push('<path d="M 30,54 L 30,63 M 95,54 L 95,63" stroke="' + SL.gold + '" stroke-width="2.2"/>');
  k.push(fkBox(72, 6, 18, 11) + fkBox(90, 2, 16, 10) + fkBox(80, -4, 14, 9)); // parcels heaped behind the seat
  if (pose === 'ride') k.push('<path d="M 66,10 Q 100,20 124,46" stroke="' + SL.antler + '" stroke-width="1.3" fill="none"/>'); // reins
  k.push(fkRect(34, 6, 22, 26, 8, SL.red) + fkRect(34, 22, 22, 4, 1, SL.belt) + fkRect(43, 21.5, 5, 5, 1, SL.gold)); // Santa's coat and belt
  k.push(fkCircle(45, -2, 8, SL.santaSkin) + '<ellipse cx="45" cy="5.5" rx="7.8" ry="6" fill="' + SL.white + '"/>' + fkCircle(48, -3, 1, FK.eye) + fkCircle(51, -0.5, 1.6, '#F28B82'));
  k.push('<path d="M 37,-6.5 Q 45,-20 58,-12 L 54,-6 Z" fill="' + SL.red + '"/>' + fkRect(36, -8, 19, 3.6, 1.6, SL.white) + fkCircle(58.5, -12.5, 2.8, SL.white)); // hat
  k.push(arm(pose === 'wave' ? (B ? -118 : -95) : 8));
  k.push('<path d="M 18,54 L 14,30 Q 12,14 34,16 L 96,16 Q 118,16 116,36 L 110,54 Z" fill="' + SL.red + '" stroke="' + SL.gold + '" stroke-width="1.8" stroke-linejoin="round"/>' +
    '<path d="M 24,50 L 21,32 Q 20,22 34,22 L 94,22 Q 108,22 106,34 L 103,50 Z" fill="' + SL.redDk + '" opacity="0.5"/>'); // the body hides Santa's legs
  return fkG(k.join(''), 'translate(0,28)');
}
function slSleighHtml(pose) {
  var mk = function(which) { return function(cls) { return forkSvgWrap(slSleighFrame(pose, which), 170, 98, stPx(170), stPx(98), cls); }; };
  return catTwoFrames(mk('A'), mk('B'), ' wv');
}
// The Christmas tree waiting in the corner they're all headed for (80×110, floor at 108):
// three tiers with garlands and baubles, a star on top, two gifts underneath
function slTreeSvg() {
  var k = [fkEllipse(40, 108, 34, 2.6, FK.shadow)];
  k.push(fkRect(35, 90, 10, 18, 2, SL.antler));
  [[4, 96, 52], [10, 70, 32], [16, 48, 12]].forEach(function(t) {
    k.push('<path d="M ' + t[0] + ',' + t[1] + ' L ' + (80 - t[0]) + ',' + t[1] + ' L 40,' + t[2] + ' Z" fill="' + SL.green + '" stroke="' + SL.greenDk + '" stroke-width="1.2" stroke-linejoin="round"/>');
  });
  k.push('<path d="M 14,88 Q 40,80 66,90 M 20,64 Q 40,57 60,66 M 26,42 Q 40,37 54,43" stroke="' + SL.gold + '" stroke-width="1.4" fill="none" opacity="0.9"/>');
  [[22, 90, SL.red], [40, 84, '#3B82F6'], [58, 91, SL.gold], [30, 66, SL.gold], [50, 62, SL.red], [40, 72, SL.white], [34, 44, '#3B82F6'], [46, 40, SL.red], [40, 54, SL.gold]].forEach(function(b) { k.push(fkCircle(b[0], b[1], 2.7, b[2])); });
  k.push('<path d="M 40,2 L 42.4,8.2 L 49,8.6 L 43.8,12.8 L 45.6,19.2 L 40,15.6 L 34.4,19.2 L 36.2,12.8 L 31,8.6 L 37.6,8.2 Z" fill="' + SL.gold + '"/>');
  k.push(slGift(2, 94) + fkG(slGift(0, 0), 'translate(62,96) scale(0.85)'));
  return forkSvgWrap(k.join(''), 80, 110, stPx(80), stPx(110), '');
}
function slFlakeSvg(r) { return forkSvgWrap(fkCircle(5, 5, r, SL.snow, ' opacity="' + (0.5 + r * 0.12).toFixed(2) + '"'), 10, 10, 12, 12, ''); }
function sleighRun() {
  try {
    stEnsureStyle();
    var vw = window.innerWidth;
    var lr = Math.random() < 0.5; // which way the sleigh runs
    var flip = lr ? '' : ' scaleX(-1)';
    var X = function(s, w) { return Math.round(lr ? s : vw - s - w); };
    var W = stPx(90), H = stPx(112), CW = stPx(58), CH = stPx(44), SW = stPx(170), SH = stPx(98);
    var els = [];
    var keep = function(el) { els.push(el); return el; };
    var drop = function(el) { try { if (el) el.remove(); } catch (e) {} };
    var at = function(ms, fn) { setTimeout(function() { try { fn(); } catch (e) {} }, ms); };

    // The Christmas tree waits in the corner they're all headed for; it fades in first
    var TRW = stPx(80), TRH = stPx(110);
    at(0, function() {
      var tree = keep(stSpawn(slTreeSvg(), TRW, TRH, 4, X(vw - TRW - 12, TRW), 0, ''));
      tree.style.opacity = '0';
      tree.getBoundingClientRect();
      tree.style.transition = 'opacity 700ms ease-out';
      tree.style.opacity = '1';
    });

    // Snow: two dozen flakes drift down the bottom strip, each falling three times
    var fall = 240;
    for (var i = 0; i < 24; i++) (function(i) {
      var x = Math.round(vw * ((i * 61) % 97) / 97), r = 1.6 + ((i * 7) % 4) * 0.7, dur = 4200 + ((i * 13) % 5) * 300, sway = ((i % 2) ? 1 : -1) * (14 + (i % 4) * 8);
      var el = null;
      [0, 1, 2].forEach(function(round) {
        at(i * 170 + round * (dur + 150), function() {
          if (!el) el = keep(stSpawn(slFlakeSvg(r), 12, 12, 0, x, -fall, ''));
          el.style.transition = 'none';
          el.style.transform = 'translate(' + x + 'px,' + (-fall) + 'px)';
          el.getBoundingClientRect();
          stMove(el, x + sway, 0, dur, 'linear');
        });
      });
    })(i);

    // The agents cross in their holiday trim, about 1.3 s apart
    var v = (vw + W + 40) / 9000, tCross = Math.round((vw + W + 40) / v);
    [[600, 'emma'], [1900, 'danny'], [3200, 'sage']].forEach(function(a) {
      at(a[0], function() {
        var el = keep(stSpawn(slHtml(a[1]), W, H, 4, X(-W - 20, W), 0, flip));
        stMove(el, X(vw + 20, W), 0, tCross, 'linear', flip);
      });
    });

    // The cat pulls the sleigh in and they ease to a stop at center
    var vS = 0.26, tS = 6500, sStop = Math.round(vw / 2 - stPx(65)), sEase = sStop - stPx(40), catOff = stPx(152);
    var dS = Math.round((sEase + SW + 20) / vS), tStop = tS + dS + 600, sleigh = null, cat = null;
    at(tS, function() {
      sleigh = keep(stSpawn(slSleighHtml('ride'), SW, SH, 4, X(-SW - 20, SW), 0, flip));
      cat = keep(stSpawn(slCatHtml(), CW, CH, 4, X(-SW - 20 + catOff, CW), 0, flip));
      stMove(sleigh, X(sEase, SW), 0, dS, 'linear', flip);
      stMove(cat, X(sEase + catOff, CW), 0, dS, 'linear', flip);
    });
    at(tS + dS, function() {
      stMove(sleigh, X(sStop, SW), 0, 600, 'ease-out', flip);
      stMove(cat, X(sStop + catOff, CW), 0, 600, 'ease-out', flip);
    });
    at(tStop + 200, function() { cat.innerHTML = slCatHtml().replace(/catfr f2 fast/g, 'catfr f2 wv').replace(/catfr fast/g, 'catfr wv'); });
    // Santa waves: "ho ho ho!" — then sleigh, cat and all fade away
    at(tStop + 300, function() { sleigh.innerHTML = slSleighHtml('wave'); });
    var santaX = function(s) { return lr ? X(s, SW) + stPx(46) : X(s, SW) + SW - stPx(46); };
    at(tStop + 500, function() {
      var b = keep(stBubble('ho ho ho!', santaX(sStop) - 20, 4 + SH + 6));
      at(1500, function() { drop(b); });
    });
    at(tStop + 2100, function() {
      [sleigh, cat].forEach(function(el) { el.style.transition = 'opacity 800ms ease-in'; el.style.opacity = '0'; });
    });
    var tEnd = Math.max(3200 + tCross, tStop + 3000) + 300;
    at(tEnd - 800, function() { els.forEach(function(el) { if (el === sleigh || el === cat) return; el.style.transition = 'opacity 700ms ease-in'; el.style.opacity = '0'; }); });
    at(tEnd, function() { els.forEach(drop); });
  } catch (e) {}
}
window.sleighRun = sleighRun;

// ─── Marketplace order page (Anthony 2026-09-23, to-do 15): the seller-side page for a channel order
//     id — Amazon by its 3-7-7 id (Seller Central of the channel's country), eBay by its 2-5-5 id,
//     Walmart by channel name. Null when we know no page (Faire, Shopify relays, SHEIN …). ───
function marketplaceOrderUrl(channel, channelOrderId) {
  var id = String(channelOrderId || '').trim();
  var ch = String(channel || '').toLowerCase();
  if (!id) return null;
  var enc = encodeURIComponent(id);
  if (/^\d{3}-\d{7}-\d{7}$/.test(id)) {
    var host = /canada|\bca\b/.test(ch) ? 'sellercentral.amazon.ca' : /mexico|\bmx\b/.test(ch) ? 'sellercentral.amazon.com.mx' : /australia|\bau\b/.test(ch) ? 'sellercentral.amazon.com.au' : /brazil|\bbr\b/.test(ch) ? 'sellercentral.amazon.com.br' : 'sellercentral.amazon.com';
    return 'https://' + host + '/orders-v3/order/' + enc;
  }
  if (/^\d{2}-\d{5}-\d{5}$/.test(id) || /ebay/.test(ch)) return 'https://www.ebay.com/sh/ord/details?orderid=' + enc;
  if (/walmart/.test(ch)) return 'https://seller.walmart.com/order-management/details?purchaseOrderId=' + enc;
  return null;
}
// The id as a new-tab link when we know its page, else plain text (app-utils has no global h)
function marketplaceOrderLink(channel, channelOrderId, style) {
  var url = marketplaceOrderUrl(channel, channelOrderId);
  var text = channelOrderId == null || channelOrderId === '' ? '—' : String(channelOrderId);
  if (!url) return React.createElement('span', { style: style || null }, text);
  return React.createElement('a', { href: url, target: '_blank', rel: 'noopener', title: 'Open this order on the marketplace',
    onClick: function(e) { e.stopPropagation(); },
    style: Object.assign({ color: C.accent, textDecoration: 'none' }, style || {}) }, text);
}
window.marketplaceOrderUrl = marketplaceOrderUrl;
window.marketplaceOrderLink = marketplaceOrderLink;

;
/* ===== app-i18n.js ===== */
// ─── UI TRANSLATOR (window.I18N) ───
// Live DOM translation for users whose profile language isn't English.
//
// REACT-SAFETY INVARIANT — the reason this exists instead of Chrome's page
// translate: we ONLY ever write text via node.nodeValue and attributes via
// setAttribute. We never insert, remove, wrap, or re-parent nodes. Chrome's
// translator wraps text nodes in <font> elements, and the next React commit
// that touches them throws "removeChild ... not a child of this node"
// (error_log 334/335). Keep it that way: any change to this file that
// mutates DOM *structure* re-introduces that crash.
//
// Mechanics: digit-bearing tokens (counts, SKUs, dates, prices) are swapped
// for ⟦N⟧ placeholders before dictionary lookup, so volatile values never
// reach the server and can never be mangled by translation. Lookups hit an
// in-memory Map seeded from GET /api/i18n/bundle; misses batch-POST to
// /api/i18n/translate (debounced), then the document is re-walked.
// When React re-renders a translated label, the new English text appears for
// one frame and the MutationObserver re-translates it.

(function() {
  var state = {
    lang: null,
    dict: new Map(),      // placeholderized source -> translation (null = gave up this session)
    pending: new Set(),   // keys awaiting server translation
    attempts: new Map(),  // key -> failed batch count
    observer: null,
    timer: null,
    inflight: false,
  };
  // Guards against reacting to our own writes: MutationObserver records arrive
  // async, so suppression flags don't work — instead remember what we wrote
  // per node/attribute and skip mutations that match it.
  var lastWritten = new WeakMap();     // Text node -> last value we wrote
  var lastWrittenAttr = new WeakMap(); // Element -> { attrName: last value we wrote }
  // What each node said BEFORE we translated it — lets a switch back to
  // English restore the page live instead of asking for a refresh.
  var originals = new WeakMap();       // Text node -> original value
  var originalAttrs = new WeakMap();   // Element -> { attrName: original value }

  var SKIP_TAGS = { SCRIPT: 1, STYLE: 1, NOSCRIPT: 1, TEXTAREA: 1, IFRAME: 1, CANVAS: 1 };
  var ATTRS = ['placeholder', 'title', 'aria-label'];

  function skipElement(el) {
    for (var e = el; e && e.nodeType === 1; e = e.parentElement) {
      if (SKIP_TAGS[e.tagName]) return true;
      if (e.isContentEditable) return true;
      if (e.hasAttribute && (e.hasAttribute('data-no-i18n') || e.getAttribute('translate') === 'no')) return true;
      if (e.classList && e.classList.contains('notranslate')) return true;
    }
    return false;
  }

  // Swap every whitespace-delimited run containing a digit for ⟦N⟧ — "Box 12"
  // → "Box ⟦0⟧", "MFK-ROUGE-540" → "⟦0⟧", "8.5x11 in" → "⟦0⟧ in". Keeps the
  // dictionary low-cardinality and guarantees codes survive verbatim.
  function keyFor(text) {
    var tokens = [];
    var key = text.replace(/\S*\d\S*/g, function(m) { tokens.push(m); return '⟦' + (tokens.length - 1) + '⟧'; });
    return { key: key, tokens: tokens };
  }
  function restore(translated, tokens) {
    return translated.replace(/⟦(\d+)⟧/g, function(m, i) {
      return tokens[+i] !== undefined ? tokens[+i] : m;
    });
  }
  function eligible(key) {
    if (!key || key.length < 2 || key.length > 250) return false;
    if (!/[A-Za-z]{2,}/.test(key)) return false;         // pure numbers/symbols/single letters
    if (/^https?:\/\/|@.*\./.test(key)) return false;    // URLs, email-ish
    return true;
  }

  function translateText(raw, node, write) {
    var m = raw.match(/^(\s*)([\s\S]*?)(\s*)$/);
    var lead = m[1], core = m[2], trail = m[3];
    if (!core) return;
    var pk = keyFor(core);
    if (!eligible(pk.key)) return;
    var tr = state.dict.get(pk.key);
    if (tr === undefined) {
      state.pending.add(pk.key);
      scheduleFlush();
    } else if (tr !== null) {
      var out = lead + restore(tr, pk.tokens) + trail;
      if (out !== raw) write(out);
    }
  }

  function applyToTextNode(node) {
    if (!state.lang || !node.parentElement || skipElement(node.parentElement)) return;
    var raw = node.nodeValue;
    if (raw == null || lastWritten.get(node) === raw) return;
    translateText(raw, node, function(out) {
      originals.set(node, raw);
      lastWritten.set(node, out);
      node.nodeValue = out; // nodeValue write only — never restructure (see header)
    });
  }

  function applyToAttr(el, attr) {
    if (!state.lang || skipElement(el)) return;
    var raw = el.getAttribute && el.getAttribute(attr);
    if (!raw) return;
    var mine = lastWrittenAttr.get(el);
    if (mine && mine[attr] === raw) return;
    translateText(raw, el, function(out) {
      var oa = originalAttrs.get(el);
      if (!oa) { oa = {}; originalAttrs.set(el, oa); }
      oa[attr] = raw;
      if (!mine) { mine = {}; lastWrittenAttr.set(el, mine); }
      mine[attr] = out;
      el.setAttribute(attr, out);
    });
  }

  function walk(root) {
    if (!root) return;
    if (root.nodeType === 3) { applyToTextNode(root); return; }
    if (root.nodeType !== 1 && root.nodeType !== 9) return;
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
    var n = root.nodeType === 1 ? root : walker.nextNode();
    if (root.nodeType === 1) {
      for (var a = 0; a < ATTRS.length; a++) if (root.hasAttribute(ATTRS[a])) applyToAttr(root, ATTRS[a]);
      n = walker.nextNode();
    }
    while (n) {
      if (n.nodeType === 3) applyToTextNode(n);
      else for (var i = 0; i < ATTRS.length; i++) if (n.hasAttribute(ATTRS[i])) applyToAttr(n, ATTRS[i]);
      n = walker.nextNode();
    }
  }

  function scheduleFlush() {
    if (state.timer) return;
    state.timer = setTimeout(function() { state.timer = null; flush(); }, 500);
  }

  // One POST of up to 200 pending keys. Resolves after the response is merged
  // and re-applied (never rejects — failures feed the retry/give-up counters).
  function flushBatch() {
    if (!state.lang || !state.pending.size) return Promise.resolve();
    var batch = Array.from(state.pending).slice(0, 200);
    batch.forEach(function(k) { state.pending.delete(k); });
    return fetch('/api/i18n/translate', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ lang: state.lang, strings: batch }),
    }).then(function(r) {
      if (!r.ok) throw new Error('translate ' + r.status);
      return r.json();
    }).then(function(data) {
      var entries = (data && data.entries) || {};
      batch.forEach(function(k) {
        if (entries[k] !== undefined) { state.dict.set(k, entries[k]); state.attempts.delete(k); }
      });
      walk(document.body);
    }).catch(function() {
      // Transient failure: retry each key once more, then give up for the
      // session (dict=null) so a broken endpoint can't hot-loop requests.
      batch.forEach(function(k) {
        var n = (state.attempts.get(k) || 0) + 1;
        state.attempts.set(k, n);
        if (n >= 2) state.dict.set(k, null);
        else state.pending.add(k);
      });
    });
  }

  // Background path: one batch at a time, re-scheduled while misses remain.
  function flush() {
    if (!state.lang || state.inflight) { if (state.pending.size) scheduleFlush(); return; }
    if (!state.pending.size) return;
    state.inflight = true;
    flushBatch().finally(function() {
      state.inflight = false;
      if (state.pending.size) scheduleFlush();
    });
  }

  // Blocking path: translate every pending key NOW, batch after batch, until
  // the queue is dry or the deadline passes (leftovers finish in background).
  function drain(deadline) {
    if (!state.lang || !state.pending.size || Date.now() > deadline) return Promise.resolve();
    if (state.inflight) { // background flush racing us — wait a beat, retry
      return new Promise(function(res) { setTimeout(res, 150); }).then(function() { return drain(deadline); });
    }
    state.inflight = true;
    return flushBatch().finally(function() { state.inflight = false; })
      .then(function() { return drain(deadline); });
  }

  // Full-screen progress overlay for interactive language switches. Appended
  // directly to document.body — OUTSIDE React's root, so inserting/removing it
  // never touches React-managed nodes (same pattern as download anchors).
  var overlayEl = null;
  function showOverlay() {
    if (overlayEl) return;
    overlayEl = document.createElement('div');
    overlayEl.setAttribute('data-no-i18n', 'true');
    overlayEl.style.cssText = 'position:fixed;inset:0;z-index:100000;display:flex;flex-direction:column;align-items:center;justify-content:center;gap:14px;background:rgba(10,12,18,0.78);backdrop-filter:blur(2px);color:#E8EAF0;font-family:inherit;';
    var spinner = document.createElement('div');
    spinner.style.cssText = 'width:34px;height:34px;border-radius:50%;border:3px solid rgba(255,255,255,0.25);border-top-color:#4F6EF7;animation:i18nspin 0.8s linear infinite;';
    var label = document.createElement('div');
    label.style.cssText = 'font-size:14px;font-weight:600;';
    label.textContent = 'Traduciendo la interfaz…';
    var sub = document.createElement('div');
    sub.style.cssText = 'font-size:12px;opacity:0.7;';
    sub.textContent = 'Translating the interface…';
    var style = document.createElement('style');
    style.textContent = '@keyframes i18nspin{to{transform:rotate(360deg)}}';
    overlayEl.appendChild(style);
    overlayEl.appendChild(spinner);
    overlayEl.appendChild(label);
    overlayEl.appendChild(sub);
    document.body.appendChild(overlayEl);
  }
  function hideOverlay() {
    if (overlayEl && overlayEl.parentNode) overlayEl.parentNode.removeChild(overlayEl);
    overlayEl = null;
  }

  function start(lang, block) {
    state.lang = lang;
    if (block) showOverlay();
    fetch('/api/i18n/bundle?lang=' + encodeURIComponent(lang), { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : { entries: [] }; })
      .then(function(data) {
        ((data && data.entries) || []).forEach(function(pair) { state.dict.set(pair[0], pair[1]); });
      })
      .catch(function() { /* dictionary warms via /translate misses instead */ })
      .finally(function() {
        if (state.lang !== lang) { hideOverlay(); return; } // switched off while bundle loaded
        walk(document.body);
        if (block) {
          // Hold the overlay until every visible string is translated (or the
          // deadline passes — leftovers then finish via the background path).
          drain(Date.now() + 20000).finally(hideOverlay);
        }
        if (!state.observer) {
          state.observer = new MutationObserver(function(muts) {
            for (var i = 0; i < muts.length; i++) {
              var mu = muts[i];
              if (mu.type === 'characterData') applyToTextNode(mu.target);
              else if (mu.type === 'attributes') applyToAttr(mu.target, mu.attributeName);
              else if (mu.type === 'childList') {
                for (var j = 0; j < mu.addedNodes.length; j++) walk(mu.addedNodes[j]);
              }
            }
          });
          state.observer.observe(document.body, {
            subtree: true, childList: true, characterData: true,
            attributes: true, attributeFilter: ATTRS,
          });
        }
      });
  }

  // Put every node we translated back to its original text — same
  // nodeValue/setAttribute-only writes as translation itself.
  function restoreAll() {
    if (!document.body) return;
    var walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT, null);
    for (var n = walker.nextNode(); n; n = walker.nextNode()) {
      if (n.nodeType === 3) {
        var orig = originals.get(n);
        if (orig !== undefined) { n.nodeValue = orig; originals.delete(n); lastWritten.delete(n); }
      } else {
        var oa = originalAttrs.get(n);
        if (oa) {
          for (var a in oa) n.setAttribute(a, oa[a]);
          originalAttrs.delete(n); lastWrittenAttr.delete(n);
        }
      }
    }
  }

  function stop() {
    state.lang = null;
    hideOverlay();
    // Disconnect BEFORE restoring so the observer never sees our own reverts
    if (state.observer) { state.observer.disconnect(); state.observer = null; }
    if (state.timer) { clearTimeout(state.timer); state.timer = null; }
    state.pending.clear();
    restoreAll();
  }

  // Idempotent entry point — call with the user's preference ('es', 'en',
  // null/undefined). English or empty turns the translator off. Pass
  // opts.block=true (interactive switches) to hold a progress overlay until
  // the visible page is fully translated; boot-time calls stay non-blocking.
  function setLanguage(lang, opts) {
    if (!lang || lang === 'en') { stop(); return; }
    if (lang === state.lang) return;
    stop();
    start(lang, !!(opts && opts.block));
  }

  // Self-contained boot for shells that don't already fetch preferences:
  // reads the logged-in user's saved language and applies it.
  function boot() {
    fetch('/api/auth/preferences', { credentials: 'include' })
      .then(function(r) { return r.ok ? r.json() : {}; })
      .then(function(prefs) { setLanguage(prefs && prefs.language); })
      .catch(function() { /* stay in English */ });
  }

  window.I18N = { setLanguage: setLanguage, boot: boot };
})();

;
/* ===== app-chat.js ===== */
// AI Chat module — full page (ChatPage) + floating quick-ask widget (ChatWidget).
// Admin-only (area 'chat'). Talks to /api/chat/* (routes/chat.js); assistant
// replies stream over SSE. Exports: window.ChatPage, window.ChatWidget.
(function () {
  var h = React.createElement;

  // ─── Markdown-lite renderer (bold, inline code, fences, tables, lists, headers) ───
  function mdInline(text, keyBase) {
    var parts = String(text).split(/(\*\*[^*]+\*\*|`[^`]+`)/g);
    return parts.map(function (p, i) {
      if (/^\*\*[^*]+\*\*$/.test(p)) return h('strong', { key: keyBase + ':' + i }, p.slice(2, -2));
      if (/^`[^`]+`$/.test(p)) return h('code', { key: keyBase + ':' + i, style: { background: C.surfaceHover, border: '1px solid ' + C.border, borderRadius: 4, padding: '0 4px', fontSize: '0.92em' } }, p.slice(1, -1));
      return p;
    });
  }

  function mdRender(text) {
    var lines = String(text || '').split('\n');
    var out = [], i = 0, key = 0;
    function tableAt(idx) { // returns rows count if a md table starts at idx
      if (!/^\s*\|.*\|\s*$/.test(lines[idx] || '')) return 0;
      if (!/^\s*\|[\s:|-]+\|\s*$/.test(lines[idx + 1] || '')) return 0;
      var n = 2;
      while (/^\s*\|.*\|\s*$/.test(lines[idx + n] || '')) n++;
      return n;
    }
    while (i < lines.length) {
      var line = lines[i];
      if (/^```/.test(line)) {
        var code = []; i++;
        while (i < lines.length && !/^```/.test(lines[i])) { code.push(lines[i]); i++; }
        i++;
        out.push(h('pre', { key: key++, style: { background: C.bg, border: '1px solid ' + C.border, borderRadius: 8, padding: 10, fontSize: 11.5, overflowX: 'auto', margin: '6px 0' } }, code.join('\n')));
        continue;
      }
      var tn = tableAt(i);
      if (tn) {
        var cells = function (l) { return l.replace(/^\s*\||\|\s*$/g, '').split('|').map(function (c) { return c.trim(); }); };
        var head = cells(lines[i]);
        var rows = [];
        for (var r = 2; r < tn; r++) rows.push(cells(lines[i + r]));
        out.push(h('div', { key: key++, style: { overflowX: 'auto', margin: '6px 0' } },
          h('table', { style: { borderCollapse: 'collapse', fontSize: 12, minWidth: 240 } },
            h('thead', null, h('tr', null, head.map(function (c, ci) {
              return h('th', { key: ci, style: { textAlign: 'left', padding: '4px 10px', borderBottom: '1px solid ' + C.border, color: C.textMuted, fontWeight: 600, whiteSpace: 'nowrap' } }, mdInline(c, 'th' + ci));
            }))),
            h('tbody', null, rows.map(function (row, ri) {
              return h('tr', { key: ri }, row.map(function (c, ci) {
                return h('td', { key: ci, style: { padding: '3px 10px', borderBottom: '1px solid ' + C.border, whiteSpace: 'nowrap' } }, mdInline(c, 'td' + ri + ':' + ci));
              }));
            }))
          )));
        i += tn;
        continue;
      }
      var mHead = /^(#{1,4})\s+(.*)$/.exec(line);
      if (mHead) {
        out.push(h('div', { key: key++, style: { fontWeight: 700, fontSize: mHead[1].length <= 2 ? 14 : 12.5, margin: '10px 0 4px' } }, mdInline(mHead[2], 'h' + key)));
        i++; continue;
      }
      if (/^\s*([-*]|\d+\.)\s+/.test(line)) {
        var items = [];
        while (i < lines.length && /^\s*([-*]|\d+\.)\s+/.test(lines[i])) {
          items.push(lines[i].replace(/^\s*([-*]|\d+\.)\s+/, ''));
          i++;
        }
        out.push(h('ul', { key: key++, style: { margin: '4px 0', paddingLeft: 20 } }, items.map(function (it, ii) {
          return h('li', { key: ii, style: { margin: '2px 0' } }, mdInline(it, 'li' + ii));
        })));
        continue;
      }
      if (line.trim() === '') { out.push(h('div', { key: key++, style: { height: 6 } })); i++; continue; }
      out.push(h('div', { key: key++, style: { margin: '2px 0' } }, mdInline(line, 'p' + key)));
      i++;
    }
    return out;
  }

  // ─── Shared bits ───────────────────────────────────────────────────────────
  function toolChip(t, i) {
    return h('span', { key: i, style: { display: 'inline-flex', alignItems: 'center', gap: 5, background: C.accentSoft, border: '1px solid ' + C.border, color: C.textMuted, borderRadius: 12, padding: '2px 9px', fontSize: 10.5, marginRight: 6, marginBottom: 4 } },
      h(LIcon, { name: t.running ? 'loader-2' : 'wrench', size: 11, spin: !!t.running, color: C.accent }),
      t.label || t.name,
      t.summary ? h('span', { style: { color: C.textDim } }, '· ' + t.summary) : null);
  }

  function extractStored(m) {
    // Map a stored chat_messages row to renderable {kind, text, tools}
    var blocks = Array.isArray(m.content) ? m.content : [];
    if (m.role === 'user') {
      if (blocks.some(function (b) { return b.type === 'tool_result'; })) return null; // internal
      var text = blocks.filter(function (b) { return b.type === 'text' && !/^\[context\]/.test(b.text || ''); })
        .map(function (b) { return b.text; }).join('\n');
      return text ? { kind: 'user', text: text } : null;
    }
    var tools = blocks.filter(function (b) { return b.type === 'tool_use'; })
      .map(function (b) { return { name: b.name, label: window._chatToolLabel(b) }; });
    var atext = blocks.filter(function (b) { return b.type === 'text'; }).map(function (b) { return b.text; }).join('');
    if (!tools.length && !atext) return null;
    return { kind: 'assistant', text: atext, tools: tools, usage: m.usage, cost: m.cost };
  }

  window._chatToolLabel = function (b) {
    var inp = b.input || {};
    switch (b.name) {
      case 'run_report': return 'Report: ' + (inp.report_id || '');
      case 'days_of_stock': return 'Days of stock';
      case 'inventory_valuation': return 'Inventory valuation';
      case 'product_lookup': return 'Lookup: ' + (inp.query || '');
      case 'list_tables': return 'List tables';
      case 'describe_table': return 'Describe: ' + (inp.table || '');
      case 'sql_query': return inp.purpose ? 'SQL: ' + inp.purpose : 'SQL query';
      default: return b.name;
    }
  };

  // ─── ChatThread — message list + composer + streaming, for one conversation ─
  function ChatThread(props) {
    var convId = props.conversationId;
    var compact = props.compact;
    var _m = useState(null); var conv = _m[0], setConv = _m[1];
    var _s = useState(null); var live = _s[0], setLive = _s[1]; // {text, tools, status}
    var _i = useState(''); var input = _i[0], setInput = _i[1];
    var _b = useState(false); var busy = _b[0], setBusy = _b[1];
    var _e = useState(null); var err = _e[0], setErr = _e[1];
    var scrollRef = useRef(null);
    var abortRef = useRef(null);
    var liveRef = useRef(null);
    var loadSeqRef = useRef(0); // review M7 #105: switching conversations mid-load must not paint the old one

    function load(id) {
      var target = id || convId;
      if (!target) { setConv(null); return; }
      var seq = ++loadSeqRef.current;
      api('/chat/conversations/' + target)
        .then(function (c) { if (seq === loadSeqRef.current) setConv(c); })
        .catch(function (e) { if (seq === loadSeqRef.current) setErr(e.message); });
    }
    useEffect(function () { load(); }, [convId]);
    useEffect(function () {
      var el = scrollRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });

    function updateLive(fn) {
      liveRef.current = fn(liveRef.current || { text: '', tools: [], status: null });
      setLive(Object.assign({}, liveRef.current));
    }

    async function sendMessage() {
      var text = input.trim();
      if (!text || busy) return;
      var cid = convId;
      if (!cid) {
        // Conversations are created on first send — never empty shells.
        if (!props.ensureConversation) return;
        try { cid = await props.ensureConversation(); } catch (e) { setErr(e.message); return; }
        if (!cid) return;
      }
      setInput(''); setErr(null); setBusy(true);
      liveRef.current = { text: '', tools: [], status: null, userText: text };
      setLive(Object.assign({}, liveRef.current));
      var ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        var resp = await fetch(API_BASE + '/chat/conversations/' + cid + '/message', {
          method: 'POST', credentials: 'include', signal: ctrl.signal,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text: text, model: props.model }),
        });
        if (!resp.ok) {
          var eb = await resp.json().catch(function () { return {}; });
          throw new Error(eb.error || 'Request failed (' + resp.status + ')');
        }
        var reader = resp.body.getReader();
        var dec = new TextDecoder();
        var buf = '';
        while (true) {
          var chunk = await reader.read();
          if (chunk.done) break;
          buf += dec.decode(chunk.value, { stream: true });
          var idx;
          while ((idx = buf.indexOf('\n\n')) !== -1) {
            var frame = buf.slice(0, idx); buf = buf.slice(idx + 2);
            frame.split('\n').forEach(function (line) {
              if (!line.startsWith('data: ')) return;
              var ev; try { ev = JSON.parse(line.slice(6)); } catch (_) { return; }
              if (ev.type === 'text') updateLive(function (l) { l.text += ev.delta; l.status = null; return l; });
              else if (ev.type === 'status') updateLive(function (l) { l.status = ev.status; return l; });
              else if (ev.type === 'tool') updateLive(function (l) { l.tools.push({ name: ev.name, label: ev.label, running: true }); l.status = null; return l; });
              else if (ev.type === 'tool_done') updateLive(function (l) {
                for (var k = l.tools.length - 1; k >= 0; k--) if (l.tools[k].name === ev.name && l.tools[k].running) { l.tools[k].running = false; l.tools[k].summary = ev.summary; break; }
                return l;
              });
              else if (ev.type === 'usage') updateLive(function (l) { l.usage = ev; return l; });
              else if (ev.type === 'error') { setErr(ev.message); }
            });
          }
        }
      } catch (e) {
        if (e.name !== 'AbortError') setErr(e.message);
      } finally {
        abortRef.current = null;
        setBusy(false);
        liveRef.current = null;
        setLive(null);
        load(cid);
        if (props.onActivity) props.onActivity();
      }
    }

    function stop() { if (abortRef.current) abortRef.current.abort(); }

    var msgs = ((conv && conv.messages) || []).map(extractStored).filter(Boolean);
    var fs = 14; // 9-05: was 12 (widget) / 13 (page) — too small to read comfortably

    function bubble(m, i) {
      if (m.kind === 'user') {
        return h('div', { key: i, style: { display: 'flex', justifyContent: 'flex-end', margin: '8px 0' } },
          h('div', { style: { background: C.accentSoft, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 2px 12px', padding: '9px 13px', maxWidth: '82%', fontSize: fs, whiteSpace: 'pre-wrap' } }, m.text));
      }
      return h('div', { key: i, style: { margin: '8px 0' } },
        m.tools.length ? h('div', { style: { marginBottom: 4 } }, m.tools.map(toolChip)) : null,
        m.text ? h('div', { style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 12px 2px', padding: '10px 13px', maxWidth: '95%', fontSize: fs, lineHeight: 1.55 } }, mdRender(m.text)) : null);
    }

    return h('div', { style: { display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 } },
      h('div', { ref: scrollRef, style: { flex: 1, overflowY: 'auto', padding: compact ? '10px 12px' : '4px 4px 12px', minHeight: 0 } },
        convId && !conv ? h('div', { style: { color: C.textDim, fontSize: 14, textAlign: 'center', marginTop: 40 } }, 'Loading…') :
        msgs.length === 0 && !live ? h('div', { style: { color: C.textDim, fontSize: 14, textAlign: 'center', marginTop: 40 } },
          'Ask about sales, inventory, purchasing, shipments…', h('br'), h('span', { style: { fontSize: 12.5 } }, 'e.g. "top 10 overstocked SKUs by tied-up value"')) : null,
        msgs.map(bubble),
        live ? [
          live.userText ? bubble({ kind: 'user', text: live.userText }, 'lu') : null,
          h('div', { key: 'la', style: { margin: '8px 0' } },
            live.tools.length ? h('div', { style: { marginBottom: 4 } }, live.tools.map(toolChip)) : null,
            (live.text || live.status) ? h('div', { style: { background: C.surface, border: '1px solid ' + C.border, color: C.text, borderRadius: '12px 12px 12px 2px', padding: '10px 13px', maxWidth: '95%', fontSize: fs, lineHeight: 1.55 } },
              live.text ? mdRender(live.text) : null,
              !live.text && live.status === 'thinking' ? h('span', { style: { color: C.textDim, fontStyle: 'italic', fontSize: 13 } }, 'Thinking…') : null,
              busy ? h('span', { style: { display: 'inline-block', width: 7, height: 13, background: C.accent, marginLeft: 3, verticalAlign: 'text-bottom', opacity: 0.8 } }) : null) : null,
            live.usage ? h('div', { style: { color: C.textDim, fontSize: 11.5, marginTop: 4 } },
              '$' + Number(live.usage.cost).toFixed(3) + ' · ' + Math.round((live.usage.input_tokens + live.usage.cache_read_input_tokens + live.usage.cache_creation_input_tokens) / 1000) + 'k in / ' + Math.round(live.usage.output_tokens / 1000 * 10) / 10 + 'k out' + (live.usage.tool_calls ? ' · ' + live.usage.tool_calls + ' tools' : '')) : null)
        ] : null,
        err ? h('div', { style: { color: C.danger, fontSize: 13, margin: '8px 0' } }, err) : null),
      h('div', { style: { borderTop: '1px solid ' + C.border, padding: compact ? '8px 10px' : '10px 4px 0', display: 'flex', gap: 8, alignItems: 'flex-end' } },
        h('textarea', {
          value: input, rows: compact ? 3 : 2,
          placeholder: busy ? 'Answering…' : 'Ask a question… (Enter to send)',
          onChange: function (ev) { setInput(ev.target.value); },
          onKeyDown: function (ev) { if (ev.key === 'Enter' && !ev.shiftKey) { ev.preventDefault(); sendMessage(); } },
          style: { flex: 1, resize: 'none', background: C.surface, color: C.text, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 12px', fontSize: fs, fontFamily: 'inherit', outline: 'none' },
        }),
        busy
          ? h('button', { onClick: stop, style: { background: C.dangerSoft, color: C.danger, border: '1px solid ' + C.border, borderRadius: 10, padding: '10px 14px', cursor: 'pointer', fontSize: 13.5, fontFamily: 'inherit' } }, 'Stop')
          : (function () {
              var canSend = !!input.trim() && (!!convId || !!props.ensureConversation);
              return h('button', { onClick: sendMessage, disabled: !canSend, style: { background: canSend ? C.accent : C.surfaceHover, color: canSend ? '#fff' : C.textDim, border: 'none', borderRadius: 10, padding: '10px 14px', cursor: canSend ? 'pointer' : 'default', fontSize: 12, display: 'flex', alignItems: 'center', gap: 5 } },
                h(LIcon, { name: 'send', size: 16, color: canSend ? '#fff' : C.textDim }), compact ? null : 'Send');
            })()));
  }

  // ─── Model picker (Opus / Haiku) ───────────────────────────────────────────
  function ModelPicker(props) {
    return h('select', {
      value: props.value,
      onChange: function (ev) { props.onChange(ev.target.value); },
      style: { background: C.surface, color: C.textMuted, border: '1px solid ' + C.border, borderRadius: 8, fontSize: 12.5, padding: '5px 8px', outline: 'none', fontFamily: 'inherit' },
    },
      h('option', { value: 'fable' }, 'Fable 5.1 (most capable)'),
      h('option', { value: 'opus' }, 'Opus 5 (smart)'),
      h('option', { value: 'haiku' }, 'Haiku (fast/cheap)'));
  }
  function modelLabel(m) { return m === 'haiku' ? 'Haiku' : m === 'fable' ? 'Fable 5.1' : 'Opus'; }

  // ─── Full page ─────────────────────────────────────────────────────────────
  function ChatPage(props) {
    var _c = useState([]); var convs = _c[0], setConvs = _c[1];
    var _sel = useState(null); var selId = _sel[0], setSelId = _sel[1];
    var _mdl = useState('opus'); var model = _mdl[0], setModel = _mdl[1];

    function loadConvs(selectFirst) {
      api('/chat/conversations').then(function (rows) {
        setConvs(rows);
        if (selectFirst && rows.length && !selId) setSelId(rows[0].id);
      }).catch(function () {});
    }
    useEffect(function () { loadConvs(true); }, []);
    useEffect(function () {
      var cur = convs.find(function (c) { return c.id === selId; });
      if (cur) setModel(cur.model || 'opus');
    }, [selId]);

    // Conversations are created on first send (never empty shells) — "+ New"
    // just clears the selection so the composer targets a fresh chat.
    function newChat() { setSelId(null); }
    async function ensureConversation() {
      var c = await api('/chat/conversations', { method: 'POST', body: JSON.stringify({ model: model }) });
      setConvs(function (prev) { return [c].concat(prev); });
      setSelId(c.id);
      return c.id;
    }
    async function delChat(id, ev) {
      ev.stopPropagation();
      if (!window.confirm('Delete this conversation?')) return;
      await api('/chat/conversations/' + id, { method: 'DELETE' });
      if (selId === id) setSelId(null);
      loadConvs(false);
    }
    function changeModel(m) {
      setModel(m);
      if (selId) api('/chat/conversations/' + selId, { method: 'PATCH', body: JSON.stringify({ model: m }) }).catch(function () {});
    }

    var totalCost = convs.reduce(function (s, c) { return s + (parseFloat(c.total_cost) || 0); }, 0);

    return h('div', { style: { display: 'flex', gap: 16, height: 'calc(100vh - 110px)', minHeight: 480 } },
      // Conversation list
      h('div', { style: { width: 250, flexShrink: 0, display: 'flex', flexDirection: 'column', background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, overflow: 'hidden' } },
        h('div', { style: { padding: '10px 12px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', justifyContent: 'space-between' } },
          h('div', { style: { fontWeight: 700, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 } }, h(LIcon, { name: 'sparkles', size: 15, color: C.accent }), 'Ask AI'),
          h('button', { onClick: newChat, style: { background: C.accent, color: '#fff', border: 'none', borderRadius: 8, padding: '4px 9px', fontSize: 11.5, cursor: 'pointer' } }, '+ New')),
        h('div', { style: { flex: 1, overflowY: 'auto' } },
          convs.map(function (c) {
            var sel = c.id === selId;
            return h('div', {
              key: c.id, onClick: function () { setSelId(c.id); },
              style: { padding: '8px 12px', cursor: 'pointer', background: sel ? C.accentSoft : 'transparent', borderLeft: '3px solid ' + (sel ? C.accent : 'transparent'), position: 'relative' },
            },
              h('div', { style: { fontSize: 12, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', paddingRight: 18 } }, c.title || 'New chat'),
              h('div', { style: { fontSize: 10, color: C.textDim, marginTop: 2 } },
                modelLabel(c.model) + ' · ' + c.message_count + ' msgs' + (parseFloat(c.total_cost) ? ' · $' + parseFloat(c.total_cost).toFixed(2) : '')),
              h('span', { onClick: function (ev) { delChat(c.id, ev); }, style: { position: 'absolute', right: 8, top: 9, opacity: 0.55, cursor: 'pointer' } }, h(LIcon, { name: 'x', size: 12, color: C.textMuted })));
          })),
        totalCost > 0 ? h('div', { style: { padding: '7px 12px', borderTop: '1px solid ' + C.border, fontSize: 10.5, color: C.textDim } }, 'Total spend: $' + totalCost.toFixed(2)) : null),
      // Thread
      h('div', { style: { flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: C.surface, border: '1px solid ' + C.border, borderRadius: 12, padding: '12px 16px' } },
        h('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 8, borderBottom: '1px solid ' + C.border } },
          h('div', { style: { fontSize: 12.5, color: C.textMuted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } },
            (convs.find(function (c) { return c.id === selId; }) || {}).title || 'Ask questions over live app data'),
          h(ModelPicker, { value: model, onChange: changeModel })),
        h(ChatThread, { conversationId: selId, model: model, ensureConversation: ensureConversation, onActivity: function () { loadConvs(false); } })));
  }

  // ─── Floating widget ───────────────────────────────────────────────────────
  function ChatWidget(props) {
    var _o = useState(false); var open = _o[0], setOpen = _o[1];
    var _c = useState(null); var convId = _c[0], setConvId = _c[1];
    var _mdl = useState('opus'); var model = _mdl[0], setModel = _mdl[1];
    var _v = useState('thread'); var view = _v[0], setView = _v[1]; // 'thread' | 'list'
    var _h = useState([]); var hist = _h[0], setHist = _h[1];
    var restoredRef = useRef(false);

    function toggle() {
      var next = !open;
      setOpen(next);
      if (next && !restoredRef.current) {
        restoredRef.current = true;
        var saved = parseInt(localStorage.getItem('chat_widget_conv') || '', 10);
        if (saved) api('/chat/conversations/' + saved)
          .then(function (c) { setConvId(c.id); setModel(c.model || 'opus'); })
          .catch(function () { localStorage.removeItem('chat_widget_conv'); });
      }
    }
    async function ensureConversation() {
      var nc = await api('/chat/conversations', { method: 'POST', body: JSON.stringify({ model: model }) });
      localStorage.setItem('chat_widget_conv', String(nc.id));
      setConvId(nc.id);
      return nc.id;
    }
    function fresh() { setConvId(null); localStorage.removeItem('chat_widget_conv'); setView('thread'); }
    function openHistory() {
      setView('list');
      api('/chat/conversations').then(setHist).catch(function () { setHist([]); });
    }
    function pick(c) {
      setConvId(c.id); setModel(c.model || 'opus');
      localStorage.setItem('chat_widget_conv', String(c.id));
      setView('thread');
    }
    function changeModel(m) {
      setModel(m);
      if (convId) api('/chat/conversations/' + convId, { method: 'PATCH', body: JSON.stringify({ model: m }) }).catch(function () {});
    }

    var header = view === 'list'
      ? h('div', { style: { padding: '11px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
          h('span', { onClick: function () { setView('thread'); }, title: 'Back', style: { cursor: 'pointer', display: 'flex' } }, h(LIcon, { name: 'chevron-left', size: 18, color: C.textMuted })),
          h('div', { style: { fontWeight: 700, fontSize: 14.5, flex: 1 } }, 'Conversations'),
          h('span', { onClick: fresh, title: 'New conversation', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'plus', size: 18, color: C.textMuted })),
          h('span', { onClick: toggle, style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'x', size: 18, color: C.textMuted })))
      : h('div', { style: { padding: '11px 14px', borderBottom: '1px solid ' + C.border, display: 'flex', alignItems: 'center', gap: 10 } },
          h('span', { onClick: openHistory, title: 'All conversations', style: { cursor: 'pointer', display: 'flex' } }, h(LIcon, { name: 'history', size: 18, color: C.textMuted })),
          h(LIcon, { name: 'sparkles', size: 18, color: C.accent }),
          h('div', { style: { fontWeight: 700, fontSize: 14.5, flex: 1 } }, 'Ask AI'),
          h(ModelPicker, { value: model, onChange: changeModel }),
          h('span', { onClick: fresh, title: 'New conversation', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'plus', size: 18, color: C.textMuted })),
          props.onOpenFull ? h('span', { onClick: function () { setOpen(false); props.onOpenFull(); }, title: 'Open full page', style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'maximize-2', size: 14, color: C.textMuted })) : null,
          h('span', { onClick: toggle, style: { cursor: 'pointer', opacity: 0.7, display: 'flex' } }, h(LIcon, { name: 'x', size: 18, color: C.textMuted })));

    var listBody = h('div', { style: { flex: 1, overflowY: 'auto', minHeight: 0 } },
      hist.length === 0 ? h('div', { style: { color: C.textDim, fontSize: 13.5, textAlign: 'center', marginTop: 30 } }, 'No conversations yet') : null,
      hist.map(function (c) {
        return h('div', { key: c.id, onClick: function () { pick(c); }, style: { padding: '11px 14px', cursor: 'pointer', borderBottom: '1px solid ' + C.border, background: c.id === convId ? C.accentSoft : 'transparent' } },
          h('div', { style: { fontSize: 13.5, color: C.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } }, c.title || 'New chat'),
          h('div', { style: { fontSize: 11.5, color: C.textDim, marginTop: 3 } },
            modelLabel(c.model) + ' · ' + c.message_count + ' msgs · ' + new Date(c.updated_at).toLocaleDateString() + (parseFloat(c.total_cost) ? ' · $' + parseFloat(c.total_cost).toFixed(2) : '')));
      }));

    return h(React.Fragment, null,
      open ? h('div', { style: { position: 'fixed', bottom: 92, right: 20, width: 470, height: 'min(720px, 82vh)', background: C.surface, border: '1px solid ' + C.border, borderRadius: 14, boxShadow: '0 12px 40px rgba(0,0,0,0.45)', zIndex: 940, display: 'flex', flexDirection: 'column', overflow: 'hidden' } },
        header,
        view === 'list' ? listBody :
        h('div', { style: { flex: 1, minHeight: 0 } },
          h(ChatThread, { conversationId: convId, model: model, compact: true, ensureConversation: ensureConversation }))) : null,
      h('button', {
        onClick: toggle, title: 'Ask AI',
        style: { position: 'fixed', bottom: 20, right: 20, width: 54, height: 54, borderRadius: 27, background: C.accent, border: 'none', cursor: 'pointer', zIndex: 940, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 6px 20px rgba(79,110,247,0.45)' } },
        h(LIcon, { name: open ? 'chevron-down' : 'sparkles', size: 24, color: '#fff' })));
  }

  window.ChatPage = ChatPage;
  window.ChatWidget = ChatWidget;
})();

;
/* ===== app-advertising.js ===== */
// ─── Advertising subdomain app (ads.atharvatoolz.com) ───
// Served by ads.html as its own bundle (dist/ads.bundle.js = app-utils.js + this file —
// see scripts/build-client.js BUILDS). Login rides the .atharvatoolz.com-scoped session
// cookie set by the main app. Design of record: docs/14-advertising.md.
//
// Phase 2: dashboard (KPIs + trend + top campaigns), campaign explorer (list + detail
// with ad groups/ads/keywords/targets/search terms/negatives), hand-rolled pushState
// routing (/ , /campaigns , /campaign/:id). Display metrics use the 7-day attribution
// columns per docs/14. Review queue (P3) and AI insights (P4) mount here later.

var MAIN_APP_URL = 'https://app.atharvatoolz.com';

// ─── Formatters (repo convention: inline helpers, no global module) ───
function adsMoney(v, currency) {
  if (v == null) return '—';
  var n = Number(v);
  var prefix = currency === 'CAD' ? 'C$' : '$';
  if (Math.abs(n) >= 10000) return prefix + n.toLocaleString(undefined, { maximumFractionDigits: 0 });
  return prefix + n.toFixed(2);
}
function adsPct(v) { return v == null ? '—' : Number(v).toFixed(1) + '%'; }
function adsNum(v) { return v == null ? '—' : Number(v).toLocaleString(); }
function adsStateBadge(state) {
  var s = String(state || '').toLowerCase();
  var color = s === 'enabled' ? C.success : s === 'paused' ? C.warning : C.textDim;
  return React.createElement('span', { style: { fontSize: 11, fontWeight: 600, color: color, textTransform: 'uppercase', letterSpacing: 0.4 } }, s || '—');
}

// Chart wrapper — canvasRef/chartRef destroy-recreate idiom; NO devicePixelRatio or
// offset math (the global zoom shim in app-utils.js handles both).
function AdsChart(props) {
  var canvasRef = useRef(null);
  var chartRef = useRef(null);
  useEffect(function() {
    if (!canvasRef.current || !props.config) return;
    if (chartRef.current) chartRef.current.destroy();
    chartRef.current = new Chart(canvasRef.current.getContext('2d'), props.config);
    return function() { if (chartRef.current) { chartRef.current.destroy(); chartRef.current = null; } };
  }, [props.config]);
  return React.createElement('div', { style: { height: props.height || 260 } },
    React.createElement('canvas', { ref: canvasRef, style: { width: '100%', maxHeight: props.height || 260 } }));
}

function adsTrendConfig(days, currency) {
  return {
    type: 'bar',
    data: {
      labels: days.map(function(d) { return d.date.slice(5); }),
      datasets: [
        { type: 'bar', label: 'Spend', data: days.map(function(d) { return d.cost; }), backgroundColor: C.accent + '99', borderRadius: 3, yAxisID: 'y' },
        { type: 'line', label: 'Ad Sales', data: days.map(function(d) { return d.sales; }), borderColor: C.success, backgroundColor: C.success, tension: 0.3, pointRadius: 0, borderWidth: 2, yAxisID: 'y' },
        { type: 'line', label: 'ACoS %', data: days.map(function(d) { return d.sales > 0 ? (d.cost / d.sales) * 100 : null; }), borderColor: C.warning, backgroundColor: C.warning, borderDash: [5, 4], tension: 0.3, pointRadius: 0, borderWidth: 1.5, yAxisID: 'y1' },
      ],
    },
    options: {
      responsive: true, maintainAspectRatio: false, interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { labels: { color: C.textMuted, font: { family: 'DM Sans', size: 11 }, boxWidth: 12, padding: 14 } },
        tooltip: {
          backgroundColor: C.surface, titleColor: C.text, bodyColor: C.textMuted, borderColor: C.border, borderWidth: 1,
          callbacks: { label: function(ctx) {
            if (ctx.dataset.label === 'ACoS %') return ' ACoS: ' + (ctx.parsed.y != null ? ctx.parsed.y.toFixed(1) + '%' : '—');
            return ' ' + ctx.dataset.label + ': ' + adsMoney(ctx.parsed.y, currency);
          } },
        },
      },
      scales: {
        x: { ticks: { color: C.textDim, font: { size: 10 }, maxTicksLimit: 16 }, grid: { display: false } },
        y: { beginAtZero: true, ticks: { color: C.textDim, font: { size: 10 }, callback: function(v) { return adsMoney(v, currency); } }, grid: { color: C.border + '33' } },
        y1: { position: 'right', beginAtZero: true, ticks: { color: C.warning, font: { size: 10 }, callback: function(v) { return v + '%'; } }, grid: { display: false } },
      },
    },
  };
}

// ─── Shared controls (styling mirrors the main app exactly) ───
var ADS_PERIODS = [7, 14, 30, 60, 90];

// Range-style button (main DashboardPage period buttons)
function adsRangeBtn(label, active, onClick, key) {
  return React.createElement('button', { key: key || label, onClick: onClick,
    style: { padding: '6px 12px', fontSize: 12, borderRadius: 6, border: '1px solid ' + (active ? C.accent + '40' : C.border),
      background: active ? C.accent + '20' : C.surface, color: active ? C.accent : C.textMuted,
      cursor: 'pointer', fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s' } }, label);
}
// Filter-pill button (main PO-list status tabs; .filter-pill hover CSS comes from app-utils)
function adsFilterPill(label, active, onClick, key) {
  return React.createElement('button', { key: key || label, onClick: onClick,
    className: active ? '' : 'filter-pill',
    style: { padding: '6px 14px', fontSize: 12, borderRadius: 6, border: '1px solid transparent', cursor: 'pointer',
      fontWeight: 500, fontFamily: 'inherit', transition: 'all 0.15s',
      background: active ? C.accent : C.surface, color: active ? '#fff' : C.textMuted } }, label);
}

function AdsControls(props) {
  // Channel selection lives in the sidebar — the header only carries the date window
  return React.createElement('div', { style: { display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' } },
    ADS_PERIODS.map(function(p) { return adsRangeBtn(p + 'd', props.days === p, function() { props.setDays(p); }, 'd-' + p); })
  );
}

// Calendar shift of a YYYY-MM-DD string (UTC anchor both ways — zone-free)
function adsShiftDay(ymd, n) {
  var d = new Date(ymd + 'T00:00:00Z'); d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}
// Window ends YESTERDAY in America/New_York (the business day) — a UTC "yesterday" is
// already today in ET after 8 PM and would pull a partial day.
function adsRangeQS(mp, days, now) {
  var todayEt = (now || new Date()).toLocaleDateString('en-CA', { timeZone: 'America/New_York' });
  var end = adsShiftDay(todayEt, -1);
  return 'marketplace=' + mp + '&start=' + adsShiftDay(end, -(days - 1)) + '&end=' + end;
}

// Load error card with Retry (never an empty table or an endless spinner on failure)
function AdsLoadError(props) {
  return React.createElement(Card, { style: { padding: '14px 16px', border: '1px solid ' + C.danger + '55', background: C.danger + '0d' } },
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' } },
      React.createElement(LIcon, { name: 'alert-triangle', size: 16, style: { color: C.danger } }),
      React.createElement('div', { style: { flex: 1, minWidth: 200, fontSize: 13, color: C.text } },
        (props.title || 'Could Not Load') + ': ',
        React.createElement('span', { style: { color: C.textMuted } }, props.message || 'Unknown error')),
      props.onRetry && React.createElement(Btn, { size: 'sm', onClick: props.onRetry }, 'Retry')));
}

// Fetch hook with a request sequence guard: only the LATEST request for the current
// deps may set state, so a slow response for an old channel/window never overwrites
// the new one. Returns [data, error, reload].
function useAdsFetch(path, deps) {
  var _d = useState(null); var data = _d[0], setData = _d[1];
  var _e = useState(null); var err = _e[0], setErr = _e[1];
  var _n = useState(0); var nonce = _n[0], setNonce = _n[1];
  var seqRef = useRef(0);
  useEffect(function() {
    var seq = ++seqRef.current;
    setData(null); setErr(null);
    api(path).then(function(r) { if (seq === seqRef.current) setData(r); })
      .catch(function(e) { if (seq === seqRef.current) setErr((e && e.message) || 'Request failed'); });
  }, deps.concat([nonce]));
  return [data, err, function() { setNonce(function(n) { return n + 1; }); }];
}

// Stat card + grid wrapper (main DashboardPage summary-card styling: uppercase label,
// bold value, auto-fit grid)
function KpiCard(props) {
  return React.createElement('div', { style: { background: C.surface, border: '1px solid ' + C.border, borderRadius: 8, padding: 16 } },
    React.createElement('div', { style: { fontSize: 11, color: C.textMuted, marginBottom: 4, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.05em', whiteSpace: 'nowrap' } }, props.label),
    React.createElement('div', { style: { fontSize: 20, fontWeight: 700, color: props.color || C.text, fontFamily: 'inherit', whiteSpace: 'nowrap' } }, props.value)
  );
}
function KpiGrid(props) {
  return React.createElement('div', { style: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 12, marginBottom: 20 } }, props.children);
}

// ─── Dashboard ───
function AdsDashboardPage(props) {
  var _f = useAdsFetch('/advertising/summary?' + adsRangeQS(props.mp, props.days), [props.mp, props.days]);
  var data = _f[0], err = _f[1], reload = _f[2];
  // Hook order must be stable — memo lives ABOVE the early returns (theme in deps: recolor on toggle)
  var chartConfig = useMemo(function() { return data ? adsTrendConfig(data.days, data.currency) : null; }, [data, props.theme]);

  if (err) return React.createElement(AdsLoadError, { title: 'Could Not Load the Dashboard', message: err, onRetry: reload });
  if (!data) return React.createElement('div', { style: { padding: 40, textAlign: 'center' } }, React.createElement(LIcon, { name: 'loader-2', size: 22, spin: true, style: { color: C.textMuted } }));

  var t = data.totals, cur = data.currency;
  var noData = !t.impressions && !t.cost && !t.sales;

  return React.createElement('div', null,
    noData && React.createElement(Card, { style: { padding: '12px 16px', marginBottom: 14, border: '1px solid ' + C.warning + '55', background: C.warning + '0d' } },
      React.createElement('div', { style: { fontSize: 12.5, color: C.textMuted, display: 'flex', alignItems: 'center', gap: 8 } },
        React.createElement(LIcon, { name: 'info', size: 15, style: { color: C.warning } }),
        'No ads data for this window yet. Once Amazon Ads API access is approved: connect + enable Data Sync in the main app (Settings > Integrations > Amazon Advertising) — the first sync backfills ~90 days.')),
    React.createElement(KpiGrid, null,
      KpiCard({ label: 'Spend', value: adsMoney(t.cost, cur) }),
      KpiCard({ label: 'Ad Sales', value: adsMoney(t.sales, cur), color: C.success }),
      KpiCard({ label: 'ACoS', value: adsPct(t.acos), color: t.acos != null && t.acos > 40 ? C.danger : undefined }),
      KpiCard({ label: 'TACoS', value: adsPct(t.tacos) }),
      KpiCard({ label: 'Total Revenue', value: adsMoney(t.revenue_display != null ? t.revenue_display : t.revenue, t.revenue_display != null ? cur : 'USD'), color: C.accent }),
      KpiCard({ label: 'Ad Orders', value: adsNum(t.orders) }),
      KpiCard({ label: 'Clicks', value: adsNum(t.clicks) }),
      KpiCard({ label: 'CTR', value: t.ctr != null ? Number(t.ctr).toFixed(2) + '%' : '—' }),
      KpiCard({ label: 'CPC', value: adsMoney(t.cpc, cur) })
    ),
    React.createElement(Card, { style: { padding: 16, marginBottom: 14 } },
      React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 } }, 'Daily Spend · Ad Sales · ACoS'),
      React.createElement(AdsChart, { config: chartConfig, height: 280 })),
    React.createElement(Card, { style: { padding: 16 } },
      React.createElement('div', { style: { fontSize: 13, fontWeight: 600, color: C.text, marginBottom: 10 } }, 'Top Campaigns by Spend'),
      (data.top_campaigns || []).length === 0
        ? React.createElement('div', { style: { fontSize: 12.5, color: C.textMuted } }, 'Nothing spent in this window.')
        : React.createElement('table', { style: { width: '100%', fontSize: 12.5 } },
            React.createElement('thead', null, React.createElement('tr', { style: { color: C.textMuted, textAlign: 'left' } },
              ['Campaign', 'State', 'Spend', 'Sales', 'ACoS', 'Orders', 'Clicks'].map(function(h, i) {
                return React.createElement('th', { key: h, style: { padding: '6px 8px', textAlign: i >= 2 ? 'right' : 'left', fontWeight: 500 } }, h);
              }))),
            React.createElement('tbody', null, data.top_campaigns.map(function(r) {
              return React.createElement('tr', {
                key: r.id, onClick: function() { props.onNavigate('campaign-detail', r.id); },
                style: { cursor: 'pointer', borderTop: '1px solid ' + C.border + '55' },
                onMouseEnter: function(e) { e.currentTarget.style.background = C.surfaceHover || C.bg; },
                onMouseLeave: function(e) { e.currentTarget.style.background = 'transparent'; }
              },
                React.createElement('td', { style: { padding: '7px 8px', color: C.accent } },
                  React.createElement('a', { href: '/' + props.chSlug + '/campaign/' + r.id, onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) { e.stopPropagation(); return; } e.preventDefault(); }, style: { color: C.accent } }, r.name || ('#' + r.id))),
                React.createElement('td', { style: { padding: '7px 8px' } }, adsStateBadge(r.state)),
                React.createElement('td', { style: { padding: '7px 8px', textAlign: 'right' } }, adsMoney(r.cost, cur)),
                React.createElement('td', { style: { padding: '7px 8px', textAlign: 'right' } }, adsMoney(r.sales, cur)),
                React.createElement('td', { style: { padding: '7px 8px', textAlign: 'right' } }, adsPct(r.acos)),
                React.createElement('td', { style: { padding: '7px 8px', textAlign: 'right' } }, adsNum(r.orders)),
                React.createElement('td', { style: { padding: '7px 8px', textAlign: 'right' } }, adsNum(r.clicks))
              );
            })))
    )
  );
}

// ─── Campaigns list ───
var ADS_CAMPAIGN_TABS = ['all', 'enabled', 'paused', 'archived'];

function AdsCampaignsPage(props) {
  var _f = useAdsFetch('/advertising/campaigns?' + adsRangeQS(props.mp, props.days), [props.mp, props.days]);
  var data = _f[0], err = _f[1], reload = _f[2];
  var _t = useState('all'); var tab = _t[0], setTab = _t[1];

  if (err) return React.createElement(AdsLoadError, { title: 'Could Not Load Campaigns', message: err, onRetry: reload });
  if (!data) return React.createElement('div', { style: { padding: 40, textAlign: 'center' } }, React.createElement(LIcon, { name: 'loader-2', size: 22, spin: true, style: { color: C.textMuted } }));

  var cur = props.mp === 'CA' ? 'CAD' : 'USD';
  var rows = data.rows || [];
  var counts = { all: rows.length };
  ADS_CAMPAIGN_TABS.slice(1).forEach(function(s) { counts[s] = rows.filter(function(r) { return r.state === s; }).length; });
  var filtered = tab === 'all' ? rows : rows.filter(function(r) { return r.state === tab; });

  var cols = [
    { key: 'name', label: 'Campaign', locked: true, render: function(r) { return React.createElement('span', { style: { color: C.accent, fontWeight: 500 } }, r.name || ('#' + r.id)); } },
    { key: 'state', label: 'State', render: function(r) { return adsStateBadge(r.state); }, filterOptions: ['enabled', 'paused', 'archived'] },
    { key: 'targeting_type', label: 'Type', render: function(r) { return r.targeting_type || '—'; } },
    { key: 'budget_amount', label: 'Budget', style: { textAlign: 'right' }, render: function(r) { return r.budget_amount != null ? adsMoney(r.budget_amount, cur) + '/day' : '—'; } },
    { key: 'cost', label: 'Spend', style: { textAlign: 'right' }, render: function(r) { return adsMoney(r.cost, cur); } },
    { key: 'sales', label: 'Sales', style: { textAlign: 'right' }, render: function(r) { return adsMoney(r.sales, cur); } },
    { key: 'acos', label: 'ACoS', style: { textAlign: 'right' }, render: function(r) { return adsPct(r.acos); } },
    { key: 'orders', label: 'Orders', style: { textAlign: 'right' }, render: function(r) { return adsNum(r.orders); } },
    { key: 'clicks', label: 'Clicks', style: { textAlign: 'right' }, render: function(r) { return adsNum(r.clicks); } },
    { key: 'impressions', label: 'Impressions', style: { textAlign: 'right' }, render: function(r) { return adsNum(r.impressions); } },
    { key: 'ctr', label: 'CTR', style: { textAlign: 'right' }, defaultVisible: false, render: function(r) { return r.ctr != null ? Number(r.ctr).toFixed(2) + '%' : '—'; } },
    { key: 'cpc', label: 'CPC', style: { textAlign: 'right' }, defaultVisible: false, render: function(r) { return adsMoney(r.cpc, cur); } },
  ];

  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      ADS_CAMPAIGN_TABS.map(function(s) {
        var label = (s.charAt(0).toUpperCase() + s.slice(1)) + ' (' + (counts[s] || 0) + ')';
        return adsFilterPill(label, tab === s, function() { setTab(s); }, s);
      })),
    React.createElement(DataTable, {
      columns: cols,
      data: filtered,
      storageKey: 'ads_campaigns_' + tab,
      defaultSort: 'cost', defaultSortDir: 'desc',
      onRowClick: function(r) { props.onNavigate('campaign-detail', r.id); },
      rowHref: function(r) { return '/' + props.chSlug + '/campaign/' + r.id; },
      emptyTitle: 'No Campaigns',
      emptySub: rows.length === 0 ? 'Nothing synced yet — enable Data Sync in the main app Settings once API access lands.' : 'No campaigns in this state.',
    })
  );
}

// ─── Campaign detail ───
var ADS_CHILD_TABS = [
  { key: 'ad_groups', label: 'Ad Groups' },
  { key: 'ads', label: 'Ads' },
  { key: 'keywords', label: 'Keywords' },
  { key: 'targets', label: 'Targets' },
  { key: 'search_terms', label: 'Search Terms' },
  { key: 'negatives', label: 'Negatives' },
];

function AdsCampaignDetailPage(props) {
  var _f = useAdsFetch('/advertising/campaigns/' + props.campaignId + '?' + adsRangeQS(props.mp, props.days),
    [props.campaignId, props.mp, props.days]);
  var data = _f[0], err = _f[1], reload = _f[2];
  var _t = useState('ad_groups'); var tab = _t[0], setTab = _t[1];
  var _c = useState({}); var children = _c[0], setChildren = _c[1]; // { tabKey: rows | 'loading' | {error} }
  // Generation of the child-tab cache: bumps whenever the window/campaign changes so a
  // late child response for the OLD window is dropped instead of cached under the new one.
  var childGenRef = useRef(0);

  useEffect(function() {
    childGenRef.current++;
    setChildren({});
  }, [props.campaignId, props.mp, props.days]);

  var loadChild = function(key) {
    var gen = childGenRef.current;
    setChildren(function(prev) { var n = Object.assign({}, prev); n[key] = 'loading'; return n; });
    api('/advertising/campaigns/' + props.campaignId + '/children?type=' + key + '&' + adsRangeQS(props.mp, props.days))
      .then(function(r) { if (gen !== childGenRef.current) return; setChildren(function(prev) { var n = Object.assign({}, prev); n[key] = (r && r.rows) || []; return n; }); })
      .catch(function(e) { if (gen !== childGenRef.current) return; setChildren(function(prev) { var n = Object.assign({}, prev); n[key] = { error: (e && e.message) || 'Request failed' }; return n; }); });
  };

  useEffect(function() {
    if (!data || children[tab]) return;
    loadChild(tab);
  }, [tab, data, children]);

  // Hook order must be stable — memo lives ABOVE the early returns (theme in deps: recolor on toggle)
  var chartConfig = useMemo(function() {
    return data ? adsTrendConfig(data.days, data.campaign.marketplace === 'CA' ? 'CAD' : 'USD') : null;
  }, [data, props.theme]);

  if (err) return React.createElement(AdsLoadError, { title: 'Could Not Load the Campaign', message: err, onRetry: reload });
  if (!data) return React.createElement('div', { style: { padding: 40, textAlign: 'center' } }, React.createElement(LIcon, { name: 'loader-2', size: 22, spin: true, style: { color: C.textMuted } }));

  var camp = data.campaign, t = data.totals;
  var cur = camp.marketplace === 'CA' ? 'CAD' : 'USD';

  function childCols(type) {
    var metric = [
      { key: 'cost', label: 'Spend', style: { textAlign: 'right' }, render: function(r) { return adsMoney(r.cost, cur); } },
      { key: 'sales', label: 'Sales', style: { textAlign: 'right' }, render: function(r) { return adsMoney(r.sales, cur); } },
      { key: 'acos', label: 'ACoS', style: { textAlign: 'right' }, render: function(r) { return adsPct(r.acos); } },
      { key: 'orders', label: 'Orders', style: { textAlign: 'right' }, render: function(r) { return adsNum(r.orders); } },
      { key: 'clicks', label: 'Clicks', style: { textAlign: 'right' }, render: function(r) { return adsNum(r.clicks); } },
      { key: 'impressions', label: 'Impr.', style: { textAlign: 'right' }, render: function(r) { return adsNum(r.impressions); } },
    ];
    if (type === 'ad_groups') return [
      { key: 'name', label: 'Ad Group', locked: true },
      { key: 'state', label: 'State', render: function(r) { return adsStateBadge(r.state); } },
      { key: 'default_bid', label: 'Default Bid', style: { textAlign: 'right' }, render: function(r) { return r.default_bid != null ? adsMoney(r.default_bid, cur) : '—'; } },
    ].concat(metric);
    if (type === 'ads') return [
      { key: 'sku', label: 'SKU', locked: true, render: function(r) {
          return r.product_id
            ? React.createElement('a', { href: MAIN_APP_URL + '/catalog/' + r.product_id + '?tab=advertising', target: '_blank', rel: 'noopener', style: { color: C.accent } }, r.sku || '—')
            : (r.sku || '—');
        } },
      { key: 'asin', label: 'ASIN', render: function(r) { return React.createElement(CopyCell, { value: r.asin }, r.asin || '—'); } },
      { key: 'state', label: 'State', render: function(r) { return adsStateBadge(r.state); } },
    ].concat(metric);
    if (type === 'keywords') return [
      { key: 'keyword_text', label: 'Keyword', locked: true },
      { key: 'match_type', label: 'Match', render: function(r) { return (r.match_type || '—').toLowerCase(); } },
      { key: 'bid', label: 'Bid', style: { textAlign: 'right' }, render: function(r) { return r.bid != null ? adsMoney(r.bid, cur) : '—'; } },
      { key: 'state', label: 'State', render: function(r) { return adsStateBadge(r.state); } },
    ].concat(metric);
    if (type === 'targets') return [
      { key: 'resolved_expression', label: 'Target', locked: true, render: function(r) { return r.resolved_expression || (r.expression ? JSON.stringify(r.expression).slice(0, 60) : '—'); } },
      { key: 'expression_type', label: 'Type', render: function(r) { return (r.expression_type || '—').toLowerCase(); } },
      { key: 'bid', label: 'Bid', style: { textAlign: 'right' }, render: function(r) { return r.bid != null ? adsMoney(r.bid, cur) : '—'; } },
      { key: 'state', label: 'State', render: function(r) { return adsStateBadge(r.state); } },
    ].concat(metric);
    if (type === 'search_terms') return [
      { key: 'search_term', label: 'Search Term', locked: true, render: function(r) { return React.createElement(CopyCell, { value: r.search_term }, r.search_term); } },
      { key: 'targeting', label: 'Matched Target' },
      { key: 'match_type', label: 'Match', render: function(r) { return (r.match_type || '—').toLowerCase(); } },
    ].concat(metric);
    return [ // negatives
      { key: 'keyword_text', label: 'Negative', locked: true, render: function(r) { return r.keyword_text || (r.expression ? JSON.stringify(r.expression).slice(0, 60) : '—'); } },
      { key: 'match_type', label: 'Match', render: function(r) { return (r.match_type || '—').toLowerCase(); } },
      { key: 'level', label: 'Level' },
      { key: 'neg_type', label: 'Kind' },
      { key: 'state', label: 'State', render: function(r) { return adsStateBadge(r.state); } },
    ];
  }

  var body = children[tab];
  return React.createElement('div', null,
    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12, flexWrap: 'wrap' } },
      React.createElement('a', { href: '/' + props.chSlug + '/campaigns', onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey) return; e.preventDefault(); props.onNavigate('campaigns'); }, style: { color: C.textMuted, display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12.5 } },
        React.createElement(LIcon, { name: 'arrow-left', size: 14 }), 'Campaigns'),
      React.createElement('div', { style: { fontSize: 17, fontWeight: 700, color: C.text } }, camp.name || ('#' + camp.id)),
      adsStateBadge(camp.state),
      React.createElement('span', { style: { fontSize: 12, color: C.textMuted } },
        camp.marketplace + ' · ' + (camp.targeting_type || '?') + (camp.budget_amount != null ? ' · budget ' + adsMoney(camp.budget_amount, cur) + '/day' : ''))),
    React.createElement(KpiGrid, null,
      KpiCard({ label: 'Spend', value: adsMoney(t.cost, cur) }),
      KpiCard({ label: 'Sales', value: adsMoney(t.sales, cur), color: C.success }),
      KpiCard({ label: 'ACoS', value: adsPct(t.acos) }),
      KpiCard({ label: 'Orders', value: adsNum(t.orders) }),
      KpiCard({ label: 'Units', value: adsNum(t.units) }),
      KpiCard({ label: 'Clicks', value: adsNum(t.clicks) }),
      KpiCard({ label: 'CTR', value: t.ctr != null ? Number(t.ctr).toFixed(2) + '%' : '—' }),
      KpiCard({ label: 'CPC', value: adsMoney(t.cpc, cur) })),
    React.createElement(Card, { style: { padding: 16, marginBottom: 14 } },
      React.createElement(AdsChart, { config: chartConfig, height: 220 })),
    React.createElement('div', { style: { display: 'flex', gap: 6, marginBottom: 16, flexWrap: 'wrap' } },
      ADS_CHILD_TABS.map(function(ct) {
        return adsFilterPill(ct.label, tab === ct.key, function() { setTab(ct.key); }, ct.key);
      })),
    body === 'loading' || body === undefined
      ? React.createElement('div', { style: { padding: 30, textAlign: 'center' } }, React.createElement(LIcon, { name: 'loader-2', size: 20, spin: true, style: { color: C.textMuted } }))
      : body && body.error
        ? React.createElement(AdsLoadError, { title: 'Could Not Load ' + ((ADS_CHILD_TABS.find(function(ct) { return ct.key === tab; }) || {}).label || 'Rows'), message: body.error, onRetry: function() { loadChild(tab); } })
        : React.createElement(DataTable, {
            columns: childCols(tab),
            data: body,
            storageKey: 'ads_cmp_' + tab,
            defaultSort: tab === 'negatives' ? 'keyword_text' : 'cost',
            defaultSortDir: tab === 'negatives' ? 'asc' : 'desc',
            emptyTitle: 'Nothing Here',
            emptySub: 'No ' + tab.replace('_', ' ') + ' synced for this campaign.',
          })
  );
}

// ─── App shell: auth gate + channel sidebar + pushState router ───
// Every enabled channel (GET /api/advertising/channels; default Amazon US + CA) is a
// sidebar group with its own sub-pages, and routes are channel-scoped:
//   /us · /us/campaigns · /us/campaign/:id · /ca · ...
var ADS_DEFAULT_CHANNELS = [
  { key: 'amazon_us', slug: 'us', platform: 'amazon', marketplace: 'US', label: 'Amazon US', currency: 'USD' },
  { key: 'amazon_ca', slug: 'ca', platform: 'amazon', marketplace: 'CA', label: 'Amazon CA', currency: 'CAD' },
];
var ADS_CHANNEL_PAGES = [
  { id: 'dashboard', icon: 'bar-chart-3', label: 'Dashboard' },
  { id: 'campaigns', icon: 'list', label: 'Campaigns' },
];
function adsChannelIcon(platform) { return platform === 'walmart' ? 'store' : 'shopping-bag'; }

function adsPathToPage(pathname) {
  var m = pathname.match(/^\/([a-z]{2,12})(?:\/(campaigns)|\/campaign\/(\d+))?\/?$/i);
  if (m && m[1]) {
    var ch = m[1].toLowerCase();
    if (m[3]) return { ch: ch, page: 'campaign-detail', arg: parseInt(m[3]) };
    if (m[2]) return { ch: ch, page: 'campaigns', arg: null };
    return { ch: ch, page: 'dashboard', arg: null };
  }
  // Legacy unprefixed paths from before channel routing → default channel
  var lm = pathname.match(/^\/campaign\/(\d+)$/);
  if (lm) return { ch: 'us', page: 'campaign-detail', arg: parseInt(lm[1]) };
  if (pathname === '/campaigns') return { ch: 'us', page: 'campaigns', arg: null };
  return { ch: 'us', page: 'dashboard', arg: null };
}
function adsPageToPath(page, arg, ch) {
  var base = '/' + (ch || 'us');
  if (page === 'campaign-detail') return base + '/campaign/' + arg;
  if (page === 'campaigns') return base + '/campaigns';
  return base;
}

// Logged-out visitors are redirected to the main app's login with a returnTo back to
// this exact URL (LoginPage honors allowlisted ads.atharvatoolz.com returns) — one
// canonical sign-in for the whole platform.
function adsRedirectToLogin() {
  var target = MAIN_APP_URL + '/login?returnTo=' + encodeURIComponent(window.location.href);
  try { window.location.replace(target); } catch (e) { window.location.href = target; }
}


function AdsApp() {
  var _a = useState('loading'); var auth = _a[0], setAuth = _a[1]; // loading | ok | anon | denied
  var _u = useState(null); var user = _u[0], setUser = _u[1];
  var initial = adsPathToPage(window.location.pathname);
  var _p = useState(initial.page); var page = _p[0], setPage = _p[1];
  var _g = useState(initial.arg); var pageArg = _g[0], setPageArg = _g[1];
  var _ch = useState(initial.ch); var chSlug = _ch[0], setChSlug = _ch[1];
  var _cs = useState(ADS_DEFAULT_CHANNELS); var channels = _cs[0], setChannels = _cs[1];
  var _dy = useState(30); var days = _dy[0], setDays = _dy[1];
  var _lg = useState(null); var logoUrl = _lg[0], setLogoUrl = _lg[1];
  var _th = useState('dark'); var theme = _th[0], setTheme = _th[1];
  // Per-channel group expansion (default: all open), persisted per-origin
  var _sg = useState(function() {
    try { return JSON.parse(localStorage.getItem('ads_sidebar_groups') || '{}'); } catch (e) { return {}; }
  });
  var sideGroups = _sg[0], setSideGroups = _sg[1];
  var toggleGroup = function(slug) {
    setSideGroups(function(prev) {
      var n = Object.assign({}, prev);
      n[slug] = prev[slug] === false; // undefined (default open) → false; false → true
      try { localStorage.setItem('ads_sidebar_groups', JSON.stringify(n)); } catch (e) {}
      return n;
    });
  };
  // Pin state persists per-origin under the same key name the main app uses
  var _sp = useState(function() {
    try { var sc = localStorage.getItem('sidebar_collapsed'); return sc === null ? true : sc !== 'true'; } catch (e) { return true; }
  });
  var sidePinned = _sp[0], setSidePinned = _sp[1];
  var _sh = useState(false); var sideHover = _sh[0], setSideHover = _sh[1];
  var sideHoverTimer = useRef(null);
  // Phones / tablets (< 900 layout px): top bar + hamburger drawer instead of the sidebar (9-27)
  var viewport = useViewport();
  var toggleSidebar = function() {
    var pinned = !sidePinned;
    setSidePinned(pinned); setSideHover(false);
    if (window.saveSidebarPref) window.saveSidebarPref(!pinned);
  };

  var navigate = function(pg, arg, ch) {
    ch = ch || chSlug;
    setPage(pg); setPageArg(arg || null); setChSlug(ch);
    try { history.pushState({}, '', adsPageToPath(pg, arg, ch)); } catch (e) {}
  };

  useEffect(function() {
    var onPop = function() {
      var r = adsPathToPage(window.location.pathname);
      setPage(r.page); setPageArg(r.arg); setChSlug(r.ch);
    };
    window.addEventListener('popstate', onPop);
    return function() { window.removeEventListener('popstate', onPop); };
  }, []);

  // Raw fetch (NOT api()) — api() reloads the page on 401, which would loop for
  // anonymous visitors on this host. Anonymous → straight to the main app's login
  // with returnTo back here ('anon' state just renders the redirect splash).
  // ONLY a real 401 bounces to login; transient failures (refresh racing a pm2
  // reload, network blip, 5xx) retry twice first (mirrors the ship shell, 8-11).
  var _bootTries = { n: 0 };
  var bootAuth = function() {
    fetch('/api/auth/me', { credentials: 'include' })
      .then(function(r) {
        if (r.status === 401) return null;
        if (!r.ok) throw new Error('auth probe ' + r.status);
        return r.json();
      })
      .then(function(u) {
        if (!u || !u.id) { setAuth('anon'); adsRedirectToLogin(); return; }
        setUser(u);
        if (!hasPermission(u, 'advertising', 'read')) { setAuth('denied'); return; }
        setAuth('ok');
        // Theme preference is account-wide (same /api/auth/preferences as the main app)
        fetch('/api/auth/preferences', { credentials: 'include' })
          .then(function(r) { return r.ok ? r.json() : {}; })
          .then(function(prefs) {
            if (prefs && prefs.theme && prefs.theme !== 'dark') { setThemeColors(prefs.theme); setTheme(prefs.theme); }
            if (window.I18N) window.I18N.setLanguage(prefs && prefs.language);
            if (prefs && typeof prefs.sidebar_collapsed === 'boolean') {
              try { localStorage.setItem('sidebar_collapsed', String(prefs.sidebar_collapsed)); } catch (e) {}
              setSidePinned(!prefs.sidebar_collapsed);
            }
          }).catch(function() {});
        // Enabled channels drive the sidebar; fall back to the US+CA defaults on error
        api('/advertising/channels').then(function(r) {
          var list = (r && r.channels && r.channels.length) ? r.channels : ADS_DEFAULT_CHANNELS;
          setChannels(list);
          var current = adsPathToPage(window.location.pathname);
          if (!list.some(function(c) { return c.slug === current.ch; })) {
            // URL points at a channel that isn't enabled → first enabled channel's dashboard
            setChSlug(list[0].slug); setPage('dashboard'); setPageArg(null);
            try { history.replaceState({}, '', adsPageToPath('dashboard', null, list[0].slug)); } catch (e) {}
          } else if (window.location.pathname === '/') {
            try { history.replaceState({}, '', adsPageToPath('dashboard', null, current.ch)); } catch (e) {}
          }
        }).catch(function() {});
      })
      .catch(function() {
        _bootTries.n++;
        if (_bootTries.n <= 2) setTimeout(bootAuth, 1200 * _bootTries.n);
        else { setAuth('anon'); adsRedirectToLogin(); }
      });
  };

  var toggleTheme = function() {
    var next = theme === 'dark' ? 'light' : 'dark';
    setThemeColors(next);
    setTheme(next);
    fetch('/api/auth/preferences', { method: 'PUT', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
      body: JSON.stringify({ theme: next }) }).catch(function() {});
  };
  useEffect(function() {
    bootAuth();
    fetch('/api/settings/logo').then(function(r) { if (r.ok) setLogoUrl('/api/settings/logo'); }).catch(function() {});
  }, []);

  var handleLogout = async function() {
    try { await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' }); } catch (e) {}
    if (window.setAuthToken) window.setAuthToken(null);
    setUser(null); setAuth('anon');
    adsRedirectToLogin();
  };

  if (auth === 'loading' || auth === 'anon') {
    return React.createElement('div', { style: { minHeight: 'calc(100vh * var(--invzoom, 0.8))', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 12 } },
      React.createElement(LIcon, { name: 'loader-2', size: 28, spin: true, style: { color: C.textMuted } }),
      auth === 'anon' && React.createElement('div', { style: { fontSize: 13, color: C.textMuted } }, 'Redirecting to sign-in…'));
  }
  if (auth === 'denied') {
    return React.createElement('div', { style: { minHeight: 'calc(100vh * var(--invzoom, 0.8))', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 } },
      React.createElement(Card, { style: { maxWidth: 440, width: '100%', padding: 32, textAlign: 'center' } },
        React.createElement(LIcon, { name: 'lock', size: 32, style: { color: C.warning, marginBottom: 12 } }),
        React.createElement('div', { style: { fontSize: 18, fontWeight: 600, color: C.text, marginBottom: 8 } }, 'No Advertising Access'),
        React.createElement('div', { style: { fontSize: 13, color: C.textMuted, lineHeight: 1.6, marginBottom: 16 } },
          'Your account (' + ((user && user.display_name) || '') + ') does not have the Advertising area enabled. Ask an admin to grant it under Settings > Users > Roles.'),
        React.createElement(Btn, { size: 'sm', onClick: handleLogout }, 'Sign in as someone else')));
  }

  // ─── Sidebar (same look + pin/unpin/group mechanics as the main app, channel-oriented) ───
  // Each enabled channel is a collapsible group whose children are its own pages.
  var channelGroup = function(ch, inDrawer) {
    var expanded = sideGroups[ch.slug] !== false; // default open
    var groupActive = chSlug === ch.slug;
    var header = React.createElement('button', {
      key: ch.slug + '_h',
      onClick: function() { toggleGroup(ch.slug); },
      className: 'side-nav-btn',
      style: SIDENAV.item(groupActive)
    },
      React.createElement('span', { style: { flex: 1 } }, ch.label), // no row icon (Anthony 2026-09-23)
      React.createElement('span', { style: SIDENAV.chevron(expanded) },
        React.createElement(LIcon, { name: 'chevron-down', size: 14 })));
    var childrenEl = expanded && React.createElement('div', { key: ch.slug + '_c', style: SIDENAV.childWrap(expanded) },
      ADS_CHANNEL_PAGES.map(function(p) {
        var childActive = chSlug === ch.slug && (page === p.id || (p.id === 'campaigns' && page === 'campaign-detail'));
        return React.createElement('a', {
          key: p.id,
          href: adsPageToPath(p.id, null, ch.slug),
          onClick: function(e) {
            if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return;
            e.preventDefault(); navigate(p.id, null, ch.slug);
            if (inDrawer) setSideHover(false);
          },
          className: 'side-nav-child',
          style: SIDENAV.child(childActive)
        }, p.label);
      }));
    return React.createElement(React.Fragment, { key: ch.slug }, header, childrenEl);
  };
  var sideBottomBtn = function(props2) {
    return React.createElement(props2.href ? 'a' : 'button', {
      key: props2.label,
      href: props2.href, target: props2.href ? '_blank' : undefined, rel: props2.href ? 'noopener' : undefined,
      onClick: props2.onClick, title: props2.iconOnly ? props2.label : undefined,
      className: 'side-bottom-btn',
      style: props2.iconOnly
        ? SIDENAV.railBottomBtn({ topGap: props2.topGap })
        : SIDENAV.bottomBtn({ topGap: props2.topGap })
    }, props2.iconOnly ? React.createElement(LIcon, { name: props2.icon, size: 16 }) // icon only on the rail (Anthony 2026-09-23)
      : props2.href ? React.createElement(React.Fragment, null, props2.label, React.createElement(LIcon, { name: 'arrow-up-right', size: 13, style: SIDENAV.newTab })) // a link opens a new tab: say so (Anthony 2026-10-13)
      : props2.label);
  };

  // Full sidebar content — shared by the pinned nav and the hover drawer
  // narrow = the phone/tablet drawer: same content minus the Pin button (no sidebar to pin)
  var sidebarInner = function(inDrawer, narrow) {
    return [
      React.createElement('div', { key: 'logo', style: SIDENAV.logoBox },
        logoUrl
          ? React.createElement('img', { src: logoUrl, alt: 'Logo', style: SIDENAV.logoImg })
          : React.createElement('div', { style: { fontWeight: 700, fontSize: 14, color: '#111827', display: 'flex', alignItems: 'center', gap: 6 } },
              React.createElement(LIcon, { name: 'megaphone', size: 15 }), ' Advertising')),
      React.createElement('div', { key: 'label', style: SIDENAV.sectionLabel }, 'Channels'),
      React.createElement('div', { key: 'items', style: SIDENAV.scroller },
        channels.map(function(ch) { return channelGroup(ch, inDrawer); }),
        React.createElement('div', { style: SIDENAV.divider }),
        sideBottomBtn({ href: MAIN_APP_URL, icon: 'arrow-up-right', label: 'AtharvaToolz' })),
      React.createElement('div', { key: 'bottom', style: SIDENAV.bottomWrap },
        sideBottomBtn({ onClick: toggleTheme, icon: theme === 'dark' ? 'sun' : 'moon', label: theme === 'dark' ? 'Light Mode' : 'Dark Mode' }),
        sideBottomBtn({ onClick: handleLogout, icon: 'log-out', label: 'Sign Out' }),
        !narrow && sideBottomBtn({ onClick: toggleSidebar, icon: 'pin', label: sidePinned ? 'Unpin' : 'Pin Open', topGap: true })),
    ];
  };

  var sidebar;
  if (viewport.isNarrow) {
    sidebar = null; // narrow: the NarrowNav top bar + drawer below replaces sidebar and rail
  } else if (sidePinned) {
    sidebar = React.createElement('nav', { style: SIDENAV.nav }, sidebarInner(false));
  } else {
    // Unpinned: 56px icon strip + hover drawer overlay (main-app mechanics)
    var hoverIn = function() { if (sideHoverTimer.current) clearTimeout(sideHoverTimer.current); setSideHover(true); };
    var hoverOut = function() { sideHoverTimer.current = setTimeout(function() { setSideHover(false); }, 300); };
    sidebar = React.createElement(React.Fragment, null,
      React.createElement('nav', {
        onMouseEnter: hoverIn, onMouseLeave: hoverOut,
        style: SIDENAV.rail
      },
        React.createElement('div', { style: SIDENAV.railLogoBox },
          React.createElement('img', { src: '/api/settings/logo-icon', alt: '', style: SIDENAV.railLogoImg, onError: function(e) { e.target.style.display = 'none'; } })),
        React.createElement('div', { style: SIDENAV.railList },
          channels.map(function(ch) {
            var active = chSlug === ch.slug;
            return React.createElement('a', {
              key: ch.slug,
              href: adsPageToPath('dashboard', null, ch.slug),
              title: ch.label,
              onClick: function(e) { if (e.ctrlKey || e.metaKey || e.shiftKey || e.button === 1) return; e.preventDefault(); navigate('dashboard', null, ch.slug); },
              className: 'side-nav-btn',
              style: Object.assign({}, SIDENAV.railItem(active), { flexDirection: 'column', gap: 1 })
            },
              React.createElement(LIcon, { name: adsChannelIcon(ch.platform), size: 16 }),
              React.createElement('span', { style: { fontSize: 8.5, fontWeight: 700, letterSpacing: 0.4 } }, (ch.marketplace || ch.slug).toUpperCase()));
          }),
          React.createElement('div', { style: { borderTop: '1px solid #E5E7EB', margin: '8px 0' } }),
          sideBottomBtn({ href: MAIN_APP_URL, icon: 'arrow-up-right', label: 'AtharvaToolz', iconOnly: true })),
        React.createElement('div', { style: SIDENAV.railBottomWrap },
          sideBottomBtn({ onClick: toggleTheme, icon: theme === 'dark' ? 'sun' : 'moon', label: theme === 'dark' ? 'Light Mode' : 'Dark Mode', iconOnly: true }),
          sideBottomBtn({ onClick: handleLogout, icon: 'log-out', label: 'Sign Out', iconOnly: true }),
          sideBottomBtn({ onClick: toggleSidebar, icon: 'pin', label: 'Pin Open', iconOnly: true }))),
      React.createElement('div', {
        onMouseEnter: hoverIn, onMouseLeave: hoverOut,
        style: SIDENAV.drawer(sideHover)
      }, sidebarInner(true)));
  }

  var activeChannel = channels.find(function(c) { return c.slug === chSlug; }) || channels[0] || ADS_DEFAULT_CHANNELS[0];
  var mp = activeChannel.marketplace;
  var pageTitle = activeChannel.label + ' · ' + (page === 'campaigns' ? 'Campaigns' : page === 'campaign-detail' ? 'Campaign' : 'Dashboard');

  if (viewport.isNarrow) {
    // Narrow: 48px top bar (hamburger + title), full-width content with 12px sides
    return React.createElement('div', { style: { minHeight: 'calc(100vh * var(--invzoom, 0.8))', display: 'flex', flexDirection: 'column', background: C.bg } },
      React.createElement(window.NarrowNav, { navKey: page + ':' + (pageArg || '') + ':' + chSlug, title: pageTitle, drawer: sidebarInner(true, true) }),
      React.createElement('div', { style: { flex: 1, minWidth: 0, width: '100%', padding: 12, boxSizing: 'border-box', overflowX: 'hidden' } },
        React.createElement('div', { style: { display: 'flex', justifyContent: 'flex-end', alignItems: 'center', marginBottom: 12, flexWrap: 'wrap', gap: 10 } },
          React.createElement(AdsControls, { days: days, setDays: setDays })),
        React.createElement(window.ErrorBoundary, { key: 'eb:' + page + ':' + activeChannel.slug, area: 'ads:' + page },
          page === 'campaigns' ? React.createElement(AdsCampaignsPage, { key: 'cl-' + activeChannel.slug, onNavigate: navigate, mp: mp, chSlug: activeChannel.slug, days: days, theme: theme })
          : page === 'campaign-detail' ? React.createElement(AdsCampaignDetailPage, { key: 'cmp-' + activeChannel.slug + '-' + pageArg, campaignId: pageArg, onNavigate: navigate, mp: mp, chSlug: activeChannel.slug, days: days, theme: theme })
          : React.createElement(AdsDashboardPage, { key: 'db-' + activeChannel.slug, onNavigate: navigate, mp: mp, chSlug: activeChannel.slug, days: days, theme: theme }))),
      user && hasPermission(user, 'chat', 'read') && window.ChatWidget
        ? React.createElement(window.ChatWidget, { onOpenFull: function() { window.open(MAIN_APP_URL + '/chat', '_blank'); } }) : null
    );
  }

  return React.createElement('div', { style: { minHeight: 'calc(100vh * var(--invzoom, 0.8))', display: 'flex', background: C.bg } },
    sidebar,
    React.createElement('div', { style: { flex: 1, minWidth: 0, padding: 24, maxWidth: 1320, width: '100%', margin: '0 auto', overflowX: 'hidden' } },
      // Main-app page header convention: h2 + right-aligned controls, no separate bar
      React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, flexWrap: 'wrap', gap: 10 } },
        React.createElement('h2', { style: { margin: 0, fontSize: 20, fontWeight: 600, color: C.text } }, pageTitle),
        React.createElement(AdsControls, { days: days, setDays: setDays })),
      // Crash containment: mirror the main app/ship shell — page errors render the
      // boundary's retry card and log the React componentStack instead of white-screening.
      React.createElement(window.ErrorBoundary, { key: 'eb:' + page + ':' + activeChannel.slug, area: 'ads:' + page },
        page === 'campaigns' ? React.createElement(AdsCampaignsPage, { key: 'cl-' + activeChannel.slug, onNavigate: navigate, mp: mp, chSlug: activeChannel.slug, days: days, theme: theme })
        : page === 'campaign-detail' ? React.createElement(AdsCampaignDetailPage, { key: 'cmp-' + activeChannel.slug + '-' + pageArg, campaignId: pageArg, onNavigate: navigate, mp: mp, chSlug: activeChannel.slug, days: days, theme: theme })
        : React.createElement(AdsDashboardPage, { key: 'db-' + activeChannel.slug, onNavigate: navigate, mp: mp, chSlug: activeChannel.slug, days: days, theme: theme }))),
    // Ask-AI floating widget — same 'chat' permission gate as the main app;
    // full-chat opens the main app in a new tab (no chat page in this shell)
    user && hasPermission(user, 'chat', 'read') && window.ChatWidget
      ? React.createElement(window.ChatWidget, { onOpenFull: function() { window.open(MAIN_APP_URL + '/chat', '_blank'); } }) : null
  );
}

window.AdsApp = AdsApp;
window.AdsDashboardPage = AdsDashboardPage;
window.AdsCampaignsPage = AdsCampaignsPage;
window.AdsCampaignDetailPage = AdsCampaignDetailPage;

// Self-mount when loaded on the ads subdomain. Detection is hostname-based because the
// app's CSP has no 'unsafe-inline' — an inline <script> flag in ads.html never executes.
// The main bundle never includes this file, but the guard keeps a future inclusion from
// double-mounting.
if ((window.ADS_APP || /^ads\./i.test(window.location.hostname)) && document.getElementById('root')) {
  ReactDOM.render(React.createElement(React.Fragment, null, React.createElement(AdsApp), React.createElement(AppDialogHost)), document.getElementById('root')); // AppDialogHost: in-app confirm / prompt / alert (2026-09-29)
}
